/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ==========================================================================
   Google Ads Click Tracking via GTM dataLayer
   --------------------------------------------------------------------------
   Pushes custom events to window.dataLayer when users click tracked buttons.
   GTM picks up these events for Google Ads conversion tracking.

   Tracked buttons:
   1. "View in your room" AR buttons  (#ar-link, #ar-qr-trigger, #ar-prompt-link)

   Save-to-list ("wishlist") events are NOT delegated here: the native
   saved-rugs module below owns its buttons and pushes add_to_wishlist_click /
   remove_from_wishlist_click directly from its save/remove handlers. The
   in-house offer widget likewise pushes its own make_an_offer_* events.

   Uses event delegation on document so buttons revealed or injected after
   load (the AR snippet unhides its own) are still caught.
   ========================================================================== */

(function () {
  'use strict';

  function trackClick(eventName, buttonType) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      button_type: buttonType,
      page_url: window.location.href,
      page_path: window.location.pathname
    });
  }

  document.addEventListener('click', function (e) {
    var target = e.target;

    // AR: Mobile native link
    if (target.closest('#ar-link')) {
      trackClick('ar_view_in_room', 'ar_mobile');
      return;
    }

    // AR: Desktop QR trigger
    if (target.closest('#ar-qr-trigger')) {
      trackClick('ar_view_in_room_qr', 'ar_desktop_qr');
      return;
    }

    // AR: Modal prompt link
    if (target.closest('#ar-prompt-link')) {
      trackClick('ar_view_in_room', 'ar_mobile_prompt');
    }
  }, true);
})();

/* ==========================================================================
   Storefront telemetry → Grafana Cloud (Faro collector)
   --------------------------------------------------------------------------
   Ships offer-form field-census data and frontend JS errors to the same
   Grafana Cloud stack the backend sync job logs to, clearly delineated by
   the Faro app name ('storefront' vs the backend's Promtail streams).

   The collector URL below belongs to the 'storefront' Frontend
   Observability app in the rugstyling Grafana Cloud stack (created
   2026-07-15; allowed origins: rugstyling.com, www.rugstyling.com,
   eliko-jr.myshopify.com). It is public-by-design (like a Sentry DSN) —
   it can only ingest, never read. window.__FARO_COLLECTOR_URL (set before
   this script loads) overrides the constant; tests use that to point at a
   mock, and local/dev hostnames are hard-disabled below so CI and theme
   dev can never post real telemetry.

   Collector contract (verified by POSTing an envelope — collector answers
   202 on success, 400 with a reason string otherwise):
     - X-Faro-Session-Id header is REQUIRED (hence fetch everywhere;
       sendBeacon cannot send headers, so unload uses fetch keepalive)
     - meta.sdk { name, version } is REQUIRED

   What we send (Faro collector v1 envelope: { meta, events, exceptions }):
     - mao_open           the offer form was opened (session-correlated)
     - mao_submit_attempt Send-offer clicked + census of which fields were
                          filled vs empty at that moment
     - mao_outcome        success/error outcome mirrored from the GA
                          funnel, with status/reason
     - mao_abandon        form opened but page left / modal closed with no
                          outcome — census shows where people stall
     - concierge_outcome  Rug Finder request outcomes (submit / results_shown /
                          no_results / unavailable / error, with stage, reason
                          and HTTP status; unavailable is the non-US 403) mirrored
                          from the GA funnel. Its GA4 path is analytics-only,
                          so this is the only concierge signal anything can
                          alert on: a broken App Proxy secret or a service
                          outage shows up here as an error spike.
     - js_error           window.onerror / unhandledrejection (theme JS is
                          unminified, so raw stacks are readable)

   PRIVACY: census records ONLY per-field filled/empty booleans keyed by a
   normalized field label. Field VALUES (name, email, phone, message, offer
   amount) must never be read into a telemetry payload.

   Batching: events queue and flush every 5s / at 20 events via fetch, and
   on pagehide via fetch(keepalive) so abandonment events survive
   navigation. Telemetry must never throw into the page.
   ========================================================================== */
(function () {
  'use strict';

  var FARO_COLLECTOR_URL =
    'https://faro-collector-prod-us-east-2.grafana.net/collect/9e6b42fc2195cca97c49ec6d8d0e7c15';
  var APP_NAME = 'storefront';
  var APP_ENV = 'production';
  // Test hook: fixtures shrink the flush interval so specs don't wait 5s.
  // Must be set before this script loads; production never sets it.
  var FLUSH_INTERVAL_MS =
    typeof window.__FARO_FLUSH_MS === 'number' ? window.__FARO_FLUSH_MS : 5000;
  var FLUSH_AT = 20;

  // The in-house offer widget's DOM (snippets/make-an-offer-widget.liquid).
  // The event names keep their mao_ prefix from the Magical Make an Offer
  // app this census was built for, so the Grafana funnel reads one series
  // across the cutover.
  var SUBMIT_SELECTOR = '.rs-offer-submit';
  var OPENER_SELECTOR = '.rs-offer-badge';
  var CLOSE_SELECTOR = '.rs-offer-close';
  var OVERLAY_SELECTOR = '.rs-offer-overlay';
  var CONTAINER_SELECTOR = '.rs-offer-modal';

  var collectorUrl =
    typeof window.__FARO_COLLECTOR_URL === 'string'
      ? window.__FARO_COLLECTOR_URL
      : FARO_COLLECTOR_URL;
  // Local fixtures and `shopify theme dev` must never post real telemetry:
  // on local hostnames only the explicit test override may enable it.
  if (
    collectorUrl === FARO_COLLECTOR_URL &&
    /^(localhost|127\.|0\.0\.0\.0)/.test(window.location.hostname)
  ) {
    collectorUrl = '';
  }
  if (!collectorUrl) return; // telemetry fully disabled

  var sessionId = 's-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  var queue = { events: [], exceptions: [] };
  var flushTimer = null;

  function meta() {
    return {
      // Required by the collector — requests without sdk meta are 400'd.
      sdk: { name: 'rugstyling-storefront-telemetry', version: '1.0.0' },
      app: { name: APP_NAME, environment: APP_ENV },
      session: { id: sessionId },
      page: { url: window.location.href }
    };
  }

  function flush(unloading) {
    if (!queue.events.length && !queue.exceptions.length) return;
    var body = { meta: meta() };
    if (queue.events.length) body.events = queue.events;
    if (queue.exceptions.length) body.exceptions = queue.exceptions;
    queue = { events: [], exceptions: [] };
    var json = JSON.stringify(body);
    try {
      // Always fetch, never sendBeacon: the collector requires the
      // X-Faro-Session-Id header, which beacons cannot carry. keepalive
      // is set only on unload — keepalive fetches bypass Playwright route
      // interception, so regular flushes must stay plain.
      fetch(collectorUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Faro-Session-Id': sessionId
        },
        body: json,
        keepalive: !!unloading
      }).catch(function () {}); // telemetry loss is acceptable, breakage is not
    } catch (err) {
      // Never let telemetry throw into the page.
    }
  }

  function scheduleFlush() {
    if (queue.events.length + queue.exceptions.length >= FLUSH_AT) {
      flush(false);
      return;
    }
    if (flushTimer) return;
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush(false);
    }, FLUSH_INTERVAL_MS);
  }

  // All attribute values must be strings for the Faro events API. `domain`
  // separates the features sharing this collector so a Grafana query can
  // match one without catching the other; it defaults to 'mao', the offer
  // funnel this module started with.
  function pushTelemetryEvent(name, attributes, domain) {
    var attrs = { page_path: window.location.pathname };
    if (attributes) {
      for (var k in attributes) {
        if (Object.prototype.hasOwnProperty.call(attributes, k)) {
          attrs[k] = String(attributes[k]);
        }
      }
    }
    queue.events.push({
      name: name,
      domain: domain || 'mao',
      attributes: attrs,
      timestamp: new Date().toISOString()
    });
    scheduleFlush();
  }

  // ── field census (booleans only — never values) ────────
  function normalizeFieldKey(el, index) {
    var raw = el.getAttribute('name') || el.getAttribute('placeholder') || '';
    if (!raw && el.id) {
      var lf = document.querySelector('label[for="' + el.id + '"]');
      if (lf) raw = lf.textContent || '';
    }
    if (!raw) {
      var wrap = el.closest && el.closest('label');
      if (wrap) raw = wrap.textContent || '';
    }
    raw = raw.replace(/\*/g, '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return raw ? raw.slice(0, 40) : 'field_' + index;
  }

  function fieldCensus(container) {
    var census = {};
    var i = 0;
    var elements = container.querySelectorAll('input, textarea, select');
    for (var n = 0; n < elements.length; n++) {
      var el = elements[n];
      var type = (el.type || '').toLowerCase();
      if (type === 'hidden' || type === 'submit' || type === 'button') continue;
      var filled;
      if (type === 'checkbox' || type === 'radio') filled = el.checked;
      else filled = !!(el.value || '').trim(); // read length only, never store the value
      census['field_' + normalizeFieldKey(el, i)] = filled ? 'filled' : 'empty';
      i++;
    }
    census.fields_total = String(i);
    return census;
  }

  function visibleContainer() {
    var candidates = document.querySelectorAll(CONTAINER_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].offsetWidth || candidates[i].offsetHeight) return candidates[i];
    }
    return candidates[0] || null;
  }

  function openOverlayEl() {
    return document.querySelector(OVERLAY_SELECTOR + ':not([hidden])');
  }

  // ── Offer funnel instrumentation ───────────────────────
  var formOpenedAt = 0;
  var outcomeSeen = false;
  var abandonSent = false;

  function censusOf(el) {
    var container = (el && el.closest && el.closest(CONTAINER_SELECTOR)) || visibleContainer();
    return container ? fieldCensus(container) : { fields_total: '0' };
  }

  function sendAbandon(el) {
    if (!formOpenedAt || outcomeSeen || abandonSent) return;
    abandonSent = true;
    var attrs = censusOf(el);
    attrs.open_ms = String(Math.round(performance.now() - formOpenedAt));
    pushTelemetryEvent('mao_abandon', attrs);
  }

  function markFormOpened() {
    // A live offer's badge opens its status drawer, not the form: only a
    // form that actually opened starts a funnel.
    if (!openOverlayEl()) return;
    formOpenedAt = performance.now();
    outcomeSeen = false;
    abandonSent = false;
    pushTelemetryEvent('mao_open', {});
  }

  // Capture phase, so each close path is seen while the overlay is still up.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    try {
      if (target.closest(SUBMIT_SELECTOR)) {
        pushTelemetryEvent('mao_submit_attempt', censusOf(target));
      } else if (target.closest(CLOSE_SELECTOR)) {
        sendAbandon(target);
        flush(false);
      } else if (target.matches && target.matches(OVERLAY_SELECTOR)) {
        // A click on the backdrop itself closes the overlay.
        sendAbandon(target.querySelector(CONTAINER_SELECTOR));
        flush(false);
      }
    } catch (err) {
      // Telemetry must never break the page.
    }
  }, true);

  // Bubble phase, so the widget's badge handler has already opened the
  // overlay. Same dispatch, not a 0 ms timer: under load a timer can fire
  // after the next pagehide or Escape, which then finds no funnel to abandon.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    try {
      if (target.closest(OPENER_SELECTOR)) markFormOpened();
    } catch (err) {
      // Telemetry must never break the page.
    }
  });

  // Escape closes the overlay too. This listener is registered before the
  // widget's focus trap, so it runs while the overlay is still visible.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    try {
      var overlay = openOverlayEl();
      if (!overlay) return;
      sendAbandon(overlay.querySelector(CONTAINER_SELECTOR));
      flush(false);
    } catch (err) {
      // Telemetry must never break the page.
    }
  }, true);

  // The Rug Finder concierge posts its funnel to this same dataLayer. Mirror
  // the request-level outcomes so a failure spike is visible (and alertable)
  // in Grafana: attempts and successes are the denominator that tells "the
  // service is down" apart from "nobody used it today".
  // Zero rugs and the non-US refusal are healthy answers with outcomes of
  // their own, so error panels and alerts count only real failures.
  var CONCIERGE_OUTCOMES = {
    concierge_submit: 'submit',
    concierge_results_shown: 'results_shown',
    concierge_error: 'error',
    concierge_no_results: 'no_results',
    concierge_unavailable: 'unavailable'
  };

  // Attributes are an explicit allowlist, never a spread of the event: the
  // concierge funnel's PII rule keeps notes, emails, photos and share URLs off
  // the dataLayer, and nothing here may widen what leaves the page.
  function mirrorConciergeEvent(ev) {
    // hasOwnProperty, not a bare lookup: an event named 'constructor' or
    // 'toString' would otherwise inherit a truthy value from Object.prototype
    // and mirror a junk outcome.
    if (!Object.prototype.hasOwnProperty.call(CONCIERGE_OUTCOMES, ev.event)) return;
    pushTelemetryEvent('concierge_outcome', {
      outcome: CONCIERGE_OUTCOMES[ev.event],
      stage: ev.stage || '',
      error_reason: ev.reason || '',
      status_code: ev.status_code === undefined || ev.status_code === null ? '' : ev.status_code
    }, 'concierge');
  }

  // Mirror the GA funnel outcomes (pushed by the in-house offer widget
  // below) so Grafana can join census → outcome without GTM in the middle.
  // We watch dataLayer.push non-invasively via the same wrapping trick GTM
  // uses.
  (function watchDataLayer() {
    window.dataLayer = window.dataLayer || [];
    var origPush = window.dataLayer.push;
    window.dataLayer.push = function () {
      try {
        for (var i = 0; i < arguments.length; i++) {
          var ev = arguments[i];
          if (ev && typeof ev.event === 'string' && ev.event.indexOf('make_an_offer_') === 0) {
            if (ev.event === 'make_an_offer_success' || ev.event === 'make_an_offer_error') {
              outcomeSeen = true;
              pushTelemetryEvent('mao_outcome', {
                outcome: ev.event.replace('make_an_offer_', ''),
                offer_status: ev.offer_status || '',
                offer_handle: ev.offer_handle || '',
                error_reason: ev.error_reason || ''
              });
            }
          } else if (ev && typeof ev.event === 'string') {
            mirrorConciergeEvent(ev);
          }
        }
      } catch (err) {
        // Never interfere with GTM's own processing.
      }
      return typeof origPush === 'function'
        ? origPush.apply(window.dataLayer, arguments)
        : Array.prototype.push.apply(window.dataLayer, arguments);
    };
  })();

  // ── JS error capture (the "do we need Sentry?" answer) ─
  function pushException(type, message, stack) {
    queue.exceptions.push({
      type: type || 'Error',
      value: String(message || '').slice(0, 500),
      stacktrace: stack ? { frames: [{ function: String(stack).slice(0, 2000) }] } : undefined,
      timestamp: new Date().toISOString()
    });
    scheduleFlush();
  }

  window.addEventListener('error', function (e) {
    if (!e) return;
    pushException(
      (e.error && e.error.name) || 'Error',
      e.message,
      e.error && e.error.stack ? e.error.stack : (e.filename || '') + ':' + (e.lineno || 0)
    );
  });

  window.addEventListener('unhandledrejection', function (e) {
    var reason = e && e.reason;
    pushException(
      (reason && reason.name) || 'UnhandledRejection',
      reason && reason.message ? reason.message : String(reason),
      reason && reason.stack
    );
  });

  // Abandonment + final flush when the page goes away.
  window.addEventListener('pagehide', function () {
    sendAbandon(null);
    flush(true);
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush(true);
  });
})();

/* ==========================================================================
   Fire a Tidio "engaged product view" event after 8s on a product page
   --------------------------------------------------------------------------
   The "Product page engagement" Flow used to start on every /products/
   page view ("Visitor opens a specific page" trigger). Tidio's flow quota
   counts unique visitors whose flows START, so that trigger burned quota
   on every browser at 0% engagement. The Flow's trigger is now "New event"
   with the exact name below (typed into the event-name field, same setup
   as the offer widget's offer_submitted), and we only emit the event once
   the visitor has spent 8 seconds of *visible* time on a product page —
   tab-hidden time is excluded via the Page Visibility API, so parking the
   tab in the background doesn't count as engagement.

   We do NOT call api.open(): the customer didn't ask for chat, so the
   Flow's own message is the only surface. The event
   fires at most once per TAB session (sessionStorage guard — sessionStorage
   is per-tab for newly opened tabs) so browsing several rugs in one tab
   doesn't restart the Flow on every product page.

   IMPORTANT — dashboard setup required: a shopper who opens several rugs
   in separate tabs gets a fresh sessionStorage per tab, so the JS guard
   alone cannot cap cross-tab repeats. The Flow trigger's limitation
   ("send once per 24 hours" on the New event trigger) is the visitor-level
   backstop and MUST stay set.
   ========================================================================== */
(function () {
  'use strict';

  // Custom event the "Product page engagement" Flow keys off. Configure
  // the Flow trigger as "New event" with this exact name.
  var TIDIO_EVENT = 'engaged_product_view';
  // Visible time on the page before the visitor counts as engaged.
  var DWELL_MS = 8000;
  var SESSION_KEY = 'tidio-engaged-product-view';

  // Same URL rule the old Flow trigger used ("address contains /products/").
  if (window.location.pathname.indexOf('/products/') === -1) return;

  // sessionStorage can throw (storage disabled / private browsing); treat
  // failures as "not fired yet" — worst case the Flow's own trigger
  // limitation is the backstop against repeats.
  function alreadyFired() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (err) { return false; }
  }
  function markFired() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (err) { /* noop */ }
  }

  if (alreadyFired()) return;

  // Tidio loads async via the storefront app embed, so the 8s mark can beat
  // the widget install.
  function whenTidioReady(fn) {
    var api = window.tidioChatApi;
    if (api && typeof api.track === 'function') {
      fn(api);
      return;
    }
    document.addEventListener('tidioChat-ready', function ready() {
      document.removeEventListener('tidioChat-ready', ready);
      if (window.tidioChatApi) fn(window.tidioChatApi);
    });
  }

  var remaining = DWELL_MS;
  var startedAt = null; // Date.now() of the current visible stretch
  var timer = null;

  function fire() {
    timer = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    whenTidioReady(function (api) {
      try {
        // Mark only at delivery: if Tidio hasn't installed by the 8s mark
        // the queued listener dies with the page, and the next product
        // page retries — marking earlier would burn the whole tab session
        // on an event that never reached Tidio (ad blockers, slow embeds).
        markFired();
        api.track(TIDIO_EVENT);
      } catch (err) {
        // Tidio API errors must never surface on the storefront.
      }
    });
  }

  function pause() {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
    remaining -= Date.now() - startedAt;
  }

  function resume() {
    if (timer !== null) return;
    if (remaining <= 0) { fire(); return; }
    startedAt = Date.now();
    timer = setTimeout(fire, remaining);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') resume();
    else pause();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (document.visibilityState === 'visible') resume();
})();

/* ==========================================================================
   PDP info-column injections: see-similar link, sold-out state, hash scroll
   --------------------------------------------------------------------------
   Product sections re-render (variant changes, quick buy) and app widgets
   land late, so we run on initial load, schedule retries to catch late
   arrivals, and use a MutationObserver as a safety net for re-renders.
   ========================================================================== */
(function () {
  'use strict';

  // Inject the subtle "see similar rugs" link, which smooth-scrolls to "Others
  // you might love". It is a fallback for product surfaces that render no Rug
  // Finder entry block (featured-product sections); PDPs get the entry's
  // "Similar rugs" button instead, sold and On Hold rugs included.
  function findSimilarSection() {
    return (
      document.getElementById('shopify-section-custom_liquid_adW7fF') ||
      Array.from(document.querySelectorAll('h1, h2, h3'))
        .find(function (h) { return /others you might love/i.test(h.textContent || ''); }) ||
      document.querySelector('[id*="similar"], [class*="similar"]')
    );
  }

  function scrollToSimilarSection() {
    var section = findSimilarSection();
    if (!section) return false;
    var rect = section.getBoundingClientRect();
    window.scrollTo({ top: window.scrollY + rect.top - 24, behavior: 'smooth' });
    return true;
  }

  function makeFindSimilarBtn() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'find-similar-link find-similar-link--subtle';
    btn.setAttribute('aria-label', 'See similar rugs');
    btn.innerHTML =
      '<span class="find-similar-link__label">See similar rugs</span>' +
      '<span class="find-similar-link__arrow" aria-hidden="true">↓</span>';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      scrollToSimilarSection();
    });
    return btn;
  }

  function ensureFindSimilarButtons() {
    // Subtle link in the product meta (in-stock state) — sits right after the
    // SKU line, at the top of the info column where eyes scan first. Keeps
    // the buy stack and trust badges entirely uninterrupted; uncertain users
    // see a discoverable escape hatch BEFORE engaging with CTA decisions,
    // which is when ambivalence is highest.
    // The Rug Finder entry block absorbs this link's job with its "Similar
    // rugs" button, on every PDP state, so skip the link when it's present.
    if (!document.querySelector('.find-similar-link--subtle') && !document.querySelector('.rug-finder-entry')) {
      // Insert INSIDE the SKU line so SKU and link sit on the same row
      // (CSS makes .product-meta__reference a flex space-between container).
      // Falls back to the bottom of .product-meta if no SKU element exists.
      var skuLine = document.querySelector('.product-meta .product-meta__reference');
      if (skuLine) {
        skuLine.appendChild(makeFindSimilarBtn());
      } else {
        var meta = document.querySelector('.product-meta');
        if (meta) meta.appendChild(makeFindSimilarBtn());
      }
    }
  }

  // When the product is sold out (or the variant is unavailable), the Add to
  // Cart button becomes disabled. Mirror that state as a single body class so
  // CSS owns what the sold state changes: the subtle link's visibility on the
  // featured-product surfaces that still get it. PDPs need no JS signal at
  // all: the entry's --gone class and the .rug-ask block are server-rendered,
  // so the CSS there is right on the first frame.
  function syncSoldOutState() {
    var addBtn = document.querySelector('#AddToCart, .product-form__add-button');
    if (!addBtn) return;
    // A button mid-request tells us nothing about availability: the theme
    // disables it for the length of the /cart/add.js fetch and marks it
    // aria-busy while it does, so reading `disabled` then would flag a rug
    // the shopper is actively buying as sold out.
    var soldOut = addBtn.getAttribute('aria-busy') !== 'true'
      && (addBtn.disabled
        || addBtn.getAttribute('aria-disabled') === 'true'
        || /sold\s*out|unavailable/i.test(addBtn.textContent || ''));
    // Only mutate when state actually needs to change — every classList write
    // fires the MutationObserver below, and unconditional writes create a
    // feedback loop that hangs the page.
    if (document.body.classList.contains('is-product-sold-out') !== soldOut) {
      document.body.classList.toggle('is-product-sold-out', soldOut);
    }
  }

  // When a user lands on a PDP via a collection-tile "Find similar" link
  // (href ends in #similar-rugs), smooth-scroll to the similar-rugs section
  // so they skip past the sold-out buy stack and go straight to browsing.
  // Retry a few times because the similar-rugs section hydrates from refs
  // after initial render; scrolling too early lands above the section.
  //
  // Given up on once the last scheduled retry has come and gone. A withheld
  // rug's tile carries this hash like any other sold tile, but its PDP is the
  // relay page, where there is no section to scroll to because the grid of
  // alternatives IS the page. Without a deadline the lookup below - a
  // getElementById, a querySelectorAll('h1, h2, h3') walked with Array.find,
  // and a querySelector - would re-run on every mutation of the document, for
  // as long as the page stays open, hunting a section that is never coming.
  var SIMILAR_HASH_DEADLINE = Date.now() + 8000;
  var similarHashSettled = false;
  function maybeScrollToSimilarFromHash() {
    if (similarHashSettled) return;
    if (window.location.hash !== '#similar-rugs') return;
    if (Date.now() > SIMILAR_HASH_DEADLINE) {
      similarHashSettled = true;
      return;
    }
    if (scrollToSimilarSection()) similarHashSettled = true;
  }

  function runRelocations() {
    ensureFindSimilarButtons();
    syncSoldOutState();
    maybeScrollToSimilarFromHash();
  }

  // Run as soon as DOM is ready, plus retries for async app injection.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runRelocations);
  } else {
    runRelocations();
  }
  [500, 1500, 3000, 5000].forEach(function (ms) { setTimeout(runRelocations, ms); });

  // Watch for re-injection (apps sometimes re-render on variant change).
  if (window.MutationObserver) {
    new MutationObserver(runRelocations).observe(
      document.body,
      { childList: true, subtree: true }
    );
  }
})();

/* ==========================================================================
   "Find similar" CTA on SOLD collection tiles
   --------------------------------------------------------------------------
   Every antique/vintage rug in the catalog is one-of-a-kind — once sold, it
   is gone forever. On a 700-item collection page with ~30% sold, each SOLD
   tile is a dead-end click: user lands on a PDP they can't buy from. This
   injects a "Find similar" action directly on each sold tile, linking to the
   PDP with a #similar-rugs hash. The PDP-side handler (in the IIFE above)
   smooth-scrolls to the "Others you might love" section on arrival, skipping
   the dead buy stack. Only that CTA carries the hash — the tile's image and
   title links open the rug itself, at the top of the page.

   Why JS + no Liquid edits:
   - snippets/product-item.liquid is a stock theme file. Edits there risk
     being overwritten by "Update from Shopify" commits.
   - The sold-out class `.product-item--sold-out` is already emitted by the
     stock template, so DOM selection is stable.
   ========================================================================== */
(function () {
  'use strict';

  var HASH = '#similar-rugs';

  function appendHash(href) {
    if (!href || href.indexOf('#') !== -1) return href;
    return href + HASH;
  }

  function ensureTileFindSimilar(tile) {
    if (tile.dataset.findSimilarInjected === '1') return;

    var titleLink = tile.querySelector('a.product-item-meta__title');
    if (!titleLink) return;
    var productUrl = titleLink.getAttribute('href') || '';
    if (!productUrl) return;

    // The tile's own image and title links are left alone: clicking the rug
    // means "show me this rug", and a PDP that opens already scrolled past it
    // answers a question the shopper didn't ask. Only the CTA below asks for
    // similar rugs, so only the CTA carries the hash.

    // Visible CTA below the product info so users understand the behavior
    // upfront, not just as a side effect of clicking the tile.
    var cta = document.createElement('a');
    cta.href = appendHash(productUrl);
    cta.className = 'tile-find-similar';
    cta.setAttribute('data-instant', '');
    var titleText = (titleLink.textContent || '').trim();
    cta.setAttribute(
      'aria-label',
      titleText ? 'Find rugs similar to ' + titleText : 'Find similar rugs'
    );
    cta.innerHTML =
      '<span class="tile-find-similar__label">Find similar</span>' +
      '<span class="tile-find-similar__arrow" aria-hidden="true">→</span>';

    // Place the CTA INSIDE the price-list, which on a sold tile is a line with
    // nothing else on it: the price is hidden there (custom.css, "No price on
    // a rug nobody can buy"), so "Find similar →" takes the row the price
    // would have used and the grid keeps its rhythm across 700 items. Falls
    // back to below-meta placement if the price-list isn't in the markup
    // (defensive — theme update, custom templates, etc.).
    var priceList = tile.querySelector('.price-list');
    if (priceList) {
      priceList.appendChild(cta);
    } else {
      var metaBlock = tile.querySelector('.product-item-meta');
      if (metaBlock && metaBlock.parentElement) {
        metaBlock.parentElement.appendChild(cta);
      } else {
        tile.appendChild(cta);
      }
    }

    tile.dataset.findSimilarInjected = '1';
  }

  function injectFindSimilarOnSoldTiles() {
    var tiles = document.querySelectorAll(
      'product-item.product-item--sold-out:not([data-find-similar-injected="1"])'
    );
    tiles.forEach(ensureTileFindSimilar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectFindSimilarOnSoldTiles);
  } else {
    injectFindSimilarOnSoldTiles();
  }

  // Cloud Search re-renders the product grid on filter changes, so we need
  // to re-inject after DOM swaps. The per-tile `data-find-similar-injected`
  // guard makes ensureTileFindSimilar idempotent — re-runs are cheap and
  // only touch tiles that haven't been handled yet.
  if (window.MutationObserver) {
    var productList = document.querySelector(
      '.product-list, [class*="product-list"]'
    );
    var target = productList || document.body;
    new MutationObserver(injectFindSimilarOnSoldTiles).observe(
      target,
      { childList: true, subtree: true }
    );
  }
})();

/* ==========================================================================
   Tile pressed state
   --------------------------------------------------------------------------
   iOS Safari applies :active only on a page that listens for touches, and a
   collection page has no listener of its own. The tile's pressed background
   (custom.css, "The whole tile is one tap target") needs one. It does nothing
   and is passive, so it never holds up a scroll.
   ========================================================================== */
document.addEventListener('touchstart', function () {}, { passive: true });

/* ==========================================================================
   filter_interaction: one schema, four surfaces
   --------------------------------------------------------------------------
   The Cloud Search sidebar, the collection filter bar, the rug size guide and
   the similar-rugs toolbar on a product page all report what a shopper asked
   to filter by. A GA4 report can only add the four up if their events carry
   the same keys under the same names, with the same vocabulary inside them,
   and that vocabulary has drifted before: this file used to call a width
   filter a price_range on one surface and a range on the other two, and no
   report could count them together until they agreed.

   So the key set, the type map and the spelling of an open-ended bound live
   here, above all four callers. What a surface filters, where it asks and
   which collection it means stay with the surface - those genuinely differ,
   and a caller that knows better than the map says so by passing its own
   type rather than by growing the map a name only it uses.
   ========================================================================== */

window.RugFilterEvents = (function () {
  'use strict';

  /* Keyed by the slug a surface filters on, not by the words it shows: every
     size control on every surface ends at a width and length range, however
     it was asked for. The sidebar's names are the app's own prose ("Width
     (inches)"), so it states its types instead of looking them up here. */
  var TYPES = { size: 'range', price: 'price_range', shape: 'list', color: 'list' };

  return {
    /* The collection in the path, which is where the sidebar and the product
       page both read one. A page with no collection in its path reports blank
       rather than a borrowed handle. The bar, which also answers off
       /a/search/<handle>, passes its own. */
    handleFromPath: function () {
      var parts = window.location.pathname.split('/');
      var i = parts.indexOf('collections');
      return i !== -1 && parts[i + 1] ? parts[i + 1] : '';
    },

    /* An unbounded end reads "any" on every surface, or the same half-open
       range asked for in two places lands in the report as two values. */
    side: function (v) { return v == null ? 'any' : v; },

    /* One event, one key set - every key written on every push, including
       size_method as undefined where the surface has no method to name. GTM
       merges pushes into one model, so a key left out keeps whatever the
       last push left there, and a sidebar filter would report itself under
       the exact size somebody asked the collection bar for two pushes ago. */
    push: function (f) {
      window.dataLayer = window.dataLayer || [];
      var e = {
        event: 'filter_interaction',
        filter_name: f.name,
        filter_value: f.value,
        filter_type: f.type || TYPES[f.name] || 'list',
        filter_action: f.action || 'applied',
        filter_source: f.source,
        size_method: f.method || undefined,
        page_url: window.location.href,
        page_path: window.location.pathname,
        collection_handle: f.handle || ''
      };
      /* Only the sidebar's ranges carry the two ends apart from the label. */
      if (f.rangeMin !== undefined) e.range_min = f.rangeMin;
      if (f.rangeMax !== undefined) e.range_max = f.rangeMax;
      window.dataLayer.push(e);
    }
  };
})();

/* ==========================================================================
   Rug Finder requests that come back without rugs: one shape, every surface
   --------------------------------------------------------------------------
   The Rug Finder page, the drawer, the product-page toolbar and saved-search
   alerts all report a request that did not end in results. Two of those
   answers are not failures, so each gets its own event and concierge_error
   counts only what actually broke:

   - concierge_no_results: a healthy 200 with zero rugs for an exact ask.
   - concierge_unavailable: the concierge declining a shopper outside the US,
     the one 403 it sends (its geo gate, ahead of every paid model call).

   Every push writes all four keys, undefined where one does not apply: GTM
   merges pushes into one model, so a key left out reports whatever the last
   push left there, and an unreachable error would claim an old 429.
   ========================================================================== */

window.RugConciergeFailures = (function () {
  'use strict';

  function isNoResults(result) {
    var body = (result && result.body) || {};
    return Boolean(result && result.status === 200 && body.ok === true &&
      body.rugs && body.rugs.length === 0);
  }

  function isUnavailable(result) {
    return Boolean(result && result.status === 403 && result.body &&
      result.body.error === 'unavailable');
  }

  return {
    isNoResults: isNoResults,

    /* `result` is the {status, body} the service answered with, absent when
       it was never reached; `reason` is the caller's word for a real failure. */
    push: function (stage, reason, result, surface) {
      var event = 'concierge_error';
      if (isNoResults(result)) {
        event = 'concierge_no_results';
        reason = 'no_results';
      } else if (isUnavailable(result)) {
        event = 'concierge_unavailable';
        reason = 'unavailable';
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: event,
        stage: stage,
        reason: reason,
        status_code: result ? result.status : undefined,
        surface: surface
      });
    }
  };
})();

/* ==========================================================================
   Escape for the theme's modal dialogs
   --------------------------------------------------------------------------
   Chrome gives Escape to the layer opened last, so a third-party script that
   opens one after our dialog (a cookie bar, an affiliate widget) takes the
   key and the dialog stays open. Each dialog answers Escape itself instead,
   through its own close, so its close event and tracking still fire.
   ========================================================================== */

window.RugDialogEscape = function (dialog, close) {
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    /* A section the theme editor re-rendered leaves its old dialog detached. */
    if (!dialog.open || !dialog.isConnected) return;
    event.preventDefault();
    close();
  });
};

/* ==========================================================================
   Product Filter Interaction Tracking via GTM dataLayer
   --------------------------------------------------------------------------
   Pushes filter_interaction events when users apply or remove product filters
   rendered by the Cloud Search app.

   Covers:
   - Checkbox / list filters  (.cloud-search-filter-value with checkbox)
   - Range filters            (.cloud-search-num-field__input for price, width, length)

   Uses event delegation on document so it survives Cloud Search's DOM updates.
   ========================================================================== */

(function () {
  'use strict';

  var FE = window.RugFilterEvents;

  function getFilterName(el) {
    var group = el.closest('[data-filter-name]');
    return group ? group.getAttribute('data-filter-name').toLowerCase() : '';
  }

  // ── Checkbox / list filters ──────────────────────────────
  // Listen for change events on checkboxes inside filter values.
  // The change event fires exactly once per toggle, after the checkbox
  // state is settled — avoids the label/synthetic-click double-fire issue.
  document.addEventListener('change', function (e) {
    if (e.target.type !== 'checkbox') return;

    var filterValue = e.target.closest('.cloud-search-filter-value');
    if (!filterValue) return;

    var valueName = filterValue.querySelector('.cloud-search-filter-value__name');
    if (!valueName) return;

    var filterName = getFilterName(filterValue);
    if (!filterName) return;

    FE.push({
      name: filterName,
      value: valueName.textContent.trim(),
      /* Stated rather than looked up: these names are the app's own prose,
         and a checkbox group it happens to call "Size" is still a list of
         values to tick, not the range the shared map means by that word. */
      type: 'list',
      action: e.target.checked ? 'applied' : 'removed',
      source: 'sidebar',
      handle: FE.handleFromPath()
    });
  }, true);

  // ── Range filters (Price, Width, Length) ──────────────────
  var rangeTimers = {};

  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!input.classList.contains('cloud-search-num-field__input')) return;

    var rangeContainer = input.closest('.cloud-search-range-inputs');
    if (!rangeContainer) return;

    var filterName = getFilterName(input);
    if (!filterName) return;

    clearTimeout(rangeTimers[filterName]);
    rangeTimers[filterName] = setTimeout(function () {
      var inputs = rangeContainer.querySelectorAll('.cloud-search-num-field__input');
      var minInput = inputs[0];
      var maxInput = inputs[1];
      var minVal = minInput ? parseFloat(minInput.value) || parseFloat(minInput.placeholder) || 0 : 0;
      var maxVal = maxInput ? parseFloat(maxInput.value) || parseFloat(maxInput.placeholder) || 0 : 0;

      FE.push({
        name: filterName,
        value: minVal + '-' + maxVal,
        /* Stated for the reason the checkbox above states its own: "width
           (inches)" is not a key any shared map can carry. This handler
           serves price, width and length alike, and used to call all three
           a price_range. Width is not a price. The bar and the size guide
           say "range" for the same shape, so a report can only add the
           three surfaces up if this one stops singling price out for a name
           the other two do not use. */
        type: filterName === 'price' ? 'price_range' : 'range',
        source: 'sidebar',
        handle: FE.handleFromPath(),
        rangeMin: minVal,
        rangeMax: maxVal
      });
    }, 500);
  }, true);
})();

/* ────────────────────────────────────────────────────────────────────
 * <hub-row> — horizontal scroller with prev/next buttons.
 * Used by the Netflix-style sub-collection rows on hub collection pages.
 * Markup is rendered by snippets/collection-hub-spoke.liquid (mode: rows).
 * ──────────────────────────────────────────────────────────────────── */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('hub-row')) {
    return;
  }

  // Scroll a "page" worth at a time — feels closer to Netflix than scrolling
  // by a fixed pixel amount that depends on viewport width.
  var SCROLL_RATIO = 0.85;

  // Pulls the collection handle out of an /collections/<handle>[/whatever] href.
  function handleFromHref(href) {
    if (!href) return '';
    var m = href.match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function parentCollectionFromLocation() {
    return handleFromHref(window.location.pathname);
  }

  var HubRow = class extends HTMLElement {
    connectedCallback() {
      this.strip = this.querySelector('.collection-hub-row__strip');
      this.prevBtn = this.querySelector('.collection-hub-row__btn--prev');
      this.nextBtn = this.querySelector('.collection-hub-row__btn--next');

      if (!this.strip || !this.prevBtn || !this.nextBtn) return;

      this.onPrev = this.scrollBy_.bind(this, -1);
      this.onNext = this.scrollBy_.bind(this, 1);
      this.onScroll = this.updateButtons_.bind(this);
      this.onTileClick = this.trackClick_.bind(this);

      this.prevBtn.addEventListener('click', this.onPrev);
      this.nextBtn.addEventListener('click', this.onNext);
      this.strip.addEventListener('scroll', this.onScroll, { passive: true });
      this.strip.addEventListener('click', this.onTileClick);
      window.addEventListener('resize', this.onScroll);

      this.updateButtons_();
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.strip) {
        this.strip.removeEventListener('scroll', this.onScroll);
        this.strip.removeEventListener('click', this.onTileClick);
      }
      window.removeEventListener('resize', this.onScroll);
    }

    scrollBy_(direction) {
      if (!this.strip) return;
      var distance = this.strip.clientWidth * SCROLL_RATIO * direction;
      this.strip.scrollBy({ left: distance, behavior: 'smooth' });
    }

    updateButtons_() {
      if (!this.strip) return;
      var max = this.strip.scrollWidth - this.strip.clientWidth;
      var atStart = this.strip.scrollLeft <= 1;
      var atEnd = this.strip.scrollLeft >= max - 1;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
      // Hide nav entirely when content fits in one viewport.
      var fits = max <= 1;
      this.prevBtn.hidden = fits;
      this.nextBtn.hidden = fits;
    }

    // Emits a `collection_tile_click` event to dataLayer so analytics can
    // accumulate hub-row click data — the eventual feedback loop for
    // popularity-driven featuredSubcollections curation.
    trackClick_(e) {
      var link = e.target.closest('a.collection-hub-spoke-tile__link');
      if (!link || !this.contains(link)) return;
      var tile = link.closest('.collection-hub-spoke-tile');
      var tiles = this.querySelectorAll('.collection-hub-spoke-tile');
      var position = -1;
      for (var i = 0; i < tiles.length; i++) {
        if (tiles[i] === tile) { position = i; break; }
      }
      var label = (this.querySelector('.collection-hub-row__title') || {}).textContent || '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'collection_tile_click',
        source: 'hub_row',
        row_label: label.trim(),
        position: position,
        collection_handle: handleFromHref(link.getAttribute('href')),
        parent_collection: parentCollectionFromLocation(),
        page_url: window.location.href,
        page_path: window.location.pathname,
      });
    }
  };

  customElements.define('hub-row', HubRow);
})();

/* ------------------------------------------------------------------------------------------------------------
 * ROOM CONCIERGE: a conversation, not a form
 * ------------------------------------------------------------------------------------------------------------
 * Enhances snippets/room-concierge-form.liquid. The customer describes their
 * room in free text (palette chips + an optional size composed in); we post JSON
 * to the concierge service via the Shopify App Proxy and render the reply (a
 * stylist brief + a grid of collection-style rug tiles) as a chat-like thread
 * they can keep refining. Anonymous: no name/email, a client session id keys the
 * rate limit. "Email me these" captures the lead. On failure we show an inline
 * message and stay on the page (no navigation).
 */
(function () {
  // Production: the same-origin App Proxy path. Local dev (shopify theme dev on
  // 127.0.0.1/localhost): a locally-run concierge. window override wins.
  var ENDPOINT = window.RUG_CONCIERGE_ENDPOINT ||
    ((location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://127.0.0.1:8787/apps/room-concierge'
      : '/apps/room-concierge');
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;

  // The active session: the base match (kept for refines) and the current brief.
  var conversation = null; // { notes, size, brief, skus }

  // Anonymous session id, used as the rate-limit key server-side.
  function clientId() {
    try {
      var k = 'rug_concierge_cid';
      var v = window.localStorage.getItem(k);
      if (!v) {
        v = 'c-' + new Date().getTime().toString(36) + Math.random().toString(36).slice(2, 10);
        window.localStorage.setItem(k, v);
      }
      return v;
    } catch (e) {
      return undefined;
    }
  }

  // ---- GA4 funnel events (GTM dataLayer; tags "GA4 Event - concierge_*") ----
  // page_view (auto) → concierge_submit → concierge_results_shown
  //   → [concierge_refine_submit → concierge_results_refined]*
  //   → concierge_result_click | concierge_lead_captured | concierge_share_created
  // with concierge_error {stage, reason, status_code} on any failed attempt
  // (concierge_no_results / concierge_unavailable when it was not a failure).
  // PII rule: never push notes text, emails, photos or share URLs — only
  // booleans, counts, rug handles and HTTP status codes.
  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined) {
          payload[key] = params[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  // Stages: match | refine | capture | share. `result` is absent for
  // network-level failures.
  function trackError(stage, reason, result) {
    window.RugConciergeFailures.push(stage, reason, result);
  }

  function root(el) { return el.closest('.room-concierge'); }

  function statusEl(rootEl) { return rootEl.querySelector('.room-concierge__status'); }
  function showStatus(rootEl, message) {
    var el = statusEl(rootEl);
    if (!el) return;
    el.classList.remove('room-concierge__status--loading');
    el.hidden = false;
    el.textContent = message;
  }
  function clearStatus(rootEl) {
    var el = statusEl(rootEl);
    if (el) { el.hidden = true; el.textContent = ''; el.classList.remove('room-concierge__status--loading'); }
  }

  // A small camera glyph, reused by the composer and the refine bar.
  var CAMERA_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 8h3l1.2-2h7.6L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.2"/></svg>';

  // Renders a spinner + message into a status element while the model works.
  function renderLoading(el, message) {
    if (!el) return;
    el.hidden = false;
    el.classList.add('room-concierge__status--loading');
    el.textContent = '';
    var spin = document.createElement('span');
    spin.className = 'room-concierge__spinner';
    spin.setAttribute('aria-hidden', 'true');
    el.appendChild(spin);
    el.appendChild(document.createTextNode(message));
  }

  function setLoading(rootEl, loading) {
    var button = rootEl.querySelector('.room-concierge__send');
    if (!button) return;
    button.disabled = loading;
    if (loading) {
      button.dataset.originalText = button.textContent;
      button.textContent = 'Finding...';
    } else if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
    }
  }

  function selectedPalette(rootEl) {
    var terms = [];
    var chips = rootEl.querySelectorAll('.room-concierge__chip--on');
    Array.prototype.forEach.call(chips, function (c) {
      var t = c.getAttribute('data-term');
      if (t) terms.push(t);
    });
    return terms;
  }

  // Feet inputs -> a hard inch size filter. Undefined when blank/invalid, so the
  // service falls back to parsing size from the text.
  function readSize(rootEl) {
    function num(sel) {
      var input = rootEl.querySelector('[data-size="' + sel + '"]');
      return input ? parseFloat(input.value) : NaN;
    }
    var w = num('width'), l = num('length'), tol = num('tolerance');
    if (!isFinite(w) || !isFinite(l) || w <= 0 || l <= 0) return undefined;
    return {
      width_in: Math.round(w * 12),
      length_in: Math.round(l * 12),
      tolerance_in: isFinite(tol) && tol >= 0 ? Math.round(tol) : 12
    };
  }

  function hasPhoto(input) {
    return !!(input && input.files && input.files[0]);
  }

  function readPhoto(input) {
    return new Promise(function (resolve, reject) {
      var file = input && input.files && input.files[0];
      if (!file) { resolve(null); return; }
      if (file.size > MAX_PHOTO_BYTES) { reject(new Error('photo_too_large')); return; }
      var reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = function () { reject(new Error('photo_unreadable')); };
      reader.readAsDataURL(file);
    });
  }

  function post(payload) {
    // Owner testing: a locally stored token lets the backend skip its daily
    // caps (and keep those test requests out of the cap counts).
    try {
      var bypass = window.localStorage.getItem('rug_concierge_bypass');
      if (bypass) payload.bypass = bypass;
    } catch (e) { /* storage unavailable */ }
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      // A non-JSON reply (e.g. a proxy error page on a 5xx) still carries a
      // useful status: fall back to an empty body so the failure keeps it.
      return response.json().catch(function () { return {}; }).then(function (body) {
        return { status: response.status, body: body };
      });
    });
  }

  var UNREACHABLE = 'We could not reach our stylist service just now. Please try again in a moment.';

  // ---- product tiles (collection-card language) --------------------------

  function formatPrice(value) {
    var n = Number(value);
    if (!isFinite(n)) return '';
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  function feet(inches) {
    var f = Math.floor(inches / 12);
    var rest = Math.round(inches - f * 12);
    if (rest === 12) { f += 1; rest = 0; }
    return rest === 0 ? f + "'" : f + "'" + rest + '"';
  }

  function loadCardImage(card) {
    var handle = card.getAttribute('data-handle');
    if (!handle) return;
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.featured_image) return;
        var img = card.querySelector('.room-concierge-results__img');
        if (!img) return;
        var src = p.featured_image;
        if (src.indexOf('//') === 0) src = 'https:' + src;
        src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=600&height=750&crop=center';
        img.src = src;
        img.hidden = false;
      })
      .catch(function () {});
  }

  function buildCard(rug, position) {
    var card = document.createElement('a');
    card.className = 'room-concierge-results__card';
    card.href = '/products/' + rug.handle;
    card.setAttribute('data-handle', rug.handle);
    card.setAttribute('data-position', String(position));

    var media = document.createElement('div');
    media.className = 'room-concierge-results__media';
    var img = document.createElement('img');
    img.className = 'room-concierge-results__img';
    img.loading = 'lazy';
    img.alt = rug.title || '';
    img.hidden = true;
    media.appendChild(img);

    // Reuse the collection card's meta typography so tiles match the site.
    var meta = document.createElement('div');
    meta.className = 'product-item-meta room-concierge-results__meta';
    var title = document.createElement('span');
    title.className = 'product-item-meta__title';
    title.textContent = rug.title || '';
    var priceList = document.createElement('div');
    priceList.className = 'price-list price-list--centered';
    var price = document.createElement('span');
    price.className = 'price';
    var size = feet(rug.width_inches_total) + ' x ' + feet(rug.length_inches_total);
    price.textContent = size + '  ·  ' + formatPrice(rug.price);
    priceList.appendChild(price);
    meta.appendChild(title);
    meta.appendChild(priceList);

    card.appendChild(media);
    card.appendChild(meta);
    return card;
  }

  function observeGrid(grid) {
    var cards = grid.querySelectorAll('.room-concierge-results__card');
    var io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { loadCardImage(e.target); io.unobserve(e.target); }
          });
        }, { rootMargin: '400px' })
      : null;
    Array.prototype.forEach.call(cards, function (card) {
      if (io) { io.observe(card); } else { loadCardImage(card); }
    });
  }

  function buildGrid(rugs) {
    var grid = document.createElement('div');
    grid.className = 'room-concierge-results__grid';
    rugs.forEach(function (rug, i) { grid.appendChild(buildCard(rug, i + 1)); });
    return grid;
  }

  // ---- conversation thread ----------------------------------------------

  function threadEl(rootEl) { return rootEl.querySelector('.room-concierge__thread'); }

  function addYouTurn(thread, message) {
    var turn = document.createElement('div');
    turn.className = 'room-concierge__turn room-concierge__turn--you';
    var label = document.createElement('p');
    label.className = 'room-concierge__turn-label';
    label.textContent = 'You';
    var bubble = document.createElement('div');
    bubble.className = 'room-concierge__bubble';
    bubble.textContent = message;
    turn.appendChild(label);
    turn.appendChild(bubble);
    thread.appendChild(turn);
    return turn;
  }

  function addStylistTurn(thread, data) {
    var turn = document.createElement('div');
    turn.className = 'room-concierge__turn room-concierge__turn--stylist';
    var label = document.createElement('p');
    label.className = 'room-concierge__turn-label';
    label.textContent = 'Rug Styling';
    turn.appendChild(label);
    if (data.brief) {
      var brief = document.createElement('p');
      brief.className = 'room-concierge__brief';
      brief.textContent = data.brief;
      turn.appendChild(brief);
    }
    var grid = buildGrid(data.rugs);
    turn.appendChild(grid);
    thread.appendChild(turn);
    observeGrid(grid);
    return turn;
  }

  function rememberSkus(data) {
    conversation.skus = data.rugs.map(function (r) { return r.sku; });
  }

  // ---- the persistent composer (refine + capture) ------------------------

  function buildDock(rootEl) {
    var dock = document.createElement('div');
    dock.className = 'room-concierge__dock';

    var refine = document.createElement('form');
    refine.className = 'room-concierge__refine';
    var refinePhoto = document.createElement('button');
    refinePhoto.type = 'button';
    refinePhoto.className = 'room-concierge__refine-photo';
    refinePhoto.setAttribute('aria-label', 'Add a photo');
    refinePhoto.setAttribute('title', 'Add a photo');
    refinePhoto.innerHTML = CAMERA_SVG;
    var refineFile = document.createElement('input');
    refineFile.type = 'file';
    refineFile.className = 'room-concierge__refine-photo-input';
    refineFile.accept = 'image/jpeg,image/png,image/webp';
    refineFile.hidden = true;
    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'room-concierge__refine-input';
    input.setAttribute('aria-label', 'Refine your results');
    input.setAttribute('placeholder', "Not quite? Tell us what to change...");
    var send = document.createElement('button');
    send.type = 'submit';
    send.className = 'room-concierge__refine-send button button--primary';
    send.textContent = 'Update';
    refine.appendChild(refinePhoto);
    refine.appendChild(input);
    refine.appendChild(send);
    refine.appendChild(refineFile);
    refine.addEventListener('submit', function (e) {
      e.preventDefault();
      onRefine(rootEl, input.value);
    });
    dock.appendChild(refine);

    var extras = document.createElement('div');
    extras.className = 'room-concierge__dock-extras';
    var again = document.createElement('button');
    again.type = 'button';
    again.className = 'room-concierge__again';
    again.textContent = 'Start over';
    again.addEventListener('click', function () { window.location.reload(); });
    var share = document.createElement('button');
    share.type = 'button';
    share.className = 'room-concierge__share';
    // One word: it sits under the picks, and three actions share a phone row.
    share.textContent = 'Share';
    share.addEventListener('click', function () { onShare(dock); });
    var save = document.createElement('button');
    save.type = 'button';
    save.className = 'room-concierge__save';
    save.textContent = 'Email these to me';
    save.addEventListener('click', function () { toggleCapture(dock); });
    extras.appendChild(again);
    extras.appendChild(share);
    extras.appendChild(save);
    dock.appendChild(extras);

    var capture = document.createElement('form');
    capture.className = 'room-concierge__capture';
    capture.hidden = true;
    var email = document.createElement('input');
    email.type = 'email';
    email.className = 'room-concierge__capture-input';
    email.setAttribute('placeholder', 'you@email.com');
    email.setAttribute('aria-label', 'Your email');
    var csend = document.createElement('button');
    csend.type = 'submit';
    csend.className = 'room-concierge__capture-send button button--primary';
    csend.textContent = 'Send';
    capture.appendChild(email);
    capture.appendChild(csend);
    var optin = document.createElement('label');
    optin.className = 'room-concierge__optin';
    var optinBox = document.createElement('input');
    optinBox.type = 'checkbox';
    optinBox.className = 'room-concierge__optin-box';
    optinBox.checked = !!(conversation && conversation.us);
    var optinText = document.createElement('span');
    optinText.textContent = 'Also alert me to new arrivals like these.';
    optin.appendChild(optinBox);
    optin.appendChild(optinText);
    capture.appendChild(optin);
    capture.addEventListener('submit', function (e) {
      e.preventDefault();
      onCapture(dock, email.value);
    });
    dock.appendChild(capture);

    // The share link row, revealed once a link exists. Readonly URL + Copy;
    // a native share-sheet button too where the browser offers one.
    var shareRow = document.createElement('div');
    shareRow.className = 'room-concierge__share-row';
    shareRow.hidden = true;
    var shareLink = document.createElement('input');
    shareLink.type = 'text';
    shareLink.readOnly = true;
    shareLink.className = 'room-concierge__share-link';
    shareLink.setAttribute('aria-label', 'Link to these picks');
    shareLink.addEventListener('focus', function () { shareLink.select(); });
    var shareCopy = document.createElement('button');
    shareCopy.type = 'button';
    shareCopy.className = 'room-concierge__share-copy button button--primary';
    shareCopy.textContent = 'Copy';
    shareCopy.addEventListener('click', function () { onShareCopy(dock); });
    shareRow.appendChild(shareLink);
    shareRow.appendChild(shareCopy);
    if (navigator.share) {
      var shareNative = document.createElement('button');
      shareNative.type = 'button';
      shareNative.className = 'room-concierge__share-native';
      shareNative.textContent = 'Share...';
      shareNative.addEventListener('click', function () {
        // Cancelling the sheet rejects; that's not an error.
        navigator.share({ title: 'Rug picks from Rug Styling', url: shareLink.value }).catch(function () {});
      });
      shareRow.appendChild(shareNative);
    }
    dock.appendChild(shareRow);

    var status = document.createElement('div');
    status.className = 'room-concierge__dock-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    dock.appendChild(status);

    return dock;
  }

  function dockStatus(dock, message) {
    var s = dock.querySelector('.room-concierge__dock-status');
    if (!s) return;
    s.classList.remove('room-concierge__status--loading');
    if (message) { s.hidden = false; s.textContent = message; }
    else { s.hidden = true; s.textContent = ''; }
  }

  function toggleCapture(dock) {
    var form = dock.querySelector('.room-concierge__capture');
    if (form) {
      form.hidden = !form.hidden;
      if (!form.hidden) {
        // One expanded row at a time keeps the dock quiet.
        var row = dock.querySelector('.room-concierge__share-row');
        if (row) row.hidden = true;
        var input = form.querySelector('.room-concierge__capture-input');
        if (input) input.focus();
      }
    }
  }

  function enterConversation(rootEl, firstMessage, data) {
    var hero = rootEl.querySelector('.room-concierge__hero');
    if (hero) hero.hidden = true;

    var thread = document.createElement('div');
    thread.className = 'room-concierge__thread';
    rootEl.appendChild(thread);

    conversation = {
      notes: conversation.notes,
      size: conversation.size,
      brief: data.brief,
      // Confident-US hint from the service's geoip: decides whether the
      // marketing opt-in box may default checked (CAN-SPAM allows it there;
      // unknown or non-US visitors must actively tick it).
      us: data.us === true
    };
    rememberSkus(data);

    addYouTurn(thread, firstMessage);
    addStylistTurn(thread, data);
    rootEl.appendChild(buildDock(rootEl));

    track('concierge_results_shown', { count: data.rugs.length });
  }

  // ---- first submit ------------------------------------------------------

  function onComposerSubmit(event) {
    var form = event.target;
    if (!form.classList || !form.classList.contains('room-concierge__composer-form')) return;
    event.preventDefault();

    var rootEl = root(form);
    if (!rootEl) return;
    if (!window.fetch || !window.FileReader) {
      showStatus(rootEl, 'This needs a modern browser. Please email us and we will help.');
      return;
    }

    var input = rootEl.querySelector('.room-concierge__input');
    var text = input ? input.value.trim() : '';
    var palette = selectedPalette(rootEl);
    var notes = [text].concat(palette).filter(Boolean).join('. ');
    if (!notes) {
      showStatus(rootEl, 'Tell us a little about your room to get started.');
      return;
    }
    var size = readSize(rootEl);

    setLoading(rootEl, true);
    renderLoading(statusEl(rootEl), 'Finding your rugs...');

    var photoInput = rootEl.querySelector('.room-concierge__photo-input');
    track('concierge_submit', {
      has_photo: hasPhoto(photoInput),
      has_size: !!size,
      palette_count: palette.length
    });
    readPhoto(photoInput)
      .then(function (photo) {
        var payload = { clientId: clientId(), notes: notes, size: size };
        if (photo) payload.photo = photo;
        return post(payload);
      })
      .then(function (result) {
        setLoading(rootEl, false);
        var body = result.body || {};
        if (result.status === 200 && body.rugs && body.rugs.length > 0) {
          conversation = { notes: notes, size: size, brief: body.brief };
          clearStatus(rootEl);
          enterConversation(rootEl, text || notes, body);
        } else if (body.message) {
          trackError('match', 'server_message', result);
          showStatus(rootEl, body.message);
        } else {
          trackError('match', 'server_error', result);
          showStatus(rootEl, UNREACHABLE);
        }
      })
      .catch(function (error) {
        setLoading(rootEl, false);
        if (error && error.message === 'photo_too_large') {
          trackError('match', 'photo_too_large');
          showStatus(rootEl, 'That photo is larger than 10MB. Please choose a smaller one, or send without a photo.');
          return;
        }
        trackError('match', 'unreachable');
        showStatus(rootEl, UNREACHABLE);
      });
  }

  // ---- refine ------------------------------------------------------------

  function resetRefinePhoto(dock) {
    var input = dock.querySelector('.room-concierge__refine-photo-input');
    if (input) input.value = '';
    var btn = dock.querySelector('.room-concierge__refine-photo');
    if (btn) btn.classList.remove('room-concierge__refine-photo--attached');
  }

  function onRefine(rootEl, text) {
    text = (text || '').trim();
    if (!text || !conversation) return;
    var dock = rootEl.querySelector('.room-concierge__dock');
    var send = dock && dock.querySelector('.room-concierge__refine-send');
    var photoInput = dock && dock.querySelector('.room-concierge__refine-photo-input');
    if (send) send.disabled = true;
    renderLoading(dock.querySelector('.room-concierge__dock-status'), 'Updating your picks...');

    track('concierge_refine_submit', { has_photo: hasPhoto(photoInput) });
    readPhoto(photoInput)
      .then(function (photo) {
        var payload = {
          clientId: clientId(),
          notes: conversation.notes,
          size: conversation.size,
          refine: text,
          prior_brief: conversation.brief
        };
        // A photo attached to the follow-up refines the match with fresh visual
        // context; the service embeds it exactly as it does on the first turn.
        if (photo) payload.photo = photo;
        return post(payload);
      })
      .then(function (result) {
        if (send) send.disabled = false;
        var body = result.body || {};
        if (result.status === 200 && body.rugs && body.rugs.length > 0) {
          conversation.brief = body.brief || conversation.brief;
          rememberSkus(body);
          // The picks changed, so a visible share link no longer matches them.
          hideShareRow(rootEl);
          var thread = threadEl(rootEl);
          addYouTurn(thread, text);
          var turn = addStylistTurn(thread, body);
          dockStatus(dock, '');
          var refineInput = dock.querySelector('.room-concierge__refine-input');
          if (refineInput) refineInput.value = '';
          resetRefinePhoto(dock);
          if (turn && turn.scrollIntoView) turn.scrollIntoView({ behavior: 'smooth', block: 'start' });
          track('concierge_results_refined', { count: body.rugs.length });
        } else if (body.message) {
          trackError('refine', 'server_message', result);
          dockStatus(dock, body.message);
        } else {
          trackError('refine', 'server_error', result);
          dockStatus(dock, UNREACHABLE);
        }
      })
      .catch(function (error) {
        if (send) send.disabled = false;
        if (error && error.message === 'photo_too_large') {
          trackError('refine', 'photo_too_large');
          dockStatus(dock, 'That photo is larger than 10MB. Please choose a smaller one, or refine without a photo.');
          return;
        }
        trackError('refine', 'unreachable');
        dockStatus(dock, UNREACHABLE);
      });
  }

  // ---- capture ("email me these") ---------------------------------------

  function onCapture(dock, email) {
    email = (email || '').trim();
    if (!email || !conversation) return;
    var send = dock.querySelector('.room-concierge__capture-send');
    if (send) send.disabled = true;
    dockStatus(dock, 'Sending...');

    var optinBox = dock.querySelector('.room-concierge__optin-box');
    var marketing = !!(optinBox && optinBox.checked);
    post({
      captureOnly: true,
      clientId: clientId(),
      email: email,
      marketing: marketing,
      skus: conversation.skus || [],
      prior_brief: conversation.brief
    })
      .then(function (result) {
        if (send) send.disabled = false;
        var body = result.body || {};
        if (result.status === 200 && body.ok) {
          track('concierge_lead_captured', { marketing_opt_in: marketing });
          var form = dock.querySelector('.room-concierge__capture');
          if (form) form.hidden = true;
          dockStatus(dock, body.message || 'Sent. Check your inbox.');
        } else {
          trackError('capture', body && body.message ? 'server_message' : 'server_error', result);
          dockStatus(dock, (body && body.message) || 'We could not send those just now. Please try again.');
        }
      })
      .catch(function () {
        if (send) send.disabled = false;
        trackError('capture', 'unreachable');
        dockStatus(dock, 'We could not send those just now. Please try again.');
      });
  }

  // ---- share ("share these picks") ---------------------------------------

  var SHARE_FAILED = 'We could not create your link just now. Please try again.';

  function skusSignature() {
    return ((conversation && conversation.skus) || []).join(',');
  }

  function showShareRow(dock, url) {
    var capture = dock.querySelector('.room-concierge__capture');
    if (capture) capture.hidden = true;
    var row = dock.querySelector('.room-concierge__share-row');
    if (!row) return;
    var input = row.querySelector('.room-concierge__share-link');
    if (input) input.value = url;
    row.hidden = false;
    if (input) {
      input.focus(); // focus selects (see the listener), ready to copy
      input.scrollLeft = 0; // show the recognizable start of the URL, not the token tail
    }
  }

  function hideShareRow(rootEl) {
    var row = rootEl.querySelector('.room-concierge__share-row');
    if (row) row.hidden = true;
  }

  function onShare(dock) {
    if (!conversation) return;
    // The same picks already have a link: re-show it, don't mint a duplicate.
    if (conversation.shareUrl && conversation.shareSkus === skusSignature()) {
      dockStatus(dock, '');
      showShareRow(dock, conversation.shareUrl);
      return;
    }
    var btn = dock.querySelector('.room-concierge__share');
    if (btn) btn.disabled = true;
    renderLoading(dock.querySelector('.room-concierge__dock-status'), 'Creating your link...');

    post({
      share: true,
      clientId: clientId(),
      skus: conversation.skus || [],
      prior_brief: conversation.brief
    })
      .then(function (result) {
        if (btn) btn.disabled = false;
        var body = result.body || {};
        if (result.status === 200 && body.ok && body.share_url) {
          conversation.shareUrl = body.share_url;
          conversation.shareSkus = skusSignature();
          dockStatus(dock, '');
          showShareRow(dock, body.share_url);
          track('concierge_share_created');
        } else {
          trackError('share', body && body.message ? 'server_message' : 'server_error', result);
          dockStatus(dock, (body && body.message) || SHARE_FAILED);
        }
      })
      .catch(function () {
        if (btn) btn.disabled = false;
        trackError('share', 'unreachable');
        dockStatus(dock, SHARE_FAILED);
      });
  }

  function onShareCopy(dock) {
    var input = dock.querySelector('.room-concierge__share-link');
    var btn = dock.querySelector('.room-concierge__share-copy');
    if (!input || !input.value) return;
    var confirm = function () {
      if (!btn || btn.dataset.copied) return;
      btn.dataset.copied = '1';
      var prev = btn.textContent;
      btn.textContent = 'Copied';
      window.setTimeout(function () {
        btn.textContent = prev;
        delete btn.dataset.copied;
      }, 2000);
    };
    var fallback = function () {
      input.focus();
      input.select();
      try { document.execCommand('copy'); } catch (e) { /* selection stays for a manual copy */ }
      confirm();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(input.value).then(confirm).catch(fallback);
    } else {
      fallback();
    }
  }

  // ---- chip toggle, photo attach ----------------------------------------

  function onClick(event) {
    // Which picks earn a product-page visit, and from which grid slot.
    // Delegated (like the file's other click tracking) so refine turns don't
    // accumulate per-card listeners; navigation proceeds untouched.
    var card = event.target.closest && event.target.closest('.room-concierge-results__card');
    if (card) {
      // The toolbar grid's fallback cards report through its own capture
      // listener (surface: 'toolbar'); counting them here doubles every click.
      if (card.closest('[data-rft-results]')) return;
      var position = parseInt(card.getAttribute('data-position'), 10);
      track('concierge_result_click', {
        handle: card.getAttribute('data-handle') || undefined,
        position: isFinite(position) ? position : undefined
      });
      return;
    }
    var chip = event.target.closest && event.target.closest('.room-concierge__chip');
    if (chip) {
      event.preventDefault();
      chip.classList.toggle('room-concierge__chip--on');
      return;
    }
    var photoBtn = event.target.closest && event.target.closest('.room-concierge__photo-btn');
    if (photoBtn) {
      event.preventDefault();
      var rootEl = root(photoBtn);
      var input = rootEl && rootEl.querySelector('.room-concierge__photo-input');
      if (input) input.click();
      return;
    }
    var refinePhotoBtn = event.target.closest && event.target.closest('.room-concierge__refine-photo');
    if (refinePhotoBtn) {
      event.preventDefault();
      var refineForm = refinePhotoBtn.closest('.room-concierge__refine');
      var rInput = refineForm && refineForm.querySelector('.room-concierge__refine-photo-input');
      if (rInput) rInput.click();
    }
  }

  function onPhotoChange(event) {
    var input = event.target;
    if (!input.classList) return;

    // Refine bar photo: mark the button attached (or reject an oversized file).
    if (input.classList.contains('room-concierge__refine-photo-input')) {
      var refineForm = input.closest('.room-concierge__refine');
      var rBtn = refineForm && refineForm.querySelector('.room-concierge__refine-photo');
      var rFile = input.files && input.files[0];
      if (rFile && rFile.size > MAX_PHOTO_BYTES) {
        input.value = '';
        if (rBtn) rBtn.classList.remove('room-concierge__refine-photo--attached');
        var dock = input.closest('.room-concierge__dock');
        if (dock) dockStatus(dock, 'That photo is larger than 10MB. Please choose a smaller one.');
      } else if (rBtn) {
        rBtn.classList.toggle('room-concierge__refine-photo--attached', !!rFile);
      }
      return;
    }

    if (!input.classList.contains('room-concierge__photo-input')) return;
    var rootEl = root(input);
    var preview = rootEl ? rootEl.querySelector('.room-concierge__photo-preview') : null;
    var btn = rootEl ? rootEl.querySelector('.room-concierge__photo-btn') : null;
    var file = input.files && input.files[0];
    if (!preview) return;
    if (file && file.size <= MAX_PHOTO_BYTES && window.URL && URL.createObjectURL) {
      preview.src = URL.createObjectURL(file);
      preview.hidden = false;
      if (btn) btn.classList.add('room-concierge__photo-btn--attached');
    } else {
      preview.hidden = true;
      if (btn) btn.classList.remove('room-concierge__photo-btn--attached');
      if (file && file.size > MAX_PHOTO_BYTES && rootEl) {
        showStatus(rootEl, 'That photo is larger than 10MB. Please choose a smaller one.');
      }
    }
  }

  // Grow the composer textarea with its content (up to the CSS max-height) so a
  // long description is never clipped.
  function onInput(event) {
    var el = event.target;
    if (!el.classList || !el.classList.contains('room-concierge__input')) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }

  document.addEventListener('submit', onComposerSubmit, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onPhotoChange, true);
  document.addEventListener('input', onInput, true);
})();

/* ------------------------------------------------------------------------------------------------------------
 * RUG FINDER DRAWER: question-first, anchored on the current rug
 * ------------------------------------------------------------------------------------------------------------
 * Enhances snippets/rug-finder-entry.liquid + snippets/rug-finder-drawer.liquid.
 * The entry block ("Find…" buttons + a tell-us box) is relocated into the
 * description under the leading Dimensions/Circa lines. One deterministic
 * path: button → one question → loading → results; nothing searches until the
 * shopper sends. Size/price/similar use the concierge's structured fast-match
 * mode (anchor_skus + hard filters, no LLM); free text rides the typed path.
 * Constraints accumulate client-side ({size_range, max_price_usd}) and render
 * as editable pills in the pinned dock; every structured change re-sends the
 * full set, plus the last typed brief (prior_brief) so the customer's words
 * keep steering the ranking through filter edits. Once results exist, pill
 * taps open the form in an inline editor above the dock instead of replacing
 * the grid. Result tiles are the theme's real product-item cards: precomputed
 * similars come from a server-rendered <template>, live results from the
 * search page's section rendering (one request for all SKUs), with the plain
 * JSON tiles as fallback. There is no close: minimize (─), the overlay, and
 * Escape animate the panel into an edge tab, and sessionStorage carries the
 * session across product pages.
 */
(function () {
  var ENDPOINT = window.RUG_CONCIERGE_ENDPOINT ||
    ((location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://127.0.0.1:8787/apps/room-concierge'
      : '/apps/room-concierge');
  var STORAGE_KEY = 'rug_finder_drawer';
  var UNREACHABLE = 'We could not reach our stylist service just now. Please try again in a moment.';
  var SLIDE_MS = 320; // matches the CSS panel transition, plus a beat

  // The live session. anchor pins the search; constraints is the full active
  // filter set; sizeForm/priceValue keep the raw inputs for pill editing;
  // rugs/brief are the last results.
  var state = null;

  function clientId() {
    try {
      var k = 'rug_concierge_cid';
      var v = window.localStorage.getItem(k);
      if (!v) {
        v = 'c-' + new Date().getTime().toString(36) + Math.random().toString(36).slice(2, 10);
        window.localStorage.setItem(k, v);
      }
      return v;
    } catch (e) {
      return undefined;
    }
  }

  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined) {
          payload[key] = params[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  // Stages: fast_match | match | refine | cards | capture | share.
  function trackError(stage, reason, result) {
    window.RugConciergeFailures.push(stage, reason, result);
  }

  function post(payload) {
    // Owner testing: a locally stored token lets the backend skip its daily
    // caps (and keep those test requests out of the cap counts).
    try {
      var bypass = window.localStorage.getItem('rug_concierge_bypass');
      if (bypass) payload.bypass = bypass;
    } catch (e) { /* storage unavailable */ }
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (body) {
        return { status: response.status, body: body };
      });
    });
  }

  // ---- elements ----------------------------------------------------------

  function drawerEl() { return document.querySelector('.rug-finder-drawer'); }
  function tabEl() { return document.querySelector('.rug-finder-tab'); }
  function entryEl() { return document.querySelector('.rug-finder-entry'); }
  function dockEl() {
    var drawer = drawerEl();
    return drawer ? drawer.querySelector('.rug-finder-drawer__dock') : null;
  }
  function stageEl(cls) {
    var drawer = drawerEl();
    return drawer ? drawer.querySelector(cls) : null;
  }
  function editorEl() {
    var drawer = drawerEl();
    return drawer ? drawer.querySelector('.rug-finder-drawer__editor') : null;
  }
  function editorOpen() {
    var editor = editorEl();
    return Boolean(editor && !editor.hidden);
  }
  function resultsVisible() {
    var results = stageEl('.rf-results');
    return Boolean(state && state.rugs && state.rugs.length && results && !results.hidden);
  }
  function gridHasCards() {
    var results = stageEl('.rf-results');
    var grid = results && results.querySelector('.rf-results__grid');
    return Boolean(grid && grid.children.length);
  }

  function anchorFromEntry() {
    var entry = entryEl();
    if (!entry) return null;
    return {
      sku: entry.getAttribute('data-rf-sku') || '',
      title: entry.getAttribute('data-rf-title') || '',
      priceCents: parseInt(entry.getAttribute('data-rf-price-cents'), 10) || 0
    };
  }

  // The similar-rugs toolbar owns filtering wherever it renders, which is now
  // every product state: available, sold, and the withheld rug's relay page.
  // The entry and drawer stand down to it rather than offering a second, worse
  // way to do the same thing. The one page where the entry is trimmed rather
  // than hidden is a rug out on approval; see syncStandDown.
  function toolbarActive() {
    return Boolean(document.querySelector('[data-rft]'));
  }

  // Rendered against booted: the snippet ships hidden and the toolbar's IIFE
  // unhides it only once it finds the grid and the anchor. Until then (or if
  // that boot never runs) the entry stays the page's way to search.
  function toolbarLive() {
    var el = document.querySelector('[data-rft]');
    return Boolean(el && !el.hidden);
  }

  // ---- entry placement ---------------------------------------------------

  var MOBILE_MQ = '(max-width: 740px)';
  var specsBlock = null; // the Dimensions/Circa block the entry docks under
  var specsHome = null;  // marks where that block sits on desktop

  function isSpecLine(node) {
    return Boolean(node) && node.tagName === 'P' &&
      /^\s*(Dimensions|Circa)\b/i.test(node.textContent || '');
  }

  // The block-level child of form that contains el (for anchoring moves).
  function blockOf(form, selector) {
    var el = form.querySelector(selector);
    while (el && el.parentElement && el.parentElement !== form) el = el.parentElement;
    return el && el.parentElement === form ? el : null;
  }

  // The description paragraph and the metafield write the same measurement
  // differently: "7'6" x 11'0" (229 cm x 335 cm)." against
  // "7'6" × 11'0" (229 × 335 cm)". Multiplication sign, one "cm" rather than
  // two, no full stop. Reconcile toward the metafield, which is the form the
  // rest of the catalogue is in.
  function normaliseSpecValue(value) {
    return String(value)
      .replace(/\((\d+(?:\.\d+)?)\s*cm\s*[x×]\s*(\d+(?:\.\d+)?)\s*cm\)/gi, '($1 × $2 cm)')
      .replace(/(["'′″\d])\s*[x×]\s*(\d)/gi, '$1 × $2')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // One row in the shape snippets/product-form.liquid:378 emits: a label span
  // and a value span, split on the first ": ". A line with no label (rare, but
  // the Liquid path allows it) renders as a value on its own.
  function specRow(text) {
    var clean = String(text || '').replace(/\s+/g, ' ').trim().replace(/\.$/, '');
    var split = clean.indexOf(': ');
    var row = document.createElement('div');
    row.className = 'product-form__spec';
    if (split > -1) {
      var label = document.createElement('span');
      label.className = 'product-form__spec-label';
      label.textContent = clean.slice(0, split);
      row.appendChild(label);
    }
    var value = document.createElement('span');
    value.className = 'product-form__spec-value';
    value.textContent = normaliseSpecValue(split > -1 ? clean.slice(split + 2) : clean);
    row.appendChild(value);
    return row;
  }

  // Fallback for products whose custom.specs metafield hasn't synced yet:
  // their spec lines are still the description's leading paragraphs. Move
  // (not copy) them out of the Description tab into a specs block — the info
  // column gets its Dimensions/Circa and the tab stops repeating them.
  //
  // Re-emitted as spec rows rather than carried over as <p><strong> prose,
  // because otherwise the same rug reads as a bolded sentence here and as a
  // spec sheet on the product next to it. Every product that lands in this
  // branch is one nobody can buy any more — the metafield backfill has only
  // ever skipped sold stock — so the mismatch showed up exactly on the pages
  // this page redesign is about.
  function extractSpecs() {
    var tabs = document.querySelectorAll('.product-tabs__tab-item-content');
    for (var i = 0; i < tabs.length; i++) {
      if (!isSpecLine(tabs[i].firstElementChild)) continue;
      var specs = document.createElement('div');
      specs.className = 'product-form__specs';
      specs.setAttribute('data-rug-specs', '');
      specs.setAttribute('data-rf-extracted', '');
      while (isSpecLine(tabs[i].firstElementChild)) {
        var line = tabs[i].firstElementChild;
        specs.appendChild(specRow(line.textContent));
        line.parentElement.removeChild(line);
      }
      return specs;
    }
    return null;
  }

  // Mobile lifts the Dimensions/Circa block to the top of the info column so
  // the size leads; desktop leaves it in its Liquid slot above the buy stack,
  // where the column is short enough to take in at a glance. Runs on every
  // PDP, including sold rugs that render no entry.
  function placeSpecs() {
    var form = document.querySelector('.product-form');
    if (!form) return null;

    if (!specsHome) {
      specsBlock = blockOf(form, '.product-form__specs') || extractSpecs();
      if (!specsBlock) return null;
      specsHome = document.createComment('rf-specs-home');
      // An extracted block has no Liquid slot of its own; its desktop home is
      // where the description block used to render, above the buy stack.
      var anchor = specsBlock.parentElement === form ? specsBlock :
        blockOf(form, '.product-form__buy-buttons') ||
        blockOf(form, '.product-form__payment-container') || form.firstChild;
      form.insertBefore(specsHome, anchor);
    }
    if (!specsBlock) return null;

    if (window.matchMedia(MOBILE_MQ).matches) {
      form.insertBefore(specsBlock, form.firstChild);
    } else {
      specsHome.parentElement.insertBefore(specsBlock, specsHome.nextSibling);
    }
    return specsBlock;
  }

  // The entry renders hidden after the buy stack and moves to sit directly
  // under the Dimensions/Circa block, so "Find… other sizes" reads as a
  // follow-on from the size just shown. Falls back to its rendered spot when
  // there is no spec block to dock under. Re-runs when the viewport crosses
  // the 740px boundary.
  function placeEntry() {
    var specs = placeSpecs();
    var entry = entryEl();
    if (!entry) return;
    // An On Hold rug puts "Check availability" where the Add to Cart was
    // (snippets/rug-availability-ask.liquid), and the entry follows that rather
    // than the specs: being asked about the rug you came for should come before
    // being offered a different one.
    var anchor = document.querySelector('.rug-ask') || specs;
    if (anchor) anchor.insertAdjacentElement('afterend', entry);
    syncStandDown();
  }

  // The entry stands down wherever the toolbar came up. It is a way to search
  // and the toolbar is a better one sitting a screen below it, so on an
  // available rug and on a sold one alike the whole panel goes: a sold rug's
  // entry says one more thing than the toolbar does, but the Sold chip beside
  // the title says it too, and a framed panel holding a single sentence costs
  // more vertical space than the sentence is worth twice over.
  //
  // One page keeps it, trimmed to that sentence. A rug out on approval is the
  // only state where nothing else on the page says the rug is unavailable:
  // snippets/product-info.liquid deliberately withholds the Sold pill there
  // because it would be a lie, and "Check availability" in the buy slot asks
  // a question rather than answering one. Hide the panel and the page never
  // tells the shopper why they cannot buy the rug they came for.
  //
  // Read off .rug-ask rather than off the lead's wording: that block is
  // rendered for On Hold rugs and nothing else (see
  // snippets/rug-availability-ask.liquid), and placeEntry above already
  // anchors to it for the same reason. custom.css owns the trimmed frame.
  //
  // Its own function, and the only part of placeEntry the toolbar's boot
  // wants: the boot has to re-decide this once the toolbar is visible, and
  // asking for the placement work again to get one boolean re-read would make
  // every future side effect in placeEntry run twice on a product page.
  function syncStandDown() {
    var entry = entryEl();
    if (!entry) return;
    var live = toolbarLive();
    var leadOnly = live && Boolean(document.querySelector('.rug-ask'));
    entry.hidden = live && !leadOnly;
    entry.classList.toggle('rug-finder-entry--lead-only', leadOnly);
  }

  // ---- drawer chrome -----------------------------------------------------

  var lastFocus = null;
  var hideTimer = null;

  function openDrawer(tweak) {
    var drawer = drawerEl();
    if (!drawer) return;
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    lastFocus = document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rug-finder-drawer-open');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { drawer.classList.add('rug-finder-drawer--in'); });
    });
    hideTab();
    if (state) state.minimized = false;
    save();
    var handle = (location.pathname.split('/products/')[1] || '').split('?')[0] || undefined;
    track('concierge_drawer_open', { tweak: tweak || undefined, handle: handle });
  }

  // Which stage the shopper is looking at, for the minimize event: knowing
  // WHERE people bail (a question, the loading wait, or results) is the
  // difference between fixing a form and fixing relevance.
  function currentStage() {
    var names = {
      '.rf-q--size': 'size_question',
      '.rf-q--price': 'price_question',
      '.rf-loading': 'loading',
      '.rf-results': 'results',
      '.rf-empty': 'all_sold'
    };
    for (var cls in names) {
      var el = stageEl(cls);
      if (el && !el.hidden) return names[cls];
    }
    return 'empty';
  }

  /** The panel slides toward the edge, THEN hides while the tab slides in and
   *  pulses once, so the collapse visibly becomes the tab. */
  function minimizeDrawer() {
    var drawer = drawerEl();
    if (!drawer || drawer.hidden || hideTimer) return;
    track('concierge_drawer_minimize', { status: currentStage() });
    drawer.classList.remove('rug-finder-drawer--in');
    if (state) state.minimized = true;
    save();
    hideTimer = setTimeout(function () {
      hideTimer = null;
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rug-finder-drawer-open');
      showTab();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }, SLIDE_MS);
  }

  function showTab() {
    var tab = tabEl();
    if (!tab) return;
    var count = tab.querySelector('.rug-finder-tab__count');
    if (count) {
      var n = state && state.rugs ? state.rugs.length : 0;
      count.textContent = n ? ' · ' + n + (n === 1 ? ' pick' : ' picks') : '';
    }
    tab.hidden = false;
    tab.classList.remove('rug-finder-tab--pulse');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        tab.classList.add('rug-finder-tab--in');
        tab.classList.add('rug-finder-tab--pulse');
      });
    });
  }

  function hideTab() {
    var tab = tabEl();
    if (!tab) return;
    tab.classList.remove('rug-finder-tab--in', 'rug-finder-tab--pulse');
    tab.hidden = true;
  }

  function showStage(name) {
    closeEditor();
    ['.rf-q--size', '.rf-q--price', '.rf-loading', '.rf-results', '.rf-empty'].forEach(function (cls) {
      var el = stageEl(cls);
      if (el) el.hidden = true;
    });
    var target = stageEl(name);
    if (target) target.hidden = false;
    var dock = dockEl();
    if (dock) dock.hidden = name !== '.rf-results';
  }

  // ---- inline filter editor ----------------------------------------------

  /** Moves ONE filter's form into the editor above the dock, so filter
   *  changes never take the results off the screen. The editor is compact:
   *  no title, no cross-links to the other filter, no Show rugs button; the
   *  box's arrow or Enter applies. */
  function openEditor(which) {
    var editor = editorEl();
    var q = stageEl(which === 'size' ? '.rf-q--size' : '.rf-q--price');
    if (!editor || !q) return;
    closeEditor();
    editor.querySelector('.rf-editor__slot').appendChild(q);
    q.hidden = false;
    editor.hidden = false;
    prefillQuestion(which, true);
  }

  /** A shopper-initiated close (✕ or Escape): tracked, then closed. */
  function cancelEditor() {
    var editor = editorEl();
    var slot = editor && editor.querySelector('.rf-editor__slot');
    track('concierge_filter_cancel', {
      filter: slot && slot.querySelector('.rf-q--size') ? 'size' : 'price'
    });
    closeEditor();
  }

  /** Puts any form back into the stage (hidden) and hides the editor. */
  function closeEditor() {
    var editor = editorEl();
    if (!editor) return;
    var drawer = drawerEl();
    var stage = drawer && drawer.querySelector('.rug-finder-drawer__stage');
    var slot = editor.querySelector('.rf-editor__slot');
    if (stage && slot) {
      while (slot.firstElementChild) {
        var q = slot.firstElementChild;
        q.hidden = true;
        stage.insertBefore(q, stage.querySelector('.rf-loading'));
      }
    }
    editor.hidden = true;
  }

  // ---- questions ---------------------------------------------------------

  function clearErrors(q) {
    var err = q.querySelector('.rf-err');
    if (err) { err.hidden = true; err.textContent = ''; }
    Array.prototype.forEach.call(q.querySelectorAll('.rf-num--err'), function (el) {
      el.classList.remove('rf-num--err');
    });
  }

  function fail(q, inputs, message, question, reason) {
    inputs.forEach(function (el) { if (el) el.classList.add('rf-num--err'); });
    var err = q.querySelector('.rf-err');
    if (err) { err.textContent = message; err.hidden = false; }
    if (inputs[0] && inputs[0].focus) inputs[0].focus();
    // Validation dead-ends are a UX signal: frequent ones mean the form
    // confuses people, not that people are careless.
    track('concierge_input_error', { question: question, reason: reason });
    return null;
  }

  function numOf(q, key) {
    var el = q.querySelector('[data-rf="' + key + '"]');
    if (!el) return { el: null, raw: '', value: NaN };
    var raw = el.value.trim();
    return { el: el, raw: raw, value: raw === '' ? NaN : Number(raw) };
  }

  function setVal(q, key, value) {
    var el = q.querySelector('[data-rf="' + key + '"]');
    if (el) el.value = value === undefined || value === null ? '' : String(value);
  }

  function flipSizeMode() {
    var q = stageEl('.rf-q--size');
    if (!q) return;
    var exact = q.querySelector('.rf-mode--exact');
    var range = q.querySelector('.rf-mode--range');
    var link = q.querySelector('[data-rf-flip]');
    var toRange = range.hidden;
    exact.hidden = toRange;
    range.hidden = !toRange;
    if (link) {
      link.textContent = toRange ? 'Back to a single size' : 'Have a range in mind? Set min and max';
    }
    clearErrors(q);
    track('concierge_size_mode', { mode: toRange ? 'range' : 'exact' });
  }

  /** Reveals the optional budget row inside the size question (or the size
   *  question itself from the price question, carrying the typed budget). */
  function showBudgetInSize(prefill) {
    var q = stageEl('.rf-q--size');
    if (!q) return;
    var extra = q.querySelector('.rf-extra--budget');
    var link = q.querySelector('[data-rf-extra="budget"]');
    if (extra) extra.hidden = false;
    if (link) link.hidden = true;
    if (prefill !== undefined) setVal(q, 'qprice', prefill);
  }

  /** Exact mode: width and length in feet are required; give-or-take pads both
   *  sides. Blank sends highlight the missing box, never guess. */
  function readSizeExact(q) {
    var w = numOf(q, 'w');
    var l = numOf(q, 'l');
    var badW = !(w.value > 0);
    var badL = !(l.value > 0);
    if (badW || badL) {
      var what = badW && badL ? 'a width and length' : badW ? 'a width' : 'a length';
      var reason = badW && badL ? 'missing_both' : badW ? 'missing_width' : 'missing_length';
      return fail(q, [badW ? w.el : null, badL ? l.el : null].filter(Boolean),
        'Add ' + what + ' so we match rugs that truly fit.', 'size', reason);
    }
    var tol = numOf(q, 'tol');
    var pad = tol.value >= 0 ? Math.round(tol.value) : 12;
    var widthIn = Math.round(w.value * 12);
    var lengthIn = Math.round(l.value * 12);
    state.sizeForm = { mode: 'exact', w: w.raw, l: l.raw, tol: tol.raw };
    return {
      min_width_in: Math.max(1, widthIn - pad),
      max_width_in: widthIn + pad,
      min_length_in: Math.max(1, lengthIn - pad),
      max_length_in: lengthIn + pad
    };
  }

  /** Range mode: any box may stay empty (no limit); a bound counts when its
   *  feet or inches box is filled. At least one bound overall is required. */
  function readSizeRange(q) {
    function bound(feetKey, inchKey) {
      var f = numOf(q, feetKey);
      var i = numOf(q, inchKey);
      if (f.raw === '' && i.raw === '') return undefined;
      var feet = f.raw === '' ? 0 : f.value;
      var inches = i.raw === '' ? 0 : i.value;
      if (!(feet >= 0) || !(inches >= 0)) return undefined;
      var total = Math.round(feet * 12 + inches);
      return total > 0 ? total : undefined;
    }
    var range = {
      min_width_in: bound('minwf', 'minwi'),
      max_width_in: bound('maxwf', 'maxwi'),
      min_length_in: bound('minlf', 'minli'),
      max_length_in: bound('maxlf', 'maxli')
    };
    if (range.min_width_in && range.max_width_in && range.min_width_in > range.max_width_in) {
      var w = range.min_width_in; range.min_width_in = range.max_width_in; range.max_width_in = w;
    }
    if (range.min_length_in && range.max_length_in && range.min_length_in > range.max_length_in) {
      var l = range.min_length_in; range.min_length_in = range.max_length_in; range.max_length_in = l;
    }
    var any = Object.keys(range).some(function (k) { return range[k] !== undefined; });
    if (!any) {
      return fail(q, [q.querySelector('[data-rf="minwf"]')],
        'Set at least one limit, or go back to a single size.', 'size', 'no_bounds');
    }
    var kept = {};
    ['minwf', 'minwi', 'maxwf', 'maxwi', 'minlf', 'minli', 'maxlf', 'maxli'].forEach(function (k) {
      kept[k] = numOf(q, k).raw;
    });
    state.sizeForm = { mode: 'range', bounds: kept };
    return range;
  }

  function submitSize() {
    var q = stageEl('.rf-q--size');
    if (!q) return;
    clearErrors(q);
    ensureState('size');
    var rangeMode = !q.querySelector('.rf-mode--range').hidden;
    var sizeRange = rangeMode ? readSizeRange(q) : readSizeExact(q);
    if (!sizeRange) return;
    var updates = { size_range: sizeRange };
    // A visible budget row DEFINES the budget: filled sets it, empty clears it.
    var extra = q.querySelector('.rf-extra--budget');
    if (extra && !extra.hidden) {
      var qprice = numOf(q, 'qprice');
      var value = Number(String(qprice.raw).replace(/[$,\s]/g, ''));
      updates.max_price_usd = value > 0 ? Math.round(value) : undefined;
      if (value > 0) state.priceValue = Math.round(value);
    }
    // With results on screen, applying only stages the pill; the search waits
    // for Show rugs. The first question (no results yet) searches directly.
    if (resultsVisible()) stageFilter('size', updates);
    else runFast('size', updates);
  }

  function submitPrice() {
    var q = stageEl('.rf-q--price');
    if (!q) return;
    clearErrors(q);
    ensureState('price');
    var price = numOf(q, 'price');
    var value = Number(String(price.raw).replace(/[$,\s]/g, ''));
    if (!(value > 0)) {
      fail(q, [price.el], 'Add a budget so we only show rugs under it.', 'price', 'missing_budget');
      return;
    }
    state.priceValue = Math.round(value);
    if (resultsVisible()) stageFilter('price', { max_price_usd: Math.round(value) });
    else runFast('price', { max_price_usd: Math.round(value) });
  }

  /** Fills a question with what the shopper chose last time. In the stage
   *  flow the size form also reopens with the budget row when a budget is
   *  active (the visible row defines the budget on submit); in the editor
   *  each filter stays strictly its own, and the untouched constraint simply
   *  carries through the merge. */
  function prefillQuestion(which, inEditor) {
    var q = stageEl(which === 'size' ? '.rf-q--size' : '.rf-q--price');
    if (!q) return;
    clearErrors(q);
    if (which === 'size') {
      if (state && state.sizeForm) {
        // Reopen with what they chose last time, in the same mode.
        var form = state.sizeForm;
        var rangeVisible = !q.querySelector('.rf-mode--range').hidden;
        if ((form.mode === 'range') !== rangeVisible) flipSizeMode();
        if (form.mode === 'exact') {
          setVal(q, 'w', form.w); setVal(q, 'l', form.l); setVal(q, 'tol', form.tol || '12');
        } else if (form.bounds) {
          for (var k in form.bounds) setVal(q, k, form.bounds[k]);
        }
      }
      var extra = q.querySelector('.rf-extra--budget');
      var link = q.querySelector('[data-rf-extra="budget"]');
      if (extra) extra.hidden = true;
      if (link) link.hidden = false;
      if (!inEditor && state && state.constraints && state.constraints.max_price_usd !== undefined) {
        showBudgetInSize(state.constraints.max_price_usd);
      }
    }
    if (which === 'price' && state && state.priceValue) {
      setVal(q, 'price', state.priceValue);
    }
    var first = q.querySelector('.rf-num');
    if (first && first.focus) first.focus();
  }

  /** With results on screen the question opens in the inline editor; before
   *  that, it is the stage itself. */
  function openQuestion(which) {
    if (resultsVisible()) {
      openEditor(which);
      return;
    }
    showStage(which === 'size' ? '.rf-q--size' : '.rf-q--price');
    prefillQuestion(which);
  }

  // ---- search ------------------------------------------------------------

  function loadingLabel(tweak) {
    if (tweak === 'size') return 'Finding rugs with this look in your size…';
    if (tweak === 'price') return 'Finding rugs with this look in your budget…';
    if (tweak === 'typed') return 'Reading your note and finding rugs…';
    if (tweak === 'filters') return 'Updating your rugs…';
    if (tweak === 'restore') return 'Loading your picks…';
    if (tweak === 'fresh') return 'Finding fresh picks…';
    return 'Finding the closest matches to this rug…';
  }

  /** Loading, without losing the grid: with results on screen the wait is a
   *  dimmed grid and a dock status line, not a page swap. */
  function showBusy(tweak, inline) {
    if (inline) {
      closeEditor();
      var results = stageEl('.rf-results');
      if (results) results.classList.add('rf-results--updating');
      resultsStatus(loadingLabel(tweak));
    } else {
      showLoading(tweak);
    }
  }

  function showFailure(message, inline) {
    if (inline) {
      var results = stageEl('.rf-results');
      if (results) results.classList.remove('rf-results--updating');
      resultsStatus(message);
    } else {
      showLoadingMessage(message);
    }
  }

  function showLoading(tweak) {
    showStage('.rf-loading');
    var loading = stageEl('.rf-loading');
    if (loading) {
      var label = loading.querySelector('.rf-loading__label');
      if (label) label.textContent = loadingLabel(tweak);
      var spinner = loading.querySelector('.rf-spinner');
      if (spinner) spinner.hidden = false;
    }
  }

  function showLoadingMessage(message) {
    var loading = stageEl('.rf-loading');
    if (!loading) return;
    showStage('.rf-loading');
    var label = loading.querySelector('.rf-loading__label');
    if (label) label.textContent = message;
    var spinner = loading.querySelector('.rf-spinner');
    if (spinner) spinner.hidden = true;
  }

  function ensureState(tweak) {
    var anchor = (state && state.anchor) || anchorFromEntry();
    state = state || {};
    state.anchor = anchor;
    state.tweak = tweak;
    state.constraints = state.constraints || {};
    state.brief = state.brief || '';
    state.rugs = state.rugs || [];
    state.us = state.us || false;
    state.referenceStrength = state.referenceStrength || 'none';
    state.rankingAnchors = state.rankingAnchors || [];
    state.minimized = false;
    return anchor;
  }

  /** The Similar rugs answer: the precomputed picks the pipeline already
   *  stores on the product (same embedding space as the live match), rendered
   *  with zero network calls. The live fast path only runs once a size or
   *  budget filter needs what the metafield can't precompute. */
  function staticSimilars() {
    var el = document.querySelector('.rf-similar-data');
    if (!el) return null;
    try {
      var list = JSON.parse(el.textContent);
      return Array.isArray(list) && list.length ? list : null;
    } catch (e) {
      return null;
    }
  }

  function showSimilar() {
    var anchor = ensureState('similar');
    if (!anchor || !anchor.sku) return;
    state.constraints = {};
    // Similar rugs restarts from this rug: any typed steering is dropped.
    state.typed = false;
    state.referenceStrength = 'strong';
    state.rankingAnchors = [anchor.sku];
    var list = staticSimilars();
    if (!list) { runFast('similar', {}); return; }
    state.brief = 'The closest matches to ' + (anchor.title || 'this rug') + '. Each one is one of a kind.';
    state.rugs = list;
    state.searchedSnap = constraintsSnapshot(state.constraints);
    var tiles = templateTiles();
    var cards = tiles ? stampTiles(tiles, list) : null;
    renderResults(cards ? { cards: cards } : undefined);
    save();
    track('concierge_results_shown', { count: list.length, source: 'precomputed' });
  }

  /** The staged/searched split: pills reflect state.constraints immediately,
   *  but nothing searches until the shopper says so. A snapshot of the last
   *  SEARCHED set tells the dock when to offer Show rugs. */
  function constraintsSnapshot(c) {
    c = c || {};
    return JSON.stringify({
      s: c.size_range || null,
      p: c.max_price_usd === undefined ? null : c.max_price_usd
    });
  }

  function isDirty() {
    if (!state) return false;
    var searched = state.searchedSnap || constraintsSnapshot({});
    return constraintsSnapshot(state.constraints) !== searched;
  }

  /** Applies a filter change to the pills WITHOUT searching: the expensive
   *  match runs once, when the shopper taps Show rugs in the dock. Clearing
   *  the last filter falls back to the free precomputed similars instantly. */
  function stageFilter(tweak, updates, options) {
    ensureState(tweak);
    var c = state.constraints;
    if ('size_range' in updates) c.size_range = updates.size_range;
    if ('max_price_usd' in updates) c.max_price_usd = updates.max_price_usd;
    if (c.size_range === undefined) delete c.size_range;
    if (c.max_price_usd === undefined) delete c.max_price_usd;

    if (!c.size_range && c.max_price_usd === undefined && !state.typed && staticSimilars()) {
      closeEditor();
      showSimilar();
      return;
    }

    closeEditor();
    save();
    if (!options || options.track !== false) {
      track('concierge_filter_staged', {
        filter: tweak,
        has_size: Boolean(c.size_range),
        has_price: c.max_price_usd !== undefined
      });
    }
    renderPills();
    syncReady();
    resultsStatus(isDirty() ? "Filters saved. Show rugs when you're ready." : '');
  }

  /** The dock's ONE submit serves both the staged pills and the describe box;
   *  it lights up whenever either has something new to send. */
  function syncReady() {
    var dock = dockEl();
    var form = dock && dock.querySelector('.rf-refine');
    if (!form) return;
    var input = form.querySelector('.rf-refine__input');
    var hasText = Boolean(input && input.value.trim());
    form.classList.toggle('rf-refine--ready', hasText || isDirty());
  }

  /** Merges the updates into the active constraint set and re-runs one
   *  structured fast call with the FULL set, so combined asks stay exact.
   *  options.live forces the network match: a restored session's anchor may
   *  not be this page's product, so the precomputed shortcut would show the
   *  wrong rug's picks. */
  function runFast(tweak, updates, options) {
    var anchor = ensureState(tweak);
    if (!anchor || !anchor.sku) return;
    var c = state.constraints;
    if ('size_range' in updates) c.size_range = updates.size_range;
    if ('max_price_usd' in updates) c.max_price_usd = updates.max_price_usd;
    if (c.size_range === undefined) delete c.size_range;
    if (c.max_price_usd === undefined) delete c.max_price_usd;

    // With no filters left (and no typed context to preserve), the
    // precomputed picks answer instantly.
    var live = Boolean(options && options.live);
    if (!live && !c.size_range && c.max_price_usd === undefined && !state.typed && staticSimilars()) {
      showSimilar();
      return;
    }

    var inline = resultsVisible();
    showBusy(tweak, inline);
    track('concierge_submit', {
      mode: 'fast',
      tweak: tweak,
      has_size: Boolean(c.size_range),
      has_price: c.max_price_usd !== undefined
    });
    var payload = {
      clientId: clientId(),
      anchor_skus: state.typed
        ? (state.rankingAnchors || [])
        : [anchor.sku]
    };
    if (c.size_range) payload.size_range = c.size_range;
    if (c.max_price_usd !== undefined) payload.max_price_usd = c.max_price_usd;
    // A typed session's brief and parser decision keep steering the structured
    // ranking without silently promoting page context to a strong anchor.
    if (state.typed && state.brief) {
      payload.prior_brief = state.brief;
      payload.reference_strength = state.referenceStrength || 'none';
      payload.context_skus = [anchor.sku];
    }
    post(payload).then(function (result) { handleResult(result, 'fast_match', tweak, false, inline); })
      .catch(function () {
        trackError('fast_match', 'unreachable');
        showFailure(UNREACHABLE, inline);
      });
  }

  function runTyped(text, refine) {
    var anchor = ensureState('typed');
    if (!anchor || !anchor.sku || !text) return;
    var isRefine = Boolean(refine && state.brief);
    var inline = resultsVisible();
    showBusy('typed', inline);
    // Refines reuse the page funnel's established event names so GA4 sees one
    // consistent concierge journey regardless of which surface hosted it.
    if (isRefine) track('concierge_refine_submit', { has_photo: false });
    else track('concierge_submit', { mode: 'typed', has_size: false, has_price: false });
    var payload = { clientId: clientId() };
    if (isRefine) {
      payload.refine = text;
      payload.prior_brief = state.brief;
      payload.prior_anchor_skus = state.rankingAnchors || [];
      payload.reference_strength = state.referenceStrength || 'none';
    } else {
      payload.notes = text;
      payload.context_skus = [anchor.sku];
    }
    // The active pills (including staged, unsearched ones) ride along; the
    // backend lets the typed words win wherever they overlap.
    var c = state.constraints || {};
    if (c.size_range) payload.size_range = c.size_range;
    if (c.max_price_usd !== undefined) payload.max_price_usd = c.max_price_usd;
    post(payload).then(function (result) { handleResult(result, isRefine ? 'refine' : 'match', 'typed', isRefine, inline); })
      .catch(function () {
        trackError(isRefine ? 'refine' : 'match', 'unreachable');
        showFailure(UNREACHABLE, inline);
      });
  }

  function handleResult(result, stage, tweak, isRefine, inline) {
    var body = result.body || {};
    if (result.status === 200 && body.rugs && body.rugs.length > 0) {
      state.brief = body.brief || '';
      state.rugs = body.rugs;
      state.us = body.us === true;
      // The typed flag marks a session whose ranking the customer's words
      // steer; structured turns inherit it via prior_brief.
      if (stage === 'match' || stage === 'refine') {
        state.typed = true;
        state.referenceStrength = body.reference_strength || 'none';
        state.rankingAnchors = body.anchor_skus || [];
      } else if (body.reference_strength) {
        state.referenceStrength = body.reference_strength;
        state.rankingAnchors = body.anchor_skus || state.rankingAnchors;
      }
      // These results reflect the full staged set: nothing left to apply.
      state.searchedSnap = constraintsSnapshot(state.constraints);
      save();
      track(isRefine ? 'concierge_results_refined' : 'concierge_results_shown', { count: body.rugs.length, source: 'live' });
      renderRich();
      return;
    }
    trackError(stage, body.message ? 'server_message' : 'server_error', result);
    showFailure(body.message || UNREACHABLE, inline);
    if (!inline && (tweak === 'size' || tweak === 'price')) {
      // Give the shopper their question back under the message so they can
      // widen the ask instead of dead-ending.
      var q = stageEl(tweak === 'size' ? '.rf-q--size' : '.rf-q--price');
      if (q) q.hidden = false;
    }
  }

  // ---- result cards ------------------------------------------------------
  // The grid reuses the theme's real product-item card (srcset images, hover
  // second image, quick buy) instead of bespoke tiles. Precomputed similars
  // ship in a server-rendered <template>; live results come from the search
  // page's section rendering, queried by SKU. The bespoke JSON tiles remain
  // as the fallback when either source is unavailable.

  var SECTION_ID = 'main-search';
  var SEARCH_PAGE_SIZE = 24; // templates/search.json products_per_page

  function templateTiles() {
    var template = document.querySelector('.rf-similar-tiles');
    if (!template || !template.content) return null;
    var tiles = template.content.querySelectorAll('product-item');
    return tiles.length ? tiles : null;
  }

  /** Deep-clones template tiles into grid-ready cards, in the JSON's order. */
  function stampTiles(tiles, list) {
    var cards = [];
    for (var i = 0; i < tiles.length && i < list.length; i++) {
      var node = document.importNode(tiles[i], true);
      node.removeAttribute('reveal');
      node.setAttribute('data-handle', list[i].handle || '');
      node.setAttribute('data-position', String(i + 1));
      cards.push(node);
    }
    return cards.length ? cards : null;
  }

  /* Product handles reach us from two sources that disagree on case: the
     concierge speaks SKUs ("52951BD") and Shopify's URLs are lower case
     ("/products/52951bd"). Both sides of every lookup go through here. */
  function normHandle(s) { return String(s || '').toLowerCase(); }

  function handleFromCard(node) {
    var link = node.querySelector('a[href*="/products/"]');
    if (!link) return '';
    var path = (link.getAttribute('href').split('/products/')[1] || '').split('?')[0];
    return normHandle(path.split('#')[0]);
  }

  /** The storefront search query that asks for a known set of SKUs by name.
   *  Bare terms OR'd together ("A OR B OR C") answer with nothing past two of
   *  them; the field-qualified form is the one Shopify honours in bulk, and
   *  the difference decides whether results render as theme tiles or fall back
   *  to plain JSON cards. tests/rug-finder-drawer-live.spec.js holds it to
   *  that against the real index. */
  function skuQuery(skus) {
    return skus.map(function (s) { return 'variants.sku:' + s; }).join(' OR ');
  }

  /** One search-section request (two when over a page) renders every result
   *  SKU through the same product-item snippet collections use. positionOffset
   *  keeps data-position counting through pages: page two starts at 41. */
  function fetchSectionCards(rugs, positionOffset) {
    var wanted = {};
    rugs.forEach(function (r, i) { if (r.handle) wanted[normHandle(r.handle)] = i; });
    var skus = rugs.map(function (r) { return r.sku; }).filter(Boolean);
    if (!skus.length) return Promise.reject(new Error('no_skus'));
    var query = encodeURIComponent(skuQuery(skus));
    var fetches = [];
    var pages = Math.max(1, Math.ceil(skus.length / SEARCH_PAGE_SIZE));
    for (var p = 1; p <= pages; p++) {
      var url = '/search?q=' + query + '&type=product&section_id=' + SECTION_ID +
        (p > 1 ? '&page=' + p : '');
      fetches.push(fetch(url).then(function (response) {
        if (!response.ok) throw new Error('section_' + response.status);
        return response.text();
      }));
    }
    return Promise.all(fetches).then(function (htmls) {
      var byHandle = {};
      htmls.forEach(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        Array.prototype.forEach.call(doc.querySelectorAll('product-item'), function (node) {
          var handle = handleFromCard(node);
          if (handle && wanted[handle] !== undefined && !byHandle[handle]) {
            byHandle[handle] = node;
          }
        });
      });
      var cards = [];
      var sold = {};
      rugs.forEach(function (rug, i) {
        var node = byHandle[normHandle(rug.handle)];
        // Search may fuzz or lag: skip anything we did not ask for.
        if (!node) return;
        // Sold since the match ran; one of a kind, so gone for good.
        if (node.classList.contains('product-item--sold-out')) { sold[rug.handle] = true; return; }
        node.removeAttribute('reveal');
        node.setAttribute('data-handle', rug.handle);
        node.setAttribute('data-position', String((positionOffset || 0) + i + 1));
        cards.push(node);
      });
      // Both can be empty: the caller must tell "every pick proven sold"
      // apart from "the index answered with none of them".
      return { cards: cards, sold: sold };
    });
  }

  /** Rugs the section render marked sold leave the session for good, so the
   *  tab count, email/share SKUs and any later JSON-tile fallback only speak
   *  for rugs a shopper can still buy. A session pruned to nothing stops
   *  restoring on other pages: load() rejects empty rugs. */
  function dropSoldRugs(sold) {
    var keep = state.rugs.filter(function (r) { return !sold[r.handle]; });
    if (keep.length === state.rugs.length) return;
    state.rugs = keep;
    save();
  }

  /** Tracks a fetchSectionCards failure under an allowlisted reason. Only these
   *  fixed reasons (plus the search page's HTTP status) may reach the
   *  dataLayer — never the raw error text. The empty-render verdicts
   *  (section_empty and section_missing) are not thrown; they track from
   *  renderRich's resolve path, where the sold map decides between them. */
  function trackSectionError(error) {
    var message = (error && error.message) || '';
    var http = /^section_(\d{3})$/.exec(message);
    if (http) trackError('cards', 'section_http', { status: Number(http[1]) });
    else trackError('cards', message === 'no_skus' ? message : 'section_exception');
  }

  /** Renders the current rugs as rich cards, falling back to the JSON tiles;
   *  a newer turn's results supersede an in-flight render. When no card
   *  survives, "all sold" needs positive proof — every pick carrying the
   *  sold-out mark — and only then shows the found-homes state. A render
   *  that simply matched nothing could be index lag (a just-listed rug is
   *  searchable late), so the tiles stay the honest fallback there, minus
   *  any pick the render did prove sold. */
  function renderRich() {
    var expected = state.rugs;
    fetchSectionCards(expected).then(function (found) {
      if (!state || state.rugs !== expected) return;
      dropSoldRugs(found.sold);
      if (found.cards.length) { renderResults({ cards: found.cards }); return; }
      var allSold = expected.every(function (r) { return found.sold[r.handle] === true; });
      trackError('cards', allSold ? 'section_empty' : 'section_missing');
      if (allSold) { showStage('.rf-empty'); return; }
      renderResults();
    }).catch(function (error) {
      if (!state || state.rugs !== expected) return;
      trackSectionError(error);
      renderResults();
    });
  }

  // ---- results -----------------------------------------------------------

  function formatPrice(value) {
    var n = Number(value);
    if (!isFinite(n)) return '';
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  function feet(inches) {
    var f = Math.floor(inches / 12);
    var rest = Math.round(inches - f * 12);
    if (rest === 12) { f += 1; rest = 0; }
    return rest === 0 ? f + "'" : f + "'" + rest + '"';
  }

  function loadCardImage(card) {
    var handle = card.getAttribute('data-handle');
    if (!handle) return;
    var existing = card.querySelector('.room-concierge-results__img');
    if (existing && existing.src) return;
    // A known image URL waits as data-rf-src so nothing downloads while the
    // drawer is hidden; assign it on first intersection instead of fetching.
    var direct = existing && existing.getAttribute('data-rf-src');
    if (direct) {
      existing.src = direct;
      existing.hidden = false;
      return;
    }
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.featured_image) return;
        var img = card.querySelector('.room-concierge-results__img');
        if (!img) return;
        var src = p.featured_image;
        if (src.indexOf('//') === 0) src = 'https:' + src;
        src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=600&height=750&crop=center';
        img.src = src;
        img.hidden = false;
      })
      .catch(function () {});
  }

  function buildCard(rug, position) {
    var card = document.createElement('a');
    card.className = 'room-concierge-results__card';
    card.href = '/products/' + rug.handle;
    card.setAttribute('data-handle', rug.handle);
    card.setAttribute('data-position', String(position));
    var media = document.createElement('div');
    media.className = 'room-concierge-results__media';
    var img = document.createElement('img');
    img.className = 'room-concierge-results__img';
    img.loading = 'lazy';
    img.alt = rug.title || '';
    img.hidden = true;
    if (rug.image) img.setAttribute('data-rf-src', rug.image);
    media.appendChild(img);
    var meta = document.createElement('div');
    meta.className = 'product-item-meta room-concierge-results__meta';
    var title = document.createElement('span');
    title.className = 'product-item-meta__title';
    title.textContent = rug.title || '';
    var priceList = document.createElement('div');
    priceList.className = 'price-list price-list--centered';
    var price = document.createElement('span');
    price.className = 'price';
    // Precomputed tiles carry no dimensions (the size lives in the title).
    var sizeLabel = rug.width_inches_total && rug.length_inches_total
      ? feet(rug.width_inches_total) + ' x ' + feet(rug.length_inches_total) + '  ·  '
      : '';
    price.textContent = sizeLabel + formatPrice(rug.price);
    priceList.appendChild(price);
    meta.appendChild(title);
    meta.appendChild(priceList);
    card.appendChild(media);
    card.appendChild(meta);
    return card;
  }

  function sizePillLabel() {
    var form = state.sizeForm;
    if (form && form.mode === 'exact' && form.w && form.l) {
      return form.w + '′ × ' + form.l + '′';
    }
    return 'Custom size';
  }

  /** The dock's pills: every active constraint, editable and removable, plus
   *  a + chip for whichever of size/budget is missing. */
  function renderPills() {
    var dock = dockEl();
    if (!dock || !state) return;
    var wrap = dock.querySelector('.rf-pills');
    if (!wrap) return;
    wrap.textContent = '';
    var c = state.constraints || {};

    function pill(filter, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rf-pill';
      b.setAttribute('data-rf-pill', filter);
      b.appendChild(document.createTextNode(label));
      var x = document.createElement('span');
      x.className = 'rf-pill__x';
      x.setAttribute('data-rf-pill-x', filter);
      x.setAttribute('aria-label', 'Remove');
      x.textContent = '✕';
      b.appendChild(x);
      wrap.appendChild(b);
    }
    function addChip(filter, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rf-pill rf-pill--add';
      b.setAttribute('data-rf-pill-add', filter);
      b.textContent = label;
      wrap.appendChild(b);
    }

    if (c.size_range) pill('size', sizePillLabel());
    if (c.max_price_usd !== undefined) pill('price', 'Under ' + formatPrice(c.max_price_usd));
    if (!c.size_range) addChip('size', '+ Size');
    if (c.max_price_usd === undefined) addChip('price', '+ Budget');
  }

  function renderResults(options) {
    var results = stageEl('.rf-results');
    if (!results || !state) return;
    var line = results.querySelector('.rf-results__line');
    if (line) line.textContent = state.brief;
    var grid = results.querySelector('.rf-results__grid');
    if (grid) {
      grid.textContent = '';
      var richCards = options && options.cards;
      if (richCards) {
        // Real product-item cards: native lazy srcset images, nothing to do.
        richCards.forEach(function (node) { grid.appendChild(node); });
      } else {
        state.rugs.forEach(function (rug, i) { grid.appendChild(buildCard(rug, i + 1)); });
        var cards = grid.querySelectorAll('.room-concierge-results__card');
        var io = ('IntersectionObserver' in window)
          ? new IntersectionObserver(function (entries) {
              entries.forEach(function (e) {
                if (e.isIntersecting) { loadCardImage(e.target); io.unobserve(e.target); }
              });
            }, { rootMargin: '400px' })
          : null;
        Array.prototype.forEach.call(cards, function (card) {
          if (io) { io.observe(card); } else { loadCardImage(card); }
        });
      }
    }
    results.classList.remove('rf-results--updating');
    var dock = dockEl();
    if (dock) {
      var optin = dock.querySelector('.rf-optin__box');
      if (optin) optin.checked = state.us === true;
      var status = dock.querySelector('.rf-status');
      if (status) { status.hidden = true; status.textContent = ''; }
    }
    showStage('.rf-results');
    renderPills();
    syncReady();
    var stage = drawerEl() && drawerEl().querySelector('.rug-finder-drawer__stage');
    if (stage) stage.scrollTop = 0;
  }

  function resultsStatus(message) {
    var dock = dockEl();
    var status = dock && dock.querySelector('.rf-status');
    if (!status) return;
    if (message) { status.textContent = message; status.hidden = false; }
    else { status.hidden = true; status.textContent = ''; }
  }

  // ---- capture & share ---------------------------------------------------

  function onCapture(form) {
    if (!state || !state.rugs.length) return;
    var email = (form.querySelector('.rf-capture__email').value || '').trim();
    if (!email) return;
    var optin = form.querySelector('.rf-optin__box');
    resultsStatus('Sending…');
    post({
      captureOnly: true,
      clientId: clientId(),
      email: email,
      marketing: Boolean(optin && optin.checked),
      skus: state.rugs.map(function (r) { return r.sku; }),
      prior_brief: state.brief
    }).then(function (result) {
      var body = result.body || {};
      if (result.status === 200 && body.ok) {
        form.hidden = true;
        resultsStatus(body.message || 'Sent. These picks are on their way to your inbox.');
        track('concierge_lead_captured', { count: state.rugs.length });
      } else {
        trackError('capture', body.message ? 'server_message' : 'server_error', result);
        resultsStatus(body.message || UNREACHABLE);
      }
    }).catch(function () {
      trackError('capture', 'unreachable');
      resultsStatus(UNREACHABLE);
    });
  }

  function onShare() {
    if (!state || !state.rugs.length) return;
    resultsStatus('Creating your link…');
    post({
      share: true,
      clientId: clientId(),
      skus: state.rugs.map(function (r) { return r.sku; }),
      prior_brief: state.brief
    }).then(function (result) {
      var body = result.body || {};
      var dock = dockEl();
      var row = dock && dock.querySelector('.rf-share-row');
      if (result.status === 200 && body.share_url && row) {
        var link = row.querySelector('.rf-share-row__link');
        if (link) link.value = body.share_url;
        row.hidden = false;
        resultsStatus('');
        track('concierge_share_created', { count: state.rugs.length });
      } else {
        trackError('share', body.message ? 'server_message' : 'server_error', result);
        resultsStatus(body.message || UNREACHABLE);
      }
    }).catch(function () {
      trackError('share', 'unreachable');
      resultsStatus(UNREACHABLE);
    });
  }

  // ---- session persistence ----------------------------------------------

  function save() {
    if (!state) return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        v: 2,
        anchor: state.anchor,
        brief: state.brief,
        rugs: state.rugs,
        us: state.us,
        typed: state.typed === true,
        referenceStrength: state.referenceStrength || 'none',
        rankingAnchors: state.rankingAnchors || [],
        constraints: state.constraints || {},
        searchedSnap: state.searchedSnap,
        sizeForm: state.sizeForm,
        priceValue: state.priceValue,
        minimized: state.minimized
      }));
    } catch (e) { /* storage unavailable: the session just won't persist */ }
  }

  function load() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || saved.v !== 2 || !saved.rugs || !saved.rugs.length) return null;
      return saved;
    } catch (e) {
      return null;
    }
  }

  function restoreFromStorage() {
    if (toolbarActive()) return;
    var saved = load();
    if (!saved || !drawerEl()) return;
    state = {
      anchor: saved.anchor,
      brief: saved.brief,
      rugs: saved.rugs,
      us: saved.us,
      typed: saved.typed === true,
      referenceStrength: saved.referenceStrength || 'none',
      rankingAnchors: saved.rankingAnchors || [],
      constraints: saved.constraints || {},
      searchedSnap: saved.searchedSnap,
      sizeForm: saved.sizeForm,
      priceValue: saved.priceValue,
      minimized: true
    };
    // No render yet: the grid fills (and images download) only when the
    // shopper actually reopens the tab.
    var tab = tabEl();
    if (tab) {
      var count = tab.querySelector('.rug-finder-tab__count');
      if (count) {
        var n = state.rugs.length;
        count.textContent = ' · ' + n + (n === 1 ? ' pick' : ' picks');
      }
      tab.hidden = false;
      tab.classList.add('rug-finder-tab--in');
    }
  }

  // ---- events ------------------------------------------------------------

  function onClick(event) {
    var closest = function (sel) { return event.target.closest && event.target.closest(sel); };

    // Which picks earn a product-page visit; navigation proceeds untouched.
    var richCard = closest('.rf-results__grid product-item');
    if (richCard && closest('a[href*="/products/"]')) {
      var richPos = parseInt(richCard.getAttribute('data-position'), 10);
      track('concierge_result_click', {
        handle: richCard.getAttribute('data-handle') || undefined,
        position: isFinite(richPos) ? richPos : undefined
      });
      return;
    }

    var tweakBtn = closest('.rug-finder-entry__btn');
    if (tweakBtn) {
      event.preventDefault();
      var tweak = tweakBtn.getAttribute('data-rf-tweak');
      openDrawer(tweak);
      if (tweak === 'similar') { showSimilar(); return; }
      // With results on screen this opens the inline editor over them.
      openQuestion(tweak);
      return;
    }

    if (closest('[data-rf-editor-go]')) {
      event.preventDefault();
      var goSlot = editorEl() && editorEl().querySelector('.rf-editor__slot');
      if (goSlot && goSlot.querySelector('.rf-q--size')) submitSize();
      else submitPrice();
      return;
    }

    if (closest('[data-rf-editor-close]')) {
      event.preventDefault();
      cancelEditor();
      return;
    }

    if (closest('[data-rf-min]')) { event.preventDefault(); minimizeDrawer(); return; }
    if (closest('.rug-finder-tab')) {
      event.preventDefault();
      openDrawer('restore');
      if (state && state.rugs.length) {
        if (gridHasCards()) {
          showStage('.rf-results');
          renderPills();
        } else {
          // Fresh page: the saved session renders on demand, rich first.
          showLoading('restore');
          renderRich();
        }
      }
      return;
    }
    if (closest('[data-rf-flip]')) { event.preventDefault(); flipSizeMode(); return; }

    if (closest('[data-rf-fresh]')) {
      // The all-sold state's one exit: rerun the saved search live (anchor,
      // filters and any typed brief intact); the backend only returns rugs
      // still for sale.
      event.preventDefault();
      runFast('fresh', {}, { live: true });
      return;
    }

    var extraLink = closest('[data-rf-extra]');
    if (extraLink) {
      event.preventDefault();
      var which = extraLink.getAttribute('data-rf-extra');
      if (which === 'budget') {
        track('concierge_filter_add', { filter: 'price', surface: 'question' });
        showBudgetInSize();
      } else if (which === 'size') {
        // From the price question: switch to the size question with the
        // budget row already expanded, carrying whatever they typed.
        track('concierge_filter_add', { filter: 'size', surface: 'question' });
        var priceQ = stageEl('.rf-q--price');
        var typed = priceQ ? numOf(priceQ, 'price').raw : '';
        ensureState('size');
        openQuestion('size');
        showBudgetInSize(typed || (state && state.priceValue) || '');
      }
      return;
    }

    var go = closest('[data-rf-go]');
    if (go) {
      event.preventDefault();
      if (go.getAttribute('data-rf-go') === 'size') submitSize();
      else submitPrice();
      return;
    }

    var pillX = closest('[data-rf-pill-x]');
    if (pillX) {
      event.preventDefault();
      event.stopPropagation();
      var removed = pillX.getAttribute('data-rf-pill-x');
      track('concierge_filter_removed', { filter: removed });
      ensureState('filters');
      // Removal stages like any other change; only Show rugs searches.
      if (removed === 'size') stageFilter('filters', { size_range: undefined }, { track: false });
      else stageFilter('filters', { max_price_usd: undefined }, { track: false });
      return;
    }
    var pillAdd = closest('[data-rf-pill-add]');
    if (pillAdd) {
      event.preventDefault();
      var adding = pillAdd.getAttribute('data-rf-pill-add');
      track('concierge_filter_add', { filter: adding, surface: 'dock' });
      openQuestion(adding);
      return;
    }
    var pillEdit = closest('[data-rf-pill]');
    if (pillEdit) {
      event.preventDefault();
      var editing = pillEdit.getAttribute('data-rf-pill');
      track('concierge_filter_edit', { filter: editing });
      openQuestion(editing);
      return;
    }

    if (closest('[data-rf-email]')) {
      event.preventDefault();
      var dock = dockEl();
      var capture = dock && dock.querySelector('.rf-capture');
      if (capture) {
        capture.hidden = !capture.hidden;
        if (!capture.hidden) {
          var row = dock.querySelector('.rf-share-row');
          if (row) row.hidden = true;
          var input = capture.querySelector('.rf-capture__email');
          if (input && input.focus) input.focus();
        }
      }
      return;
    }
    if (closest('[data-rf-share]')) { event.preventDefault(); onShare(); return; }

    var copy = closest('.rf-share-row__copy');
    if (copy) {
      event.preventDefault();
      var linkInput = copy.parentElement.querySelector('.rf-share-row__link');
      if (linkInput && navigator.clipboard) {
        navigator.clipboard.writeText(linkInput.value).then(function () {
          copy.textContent = 'Copied';
          setTimeout(function () { copy.textContent = 'Copy'; }, 1600);
        }).catch(function () {});
      }
    }
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form.classList) return;
    if (form.getAttribute && form.getAttribute('is') === 'product-form' && form.closest('.rf-results__grid')) {
      // Quick buy inside the finder: the theme's product-form handles the add;
      // we log it and step aside so the cart drawer (which sits underneath
      // this panel) is visible when it opens.
      var item = form.closest('product-item');
      var addPos = item ? parseInt(item.getAttribute('data-position'), 10) : NaN;
      track('concierge_result_add_to_cart', {
        handle: (item && item.getAttribute('data-handle')) || undefined,
        position: isFinite(addPos) ? addPos : undefined
      });
      setTimeout(minimizeDrawer, 250);
      return;
    }
    if (form.classList.contains('rug-finder-entry__ask')) {
      event.preventDefault();
      var input = form.querySelector('.rug-finder-entry__ask-input');
      var text = input ? input.value.trim() : '';
      if (!text) { if (input) input.focus(); return; }
      openDrawer('text');
      runTyped(text, false);
      if (input) input.value = '';
      return;
    }
    if (form.classList.contains('rf-capture')) {
      event.preventDefault();
      onCapture(form);
      return;
    }
    if (form.classList.contains('rf-refine')) {
      event.preventDefault();
      var refineInput = form.querySelector('.rf-refine__input');
      var refineText = refineInput ? refineInput.value.trim() : '';
      if (refineText) {
        // One search covers both: the typed request carries the staged pills.
        runTyped(refineText, true);
        refineInput.value = '';
        syncReady();
        return;
      }
      if (isDirty()) {
        ensureState('filters');
        runFast('filters', {});
        return;
      }
      // Nothing new to send: just invite the ask.
      if (refineInput && refineInput.focus) refineInput.focus();
    }
  }

  function onKeydown(event) {
    // Enter in any size/price box applies the filter, no button needed.
    if (event.key === 'Enter') {
      var input = event.target;
      if (input && input.classList && input.classList.contains('rf-num')) {
        if (input.closest('.rf-q--size')) { event.preventDefault(); submitSize(); }
        else if (input.closest('.rf-q--price')) { event.preventDefault(); submitPrice(); }
      }
      return;
    }
    if (event.key !== 'Escape') return;
    var drawer = drawerEl();
    if (!drawer || drawer.hidden) return;
    // First Escape closes an open inline editor; the next minimizes.
    if (editorOpen()) { cancelEditor(); return; }
    minimizeDrawer();
  }

  function onInput(event) {
    var t = event.target;
    if (t && t.classList && t.classList.contains('rf-refine__input')) syncReady();
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('input', onInput, true);

  placeEntry();
  var placementMql = window.matchMedia(MOBILE_MQ);
  if (placementMql.addEventListener) placementMql.addEventListener('change', placeEntry);
  restoreFromStorage();

  // The similar-rugs toolbar (next IIFE) speaks to the same concierge about
  // the same rug; it borrows the transport and rendering rather than carrying
  // copies that would drift.
  window.RugConcierge = {
    post: post,
    clientId: clientId,
    track: track,
    anchorFromEntry: anchorFromEntry,
    syncStandDown: syncStandDown,
    staticSimilars: staticSimilars,
    templateTiles: templateTiles,
    stampTiles: stampTiles,
    fetchSectionCards: fetchSectionCards,
    skuQuery: skuQuery,
    buildCard: buildCard,
    loadCardImage: loadCardImage,
    formatPrice: formatPrice,
    UNREACHABLE: UNREACHABLE
  };
})();

/* ==========================================================================
   "Check availability" (rug-ask)
   --------------------------------------------------------------------------
   Enhances snippets/rug-availability-ask.liquid, the CTA that On Hold rugs get
   in place of the dead Add to Cart. Everything here is an upgrade over markup
   that already works: the disclosure is a CSS state (see .rug-ask in
   custom.css) and the form is a real {% form 'contact' %} that posts natively
   with JS off. What this adds is submitting without leaving the page, the
   new-arrivals subscribe, and the funnel events.

   The opt-in is a genuine subscribe, not a note in an email: a second POST to
   the same /contact endpoint with form_type=customer and the newsletter tag,
   exactly what the footer signup sends (sections/footer.liquid). It is fired
   after the enquiry and never blocks the confirmation, because a shopper who
   asked about a rug has been heard whether or not the mailing list took them.

   COPY RULE, binding: never say why the rug is unavailable. See the snippet.
   ========================================================================== */
(function () {
  var ROOT_SELECTOR = '.rug-ask';
  var SENDING = 'Sending…';
  var FAILED = 'We could not send that just now. Please try again.';

  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined) {
          payload[key] = params[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  function handle() {
    return (location.pathname.split('/products/')[1] || '').split('?')[0] || undefined;
  }

  function optedIn(root) {
    var box = root.querySelector('.rug-ask__optin-box');
    return Boolean(box && box.checked);
  }

  function fineprint(root, message, isError) {
    var note = root.querySelector('.rug-ask__fineprint');
    if (!note) return;
    if (!note.hasAttribute('data-default')) note.setAttribute('data-default', note.textContent);
    note.textContent = message === null ? note.getAttribute('data-default') : message;
    note.classList.toggle('rug-ask__fineprint--error', Boolean(isError));
  }

  function openPanel(root) {
    if (root.classList.contains('rug-ask--open')) return;
    root.classList.add('rug-ask--open');
    var button = root.querySelector('.rug-ask__open');
    if (button) button.setAttribute('aria-expanded', 'true');
    track('availability_ask_open', { handle: handle() });
    // Focus after the panel is laid out, so the browser scrolls to a box that
    // has a position rather than to where it was while display:none.
    requestAnimationFrame(function () {
      // A frame late on a busy page must not pull the focus, and what the
      // shopper is typing, out of a box they already moved to.
      var active = document.activeElement;
      if (active && active !== document.body && active !== button) return;
      var email = root.querySelector('.rug-ask__input[type="email"]');
      if (email && email.focus) email.focus();
    });
  }

  function done(root) {
    root.classList.add('rug-ask--done');
    var panel = root.querySelector('.rug-ask__done[hidden]');
    if (panel) panel.hidden = false;
  }

  // Shopify answers a good contact post with a redirect that carries
  // contact_posted=true, so the followed response URL is the success signal.
  // Anything else (a validation error, a bot challenge) re-renders the page at
  // the same URL, which is a failure as far as this form is concerned.
  function posted(response, param) {
    return Boolean(response && response.ok && response.url && response.url.indexOf(param) !== -1);
  }

  function subscribe(action, email) {
    var data = new FormData();
    data.append('form_type', 'customer');
    data.append('utf8', '✓');
    data.append('contact[tags]', 'newsletter');
    data.append('contact[email]', email);
    return fetch(action, { method: 'POST', body: data });
  }

  function submit(form) {
    var root = form.closest(ROOT_SELECTOR);
    if (!root) return;
    if (form.reportValidity && !form.reportValidity()) return;

    var button = form.querySelector('.rug-ask__submit');
    var label = button ? button.textContent : '';
    if (button) { button.disabled = true; button.textContent = SENDING; }
    fineprint(root, null, false);

    var emailField = form.querySelector('.rug-ask__input[type="email"]');
    var email = emailField ? emailField.value.trim() : '';
    var marketing = optedIn(root);
    // Strip the fragment the {% form %} tag appends: harmless on the POST, but
    // it would ride into the response URL and muddy the success check.
    var action = (form.getAttribute('action') || '/contact').split('#')[0];

    fetch(action, { method: 'POST', body: new FormData(form) })
      .then(function (response) {
        if (!posted(response, 'contact_posted=true')) {
          throw new Error('not_posted');
        }
        track('availability_ask_submit', { handle: handle(), marketing_opt_in: marketing });
        done(root);
        if (marketing && email) {
          subscribe(action, email)
            .then(function (subscribed) {
              // Checked, not assumed: a rejected signup answers 200 at the same
              // URL, so without this a mailing list that quietly stopped taking
              // anyone would look like it was working.
              if (!posted(subscribed, 'customer_posted=true')) throw new Error('not_posted');
            })
            .catch(function () {
              // The enquiry landed and the ticked box is already in its email, so
              // a human can still add them. Nothing to tell the shopper.
              track('availability_ask_error', { handle: handle(), reason: 'subscribe_failed' });
            });
        }
      })
      .catch(function (error) {
        if (button) { button.disabled = false; button.textContent = label; }
        fineprint(root, FAILED, true);
        track('availability_ask_error', {
          handle: handle(),
          reason: error && error.message === 'not_posted' ? 'not_posted' : 'unreachable'
        });
      });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var opener = target.closest('.rug-ask__open');
    if (!opener) return;
    var root = opener.closest(ROOT_SELECTOR);
    if (root) openPanel(root);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.classList || !form.classList.contains('rug-ask__form')) return;
    event.preventDefault();
    submit(form);
  });
})();

/* ==========================================================================
   Desktop PDP: the tabs belong in the info column
   --------------------------------------------------------------------------
   Description and More details ship as their own full-width section below the
   product (sections/product-content.liquid). On a desktop PDP that reads as a
   different page: the info column runs out at about half the height of the rug
   image, so everything a shopper wants to know sits past the fold beside 300px
   of nothing. This moves that block to the end of the info column at desktop
   widths, filling space the column was already wasting.

   JS rather than Liquid because the tabs are a separate SECTION rendered after
   the product section: no Liquid change can put one section's output inside
   another's, and both files are theme-owned, so an "Update from Shopify" would
   overwrite an edit there.

   Only at 1000px and up, which is where theme.css turns .product into a
   two-column flex row. Stacked below that, the column is already full width and
   the tabs already read as the next thing on the same page; mobile also has its
   own accordion treatment, tuned separately.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 1000px)';
  var tabsBlock = null; // the .product-content__tabs node, moved not copied
  var tabsHome = null;  // marks where it renders in its own section

  function resolve() {
    if (tabsBlock) return true;
    tabsBlock = document.querySelector('.product-content__tabs');
    if (!tabsBlock) return false;
    tabsHome = document.createComment('pdp-tabs-home');
    tabsBlock.parentElement.insertBefore(tabsHome, tabsBlock);
    return true;
  }

  // The wrapper section is empty once the tabs leave, and it carries its own
  // vertical margin, so leaving it visible trades a wall of white space beside
  // the column for a wall of it below. It can hold a complementary-products row
  // too, so this asks whether anything is actually left rather than assuming.
  function syncHome() {
    var home = tabsHome && tabsHome.parentElement;
    var section = home && home.closest('section');
    if (!section) return;
    section.hidden = !home.firstElementChild;
  }

  // The nav draws its selected state as a bar it slides under the open tab, and
  // TabsNav.connectedCallback (assets/theme.js) builds a fresh one on every
  // insertion without removing the last. Moving the block re-connects the
  // element, so the abandoned bar stays frozen under whichever tab was open at
  // page load and two tabs look selected at once. Clear them on the way out;
  // the reconnect draws exactly one, already in the right place.
  function dropUnderlines() {
    var bars = tabsBlock.querySelectorAll('.tabs-nav__position');
    for (var i = 0; i < bars.length; i++) bars[i].remove();
  }

  function placeTabs() {
    if (!resolve()) return;
    var info = document.querySelector('.product__info');
    var desktop = window.matchMedia(DESKTOP_MQ).matches;

    if (desktop && info) {
      if (tabsBlock.parentElement !== info || info.lastElementChild !== tabsBlock) {
        dropUnderlines();
        info.appendChild(tabsBlock);
      }
    } else if (tabsHome.parentElement && tabsBlock.previousSibling !== tabsHome) {
      // Back into its own slot rather than appended after whatever came last:
      // the tabs render before the complementary-products row in the same
      // section, and coming back in the wrong order would reshuffle the page.
      dropUnderlines();
      tabsHome.parentElement.insertBefore(tabsBlock, tabsHome.nextSibling);
    }
    syncHome();
  }

  // Whether the description actually gets read is the only question worth
  // asking about this change, and the theme's tabs report nothing, so there
  // would be no readout either way. placement is what makes the two positions
  // comparable: the relocated column above the breakpoint, the block's own
  // section below it, where hidden-pocket swaps the nav for per-panel
  // accordion toggles.
  function slugLabel(text) {
    // Trimmed after the cut, so a truncated label never ends in an underscore.
    return String(text).toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40).replace(/^_+|_+$/g, '');
  }

  function trackOpen(event) {
    var opener = event.target.closest && event.target.closest('.tabs-nav__item, .collapsible-toggle');
    if (!opener || !tabsBlock || !tabsBlock.contains(opener)) return;
    // Capture phase, so aria-expanded is still the value from before the click:
    // a shopper collapsing a panel has not read it a second time.
    if (opener.getAttribute('aria-expanded') === 'true') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pdp_tab_open',
      tab: slugLabel(opener.textContent),
      placement: opener.closest('.product__info') ? 'info_column' : 'section',
    });
  }

  function start() {
    if (!resolve()) return;
    placeTabs();
    document.addEventListener('click', trackOpen, true);

    var mql = window.matchMedia(DESKTOP_MQ);
    if (mql.addEventListener) mql.addEventListener('change', placeTabs);
    else if (mql.addListener) mql.addListener(placeTabs);

    // Focal replaces the whole <product-rerender> subtree when a combined
    // listing option changes (assets/theme.js, onRerender_fn), and the fresh
    // server markup has no tabs in it because they belong to another section.
    // A moved block is detached along with it, so without this the page loses
    // its description for good. Watching the parent catches the swap; the
    // event the theme fires for it does not bubble, so there is nothing to
    // listen for instead. Re-placing is itself a mutation here, which is why
    // placeTabs has to be a no-op once the block is where it belongs.
    var rerender = document.querySelector('product-rerender');
    var host = rerender && rerender.parentElement;
    if (host && window.MutationObserver) {
      new MutationObserver(placeTabs).observe(host, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ==========================================================================
   PDP: how much description a shopper is shown before they ask for more
   --------------------------------------------------------------------------
   The description is the last thing in the info column and the only thing
   between the rug and the suggestions under it. Left at full length it is the
   single largest reason the first row of "Others you might love" starts below
   the fold. The two layouts answer that differently, and this decides which
   answer each one gets.

   Below the theme's 1000px breakpoint the product-content section is an
   accordion: every tab has its own heading, and the theme ships the first one
   open. There we close it and leave the theme's own control to open it, which
   costs two headings of height instead of a tab of prose, and gives a shopper
   who taps the whole description rather than a second control to find.

   At 1000px and up the accordion headings are hidden (.hidden-lap-and-up) and
   the tabs are driven by the nav above them, so there is nothing to close;
   the description is clamped instead (.rs-clamp in assets/custom.css) with a
   Read more built here. Two rules govern the clamp: a block only gets one if
   it actually overflows, and the control is built in JS rather than Liquid
   because the product-content section is theme-owned and re-pulled by an
   "Update from Shopify".

   Which layout is live is read off the theme's own heading rather than
   re-declared as a breakpoint here, so a theme update that moves 1000px moves
   both together. The theme's toggle is a customized built-in (<button
   is="toggle-button">), a form Safari has never shipped natively - but the
   theme bundles @ungap/custom-elements in vendor.js, and tapping the heading
   was checked in WebKit against the live PDP before this leaned on it.
   ========================================================================== */
(function () {
  'use strict';

  var MORE = 'Read more';
  var LESS = 'Read less';
  var seq = 0;

  function labelFor(block) {
    var tab = block.closest('.product-tabs__tab-item-wrapper');
    var heading = tab && tab.querySelector('.collapsible-toggle, .tabs-nav__item');
    var name = heading ? heading.textContent.trim().toLowerCase() : 'description';
    return name.replace(/\s+/g, ' ');
  }

  // The accordion heading, if this layout has one on screen. The theme hides
  // it with .hidden-lap-and-up at 1000px and up, so asking the button whether
  // it is displayed answers "which layout is live" without a second copy of
  // the breakpoint living here.
  function headingFor(block) {
    var tab = block.closest('.product-tabs__tab-item-wrapper');
    var toggle = tab && tab.querySelector('.collapsible-toggle');
    if (!toggle) return null;
    return window.getComputedStyle(toggle).display === 'none' ? null : toggle;
  }

  function panelFor(toggle) {
    var id = toggle.getAttribute('aria-controls');
    var tab = toggle.closest('.product-tabs__tab-item-wrapper');
    return (id && document.getElementById(id)) || (tab && tab.querySelector('collapsible-content'));
  }

  // The theme ships the first tab open, from Liquid. Closing it here is
  // silent: CollapsibleContent's constructor set ignoreNextTransition on a
  // panel that booted open, so this attribute change skips the 500ms
  // animation and spends the flag - the shopper's first tap animates as
  // normal. Skipping the animation also skips openable-element:close, which
  // is why the heading's aria-expanded is set here and not left to the theme.
  //
  // Done once per panel: a shopper who opens the description and then rotates
  // the phone into the desktop layout and back should find it as they left it.
  function collapse(toggle) {
    var panel = panelFor(toggle);
    if (!panel || panel.dataset.rsCollapsed) return;
    panel.dataset.rsCollapsed = '1';
    if (!panel.hasAttribute('open')) return;
    panel.removeAttribute('open');
    toggle.setAttribute('aria-expanded', 'false');
  }

  function unclamp(block) {
    block.classList.remove('rs-clamp', 'rs-clamp--open');
    var next = block.nextElementSibling;
    if (next && next.classList.contains('rs-readmore')) next.remove();
  }

  function build(block) {
    block.classList.add('rs-clamp');

    // scrollHeight is the unclamped height even while the clamp is applied, so
    // one measurement answers "is anything hidden". A pixel of slack keeps
    // sub-pixel line heights from growing a control over nothing.
    if (block.scrollHeight - block.clientHeight < 2) {
      block.classList.remove('rs-clamp');
      return;
    }

    if (!block.id) block.id = 'rs-clamped-' + ++seq;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'rs-readmore';
    button.textContent = MORE;
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', block.id);
    button.addEventListener('click', function () {
      var open = block.classList.toggle('rs-clamp--open');
      button.textContent = open ? LESS : MORE;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'pdp_description_expand', tab: labelFor(block) });
      } else {
        // Collapsing from below the fold would otherwise leave the shopper
        // looking at whatever the page scrolled up to reveal.
        var top = block.getBoundingClientRect().top;
        if (top < 0) block.scrollIntoView({ block: 'start' });
      }
    });

    block.insertAdjacentElement('afterend', button);
  }

  function apply() {
    var blocks = document.querySelectorAll('.product-tabs__tab-item-content');
    if (!blocks.length) return;

    var headings = [];
    for (var i = 0; i < blocks.length; i++) {
      var heading = headingFor(blocks[i]);
      if (heading) headings.push(heading);
    }

    if (headings.length) {
      // Accordion layout. Closing needs no measurement, so it does not wait
      // for the frame below: the panel sits ~900px down a phone page and is
      // never in the first paint, but a frame of full-height description is
      // still a frame of layout nobody asked for. No clamp either - a tap
      // that opens a heading should give the shopper the whole description,
      // not a second control one line under the first.
      for (var h = 0; h < headings.length; h++) collapse(headings[h]);
      for (var u = 0; u < blocks.length; u++) unclamp(blocks[u]);
      return;
    }

    // After a frame, so the spec lines extractSpecs() lifts out of the
    // Description tab are gone before anything is measured. Measuring first
    // would clamp around lines that are about to leave.
    //
    // The same frame settles the other thing this measurement depends on: on
    // desktop the block above moves the tabs into the info column, which is
    // narrower than the section they render in, so the text is taller there.
    // Both blocks start on DOMContentLoaded and that one is declared first, so
    // it has already run; the frame is what puts this after its layout. A
    // reordering that let this measure the section width would silently drop
    // the control from descriptions that do overflow the column.
    requestAnimationFrame(function () {
      for (var b = 0; b < blocks.length; b++) {
        var next = blocks[b].nextElementSibling;
        var built = blocks[b].classList.contains('rs-clamp') ||
          (next && next.classList.contains('rs-readmore'));
        if (!built) build(blocks[b]);
      }
    });
  }

  // A tablet crosses the theme's 1000px breakpoint by being turned over, so
  // the layout it booted in is not the one it stays in. Re-running on resize
  // keeps the two treatments from meeting: a clamp with no way past it in the
  // accordion, or an accordion heading over already-clamped text.
  function start() {
    apply();
    var queued = false;
    window.addEventListener('resize', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        apply();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ==========================================================================
   First-touch ad attribution (rs-attr)
   --------------------------------------------------------------------------
   Remembers, per tab, the page the visit landed on. An offer that later
   becomes a sale can then be credited to the campaign that paid for the
   click: the sale itself closes as an emailed invoice days later, so Google
   never sees it and the campaign otherwise reports nothing.

   Runs on every page (the landing page is rarely the product page), stores
   nothing but the landing url, referrer and time, and reads Google's own
   _gcl_aw cookie for the click id. Exposes window.RS_ATTRIBUTION(), which
   the offer widget posts to the offers service; the service does the
   parsing and validating. It also copies the ChatGPT Ads click reference
   (the pixel's __oppref cookie) into the rs_oppref cart attribute, so a
   direct checkout carries the click on the order itself, where inventory-sync
   reads it back for OpenAI's Conversions API. Nothing here ever reaches the
   dataLayer.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'rs_attr_first';

  function hasClickId(url) {
    return /[?&](gclid|gbraid|wbraid)=/.test(url || '');
  }

  function currentTouch() {
    return { url: location.href, ref: document.referrer || '', at: new Date().toISOString() };
  }

  function read() {
    try {
      var raw = window.sessionStorage.getItem(KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && parsed.url ? parsed : null;
    } catch (e) {
      return null; // storage disabled or private browsing
    }
  }

  // First touch wins, except when a later page view carried an actual ad
  // click the stored one lacks: a click id is the whole point of this.
  function capture() {
    var stored = read();
    var touch = stored && !(hasClickId(location.href) && !hasClickId(stored.url))
      ? stored
      : currentTouch();
    try {
      window.sessionStorage.setItem(KEY, JSON.stringify(touch));
    } catch (e) { /* storage disabled */ }
    return touch;
  }

  function cookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      return '';
    }
  }

  // Everything optional: the service drops what it cannot use and never
  // fails a submission over it.
  window.RS_ATTRIBUTION = function () {
    try {
      var touch = capture();
      var payload = { landingUrl: touch.url, firstSeenAt: touch.at };
      if (touch.ref) payload.referrer = touch.ref;
      // Google's own cookie, GCL.<timestamp>.<gclid>: carries the click id
      // when the landing url no longer does (a return visit, a shared link).
      var gclAw = cookie('_gcl_aw');
      if (gclAw) payload.gclAw = gclAw;
      // OpenAI's cookie (ChatGPT Ads): the measurement pixel in
      // layout/theme.liquid copies ?oppref= off an ad landing url into
      // __oppref, so a return visit still carries the click reference.
      var oppref = cookie('__oppref');
      if (oppref) payload.oppref = oppref;
      return payload;
    } catch (e) {
      return undefined;
    }
  };

  // A direct checkout never touches the offers service, so the click has to
  // ride the order: Shopify keeps cart attributes on the order it becomes
  // (customAttributes over the Admin API), and inventory-sync reports such an
  // order to OpenAI against this reference. Best effort, once per tab per
  // value, retried on the next page if the call fails. Main frame only: a
  // custom pixel's sandbox has no cart. Never the dataLayer.
  var CART_KEY = 'rs_attr_oppref_cart';
  var CART_ATTRIBUTE = 'rs_oppref';

  function copyOpprefToCart() {
    try {
      var oppref = cookie('__oppref');
      if (!oppref || typeof window.fetch !== 'function') return;
      var sent = '';
      try {
        sent = window.sessionStorage.getItem(CART_KEY) || '';
      } catch (e) { /* storage disabled */ }
      if (sent === oppref) return;
      var attributes = {};
      attributes[CART_ATTRIBUTE] = oppref;
      window.fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      })
        .then(function (response) {
          if (!response || !response.ok) return;
          try {
            window.sessionStorage.setItem(CART_KEY, oppref);
          } catch (e) { /* storage disabled */ }
        })
        .catch(function () { /* the next page retries */ });
    } catch (e) { /* attribution never breaks a page */ }
  }

  capture();
  copyOpprefToCart();
})();

/* ==========================================================================
   In-house Make an Offer (rs-offer)
   --------------------------------------------------------------------------
   Enhances snippets/make-an-offer-widget.liquid (rendered via custom-liquid
   blocks in templates/product*.json; the snippet's own `enabled` default is
   the single on/off switch): a badge next to the product price, an offer
   modal, and a wait/decision panel driven by the offers service behind the
   Shopify App Proxy.

   Endpoints (JSON POST, same-origin App Proxy; local dev talks to a locally
   run service, same pattern as the room concierge above):
     /apps/offers/submit  { variantId, productHandle, amountCents, email,
                            tz? (browser IANA zone; follow-ups send in it),
                            attribution? (rs-attr: landing url, referrer,
                            first-seen time, _gcl_aw; all optional) }
       -> 200 { ok, offerId, token, state: 'pending', pollMs }
       -> 400 { ok:false, error: 'invalid_amount'|'invalid_email'|'unknown_variant' }
       -> 429 { ok:false, error: 'rate_limited' }
     /apps/offers/status  { offerId, token }
       -> { ok, state, amountCents, counterCents?, agreedCents?,
            checkoutUrl?, codeExpiresAt?,
            heldUntilLabel?, heldUntil? }   (never an operator name)
       states: pending | countered | counter_back | stood_firm | accepted
               | declined | expired
       checkoutUrl (a draft-order invoice: the exact agreed total) rides on
       countered, stood_firm and accepted.
       heldUntilLabel / heldUntil (this customer's own 48 hour hold on the
       rug, the block below this badge) ride on accepted only.
     /apps/offers/respond { offerId, token, action: 'counter_back'|'decline_counter',
                            amountCents? }
       Checkout needs no action: the countered/stood_firm/accepted panels
       link the invoice directly. counter_back is the ONE bounded
       suggestion (above their offer, below our counter).

   Flow: badge click opens the form modal; a valid submit closes it and
   opens the side DRAWER (non-modal: no backdrop, no scroll lock, the page
   stays browsable), which polls status every pollMs (default 5s) while
   the state is 'pending' or 'counter_back'. The chevron collapses the
   drawer to a fixed edge tab (a bottom bar on mobile); a first-time
   decision re-expands it. Transient poll trouble (429, 5xx, network)
   retries with exponential backoff (pollMs doubling to a 60s cap, reset
   on success); ONLY 404/410 mean the record is gone (rendered as
   expired). Any other 4xx is retried a few times, then parked with a soft
   message WITHOUT touching storage. The offer record persists in
   localStorage per product handle:
     - pending / counter_back: restores quietly as the edge tab (badge
       label flips to "View offer status"); polling resumes in the
       background. Reloads never auto-expand the drawer for a waiting
       offer (no nag loop): it only expands itself for a decision the
       customer has not seen yet (`seen` flag, set when a decision panel
       renders while the drawer is expanded and the tab is visible, or
       when the customer collapses it themselves).
     - countered / stood_firm / accepted: persist with checkoutUrl +
       codeExpiresAt (default now + CODE_TTL_MS) so reloads restore the
       panel, with its live checkout link, until the link lapses.
     - declined / expired / counter_declined: cleared once seen.
   Mid-session first-time decisions always expand the drawer (that is
   the "you'll have an answer here" promise).

   Variant re-renders: Focal's <product-rerender allow-partial-rerender>
   (theme.js) replaces both the "liquid" block (our root) and the "meta"
   block (the price list the badge is relocated into) on every variant
   change. Offer state therefore lives in module-level sessions keyed by
   product handle, init() is idempotent per root and only (re)binds fresh
   DOM into the session, and a MutationObserver (same safety-net pattern
   as the sold-tile CTA injector above) re-inits replaced roots and
   re-places orphaned badges. variantId/priceCents are read from the root's data
   attributes at modal-open/submit/render time, never cached at init.

   Analytics: pushes the GA4 funnel events the Magical Make an Offer app
   was tracked by (make_an_offer_click / _submit / _success / _error;
   success and error are keyed off the submit response) so the PR #52
   dashboards carried over unchanged. Submit fires on the attempt, before
   validation, keeping that funnel's "submit attempts" semantics.
   offer_handle (the product handle) rides on submit/success/error so the
   Grafana census-to-outcome join and the GA4 offer_handle dimension keep
   working. No PII ever reaches the dataLayer.

   Offer depth on the success event: make_an_offer_success also carries
   offer_discount_pct, the ask as a whole-number percentage off the listed
   price (never the amount itself, never the price). It exists so the ad
   platforms can tell a negotiating opener from a lowball: the Google Ads
   "Make An Offer" conversion fires from a GTM-5XWGG52 tag on this event
   and is the Primary, biddable action on Shopping and PMax, so every $1
   and 99%-off offer it counted was teaching Google to find more of that
   shopper. Of the first 71 offers, 29 asked 45% off or more and none of
   those ever closed; the 30-45% block is where real customers open before
   the service counters them into its band. The GTM trigger filters on
   offer_discount_pct < LOWBALL_PCT (inventory-sync docs/runbooks/
   offer-ad-signal.md has the exact condition); the GA4 tag stays unfiltered
   so the raw funnel count survives as the cross-check. The ChatGPT Ads
   lead_created call has no GTM layer in between, so it is gated here, on
   the same line. A lowball is still an offer to the customer: the drawer,
   the poll, the Tidio hand-off and the offers service see no difference.

   Tidio: on a successful submit we set the contact card (offer_* properties,
   including a human-readable offer_notes summary, + the 'offer-pending' tag)
   and fire track('offer_submitted'), the event the Flow keys off (a "New
   event" trigger with that exact name). The offer details ride on the
   contact card and the Flow is kicked off by the event; we do NOT write a
   visitor message into the chat, so the only thing the customer sees in chat
   is the Flow's own response (no internal context ever leaks into the
   transcript). We deliberately do NOT api.open() on submit: the drawer is the
   surface, and the stood-firm panel's "Open the chat" link opens Tidio on
   demand.

   IMPORTANT — Tidio dashboard setup: setContactProperties silently drops any
   property not pre-registered under Settings → Contact Properties, so
   offer_amount (typed Number), offer_sku, offer_url, offer_product and
   offer_notes must all exist there. Tags need no registration, so the
   'offer-pending' tag still flags the conversation if one is missing.

   Copy rules (binding, from the offers build spec): customer-facing strings
   NEVER contain em or en dashes (periods, commas and the middle dot are
   fine), never mention the acceptance thresholds or any percentage, and
   never name an operator: every decision reads as the house. All
   JS-rendered copy lives in this IIFE; the form, badge and dark-ship copy
   is rendered by the snippet (the same rules bind there), and
   tests/offers-widget.spec.js sweeps both.
   ========================================================================== */
(function () {
  'use strict';

  // Production: same-origin App Proxy paths. Local dev (shopify theme dev on
  // 127.0.0.1/localhost): a locally-run offers service. Window override wins
  // (same pattern as RUG_CONCIERGE_ENDPOINT above).
  var API_BASE = window.RS_OFFER_API_BASE ||
    ((location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://127.0.0.1:8787'
      : '');
  var SUBMIT_PATH = '/apps/offers/submit';
  var STATUS_PATH = '/apps/offers/status';
  var RESPOND_PATH = '/apps/offers/respond';

  // Test hook: fixtures shrink the poll interval so specs don't sit through
  // 5s waits. Production never sets it. A pollMs in the submit response
  // still wins when present.
  var DEFAULT_POLL_MS =
    typeof window.__RS_OFFER_POLL_MS === 'number' ? window.__RS_OFFER_POLL_MS : 5000;

  // Poll backoff while the service is erroring (429 / 5xx / network):
  // doubles from the poll interval up to this cap, resets on success.
  var BACKOFF_CAP_MS = 60000;
  // Unexpected 4xx responses (not 429/404/410) get this many retries
  // before the widget parks with a soft message (storage untouched, so the
  // next visit simply tries again).
  var SOFT_FAIL_LIMIT = 3;
  // How long an accepted offer's Complete purchase panel survives reloads
  // when the service didn't send codeExpiresAt. Matches the backend's default
  // code TTL (3 weeks); the customer is never shown a date, so this only
  // bounds how long a restored panel lingers with no server confirmation.
  var CODE_TTL_MS = 21 * 24 * 60 * 60 * 1000;

  var STORAGE_PREFIX = 'rs_offer_';
  // The Flow trigger ("New event" with this exact name in the Tidio
  // dashboard).
  var TIDIO_EVENT = 'offer_submitted';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ---- customer-facing copy (no em/en dashes, no percentages, and never
  // an operator name: decisions read as the house, not a person) ----------

  var COPY = {
    labelYours: 'Your offer',
    labelReply: 'Our reply',
    yourOffer: 'Your offer',
    yourSuggestion: 'Your suggestion',
    waitTitle: 'With us now.',
    waitSub: "You'll have an answer here in minutes. Keep browsing; this panel will open when it's ready.",
    listLabel: 'List price',
    counterLabel: 'Our price for you',
    preparingCheckout: 'Preparing your checkout link. Just a moment.',
    suggestLink: 'Suggest another price',
    suggestSend: 'Send',
    suggestHint: 'One suggestion is yours to make. Somewhere between your offer and ours.',
    suggestBounds: 'Enter an amount above your offer and below ours.',
    counterDecline: 'No thank you',
    acceptedTitle: 'Your offer is accepted',
    acceptedNote: 'We are delighted to say yes. Your price is applied and ready at checkout. One small note: it is your price we are holding, not the rug itself.',
    acceptedHeldNote: 'We are delighted to say yes. Your price is applied and ready at checkout. The rug itself is held for you until ',
    stoodFirmLabel: 'The lowest we can go',
    stoodFirmBody: "This is truly our best on this piece. Working within a budget? Tell us in the chat and we'll help you find something just as special.",
    openChat: 'Open the chat',
    counterDeclinedTitle: 'Understood, thank you',
    counterDeclinedBody: 'We will leave it there for now. Keep an eye on your inbox in case we can do a little more.',
    expiredTitle: 'This offer has expired',
    expiredBody: 'Offers stay open for a limited time. You are welcome to make a new one.',
    makeAnother: 'Make another offer',
    // Badge label while an offer record exists for this product (the
    // snippet renders the default "Make an Offer" label).
    viewStatus: 'View offer status',
    // Must match the snippet's initial submit-button label.
    submitLabel: 'Submit offer',
    sending: 'Sending...',
    invalidAmount: 'Please enter an offer amount.',
    invalidEmail: 'Please enter a valid email address.',
    unavailable: 'This piece has found a home, so it is no longer taking offers.',
    rateLimited: 'You have reached the offer limit for today. Please try again tomorrow.',
    unreachable: 'We could not send your offer just now. Please try again in a moment.',
    respondFailed: 'We could not send your reply just now. Please try again in a moment.'
  };

  function waitLine(amount) {
    return amount
      ? 'Your offer of ' + amount + ' is being considered.'
      : 'Your offer is being considered.';
  }
  function suggestionLine(amount) {
    return amount
      ? 'Your suggestion of ' + amount + ' is being considered.'
      : 'Your suggestion is being considered.';
  }
  function checkoutLabel(cents) {
    var amount = formatMoney(cents);
    return amount ? 'Check out at ' + amount : 'Check out';
  }
  // The accepted panel's closing line. An offer holds the price and never the
  // rug, and the line says so as a statement about this offer, not about what
  // the store can do: the 48 hour hold below this badge can hold the rug, and
  // its email is what invites the offer. A customer who holds the rug gets
  // their own deadline here instead, because telling them the rug is not held
  // would contradict a promise made minutes earlier. The service decides
  // (heldUntilLabel on the accepted status body), never this file, so the
  // panel and the email always agree.
  function acceptedNote(heldUntilLabel) {
    return heldUntilLabel ? COPY.acceptedHeldNote + heldUntilLabel + '.' : COPY.acceptedNote;
  }

  function declinedTitle() { return 'About your offer'; }
  function declinedBody(amount) {
    return (amount
      ? 'We gave your offer of ' + amount + ' careful thought, and we are not able to accept it this time.'
      : 'We gave your offer careful thought, and we are not able to accept it this time.')
      + ' You are welcome to try a different amount.';
  }

  // ---- small helpers -----------------------------------------------------

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Same dataLayer shape the Magical Make an Offer app's tracking pushed, so
  // the GTM triggers and GA4 funnel reports built on it still apply.
  function pushEvent(eventName, buttonType, extra) {
    window.dataLayer = window.dataLayer || [];
    var payload = {
      event: eventName,
      button_type: buttonType,
      page_url: window.location.href,
      page_path: window.location.pathname
    };
    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) payload[key] = extra[key];
      }
    }
    window.dataLayer.push(payload);
  }

  // Run fn against window.tidioChatApi as soon as it's ready (Tidio loads
  // async via its app embed). The Tidio dwell IIFE keeps its own copy
  // because each section is a self-contained closure.
  function whenTidioReady(fn) {
    var api = window.tidioChatApi;
    if (api && typeof api.open === 'function') {
      fn(api);
      return;
    }
    document.addEventListener('tidioChat-ready', function ready() {
      document.removeEventListener('tidioChat-ready', ready);
      if (window.tidioChatApi) fn(window.tidioChatApi);
    });
  }

  // "$1,800" / "$1,800.50" from integer cents. Empty string when invalid so
  // callers can fall back to amount-free copy.
  function formatMoney(cents) {
    var n = Math.round(Number(cents));
    if (!isFinite(n) || n <= 0) return '';
    var dollars = Math.floor(n / 100);
    var rem = n % 100;
    var out = '$' + dollars.toLocaleString('en-US');
    return rem ? out + '.' + ('0' + rem).slice(-2) : out;
  }

  // One normalization rule shared by the live "$" formatter and the parser,
  // so the amount the customer SEES is always the amount that submits:
  // strip junk, keep the first decimal point only.
  function normalizeAmountString(raw) {
    var cleaned = String(raw == null ? '' : raw).replace(/[^\d.]/g, '');
    var firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }
    return cleaned;
  }

  // "$1,800", "1800", "1,800.50" -> positive integer cents, else null.
  function parseAmountCents(raw) {
    var cleaned = normalizeAmountString(raw);
    if (!cleaned || cleaned === '.') return null;
    var n = parseFloat(cleaned);
    if (!isFinite(n) || n <= 0) return null;
    return Math.round(n * 100);
  }

  // Live "$" formatting for the amount input: thousands-separate the integer
  // part, cap at two decimals.
  function formatAmountInput(input) {
    var raw = normalizeAmountString(input.value);
    if (!raw) {
      input.value = '';
      return;
    }
    var parts = raw.split('.');
    var intPart = parts[0].replace(/^0+(?=\d)/, '');
    var formatted = intPart ? Number(intPart).toLocaleString('en-US') : '0';
    if (parts.length > 1) formatted += '.' + parts[1].slice(0, 2);
    input.value = '$' + formatted;
  }

  // codeExpiresAt arrives as an ISO STRING from the service but as a NUMBER
  // from the client-side fallback; persist and compare numbers only, or the
  // lapse check silently never fires (number >= string is NaN-false).
  function expiryMs(value) {
    if (typeof value === 'number' && isFinite(value)) return value;
    if (typeof value === 'string') {
      var parsed = Date.parse(value);
      if (!isNaN(parsed)) return parsed;
    }
    return null;
  }

  // The ask as a whole-number percentage off the listed price, or null when
  // the root carried no price (then nothing is filtered: the success event
  // omits offer_discount_pct and the ad measurement fires as it always did).
  // Whole numbers only: the dataLayer is not where a customer's exact price
  // belongs, and a percentage rounds away the last digit of it.
  function discountPct(amountCents, priceCents) {
    if (!priceCents || priceCents <= 0) return null;
    return Math.max(0, Math.round(((priceCents - amountCents) / priceCents) * 100));
  }

  // At or past this percentage off, an offer is a lowball for AD purposes
  // only: it still counts in GA4 (the tag there ignores the parameter) and
  // it is still a real offer to the customer and to the offers service. Why
  // 45 and not the service's own 25% counter_band line: 30-45% is where most
  // real customers open, and every offer we have ever closed started under
  // 45. The GTM trigger for the Google Ads conversion holds the same number
  // (offer_discount_pct less than 45); change both or neither.
  var LOWBALL_PCT = 45;
  function isLowball(pct) {
    return pct !== null && pct >= LOWBALL_PCT;
  }

  // ChatGPT Ads conversion "Offer submitted" (Ads Manager base event
  // lead_created). layout/theme.liquid defines the oaiq() queue on every
  // page, so a call before the SDK loads is kept, and on localhost the SDK
  // never loads and the queue just fills. The SDK attaches the click
  // reference (the __oppref cookie) itself. Same PII rule as pushEvent: the
  // offer id is all that goes out, as the event_id, so a copy of the same
  // conversion sent later from the server deduplicates instead of double
  // counting. No amount: an offer is not revenue until it closes.
  var OPENAI_OFFER_EVENT = 'lead_created';
  function measureOpenAiOffer(offerId) {
    try {
      if (typeof window.oaiq !== 'function') return;
      window.oaiq('measure', OPENAI_OFFER_EVENT, { type: 'customer_action' }, { event_id: String(offerId) });
    } catch (e) { /* analytics must never break the offer flow */ }
  }

  // Where this visit came from (the rs-attr IIFE above). Best effort: a
  // missing or broken capture must never cost us the offer.
  function offerAttribution() {
    try {
      return typeof window.RS_ATTRIBUTION === 'function' ? window.RS_ATTRIBUTION() : undefined;
    } catch (e) {
      return undefined;
    }
  }

  // The customer's IANA zone, so follow-up emails can land in their late
  // afternoon; the service validates and quietly drops anything odd.
  function browserTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch (e) {
      return undefined;
    }
  }

  function post(path, payload) {
    return fetch(API_BASE + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (body) {
        return { status: response.status, body: body };
      });
    });
  }

  // ---- per-product offer persistence (survives reloads) ------------------

  function storageKey(handle) { return STORAGE_PREFIX + handle; }
  function saveOffer(handle, data) {
    try { window.localStorage.setItem(storageKey(handle), JSON.stringify(data)); } catch (e) { /* storage disabled */ }
  }
  function loadOffer(handle) {
    try { return JSON.parse(window.localStorage.getItem(storageKey(handle))); } catch (e) { return null; }
  }
  function clearOffer(handle) {
    try { window.localStorage.removeItem(storageKey(handle)); } catch (e) { /* noop */ }
  }

  // ---- offer sessions (survive variant re-renders) -----------------------
  // Keyed by product handle. Focal's <product-rerender> swaps the widget's
  // DOM out from under us on every variant change, so anything longer-lived
  // than one render (the offer record, the poll timer, the open/seen flags)
  // lives here, and every render resolves the CURRENT elements via
  // session.els (refreshed by init()). One product per page in practice,
  // but the keying keeps multi-widget pages safe.

  var sessions = {};

  function getSession(handle) {
    if (!sessions[handle]) {
      sessions[handle] = {
        handle: handle,
        root: null,          // current [data-rs-offer] element
        els: null,           // current child elements (see init)
        badgeLabel: '',      // snippet-rendered badge label ("Make an Offer")
        offer: null,         // persisted record, mirror of localStorage
        ui: null,            // null (form) | { kind: 'wait'|'problem'|'decision', body? }
        drawerMode: 'hidden', // 'hidden' | 'open' | 'tab' (the wait/decision home)
        offerEpoch: 0,       // bumped on every user action; stale polls drop
        open: false,         // form overlay currently open
        restoreFocusTo: null,
        trapHandler: null,   // document keydown handler while open
        pollMs: DEFAULT_POLL_MS,
        pollTimer: null,
        pollWanted: false,   // polling should continue (may be parked while hidden)
        pollInFlight: false,
        backoffMs: null,     // current backoff delay while the service errors
        softFails: 0,        // consecutive unexpected-4xx responses
        posted: false        // a valid offer was sent this page (rs-offer:closed)
      };
    }
    return sessions[handle];
  }

  // Fresh read on every use (modal open, submit, price row): the snippet
  // re-emits these attributes with the current variant on each re-render,
  // so caching them at init would pin the first variant's id and price.
  function readCfg(session) {
    var root = session.root;
    return {
      variantId: root.getAttribute('data-variant-id') || '',
      priceCents: parseInt(root.getAttribute('data-price-cents'), 10) || 0,
      title: root.getAttribute('data-product-title') || '',
      sku: root.getAttribute('data-product-sku') || ''
    };
  }

  // ---- record lifecycle ---------------------------------------------------

  // States that mean "the conversation is over": shown once (while actually
  // visible), then forgotten.
  function isOverState(state) {
    return state === 'declined' || state === 'expired' || state === 'counter_declined';
  }

  // Persist the record's current shape, or forget it once a conversation-
  // over state has been seen. Also keeps the badge label honest.
  function commitRecord(session) {
    var record = session.offer;
    if (record && record.seen && isOverState(record.state)) {
      session.offer = null;
      clearOffer(session.handle);
    } else if (record) {
      saveOffer(session.handle, record);
    }
    updateBadge(session);
  }

  // A decision only counts as delivered once the customer could actually
  // have seen it: the drawer must be expanded AND the tab visible. A panel
  // rendered while collapsed or in a hidden browser tab keeps its record
  // until both hold (visibilitychange listener in start(), expand click),
  // so a decision is never lost to a background render.
  function tryMarkSeen(session) {
    var record = session.offer;
    if (!record || !record.state || record.state === 'pending' || record.seen) return;
    if (!session.ui || session.ui.kind !== 'decision') return;
    if (session.drawerMode !== 'open') return;
    if (document.visibilityState !== 'visible') return;
    record.seen = true;
    commitRecord(session);
  }

  // ---- badge --------------------------------------------------------------

  function updateBadge(session) {
    var label = session.offer ? COPY.viewStatus : session.badgeLabel;
    if (session.els && session.els.badge.textContent !== label) {
      session.els.badge.textContent = label;
    }
  }

  // The badge belongs next to the price: move it into the theme's price
  // list; the click listener rides along.
  // Scoped to the widget's own section: a global .price-list fallback
  // could graft the badge onto a recommendations-card price. If the price
  // block isn't there (unusual template) the badge simply stays where the
  // block rendered it.
  function placeBadge(session) {
    var scope = session.root.closest('.shopify-section') || document;
    var priceList = scope.querySelector('.product-meta__price-list-container .price-list');
    if (priceList && !priceList.contains(session.els.badge)) {
      priceList.appendChild(session.els.badge);
    }
  }

  // ---- overlay: real modal semantics --------------------------------------
  // aria-modal is only true if we make it true: focus moves into the dialog
  // on open, Tab cycles inside it, Escape closes, body scroll is locked
  // (html.rs-offer-lock in custom.css), and focus returns where it was on
  // close. The keydown handler attaches on open and detaches on close
  // (never a page-lifetime listener), and resolves the current overlay via
  // session.els so it survives a variant re-render while open.

  function focusablesIn(overlay) {
    var nodes = overlay.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (node) {
      return node.offsetParent !== null; // skips the hidden form/panel half
    });
  }

  function onOverlayKeydown(session, e) {
    if (e.key === 'Escape') {
      closeOverlay(session);
      return;
    }
    if (e.key !== 'Tab') return;
    var focusables = focusablesIn(session.els.overlay);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    var inside = session.els.overlay.contains(active);
    if (e.shiftKey && (active === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  }

  function openOverlay(session) {
    var els = session.els;
    if (!session.open) {
      session.open = true;
      session.restoreFocusTo = document.activeElement;
      session.trapHandler = function (e) { onOverlayKeydown(session, e); };
      document.addEventListener('keydown', session.trapHandler, true);
      document.documentElement.classList.add('rs-offer-lock');
    }
    els.overlay.hidden = false;
    var target = els.form.hidden ? els.closeBtn : els.amountInput;
    try { target.focus(); } catch (err) { /* detached mid-rerender */ }
  }

  function closeOverlay(session) {
    // Closing a decision panel means the customer saw it.
    tryMarkSeen(session);
    if (session.open) {
      session.open = false;
      document.removeEventListener('keydown', session.trapHandler, true);
      session.trapHandler = null;
      document.documentElement.classList.remove('rs-offer-lock');
      // The exit survey listens for a form closed with nothing sent.
      try {
        document.dispatchEvent(new CustomEvent('rs-offer:closed', {
          detail: { handle: session.handle, submitted: Boolean(session.offer || session.posted) }
        }));
      } catch (err) { /* no CustomEvent constructor */ }
    }
    session.els.overlay.hidden = true;
    var previous = session.restoreFocusTo;
    session.restoreFocusTo = null;
    if (previous && document.contains(previous) && typeof previous.focus === 'function') {
      try { previous.focus(); } catch (err) { /* noop */ }
    }
  }

  // ---- drawer: the non-modal wait/decision home ----------------------------
  // No backdrop, no focus trap, no scroll lock: the whole point is that the
  // customer keeps browsing while the offer is considered. 'open' shows the
  // panel, 'tab' collapses it to the edge tab, 'hidden' removes both (form
  // mode). A first-time decision expands it (renderDecision); the customer
  // can collapse it any time.

  function applyDrawerMode(session) {
    session.els.drawer.hidden = session.drawerMode !== 'open';
    session.els.tab.hidden = session.drawerMode !== 'tab';
  }

  function openDrawer(session) {
    session.drawerMode = 'open';
    applyDrawerMode(session);
    tryMarkSeen(session);
  }

  function collapseDrawer(session) {
    // Collapsing a visible decision counts as having seen it (checked
    // before the mode flips; tryMarkSeen requires the open state).
    tryMarkSeen(session);
    session.drawerMode = 'tab';
    applyDrawerMode(session);
  }

  // ---- form chrome --------------------------------------------------------
  // No per-element guards here: init() hard-requires the full snippet
  // contract before a session exists.

  function showError(session, message) {
    session.els.errorEl.hidden = false;
    session.els.errorEl.textContent = message;
  }

  function clearError(session) {
    session.els.errorEl.hidden = true;
    session.els.errorEl.textContent = '';
  }

  function resetSubmitButton(session) {
    session.els.submitBtn.disabled = false;
    session.els.submitBtn.textContent = COPY.submitLabel;
  }

  // -- Tidio contact card + Flow trigger (see doc-block above).
  // A concise, customer-safe summary (amount + product name only, never the
  // SKU or URL) carried on the contact card's offer_notes, so the offer
  // details ride with the operator notification. It is NOT posted into the
  // chat transcript.
  function offerSummaryLine(cfg, amountCents) {
    var amount = formatMoney(amountCents);
    return cfg.title
      ? 'I have made an offer of ' + amount + ' on the ' + cfg.title + '.'
      : 'I have made an offer of ' + amount + '.';
  }

  function fireTidioOnOffer(session, amountCents, email) {
    var cfg = readCfg(session);
    var summary = offerSummaryLine(cfg, amountCents);
    whenTidioReady(function (api) {
      try {
        if (typeof api.setVisitorData === 'function' && email) {
          api.setVisitorData({ email: email });
        }
        if (typeof api.addVisitorTags === 'function') {
          api.addVisitorTags(['offer-pending']);
        }
        // Card properties FIRST so they are populated before the track() event
        // fires the operator notification. Every name must be pre-registered
        // in the dashboard (see doc-block above; offer_amount is typed
        // Number); offer_notes carries the human-readable summary, so a
        // notification that surfaces properties still shows the offer.
        if (typeof api.setContactProperties === 'function') {
          api.setContactProperties({
            offer_amount: Math.round(amountCents) / 100,
            offer_sku: cfg.sku,
            offer_product: cfg.title,
            offer_url: window.location.href,
            offer_notes: summary
          });
        }
        // NOTE: we deliberately do NOT post a messageFromVisitor here. The
        // offer details ride on the contact card (offer_* properties, incl.
        // offer_notes) and the Flow keys off the track() event below, so
        // nothing is written into the customer-visible chat transcript and
        // the only thing the customer sees in chat is the Flow's own response.
        if (typeof api.track === 'function') api.track(TIDIO_EVENT);
      } catch (err) {
        // Tidio API errors must never block the offer flow.
      }
    });
  }

  function openTidio() {
    whenTidioReady(function (api) {
      try { if (typeof api.open === 'function') api.open(); } catch (err) { /* noop */ }
    });
  }

  // ---- status polling -----------------------------------------------------
  // Healthy path: every pollMs while pending. Trouble path (429, 5xx,
  // network): exponential backoff, pollMs doubling to BACKOFF_CAP_MS,
  // reset on the next healthy response. ONLY 404/410 mean the record is
  // gone. Any other 4xx is a contract surprise: retried a few times, then
  // parked with a soft message WITHOUT touching storage, so the next visit
  // simply tries again. Timers are never armed while the tab is hidden;
  // the visibilitychange listener in start() polls immediately on return
  // (same pattern as the Tidio dwell IIFE above).

  function stopPolling(session) {
    session.pollWanted = false;
    if (session.pollTimer) {
      clearTimeout(session.pollTimer);
      session.pollTimer = null;
    }
  }

  function scheduleNextPoll(session, delayMs) {
    if (session.pollTimer) clearTimeout(session.pollTimer);
    session.pollTimer = null;
    session.pollWanted = true;
    if (document.hidden) return; // parked; resumes on visibilitychange
    session.pollTimer = setTimeout(function () {
      session.pollTimer = null;
      pollOnce(session);
    }, delayMs);
  }

  function scheduleBackoffPoll(session) {
    session.backoffMs = session.backoffMs
      ? Math.min(session.backoffMs * 2, BACKOFF_CAP_MS)
      : session.pollMs;
    scheduleNextPoll(session, session.backoffMs);
  }

  function pollOnce(session) {
    var offer = session.offer;
    if (!offer || !offer.offerId || session.pollInFlight) return;
    // A response that was in flight when the customer ACTED (suggested,
    // declined, resubmitted) describes a state that no longer exists: the
    // epoch bump makes it drop instead of overwriting the newer reality.
    var epoch = session.offerEpoch;
    session.pollWanted = true;
    session.pollInFlight = true;
    post(STATUS_PATH, { offerId: offer.offerId, token: offer.token })
      .then(function (result) {
        session.pollInFlight = false;
        if (session.offer !== offer || session.offerEpoch !== epoch) return;
        var body = result.body || {};
        if (result.status === 200 && body.ok && body.state) {
          session.backoffMs = null;
          session.softFails = 0;
          if (body.state === 'pending' || body.state === 'counter_back') {
            // Still waiting (a counter-back registered from the email page
            // lands here too): keep the record's shape current, and repaint
            // WHATEVER was showing (a stale countered panel would offer
            // actions the server now rejects).
            if (offer.state !== body.state) {
              offer.state = body.state;
              commitRecord(session);
              setUi(session, { kind: 'wait' });
            }
            scheduleNextPoll(session, session.pollMs);
            return;
          }
          renderDecision(session, body);
        } else if (result.status === 404 || result.status === 410) {
          // Only "record gone" may expire the offer and drop storage: a
          // 400 from a deploy hiccup must never eat a live offer.
          renderDecision(session, { state: 'expired' });
        } else if (result.status >= 400 && result.status < 500 && result.status !== 429) {
          session.softFails += 1;
          if (session.softFails > SOFT_FAIL_LIMIT) {
            stopPolling(session);
            setUi(session, { kind: 'problem' });
          } else {
            scheduleBackoffPoll(session);
          }
        } else {
          scheduleBackoffPoll(session); // 429 or 5xx: transient
        }
      })
      .catch(function () {
        session.pollInFlight = false;
        if (session.offer === offer) scheduleBackoffPoll(session); // offline blip
      });
  }

  // ---- state panels -------------------------------------------------------
  // renderUi is the single paint layer: it renders session.ui into the
  // CURRENT panel and owns the form/panel swap, so re-rendering after a
  // variant re-render is just renderUi(session) again. The build*
  // functions only produce DOM; presentation (overlay, storage, seen) is
  // decided by the callers of setUi / renderDecision.

  function setUi(session, ui) {
    session.ui = ui;
    renderUi(session);
  }

  function renderUi(session) {
    var els = session.els;
    if (!session.ui) {
      session.drawerMode = 'hidden';
      applyDrawerMode(session);
      return;
    }
    els.panel.innerHTML = '';
    if (session.ui.kind === 'wait') {
      buildWait(session, els.panel);
    } else if (session.ui.kind === 'problem') {
      // Status checks kept failing in an unexpected way: keep the wait
      // framing, add a soft retry line. The record stays in storage.
      buildWait(session, els.panel);
      els.panel.appendChild(el('p', 'rs-offer-panel-error', COPY.unreachable));
    } else {
      buildDecision(session, els.panel, session.ui.body);
    }
    els.drawerLabel.textContent =
      session.ui.kind === 'decision' ? COPY.labelReply : COPY.labelYours;
    applyDrawerMode(session);
  }

  function buildWait(session, panel) {
    var record = session.offer || {};
    var suggestion = record.state === 'counter_back';
    var amountCents = suggestion ? record.counterBackCents : record.amountCents;
    var amount = formatMoney(amountCents);
    if (amount) {
      panel.appendChild(keyValueRow(
        suggestion ? COPY.yourSuggestion : COPY.yourOffer, amount));
    }
    var head = el('div', 'rs-offer-panel-head');
    var dot = el('span', 'rs-offer-dot');
    dot.setAttribute('aria-hidden', 'true');
    head.appendChild(dot);
    head.appendChild(el('h2', 'rs-offer-panel-title', COPY.waitTitle));
    panel.appendChild(head);
    panel.appendChild(el('p', 'rs-offer-panel-line',
      suggestion ? suggestionLine(amount) : waitLine(amount)));
    panel.appendChild(el('p', 'rs-offer-panel-sub', COPY.waitSub));
  }

  // Struck list price · final price (spec's accepted-state price row). The
  // strike only renders when the offer actually beats the current list
  // price (read fresh; the variant may have changed since submit).
  function priceRow(session, finalCents) {
    var row = el('p', 'rs-offer-price-row');
    var listCents = readCfg(session).priceCents;
    if (listCents > finalCents && formatMoney(listCents)) {
      row.appendChild(el('s', 'rs-offer-price-was', formatMoney(listCents)));
      row.appendChild(document.createTextNode(' · '));
    }
    row.appendChild(el('span', 'rs-offer-price-final', formatMoney(finalCents)));
    return row;
  }

  function makeAnotherButton(session) {
    var again = el('button', 'rs-offer-again', COPY.makeAnother);
    again.type = 'button';
    again.addEventListener('click', function () { resetToForm(session); });
    return again;
  }

  function buildAccepted(session, panel, finalCents, checkoutUrl, heldUntilLabel) {
    panel.appendChild(el('h2', 'rs-offer-panel-title', COPY.acceptedTitle));
    panel.appendChild(priceRow(session, finalCents));
    if (checkoutUrl) {
      var link = el('a', 'rs-offer-buy', checkoutLabel(finalCents));
      link.href = checkoutUrl;
      panel.appendChild(link);
    } else {
      // The acceptance commits before its invoice mints: never a dead '#'
      // button. The poll keeps running and delivers the link in place.
      panel.appendChild(el('p', 'rs-offer-panel-sub', COPY.preparingCheckout));
    }
    panel.appendChild(el('p', 'rs-offer-panel-sub', acceptedNote(heldUntilLabel)));
  }

  // One builder serves the three conversation-over states: they only
  // differ by title, line and trailing actions.
  function buildClosing(panel, title, line, actions) {
    panel.appendChild(el('h2', 'rs-offer-panel-title', title));
    panel.appendChild(el('p', 'rs-offer-panel-line', line));
    for (var i = 0; i < actions.length; i++) panel.appendChild(actions[i]);
  }

  // A quiet key/value price row ("List price  $2,620" struck, "Our price
  // for you  $1,900" big). The struck list renders only when it actually
  // beats the price shown (read fresh; the variant may have changed).
  function keyValueRow(key, value, valueClass) {
    var row = el('div', 'rs-offer-row');
    row.appendChild(el('span', 'rs-offer-row-k', key));
    row.appendChild(el('span', 'rs-offer-row-v' + (valueClass ? ' ' + valueClass : ''), value));
    return row;
  }

  // The countered panel per the approved design: struck list above OUR
  // number (the customer's own offer is never echoed back), a DIRECT
  // checkout link at the counter price, the ONE bounded counter-back, and
  // a quiet decline. No accept round-trip: the link IS the acceptance.
  function buildCountered(session, panel, body) {
    var counterCents = body.counterCents;
    var listCents = readCfg(session).priceCents;
    if (listCents > counterCents && formatMoney(listCents)) {
      panel.appendChild(keyValueRow(COPY.listLabel, formatMoney(listCents), 'rs-offer-row-strike'));
    }
    panel.appendChild(keyValueRow(COPY.counterLabel, formatMoney(counterCents), 'rs-offer-row-big'));

    var respondError = el('p', 'rs-offer-panel-error');
    respondError.hidden = true;

    if (body.checkoutUrl) {
      var buy = el('a', 'rs-offer-buy', checkoutLabel(counterCents));
      buy.href = body.checkoutUrl;
      panel.appendChild(buy);
    } else {
      // The draft invoice is still minting; the poll repaints this panel
      // with the link the moment it lands.
      panel.appendChild(el('p', 'rs-offer-panel-sub', COPY.preparingCheckout));
    }

    var suggestOpen = el('button', 'rs-offer-chat rs-offer-suggest-open', COPY.suggestLink);
    suggestOpen.type = 'button';
    var suggestRow = el('div', 'rs-offer-suggest-row');
    suggestRow.hidden = true;
    var suggestInput = el('input', 'rs-offer-suggest-input');
    suggestInput.type = 'text';
    suggestInput.setAttribute('inputmode', 'decimal');
    suggestInput.setAttribute('aria-label', COPY.suggestLink);
    suggestInput.placeholder = '$0';
    var suggestSend = el('button', 'rs-offer-suggest-send', COPY.suggestSend);
    suggestSend.type = 'button';
    suggestRow.appendChild(suggestInput);
    suggestRow.appendChild(suggestSend);
    var suggestHint = el('p', 'rs-offer-panel-sub rs-offer-suggest-hint', COPY.suggestHint);
    suggestHint.hidden = true;
    var noThanks = el('button', 'rs-offer-tiny', COPY.counterDecline);
    noThanks.type = 'button';

    function setBusy(busy) {
      suggestSend.disabled = busy;
      noThanks.disabled = busy;
    }
    function fail(message) {
      setBusy(false);
      respondError.hidden = false;
      respondError.textContent = message || COPY.respondFailed;
    }

    suggestInput.addEventListener('input', function () { formatAmountInput(suggestInput); });
    suggestOpen.addEventListener('click', function () {
      suggestOpen.hidden = true;
      suggestRow.hidden = false;
      suggestHint.hidden = false;
      try { suggestInput.focus(); } catch (err) { /* noop */ }
    });
    suggestSend.addEventListener('click', function () {
      var offer = session.offer;
      if (!offer) return;
      var cents = parseAmountCents(suggestInput.value);
      // The one round is bounded: above their offer, below our counter.
      // Same rule the service enforces; checking here just saves a trip.
      if (!cents || cents <= (offer.amountCents || 0) || cents >= counterCents) {
        fail(COPY.suggestBounds);
        return;
      }
      setBusy(true);
      respondError.hidden = true;
      post(RESPOND_PATH, {
        offerId: offer.offerId, token: offer.token, action: 'counter_back', amountCents: cents
      })
        .then(function (result) {
          var resBody = result.body || {};
          if (result.status === 200 && resBody.ok) {
            offer.counterBackCents = cents;
            session.offerEpoch += 1; // stale in-flight polls drop
            renderWaiting(session, 'counter_back');
          } else if (resBody.error === 'invalid_counter_back') {
            fail(COPY.suggestBounds);
          } else if (result.status === 409) {
            // The negotiation moved on another surface (email page, other
            // tab): show the soft message and resync to the server's truth.
            fail();
            pollOnce(session);
          } else {
            fail();
          }
        })
        .catch(function () { fail(); });
    });
    noThanks.addEventListener('click', function () {
      var offer = session.offer;
      if (!offer) return;
      setBusy(true);
      respondError.hidden = true;
      post(RESPOND_PATH, { offerId: offer.offerId, token: offer.token, action: 'decline_counter' })
        .then(function (result) {
          var resBody = result.body || {};
          if (result.status === 200 && resBody.ok) {
            // Soft dismiss: our counter stays live, so we just minimize the
            // panel to the tab. No "declined" screen and no customer email;
            // the backend only notifies the operators. Re-enable the actions
            // so reopening the tab still lets them accept (the checkout link)
            // or send another suggestion.
            setBusy(false);
            collapseDrawer(session);
          } else if (result.status === 409) {
            fail();
            pollOnce(session);
          } else {
            fail();
          }
        })
        .catch(function () { fail(); });
    });

    panel.appendChild(suggestOpen);
    panel.appendChild(suggestRow);
    panel.appendChild(suggestHint);
    panel.appendChild(noThanks);
    panel.appendChild(respondError);
  }

  // Their counter-back fell short: hold our price warmly, keep the checkout
  // link one tap away, and open a door to the chat for alternatives.
  function buildStoodFirm(session, panel, body) {
    var counterCents = body.counterCents;
    panel.appendChild(keyValueRow(COPY.stoodFirmLabel, formatMoney(counterCents), 'rs-offer-row-big'));
    panel.appendChild(el('p', 'rs-offer-panel-sub', COPY.stoodFirmBody));
    if (body.checkoutUrl) {
      var buy = el('a', 'rs-offer-buy', checkoutLabel(counterCents));
      buy.href = body.checkoutUrl;
      panel.appendChild(buy);
    } else {
      panel.appendChild(el('p', 'rs-offer-panel-sub', COPY.preparingCheckout));
    }
    var chat = el('button', 'rs-offer-chat', COPY.openChat);
    chat.type = 'button';
    chat.addEventListener('click', openTidio);
    panel.appendChild(chat);
  }

  function buildDecision(session, panel, body) {
    var state = body.state;
    var amountCents = typeof body.amountCents === 'number'
      ? body.amountCents
      : (session.offer && session.offer.amountCents);
    if (state === 'accepted') {
      // The agreed price can differ from the original offer (an accepted
      // counter-back, or the storefront cap): prefer what the service says.
      var agreedCents = typeof body.agreedCents === 'number' ? body.agreedCents : amountCents;
      buildAccepted(session, panel, agreedCents, body.checkoutUrl, body.heldUntilLabel);
    } else if (state === 'countered') {
      buildCountered(session, panel, body);
    } else if (state === 'stood_firm') {
      buildStoodFirm(session, panel, body);
    } else if (state === 'counter_declined') {
      buildClosing(panel, COPY.counterDeclinedTitle, COPY.counterDeclinedBody, []);
    } else if (state === 'declined') {
      buildClosing(panel, declinedTitle(), declinedBody(formatMoney(amountCents)),
        [makeAnotherButton(session)]);
    } else {
      buildClosing(panel, COPY.expiredTitle, COPY.expiredBody, [makeAnotherButton(session)]);
    }
  }

  // Back to a waiting shape (fresh submit poll, or a counter-back just
  // sent): persists the state, repaints the wait panel, resumes polling.
  function renderWaiting(session, state) {
    var record = session.offer || (session.offer = {});
    record.state = state;
    record.seen = false;
    commitRecord(session);
    setUi(session, { kind: 'wait' });
    session.backoffMs = null;
    session.softFails = 0;
    scheduleNextPoll(session, session.pollMs);
  }

  // The single decision layer (dispatcher): folds the response into the
  // stored record, repaints, decides auto-expand, and runs the storage/seen
  // lifecycle. Everything that shows a decision (poll, respond, restore)
  // funnels through here or through setUi below it.
  function renderDecision(session, body) {
    stopPolling(session);
    var record = session.offer || (session.offer = {});
    // A re-delivery of a state the customer has already seen (e.g. the
    // refresh poll after restoring a seen counter) must not re-expand the
    // drawer: that was the reload nag loop.
    var alreadySeen = !!(record.seen && record.state === body.state);
    var changed =
      record.state !== body.state ||
      (typeof body.amountCents === 'number' && record.amountCents !== body.amountCents) ||
      (typeof body.counterCents === 'number' && record.counterCents !== body.counterCents) ||
      (typeof body.agreedCents === 'number' && record.agreedCents !== body.agreedCents) ||
      (!!body.checkoutUrl && record.checkoutUrl !== body.checkoutUrl) ||
      (!!body.heldUntilLabel && record.heldUntilLabel !== body.heldUntilLabel) ||
      !session.ui || session.ui.kind !== 'decision';
    record.state = body.state;
    if (typeof body.amountCents === 'number') record.amountCents = body.amountCents;
    if (typeof body.counterCents === 'number') record.counterCents = body.counterCents;
    if (typeof body.agreedCents === 'number') record.agreedCents = body.agreedCents;
    if (body.checkoutUrl) record.checkoutUrl = body.checkoutUrl;
    // Their own 48 hour hold on this rug, when the service found one. Kept
    // with its expiry (a NUMBER, like codeExpiresAt) so a restore after the
    // hold has lapsed says nothing stale: decisionView drops the line then.
    if (body.heldUntilLabel && expiryMs(body.heldUntil)) {
      record.heldUntilLabel = body.heldUntilLabel;
      record.heldUntil = expiryMs(body.heldUntil);
    }
    // Every state that keeps a live checkout link persists until it lapses
    // (stored as a NUMBER: the service sends an ISO string).
    if (record.state === 'accepted' || record.state === 'stood_firm' || record.state === 'countered') {
      record.codeExpiresAt =
        expiryMs(body.codeExpiresAt) || expiryMs(record.codeExpiresAt) || (Date.now() + CODE_TTL_MS);
    }
    record.seen = alreadySeen;
    if (changed) {
      // Repaints are change-gated so a quiet poll can never wipe what the
      // customer is doing; when one IS due, their half-typed suggestion
      // survives it.
      var prior = suggestSnapshot(session);
      setUi(session, { kind: 'decision', body: decisionView(record) });
      restoreSuggest(session, prior);
    }
    // The promise was "you'll have an answer here": a first-time decision
    // surfaces even if the customer collapsed the drawer while waiting.
    if (!alreadySeen) openDrawer(session);
    commitRecord(session);
    tryMarkSeen(session);
    // A decision whose draft invoice is still minting: keep polling quietly
    // until the link lands (the panel repaints in place, no re-expand).
    if (
      !record.checkoutUrl &&
      (record.state === 'countered' || record.state === 'accepted' || record.state === 'stood_firm')
    ) {
      scheduleNextPoll(session, session.pollMs);
    }
  }

  // Preserve the one thing a repaint could destroy: an open suggest input
  // and whatever the customer has typed into it.
  function suggestSnapshot(session) {
    var input = session.els.panel.querySelector('.rs-offer-suggest-input');
    if (!input || !session.els.panel.contains(input)) return null;
    var row = input.parentElement;
    if (!row || row.hidden) return null;
    return { value: input.value, focused: document.activeElement === input };
  }

  function restoreSuggest(session, prior) {
    if (!prior) return;
    var panel = session.els.panel;
    var open = panel.querySelector('.rs-offer-suggest-open');
    var row = panel.querySelector('.rs-offer-suggest-row');
    var hint = panel.querySelector('.rs-offer-suggest-hint');
    var input = panel.querySelector('.rs-offer-suggest-input');
    if (!row || !input) return;
    if (open) open.hidden = true;
    row.hidden = false;
    if (hint) hint.hidden = false;
    input.value = prior.value;
    if (prior.focused) {
      try { input.focus(); } catch (err) { /* noop */ }
    }
  }

  function decisionView(record) {
    return {
      state: record.state,
      amountCents: record.amountCents,
      counterCents: record.counterCents,
      agreedCents: record.agreedCents,
      checkoutUrl: record.checkoutUrl,
      // The hold line is shown only while the hold is still running.
      heldUntilLabel:
        record.heldUntil && Date.now() < record.heldUntil ? record.heldUntilLabel : undefined
    };
  }

  function resetToForm(session) {
    stopPolling(session);
    session.offer = null;
    session.offerEpoch += 1;
    session.backoffMs = null;
    session.softFails = 0;
    clearOffer(session.handle);
    updateBadge(session);
    session.els.amountInput.value = '';
    clearError(session);
    resetSubmitButton(session);
    setUi(session, null); // hides the drawer and tab
    openOverlay(session); // straight into a fresh form
  }

  // ---- events -------------------------------------------------------------

  function onBadgeClick(session) {
    pushEvent('make_an_offer_click', 'make_an_offer');
    // A live offer opens its drawer ("View offer status"); otherwise the
    // form modal. A leftover conversation-over panel starts fresh.
    if (session.offer) {
      openDrawer(session);
      return;
    }
    if (session.ui) setUi(session, null);
    openOverlay(session);
  }

  function onSubmit(session, e) {
    e.preventDefault();
    // Attempt semantics: fires before validation, so GA reads this as
    // "submit attempts", not offers received.
    pushEvent('make_an_offer_submit', 'make_an_offer_submit', { offer_handle: session.handle });

    var els = session.els;
    var amountCents = parseAmountCents(els.amountInput.value);
    if (!amountCents) { showError(session, COPY.invalidAmount); return; }
    var email = (els.emailInput.value || '').trim();
    if (!EMAIL_RE.test(email)) { showError(session, COPY.invalidEmail); return; }
    clearError(session);

    els.submitBtn.disabled = true;
    els.submitBtn.textContent = COPY.sending;

    var cfg = readCfg(session); // the CURRENT variant, at submit time
    session.posted = true;
    post(SUBMIT_PATH, {
      variantId: cfg.variantId,
      productHandle: session.handle,
      amountCents: amountCents,
      email: email,
      tz: browserTimeZone(),
      attribution: offerAttribution()
    })
      .then(function (result) {
        resetSubmitButton(session);
        var body = result.body || {};
        if (result.status === 200 && body.ok && body.offerId) {
          session.offer = {
            offerId: body.offerId,
            token: body.token,
            amountCents: amountCents,
            state: 'pending'
          };
          session.offerEpoch += 1;
          session.backoffMs = null;
          session.softFails = 0;
          saveOffer(session.handle, session.offer);
          updateBadge(session);
          if (typeof body.pollMs === 'number' && body.pollMs > 0) session.pollMs = body.pollMs;
          // Nothing but the offer id and the product handle may ride
          // along: the email must never reach analytics.
          var successEvent = {
            offer_status: body.state || 'pending',
            offer_id: body.offerId,
            offer_handle: session.handle
          };
          // Depth of the ask, so the Google Ads tag's trigger can skip a
          // lowball (header, "Offer depth on the success event"). A ratio,
          // not the amount; omitted when the root carried no price.
          var pct = discountPct(amountCents, cfg.priceCents);
          if (pct !== null) successEvent.offer_discount_pct = pct;
          pushEvent('make_an_offer_success', 'make_an_offer_success', successEvent);
          // Same rule for the ChatGPT Ads conversion, which has no trigger
          // layer between this code and Ads Manager.
          if (!isLowball(pct)) measureOpenAiOffer(body.offerId);
          fireTidioOnOffer(session, amountCents, email);
          // The form's job is done: the wait lives in the drawer, and the
          // page stays fully browsable underneath it.
          setUi(session, { kind: 'wait' });
          closeOverlay(session);
          openDrawer(session);
          scheduleNextPoll(session, session.pollMs);
        } else {
          var reason = (body && body.error) || 'http_' + result.status;
          pushEvent('make_an_offer_error', 'make_an_offer_error', {
            error_reason: reason,
            offer_handle: session.handle
          });
          if (body.error === 'invalid_amount') showError(session, COPY.invalidAmount);
          else if (body.error === 'invalid_email') showError(session, COPY.invalidEmail);
          else if (body.error === 'unavailable') showError(session, COPY.unavailable);
          else if (body.error === 'rate_limited') showError(session, COPY.rateLimited);
          else showError(session, COPY.unreachable);
        }
      })
      .catch(function () {
        resetSubmitButton(session);
        pushEvent('make_an_offer_error', 'make_an_offer_error', {
          error_reason: 'network',
          offer_handle: session.handle
        });
        showError(session, COPY.unreachable);
      });
  }

  // ---- boot restore -------------------------------------------------------
  // Reload behavior by stored state (see the doc block): pending restores
  // silently behind the badge with background polling; accepted states
  // restore the Complete purchase panel until codeExpiresAt; countered
  // restores the counter panel and refreshes once (the customer may have
  // responded from the email meanwhile); conversation-over states are only
  // ever stored unseen (hidden-tab renders) and clear once seen. The
  // overlay auto-opens ONLY for a decision the customer has not seen.
  function restore(session) {
    var stored = loadOffer(session.handle);
    if (!stored || !stored.offerId || !stored.token) return;
    // Records written by the pre-drawer widget: an accepted counter was
    // persisted as 'counter_accepted' with the deal in counterCents. Treat
    // it as the acceptance it is (never "expired", which would be a lie).
    if (stored.state === 'counter_accepted') {
      stored.state = 'accepted';
      if (stored.agreedCents == null) {
        stored.agreedCents = stored.counterCents || stored.amountCents;
      }
    }
    session.offer = stored;
    updateBadge(session);
    var state = stored.state;
    if (state === 'accepted' || state === 'stood_firm') {
      var lapse = expiryMs(stored.codeExpiresAt);
      if (lapse && Date.now() >= lapse) {
        // The checkout link lapsed while they were away (the backend has
        // deleted the draft by now).
        session.offer = null;
        clearOffer(session.handle);
        updateBadge(session);
        return;
      }
      // Seen decisions restore quietly to the edge tab; unseen ones expand.
      session.drawerMode = stored.seen ? 'tab' : 'open';
      setUi(session, { kind: 'decision', body: decisionView(stored) });
      if (!stored.seen) openDrawer(session);
      // The link may still have been minting when the tab closed: pick it up.
      if (!stored.checkoutUrl) pollOnce(session);
      return;
    }
    if (state && state !== 'pending' && state !== 'counter_back') {
      session.drawerMode = stored.seen ? 'tab' : 'open';
      setUi(session, { kind: 'decision', body: decisionView(stored) });
      if (!stored.seen) openDrawer(session);
      if (state === 'countered') pollOnce(session);
      return;
    }
    // Still pending (or their suggestion is under review): the drawer waits
    // quietly as the edge tab, polling in the background.
    session.drawerMode = 'tab';
    setUi(session, { kind: 'wait' });
    pollOnce(session);
  }

  // ---- init: (re)bind a root into its session -----------------------------
  // Idempotent per root (data-rs-offer-init) so the MutationObserver can
  // call it repeatedly; a NEW root for an existing session (variant
  // re-render) rebinds session.root/session.els and repaints the current
  // state into the fresh panel.
  function init(root) {
    if (root.dataset.rsOfferInit) return;
    var els = {
      badge: root.querySelector('.rs-offer-badge'),
      overlay: root.querySelector('.rs-offer-overlay'),
      form: root.querySelector('.rs-offer-form'),
      panel: root.querySelector('.rs-offer-panel'),
      drawer: root.querySelector('.rs-offer-drawer'),
      drawerLabel: root.querySelector('.rs-offer-drawer-label'),
      drawerHide: root.querySelector('.rs-offer-drawer-hide'),
      tab: root.querySelector('.rs-offer-tab'),
      amountInput: root.querySelector('.rs-offer-amount'),
      emailInput: root.querySelector('.rs-offer-email'),
      errorEl: root.querySelector('.rs-offer-error'),
      submitBtn: root.querySelector('.rs-offer-submit'),
      closeBtn: root.querySelector('.rs-offer-close')
    };
    // The full contract is required up front (one loud check instead of
    // per-use guards): a partial snippet is a build error, not something
    // to limp through with half a widget.
    for (var key in els) {
      if (Object.prototype.hasOwnProperty.call(els, key) && !els[key]) {
        if (window.console && console.error) {
          console.error('[rs-offer] missing required element "' + key + '"; widget not initialized');
        }
        return;
      }
    }
    root.dataset.rsOfferInit = '1';

    var session = getSession(root.getAttribute('data-product-handle') || '');
    var isRebind = !!session.root;
    session.root = root;
    session.els = els;
    session.badgeLabel = els.badge.textContent;

    els.badge.addEventListener('click', function () { onBadgeClick(session); });
    els.closeBtn.addEventListener('click', function () { closeOverlay(session); });
    els.overlay.addEventListener('click', function (e) {
      if (e.target === session.els.overlay) closeOverlay(session);
    });
    els.drawerHide.addEventListener('click', function () { collapseDrawer(session); });
    els.tab.addEventListener('click', function () { openDrawer(session); });
    els.amountInput.addEventListener('input', function () {
      formatAmountInput(session.els.amountInput);
    });
    els.form.addEventListener('submit', function (e) { onSubmit(session, e); });

    placeBadge(session);
    updateBadge(session);

    if (isRebind) {
      // Repaint whatever was on screen into the fresh panel; keep the
      // overlay open if it was (the keydown trap resolves session.els, so
      // it followed us here). An in-flight poll renders into these new
      // nodes because every render resolves them through the session.
      renderUi(session);
      if (session.open) els.overlay.hidden = false;
      return;
    }
    restore(session);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-rs-offer]');
    Array.prototype.forEach.call(roots, init);
    for (var handle in sessions) {
      if (!Object.prototype.hasOwnProperty.call(sessions, handle)) continue;
      var session = sessions[handle];
      if (!session.els || !document.contains(session.root)) continue;
      // A replaced meta block orphans the relocated badge (detached but
      // still ours): put the same element back into the fresh price list.
      if (!document.contains(session.els.badge)) {
        placeBadge(session);
        if (!document.contains(session.els.badge)) {
          // Relocation declined (no price list in this template): fall
          // back to the block position rather than losing the badge.
          session.root.insertBefore(session.els.badge, session.root.firstChild);
        }
      }
    }
  }

  function start() {
    // No widget root on this page (dark ship, or a non-product page):
    // nothing to bind, no observer to run. Attaching a body-wide
    // MutationObserver here would be pure per-mutation scan cost on
    // collection grids that can never host the widget (roots are only ever
    // server-rendered by product templates; product-rerender replaces
    // sections, it never introduces the widget).
    if (!document.querySelector('[data-rs-offer]')) return;
    initAll();
    // Focal's <product-rerender allow-partial-rerender> (theme.js) swaps
    // the "liquid" block (our root) and the "meta" block (the price list)
    // on every variant change. Same safety-net pattern as the sold-tile
    // CTA injector: watch the document and rebind whatever was replaced.
    // initAll is cheap and strictly idempotent (init skips flagged roots,
    // badge/label writes are change-gated), so no feedback loop.
    if (window.MutationObserver) {
      new MutationObserver(initAll).observe(document.body, { childList: true, subtree: true });
    }
    // Tab-return duties (pattern of the Tidio dwell IIFE above): resume
    // parked polls immediately, and mark decisions that rendered while
    // the tab was hidden as seen now that the customer can see them.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      for (var handle in sessions) {
        if (!Object.prototype.hasOwnProperty.call(sessions, handle)) continue;
        var session = sessions[handle];
        tryMarkSeen(session);
        if (session.pollWanted && !session.pollTimer && !session.pollInFlight) {
          pollOnce(session);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ==========================================================================
   Rug sizing - what size rug a room takes
   ==========================================================================
   The room conventions, the arithmetic that turns them into a rug, and the
   floor plan that draws it. Two places ask the same question: the Fit my room
   tab in the collection filter bar, and the rug size guide pages. They share
   this one copy so an answer edited in one is edited in both.

   The plan keeps its cfb__plan- class names. The styles for them are not
   scoped to the bar, so both callers get the same drawing from one stylesheet;
   renaming them would be churn in three files and a different picture in
   neither.
   ========================================================================== */
(function () {
  'use strict';

  /* Inches back into the feet and inches a floor is measured in. A bound can
     arrive straight off the URL, where nothing stops it being negative or not
     a number at all, and a chip reading -2' 5" is worse than one reading 0'. */
  function feetIn(inches, tight) {
    var n = Number(inches), f, i;
    if (!isFinite(n) || n < 0) n = 0;
    f = Math.floor(n / 12);
    i = Math.round(n - f * 12);
    if (i === 12) { f += 1; i = 0; }
    if (!i) return f + '\u2032';
    return f + '\u2032' + (tight ? '' : ' ') + i + '\u2033';
  }

  /* The two bounds of one dimension as numbers, with a missing one left
     missing: a bound can arrive off the URL as an empty string, and Number('')
     is 0, which is a real answer to the question "how wide". */
  function bounds(min, max) {
    var lo = min === '' || min == null ? null : Number(min);
    var hi = max === '' || max == null ? null : Number(max);
    if (lo != null && !isFinite(lo)) lo = null;
    if (hi != null && !isFinite(hi)) hi = null;
    return [lo, hi];
  }

  /* One bound of one dimension, as the notes say it. */
  function spanText(min, max) {
    var b = bounds(min, max), lo = b[0], hi = b[1];
    if (lo != null && hi != null) return feetIn(lo) + ' - ' + feetIn(hi);
    if (lo != null) return feetIn(lo) + ' and up';
    if (hi != null) return 'up to ' + feetIn(hi);
    return '';
  }

  /* ----------------------------------------------------------------- rooms */
  /* "What size rug for a living room" has no single answer, so each room here
     offers the two or three conventions a designer would actually name, and
     draws the furniture the convention is about. Everything is measured in
     feet from the top left of the floor, and the drawing reads the same
     numbers the arithmetic does, so a rug drawn half under the sofa is
     genuinely half under the sofa.

     Each room answers with:
       calc(v, cover) -> { w, l, shape, note }
       plan(v, cover, rug) -> { floor: {w, l}, rug: {x, y, w, l}, pieces: [] }

     Piece kinds the renderer knows: sofa, bed, desk, deskchair, box, chair,
     sink, door. See renderPlan(). */

  /* w and l are the mattress, in feet: 38 x 75 inches for a twin, 60 x 80 for
     a queen, 76 x 80 for a king, 72 x 84 for a California king.

     Each convention then names two rug sizes rather than one, low and high,
     because both are woven and both work under the same bed. A shopper shown
     a single figure reads it as the only figure that will do. The figures are
     curated rather than derived: they are the sizes actually sold, and
     rounding a formula lands on 7 x 9 often enough to be worth avoiding. */
  var BEDS = [
    /* A twin is only 38 inches across, so it is tempting to answer it with a
       small rug. Both conventions here are about the floor beside the bed,
       not the bed, and a 5 x 8 leaves nine inches of it.

       The surround widths are set by the nightstands rather than by the
       mattress. Wool on both sides of the bed means the bed, a bedside table
       either side of it, and floor still showing past them, so the width has
       to be the mattress plus about four feet. Every row here used to name
       the size below that, which drew a rug ending exactly at the outer edge
       of the furniture standing on it. */
    { key: 'twin', label: 'Twin', w: 3.17, l: 6.25,
      surround: [8, 10, 9, 12], twothirds: [5, 8, 6, 9] },
    { key: 'twinxl', label: 'Twin XL', w: 3.17, l: 6.67,
      surround: [8, 10, 9, 12], twothirds: [5, 8, 6, 9] },
    /* A full is six inches narrower than a queen and takes the same rug. */
    { key: 'full', label: 'Full', w: 4.5, l: 6.25,
      surround: [9, 12, 10, 14], twothirds: [5, 8, 6, 9] },
    { key: 'queen', label: 'Queen', w: 5, l: 6.67,
      surround: [9, 12, 10, 14], twothirds: [6, 9, 8, 10] },
    { key: 'king', label: 'King', w: 6.33, l: 6.67,
      surround: [10, 14, 12, 15], twothirds: [8, 10, 9, 12] },
    { key: 'calking', label: 'California King', w: 6, l: 7,
      surround: [10, 14, 12, 15], twothirds: [8, 10, 9, 12] }
  ];

  var BED_OPTIONS = BEDS.map(function (b) { return [b.key, b.label]; });

  function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }
  function round2(n) { return Math.round(n * 2) / 2; }

  /* Half a foot is the finest a rug is ever labelled, two feet is the smallest
     thing we would call a rug, and a runner has to be twice as long as it is
     wide or we should not be using the word. Every answer goes through here,
     so no arithmetic below has to remember any of that. */
  function fitRug(r) {
    var w = Math.max(2, round2(r.w));
    var l = Math.max(2, round2(r.l));
    var shape = r.shape;
    if (shape === 'Runner') {
      if (l < w * 2) w = round2(l / 2);
      if (w < 2) w = 2;
      if (l < w * 2) shape = 'Area';
    }
    /* w2 and l2 are the top of the recommendation where a room names a larger
       size that works just as well under the same furniture. Where it names
       only one, the range is that size, and rugRange widens it either way. */
    return {
      w: w, l: l,
      w2: r.w2 == null ? w : Math.max(w, round2(r.w2)),
      l2: r.l2 == null ? l : Math.max(l, round2(r.l2)),
      shape: shape, note: r.note
    };
  }

  /* What a room recommendation comes to as a filter, in inches, small side
     first because that is how a rug is labelled and how the app records it.
     A foot either side of the recommendation, because a rug labelled 9' x 12'
     measures within half a foot of that in each direction and because a floor
     does not stop working when the rug is a foot off. */
  function rugRange(rug) {
    var lo = Math.min(rug.w, rug.l), hi = Math.max(rug.w, rug.l);
    var lo2 = Math.min(rug.w2, rug.l2), hi2 = Math.max(rug.w2, rug.l2);
    return {
      wMin: Math.max(1, Math.round(lo * 12 - 12)), wMax: Math.round(lo2 * 12 + 12),
      lMin: Math.max(1, Math.round(hi * 12 - 12)), lMax: Math.round(hi2 * 12 + 12)
    };
  }

  /* fitRug is allowed to narrow a runner the room asked for wider, so any
     note that quotes a margin has to be written from the rug that came back
     rather than the one that went in. Otherwise an eight foot hall promises
     four inches of floor either side and lays down fifteen. */
  function sideNote(roomW, rug) {
    var side = Math.round((roomW - rug.w) * 6);
    if (side <= 0) return 'Wall to wall';
    /* Inches right up to two feet, because "fifteen inches of floor either
       side" is a measurement a shopper can picture and "1.5 feet" is not. */
    if (side < 24) return side + ' inches of floor either side';
    return round2(side / 12) + ' feet of floor either side';
  }

  /* The field rows declare a min and a max; this is what actually enforces
     them. Every calc and plan below may assume its values arrive inside the
     declared range, which is why none of them defends against a one foot
     room. */
  function clampFields(spec, v) {
    var out = {}, i, f;
    for (i = 0; i < spec.fields.length; i++) {
      f = spec.fields[i];
      out[f[0]] = f[3] == null ? v[f[0]] : clamp(v[f[0]] == null ? f[2] : v[f[0]], f[3], f[4]);
    }
    return out;
  }

  /* Whatever the arithmetic decided, a rug is never drawn hanging off the
     floor it is meant to be lying on. */
  function placeRug(x, y, rug, floor) {
    var w = Math.min(rug.w, floor.w);
    var l = Math.min(rug.l, floor.l);
    return { x: clamp(x, 0, floor.w - w), y: clamp(y, 0, floor.l - l), w: w, l: l };
  }

  /* ---- living ---- */
  /* Sofa against the top wall, chairs facing it, table between them. The rug
     lands differently under each convention, which is the whole point of the
     picture. */
  function livingPieces(W, L) {
    var sofaW = clamp(round2(W * 0.5), 3, 8);
    var sofaD = clamp(round2(L * 0.17), 1.6, 3);
    /* Far enough off the wall that a rug centred in the room still has the
       sofa's back legs on it, which is the whole claim of "all legs on". The
       floor is 1.2 rather than 1 because the widest rug this tab offers is
       the room less a foot each way, and a sofa flush with its edge reads as
       half off it. */
    var sofaY = clamp(round2(L * 0.08), 1.2, 1.6);
    var tableW = clamp(round2(W * 0.3), 2, 5);
    var tableD = clamp(round2(L * 0.12), 1.2, 2.2);
    var tableY = sofaY + sofaD + clamp(round2(L * 0.09), 0.8, 1.6);
    var chairW = clamp(round2(W * 0.18), 1.6, 2.5);
    var chairD = clamp(round2(L * 0.16), 1.6, 2.8);
    var chairY = clamp(tableY + tableD * 0.15, 0.3, L - chairD - 0.3);
    return {
      sofa: ['sofa', (W - sofaW) / 2, sofaY, sofaW, sofaD],
      table: ['box', (W - tableW) / 2, tableY, tableW, tableD],
      /* Inset by the same foot the "all legs on" rug leaves at the wall, and
         a little more. Sat at 0.8 the outer chair overhung that rug by a
         couple of inches, so the one convention that promises every leg on
         the rug was the one drawing a chair with two legs off it. */
      left: ['chair', 1.2, chairY, chairW, chairD],
      right: ['chair', W - 1.2 - chairW, chairY, chairW, chairD]
    };
  }

  /* The floor left between the two flanking chairs, with a few inches of
     clearance, measured off the chairs themselves so it cannot drift from
     where they are drawn. It decides both whether the room gets them at all
     and how wide the floating rug between them can be, and it has to be the
     same number in both places or the drawing contradicts the answer. */
  function livingChairGap(p, W) { return W - (p.left[1] + p.left[3] + 0.3) * 2; }

  /* Below five feet between them the pair is not seating arranged around a
     table, it is two chairs filling a room, and it leaves the floating rug
     nowhere to go. Five is the narrowest gap that still takes the smallest
     rug this tab floats with rug showing past the table on both sides. */
  var LIVING_CHAIR_GAP = 5;

  /* How much of each seat stands on the rug under "front legs on". Six to
     twelve inches is the figure the guides give, and nine is the middle of
     it: the width is drawn to the nearest half foot, which moves each edge by
     up to an inch and a half, and nine inches takes that either way without
     leaving a piece balanced on the edge of the rug. */
  var LIVING_FRONT_ON = 0.75;

  var LIVING = {
    label: 'Living room',
    /* Wider than deep, because that is how a room with a sofa along one wall
       reads from above, and a portrait drawing of it does not. */
    fields: [['w', 'Room width', 16, 8, 40], ['l', 'Room depth', 12, 8, 60]],
    coverages: [
      ['all', 'All legs on'],
      ['front', 'Front legs on'],
      ['float', 'Floating']
    ],
    cover: 'front',
    calc: function (v, cov) {
      var W = v.w, L = v.l, p = livingPieces(W, L);
      if (cov === 'all') {
        return fitRug({ w: W - 2, l: L - 2, shape: 'Area',
          note: 'Every leg of every piece stands on the rug, so the seating reads as one composed group. The largest of the three, with about a foot of floor showing all round.' });
      }
      if (cov === 'float') {
        /* Sized off the coffee table, not off the room. Taken as a share of
           the floor it grew with the room until it was wider than the sofa,
           which is a rug the seating missed rather than one it was arranged
           around: the other two conventions are the ones that answer a big
           room.

           A foot and a bit of rug past the table on each side is what it
           aims at. Two ceilings come first, because they are what the note
           promises: never as wide as the sofa, and never reaching under the
           chairs flanking the table. Where a narrow room cannot have all
           three, the border is what gives, down to about six inches. The
           floor of four feet is there because nothing smaller is a rug
           anyone floats, and the drawing rounds down so a half foot of
           rounding cannot put the rug back under a chair. */
        var cap = p.sofa[3] - 1;
        var gap = livingChairGap(p, W);
        if (gap >= LIVING_CHAIR_GAP) cap = Math.min(cap, gap);
        var fw = Math.floor(Math.max(Math.min(p.table[3] + 2.5, cap), 4) * 2) / 2;
        return fitRug({ w: fw, l: Math.max(p.table[4] + 2.5, fw * 0.7), shape: 'Area',
          note: 'The rug carries the coffee table and nothing else. The sofa sits just behind it and the chairs beside it stay on bare floor, with rug showing past the table on every side. The smallest of the three, and the one to choose when the sofa is against a wall or a larger rug would run under the doors.' });
      }
      /* Front legs on is a claim about every seat, not only the sofa. The
         chairs flanking the table face the middle, so their front legs are
         their inner ones, and they are what the width has to answer: the rug
         reaches nine inches past their inner edge and stops there. Taken as
         the room less three feet it ran almost to the far side of both of
         them, so the one convention that promises a chair half off the rug
         was drawing one with every leg on it.

         It still has to clear the sofa by a foot at either end, or it reads
         as stopping short of the piece it is anchoring. That is what sets
         the width in a room too narrow for the chairs to be the binding
         pair, and the room less three feet stays as a ceiling so the widest
         rooms keep a border. */
      var seats = W - (p.left[1] + p.left[3] - LIVING_FRONT_ON) * 2;
      return fitRug({ w: Math.min(Math.max(seats, p.sofa[3] + 2), W - 3), l: L - 4, shape: 'Area',
        note: 'The sofa and the chairs each stand their front legs on the rug and keep their back legs on bare floor, which draws the seating together and leaves a margin of floor at the walls. The most forgiving of the three in a room that is not quite square.' });
    },
    plan: function (v, cov, rug) {
      var W = v.w, L = v.l, p = livingPieces(W, L);
      var floor = { w: W, l: L };
      var pieces = [p.sofa, p.table];
      var y;
      if (livingChairGap(p, W) >= LIVING_CHAIR_GAP) pieces.push(p.left, p.right);
      /* Anchored to the sofa rather than centred, because "front legs on" is a
         statement about the seating, not about the room, and the same nine
         inches of it stands on the rug as of the chairs the width was set
         from. */
      if (cov === 'all') y = (L - rug.l) / 2;
      else if (cov === 'float') y = p.sofa[2] + p.sofa[4] + 0.4;
      else y = p.sofa[2] + p.sofa[4] - LIVING_FRONT_ON;
      return { floor: floor, rug: placeRug((W - rug.w) / 2, y, rug, floor), pieces: pieces };
    }
  };

  /* ---- dining ---- */
  /* Sized from the table, because the chairs are what fall off the edge. The
     floor is drawn as the rug plus a walking border; we never ask for it. */
  var DINING_CLEAR = { back: 2, roomy: 2.5, tight: 1.5 };

  var DINING = {
    label: 'Dining room',
    fields: [['w', 'Table width', 4, 3, 20], ['l', 'Table length', 7, 3, 30]],
    coverages: [
      ['back', 'Chairs slide back'],
      ['roomy', 'Roomy'],
      ['tight', 'Tight room']
    ],
    cover: 'back',
    calc: function (v, cov) {
      var c = DINING_CLEAR[cov] || DINING_CLEAR.back;
      var notes = {
        back: 'Two feet of rug past the table on every side, so a chair still has all four legs on it when someone pushes back from dinner.',
        roomy: 'Two and a half feet all round, which leaves room to walk behind a chair that is already pulled out.',
        tight: 'Eighteen inches all round. The chairs stay on the rug, though only just, so this one is for a room where the walls are close.'
      };
      return fitRug({ w: v.w + c * 2, l: v.l + c * 2, shape: 'Area', note: notes[cov] || notes.back });
    },
    plan: function (v, cov, rug) {
      var W = rug.w + 3, L = rug.l + 3;
      var tx = (W - v.w) / 2, ty = (L - v.l) / 2;
      var pieces = [['box', tx, ty, v.w, v.l]];
      var seat = 1.5, i, cy, n = clamp(Math.floor(v.l / 2.2), 1, 4);
      for (i = 0; i < n; i++) {
        cy = ty + (v.l / (n + 1)) * (i + 1) - seat / 2;
        pieces.push(['chair', tx - seat - 0.15, cy, seat, seat]);
        pieces.push(['chair', tx + v.w + 0.15, cy, seat, seat]);
      }
      if (v.w >= 3) {
        pieces.push(['chair', tx + (v.w - seat) / 2, ty - seat - 0.15, seat, seat]);
        pieces.push(['chair', tx + (v.w - seat) / 2, ty + v.l + 0.15, seat, seat]);
      }
      var floor = { w: W, l: L };
      return { floor: floor, rug: placeRug(1.5, 1.5, rug, floor), pieces: pieces };
    }
  };

  /* ---- bedroom ---- */
  /* A lookup rather than a formula, because the sizes designers actually put
     under each bed are not the sizes arithmetic produces. */
  function bedOf(key) {
    var i;
    for (i = 0; i < BEDS.length; i++) if (BEDS[i].key === key) return BEDS[i];
    return BEDS[3];
  }

  var BEDROOM = {
    label: 'Bedroom',
    fields: [['bed', 'Bed size', 'queen', null, null, BED_OPTIONS]],
    coverages: [
      ['surround', 'Full surround'],
      ['twothirds', 'Bottom two thirds']
    ],
    cover: 'surround',
    calc: function (v, cov) {
      var bed = bedOf(v.bed);
      var r = cov === 'twothirds' ? bed.twothirds : bed.surround;
      if (cov === 'twothirds') {
        /* Turned across the bed. That is what the placement is: the long side
           runs wall-ward, past both sides of the mattress, and the short side
           runs down the bed from a third of the way along. Drawn the other
           way up it was a strip narrower than the bed pointing at the foot of
           it, which is not a placement anybody uses. The sizes are the same
           either way, and so is the filter, because a rug is labelled with
           its small side first however it is laid. */
        return fitRug({ w: r[1], l: r[0], w2: r[3], l2: r[2], shape: 'Area',
          note: 'The rug starts a third of the way down the bed and runs past the foot, which leaves the nightstands on bare floor. Wool where your feet land, and the room reads longer for it.' });
      }
      return fitRug({ w: r[0], l: r[1], w2: r[2], l2: r[3], shape: 'Area',
        note: 'The rug runs about two feet past both sides of the bed and further past the foot, with the nightstands standing on it and floor still showing beyond them. You step onto wool whichever side you get out of.' });
    },
    plan: function (v, cov, rug) {
      var bed = bedOf(v.bed);
      var bw = bed.w, bl = bed.l, by = 1.2;
      /* A bedside table, at the size one actually is: about seventeen inches
         across and a little deeper, pushed almost against the bed. It is
         drawn at that size under both conventions. */
      var standW = 1.4, standD = 1.5, gap = 0.1;
      /* Full surround takes the whole bed, so its head starts above the
         headboard. Bottom two thirds starts a third of the way down the bed,
         which is what puts its head below the nightstands rather than over
         them: the old formula worked back from the foot instead, and since
         the rug is longer than the mattress in every row of the table it
         covered the bed entirely and contradicted its own label. */
      var ry = cov === 'twothirds' ? by + bl / 3 : by - 0.6;
      var W = Math.max(rug.w, bw + (gap + standW) * 2 + 1) + 2;
      var L = Math.max(ry + rug.l, by + bl) + 1.2;
      var bx = (W - bw) / 2;
      var floor = { w: W, l: L };
      var laid = placeRug((W - rug.w) / 2, ry, rug, floor);
      /* A full surround is wool on both sides of the bed, so the nightstands
         stand on it and the rug runs past them. The stand used to be cut down
         to whatever rug was left beside the bed instead, which put its outer
         edge exactly on the rug's: a surround drawn with no rug showing past
         the furniture standing on it, which is the one thing the placement is
         for. The margin now comes from the sizes in the table above, which
         leave four inches of it under a king and eleven under a twin.

         Bottom two thirds leaves the stands on bare boards, which is what its
         own note promises, and the floor is the same either way: W is worked
         out from the stand, so switching convention moves the furniture and
         not the room. */
      var sx = bx - gap - standW;
      return {
        floor: floor,
        rug: laid,
        pieces: [
          ['bed', bx, by, bw, bl],
          ['box', sx, by, standW, standD],
          ['box', W - sx - standW, by, standW, standD]
        ]
      };
    }
  };

  /* ---- hallway ---- */
  var HALLWAY = {
    label: 'Hallway',
    fields: [['w', 'Hall width', 4, 3, 12], ['l', 'Hall length', 14, 6, 60]],
    coverages: [
      ['classic', 'Classic runner'],
      ['full', 'Run the length']
    ],
    cover: 'classic',
    calc: function (v, cov) {
      var r, end;
      if (cov === 'full') {
        r = fitRug({ w: v.w - 0.67, l: v.l - 1, shape: 'Runner' });
        r.note = sideNote(v.w, r) +
          ' and almost the whole length. It reads closer to fitted carpet than to a rug.';
        return r;
      }
      /* A foot and a half at each end in a hall long enough to spare it, and a
         proportional gap in one that is not. */
      end = Math.min(1.5, v.l * 0.12);
      r = fitRug({ w: v.w - 1, l: v.l - end * 2, shape: 'Runner' });
      r.note = sideNote(v.w, r) + ' and a clear gap at each end, which is what keeps ' +
        (r.shape === 'Runner' ? 'a runner' : 'the rug') +
        ' looking deliberate rather than cut to fit.';
      return r;
    },
    plan: function (v, cov, rug) {
      var floor = { w: v.w, l: v.l };
      /* A door swings its own radius down the wall it is hung on, so both the
         radius and the hinge have to be answerable by the hall: at the six
         foot minimum a fixed 2.6 put the second arc a foot past the end of
         the floor, where the viewport simply cut it off. */
      var dr = Math.min(2.6, v.l / 2 - 0.3, v.w - 0.4);
      return {
        floor: floor,
        rug: placeRug((v.w - rug.w) / 2, (v.l - rug.l) / 2, rug, floor),
        pieces: [
          ['door', 0, clamp(v.l * 0.25, 0, v.l - dr), dr, 'left'],
          ['door', v.w, clamp(v.l * 0.68, 0, v.l - dr), dr, 'right']
        ]
      };
    }
  };

  /* ---- kitchen ---- */
  var KITCHEN = {
    label: 'Kitchen',
    fields: [['w', 'Aisle width', 4, 3, 12], ['l', 'Counter run', 10, 6, 40]],
    coverages: [
      ['sink', 'In front of the sink'],
      ['galley', 'Galley runner']
    ],
    cover: 'sink',
    calc: function (v, cov) {
      var r, w;
      if (cov === 'galley') {
        r = fitRug({ w: v.w - 1, l: v.l - 2, shape: 'Runner' });
        r.note = sideNote(v.w, r) + ' and a foot clear at each end. One rug down the ' +
          'working strip rather than two, with no seam underfoot where they would have met.';
        return r;
      }
      /* Long enough to read as a runner, but never longer than the counter it
         is standing in front of. */
      w = clamp(v.w - 1.5, 2, 3);
      return fitRug({ w: w, l: clamp(round2(v.l * 0.45), w * 2, Math.max(w * 2, v.l - 1)), shape: 'Runner',
        note: 'A short runner for the stretch you actually stand on, in front of the sink. Light enough to lift and air out, which is what a working kitchen asks of a rug.' });
    },
    plan: function (v, cov, rug) {
      var W = v.w + 4, L = v.l;
      var floor = { w: W, l: L };
      var y = cov === 'sink' ? L * 0.3 - rug.l / 2 : (L - rug.l) / 2;
      return {
        floor: floor,
        rug: placeRug((W - rug.w) / 2, y, rug, floor),
        pieces: [
          ['box', 0, 0, 2, L],
          ['sink', 1, L * 0.3],
          ['box', W - 2, 0, 2, L]
        ]
      };
    }
  };

  /* ---- entryway ---- */
  /* How far the door sweeps into the room. The plan draws its arc from this
     and the swing option sizes the rug to stay behind it, so the two have to
     be reading one number: sized against a flat three feet instead, the rug
     was drawn four inches inside the arc it says it clears. */
  function entryDoor(v) { return clamp(v.w * 0.6, 2, 3.2); }

  var ENTRYWAY = {
    label: 'Entryway',
    fields: [['w', 'Floor width', 5, 3, 20], ['l', 'Floor length', 7, 4, 20]],
    coverages: [
      ['swing', 'Clear the swing'],
      ['landing', 'Full landing']
    ],
    cover: 'swing',
    calc: function (v, cov) {
      var r, clear;
      if (cov === 'landing') {
        return fitRug({ w: v.w - 1, l: v.l - 1, shape: 'Area',
          note: 'Fills the floor with six inches showing on every side. It only works where the door opens outward or slides.' });
      }
      /* How much floor is left behind the arc. A rug is never drawn under
         two feet, so where the clearance is less than that no rug can stay
         out of the swing, and the note says so rather than promising
         something the picture beside it contradicts. */
      clear = v.l - entryDoor(v) - 0.8;
      r = fitRug({ w: v.w - 1, l: clamp(clear, 2, v.l - 1), shape: 'Area',
        note: 'Sits clear of the arc of the door, so nothing catches or rucks up each time somebody comes in.' });
      if (r.l > clear) {
        r.note = 'There is not enough floor behind the door for a rug to stay ' +
          'out of its path, so this one sits partly under the swing. A low ' +
          'flat weave is the one to choose here, or a door that opens outward.';
      }
      return r;
    },
    plan: function (v, cov, rug) {
      var floor = { w: v.w, l: v.l };
      return {
        floor: floor,
        rug: placeRug((v.w - rug.w) / 2, v.l - rug.l - 0.4, rug, floor),
        pieces: [['door', v.w * 0.15, 0, entryDoor(v), 'down']]
      };
    }
  };

  /* ---- office ---- */
  /* The desk the plan draws. The desk-only answer is sized from it rather
     than from the room, so the picture can keep the promise the words make:
     at half the room's length the rug stopped an inch or two short of the
     chair it says it covers. */
  function officeDesk(v) {
    return { w: clamp(v.w * 0.62, 3, 6), d: clamp(round2(v.l * 0.22), 1.6, 2.2) };
  }

  var OFFICE = {
    label: 'Office',
    fields: [['w', 'Room width', 8, 6, 30], ['l', 'Room length', 10, 6, 30]],
    coverages: [
      ['roll', 'Chair rolls freely'],
      ['desk', 'Under the desk only']
    ],
    cover: 'roll',
    calc: function (v, cov) {
      if (cov === 'desk') {
        var d = officeDesk(v);
        /* Eight inches of rug either side of the desk, and the length of the
           desk plus the chair pushed back from it. */
        return fitRug({ w: clamp(d.w + 1.6, 3, v.w - 1), l: clamp(d.d + 4.2, 3, v.l - 1), shape: 'Area',
          note: 'Covers the desk and the chair and stops there. The right choice when the chair mostly stays put.' });
      }
      return fitRug({ w: v.w - 1, l: v.l - 1, shape: 'Area',
        note: 'Covers the whole rolling zone, so a caster never drops off an edge halfway through a push.' });
    },
    plan: function (v, cov, rug) {
      var W = v.w, L = v.l, d = officeDesk(v);
      var deskW = d.w, deskD = d.d;
      var seatY = clamp(1.4 + deskD + 1.2, 0, L - 1.2);
      var floor = { w: W, l: L };
      return {
        floor: floor,
        rug: placeRug((W - rug.w) / 2, 0.8, rug, floor),
        /* The dashed circle that used to mark the rolling zone read as an
           object rather than a margin, and sitting over the desk it was the
           first thing the eye found. The rug already says how far the chair
           can roll, which is the only thing the circle was there for. */
        pieces: [
          ['desk', (W - deskW) / 2, 1.4, deskW, deskD],
          ['deskchair', W / 2, seatY, 1.15]
        ]
      };
    }
  };

  var ROOMS = {
    living: LIVING,
    dining: DINING,
    bedroom: BEDROOM,
    hallway: HALLWAY,
    kitchen: KITCHEN,
    entryway: ENTRYWAY,
    office: OFFICE
  };

  /* ------------------------------------------------------------- room plan */
  /* The plan is drawn from the same feet the arithmetic used, so the viewBox
     is simply the floor with half a foot of margin for the wall stroke.
     Strokes are declared non-scaling, which is what lets one set of CSS widths
     read correctly whether the floor is four feet across or forty. */
  var SVGNS = 'http://www.w3.org/2000/svg';

  function svgEl(name, attrs) {
    var n = document.createElementNS(SVGNS, name), k, v;
    /* Stroke widths are declared in CSS pixels and must stay there: the
       viewBox is measured in feet, so a scaled stroke would be a foot wide in
       a living room and an inch wide in a hallway. */
    n.setAttribute('vector-effect', 'non-scaling-stroke');
    for (k in attrs) {
      if (!attrs.hasOwnProperty(k)) continue;
      v = attrs[k];
      n.setAttribute(k, typeof v === 'number' ? Math.round(v * 1000) / 1000 : v);
    }
    return n;
  }

  /* A door is the architect's convention: the leaf, and a dashed arc for the
     space it needs. Which way the arc bends falls out of the cross product of
     the two radii, so the three wall directions need no special cases. */
  function drawDoor(svg, x, y, r, dir) {
    var wall, room;
    if (dir === 'right') { wall = [x, y + r]; room = [x - r, y]; }
    else if (dir === 'down') { wall = [x + r, y]; room = [x, y + r]; }
    else { wall = [x, y + r]; room = [x + r, y]; }
    var cross = (wall[0] - x) * (room[1] - y) - (wall[1] - y) * (room[0] - x);
    var n3 = function (v) { return Math.round(v * 1000) / 1000; };
    svg.appendChild(svgEl('path', {
      'class': 'cfb__plan-ghost',
      d: 'M' + n3(wall[0]) + ',' + n3(wall[1]) + 'A' + n3(r) + ',' + n3(r) +
         ' 0 0 ' + (cross > 0 ? 1 : 0) + ' ' + n3(room[0]) + ',' + n3(room[1])
    }));
    svg.appendChild(svgEl('line', {
      'class': 'cfb__plan-leaf', x1: x, y1: y, x2: room[0], y2: room[1]
    }));
  }

  function drawPiece(svg, p) {
    var kind = p[0], arm, back, inner, seats, gap, cw, i;
    if (kind === 'box' || kind === 'chair') {
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-piece', x: p[1], y: p[2],
        width: p[3], height: p[4], rx: kind === 'chair' ? 0.2 : 0.1 }));
      return;
    }
    if (kind === 'sofa') {
      /* Two arms, a back and the cushions between them. Without the cushions
         it is one more box at 150 pixels, and the sofa is the piece the whole
         picture is oriented by. */
      arm = Math.min(0.55, p[3] * 0.14);
      back = Math.min(0.45, p[4] * 0.3);
      inner = p[3] - arm * 2;
      seats = clamp(Math.round(inner / 2.5), 2, 4);
      gap = 0.08;
      cw = (inner - gap * (seats + 1)) / seats;
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-piece', x: p[1], y: p[2],
        width: p[3], height: p[4], rx: 0.25 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-detail', x: p[1], y: p[2],
        width: arm, height: p[4], rx: 0.2 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-detail', x: p[1] + p[3] - arm,
        y: p[2], width: arm, height: p[4], rx: 0.2 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-detail', x: p[1] + arm, y: p[2],
        width: inner, height: back }));
      /* A sofa too narrow to divide is left plain rather than seamed with
         slivers. The test belongs here, where it is decided once. */
      if (cw > 0.1) {
        for (i = 0; i < seats; i++) {
          svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-seam',
            x: p[1] + arm + gap + i * (cw + gap), y: p[2] + back + gap,
            width: cw, height: Math.max(0.1, p[4] - back - gap * 2), rx: 0.12 }));
        }
      }
      return;
    }
    if (kind === 'desk') {
      /* A monitor and a keyboard, drawn small. It is the difference between a
         desk and a kitchen island. */
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-piece', x: p[1], y: p[2],
        width: p[3], height: p[4], rx: 0.1 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-detail', x: p[1] + p[3] * 0.35,
        y: p[2] + 0.12, width: p[3] * 0.3, height: Math.min(0.32, p[4] * 0.2), rx: 0.08 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-seam', x: p[1] + p[3] * 0.28,
        y: p[2] + p[4] - Math.min(0.42, p[4] * 0.3),
        width: p[3] * 0.44, height: Math.min(0.3, p[4] * 0.2), rx: 0.06 }));
      return;
    }
    if (kind === 'deskchair') {
      /* Seat and back, the way a chair is drawn on a floor plan. A bare
         circle in front of a desk reads as a lamp. */
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-piece', x: p[1] - p[3],
        y: p[2] - p[3], width: p[3] * 2, height: p[3] * 2, rx: p[3] * 0.55 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-detail', x: p[1] - p[3] * 0.92,
        y: p[2] + p[3] * 0.42, width: p[3] * 1.84, height: p[3] * 0.5, rx: p[3] * 0.25 }));
      return;
    }
    if (kind === 'bed') {
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-piece', x: p[1], y: p[2],
        width: p[3], height: p[4], rx: 0.2 }));
      svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-detail', x: p[1] + 0.2, y: p[2] + 0.2,
        width: p[3] - 0.4, height: Math.min(1, p[4] * 0.18), rx: 0.15 }));
      return;
    }
    if (kind === 'sink') {
      svg.appendChild(svgEl('circle', { 'class': 'cfb__plan-detail', cx: p[1], cy: p[2], r: 0.45 }));
      return;
    }
    if (kind === 'door') drawDoor(svg, p[1], p[2], p[3], p[4]);
  }

  /* Floor, then rug, then furniture: the sofa has to sit on top of the rug or
     the picture does not answer the question the tab is asking. */
  function renderPlan(svg, title, plan) {
    var pad = 0.5;
    svg.textContent = '';
    if (title) svg.appendChild(title);
    svg.setAttribute('viewBox', -pad + ' ' + -pad + ' ' +
      (plan.floor.w + pad * 2) + ' ' + (plan.floor.l + pad * 2));
    svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-floor', x: 0, y: 0,
      width: plan.floor.w, height: plan.floor.l }));
    svg.appendChild(svgEl('rect', { 'class': 'cfb__plan-rug', x: plan.rug.x, y: plan.rug.y,
      width: plan.rug.w, height: plan.rug.l }));
    plan.pieces.forEach(function (p) { drawPiece(svg, p); });
  }

  /* --------------------------------------------------------------- controls */
  /* The controls themselves, not just the arithmetic behind them. Both entry
     points show the same three things - a row of fields, a row of coverage
     pills, and the plan they feed - so they are built here once. What differs
     is where the room comes from (a select in the bar and on the hub, a pinned
     attribute on a room page) and what is done with the answer, and those are
     what a caller passes in. */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function num(input) {
    var v = parseFloat(input.value);
    return isFinite(v) && v > 0 ? v : null;
  }

  function pill(label, active, onClick) {
    var b = el('button', 'cfb__pill', label);
    b.type = 'button';
    b.setAttribute('aria-pressed', active ? 'true' : 'false');
    b.addEventListener('click', onClick);
    return b;
  }

  /* opts.spec names the room and is asked again on every read, so a caller
     whose room can change does not have to tell anyone. opts.idPrefix keeps
     the two callers' field ids apart, because a label needs one to point at.
     opts.onDraw, if given, is handed the current answer, or null while a box
     is empty. */
  function mountRoom(opts) {
    var values = {};
    var pills = [];
    var cover = null;

    function spec() { return opts.spec(); }

    function markCovers() {
      pills.forEach(function (b) {
        b.setAttribute('aria-pressed',
          b.getAttribute('data-cover') === cover ? 'true' : 'false');
      });
    }

    /* Each room offers the two or three conventions a designer would name.
       A press moves aria-pressed along the row rather than rebuilding it:
       rebuilding destroys the button the press came from, which leaves a
       keyboard standing on nothing and drops focus to the top of the page. */
    function buildCovers() {
      pills = [];
      opts.covers.textContent = '';
      spec().coverages.forEach(function (c) {
        var b = pill(c[1], c[0] === cover, function () {
          cover = c[0];
          markCovers();
          draw();
        });
        b.setAttribute('data-cover', c[0]);
        pills.push(b);
        opts.covers.appendChild(b);
      });
    }

    function buildFields() {
      values = {};
      opts.fields.textContent = '';

      spec().fields.forEach(function (f) {
        var key = f[0], text = f[1], def = f[2], min = f[3], max = f[4], options = f[5];
        var id = opts.idPrefix + key;
        var wrap = el('div', 'cfb__field');
        var lab = el('label', 'cfb__label', text);
        var input, dims, sel;
        lab.htmlFor = id;
        wrap.appendChild(lab);

        if (options) {
          input = el('select', 'cfb__select');
          options.forEach(function (o) {
            var opt = el('option', null, o[1]);
            opt.value = o[0];
            input.appendChild(opt);
          });
          input.value = def;
          sel = el('span', 'cfb__select-wrap');
          sel.appendChild(input);
          wrap.appendChild(sel);
        } else {
          dims = el('div', 'cfb__dims');
          input = el('input', 'cfb__input');
          input.type = 'number';
          input.inputMode = 'numeric';
          input.min = min;
          input.max = max;
          input.step = '1';
          input.value = def;
          dims.appendChild(input);
          dims.appendChild(el('span', 'cfb__unit', 'ft'));
          wrap.appendChild(dims);
        }
        input.id = id;

        values[key] = input;
        input.addEventListener('input', draw);
        input.addEventListener('change', draw);
        opts.fields.appendChild(wrap);
      });
    }

    /* The room as it stands, or null while any box is empty. */
    function read() {
      var s = spec();
      var v = {}, ok = true;
      s.fields.forEach(function (f) {
        var input = values[f[0]];
        if (!input) { ok = false; return; }
        if (input.tagName === 'SELECT') { v[f[0]] = input.value; return; }
        var n = num(input);
        if (n == null) { ok = false; return; }
        v[f[0]] = n;
      });
      if (!ok) return null;
      /* Clamped before anything is calculated, because every calc and plan
         above is written on the assumption that its numbers arrive inside the
         range the field row declared. */
      v = clampFields(s, v);
      return { v: v, spec: s, rug: s.calc(v, cover) };
    }

    /* The size the room comes to, said in the same words the filter is about
       to be set in, and rewritten on every keystroke. A drawing on its own
       leaves the shopper to guess what to type into the other two tabs. */
    function planRange(rug) {
      var g = rugRange(rug);
      return 'Look for ' + spanText(g.wMin, g.wMax) + ' \u00d7 ' +
        spanText(g.lMin, g.lMax) + (rug.shape === 'Runner' ? ', a runner' : '');
    }

    function draw() {
      var r = read();
      if (opts.onDraw) opts.onDraw(r);
      if (!r || !opts.plan) { if (opts.plan) opts.plan.hidden = true; return; }
      opts.plan.hidden = false;
      renderPlan(opts.planSvg, opts.planTitle, r.spec.plan(r.v, cover, r.rug));
      /* Small side first, the way the range line underneath says it and the
         way the rug is labelled, so the two do not read as two answers. */
      opts.planTitle.textContent = r.spec.label + ' seen from above, with a rug about ' +
        Math.min(r.rug.w, r.rug.l) + ' by ' + Math.max(r.rug.w, r.rug.l) + ' feet';
      if (opts.planSize) opts.planSize.textContent = planRange(r.rug);
      if (opts.planText) opts.planText.textContent = r.rug.note;
    }

    /* Called for the first room and for every room after it. The coverage
       goes back to the one the room opens on, because a convention named for
       a sofa means nothing under a dining table. */
    function setRoom() {
      cover = spec().cover;
      buildFields();
      buildCovers();
      draw();
    }

    setRoom();

    return {
      setRoom: setRoom,
      read: read,
      cover: function () { return cover; },
      /* The courtesy an emptied box is owed: the caret back, rather than a
         button that quietly does nothing. */
      focusEmpty: function () {
        return spec().fields.some(function (f) {
          var input = values[f[0]];
          if (!input || input.tagName === 'SELECT' || num(input) != null) return false;
          input.focus();
          return true;
        });
      }
    };
  }

  /* ------------------------------------------------- reading a size picker */

  /* A size in feet and a give in inches as the four bounds they filter on.
     Small side first, because a rug is labelled that way and the app stores
     it that way: 12 by 9 and 9 by 12 are the same rug. */
  function givenRange(sz) {
    var small = Math.min(sz.w, sz.l), large = Math.max(sz.w, sz.l);
    return {
      wMin: Math.max(1, Math.round(small * 12 - sz.give)),
      wMax: Math.round(small * 12 + sz.give),
      lMin: Math.max(1, Math.round(large * 12 - sz.give)),
      lMax: Math.round(large * 12 + sz.give)
    };
  }

  /* The size and the give the Size tab would have had in its boxes to ask for
     this range, or null when nothing it can be asked lands here.

     The centre of each bound is the side that was typed and half the span is
     the give, but working them out is not the same as checking them: the tab
     rounds a size to two decimals of a foot, floors the give at six inches
     and puts the smaller side first, and any of those can land somewhere
     other than where the range arrived from. So it lays the range those three
     numbers filter on back over the one that arrived and only answers when
     they are the same range. A room's two recommended sizes, a lopsided range
     and a width typed wider than the length all fail that, and are said as
     the ranges they are. */
  function sizeAndGive(w, l) {
    var sz, r;
    if (w[0] == null || w[1] == null || l[0] == null || l[1] == null) return null;
    sz = {
      w: Math.round((w[0] + w[1]) / 24 * 100) / 100,
      l: Math.round((l[0] + l[1]) / 24 * 100) / 100,
      give: clamp(Math.round((w[1] - w[0]) / 2), 6, 60)
    };
    r = givenRange(sz);
    if (r.wMin !== w[0] || r.wMax !== w[1]) return null;
    if (r.lMin !== l[0] || r.lMax !== l[1]) return null;
    return sz;
  }

  /* Inches, as the filter sends them. `null` for a bound left open. */
  function rangeOf(r) {
    return {
      min_width_in: r.wMin, max_width_in: r.wMax,
      min_length_in: r.lMin, max_length_in: r.lMax
    };
  }

  /* One size_range in the words the chips use: 8′–10′ × 11′–13′. A dimension
     with no bound at all reads "any", so a range drawn on one side only still
     says which side it was. */
  function rangeText(sr) {
    function side(lo, hi) {
      var a = lo != null ? feetIn(lo, true) : null;
      var b = hi != null ? feetIn(hi, true) : null;
      if (a && b) return a + '–' + b;
      if (a) return a + ' and up';
      if (b) return 'up to ' + b;
      return 'any';
    }
    if (!sr) return '';
    return side(sr.min_width_in, sr.max_width_in) + ' × ' +
      side(sr.min_length_in, sr.max_length_in);
  }

  /* The size-picker snippet wired to answer one question - what size did they
     say - and nothing else.

     The collection bar and the similar-rugs toolbar predate this and still
     wire their own copies, because for them an apply is also a navigation or
     a fresh search; this is for a host where applying is just an answer. All
     three render the same markup (snippets/size-picker.liquid), so the hooks
     below are the hooks they use.

     opts.root    the element the picker was rendered into
     opts.prefix  the prefix it was rendered with
     opts.onApply given a size_range when a tab applies one
     opts.onClear called when Clear is pressed */
  function mountSizePanel(opts) {
    var root = opts.root, p = opts.prefix;
    var q = function (sel) { return root.querySelector(sel); };
    var qa = function (sel) { return [].slice.call(root.querySelectorAll(sel)); };
    var tabs = qa('[data-' + p + '-tab]');

    function selectTab(tab, focus) {
      var name = tab.getAttribute('data-' + p + '-tab');
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      qa('[data-' + p + '-tabpanel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-' + p + '-tabpanel') !== name;
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectTab(tab); });
      tab.addEventListener('keydown', function (e) {
        var to = null;
        if (e.key === 'ArrowRight') to = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') to = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') to = tabs[0];
        else if (e.key === 'End') to = tabs[tabs.length - 1];
        if (!to) return;
        e.preventDefault();
        selectTab(to, true);
      });
    });

    // ---- the exact tab
    var exW = q('[data-' + p + '-exact-w]');
    var exL = q('[data-' + p + '-exact-l]');
    var exTol = q('[data-' + p + '-exact-tol]');
    var exNote = q('[data-' + p + '-exact-note]');
    var EX_NOTE = exNote ? exNote.textContent : '';

    function exactTol() {
      var tol = parseFloat(exTol.value);
      if (!(tol >= 0)) tol = 12;
      return clamp(Math.round(tol), 6, 60);
    }

    function exactRange() {
      var w = num(exW), l = num(exL);
      if (!w || !l) return null;
      return givenRange({ w: w, l: l, give: exactTol() });
    }

    function exactNote() {
      var r = exactRange();
      if (!exNote) return;
      exNote.textContent = r
        ? 'Looks for rugs ' + spanText(r.wMin, r.wMax) + ' wide and ' +
          spanText(r.lMin, r.lMax) + ' long.'
        : EX_NOTE;
    }
    [exW, exL, exTol].forEach(function (box) {
      box.addEventListener('input', exactNote);
      box.addEventListener('change', exactNote);
    });

    // ---- the range tab
    var rW = ['wminf', 'wmini', 'wmaxf', 'wmaxi'].map(function (k) {
      return q('[data-' + p + '-range-' + k + ']');
    });
    var rL = ['lminf', 'lmini', 'lmaxf', 'lmaxi'].map(function (k) {
      return q('[data-' + p + '-range-' + k + ']');
    });

    /* Either box of a bound counts as that bound being set, and an empty pair
       is no bound rather than zero. */
    function bound(feetInput, inchInput) {
      var fr = feetInput.value.replace(/^\s+|\s+$/g, '');
      var ir = inchInput.value.replace(/^\s+|\s+$/g, '');
      if (fr === '' && ir === '') return null;
      var f = fr === '' ? 0 : parseFloat(fr);
      var i = ir === '' ? 0 : parseFloat(ir);
      if (!isFinite(f) || !isFinite(i) || f < 0 || i < 0) return null;
      var total = Math.round(f * 12 + i);
      return total > 0 ? total : null;
    }

    function pair(boxes) {
      var min = bound(boxes[0], boxes[1]), max = bound(boxes[2], boxes[3]), t;
      if (min != null && max != null && min > max) { t = min; min = max; max = t; }
      return { min: min, max: max };
    }

    function rangeRange() {
      var w = pair(rW), l = pair(rL), sr = {};
      if (w.min == null && w.max == null && l.min == null && l.max == null) return null;
      if (w.min != null) sr.min_width_in = w.min;
      if (w.max != null) sr.max_width_in = w.max;
      if (l.min != null) sr.min_length_in = l.min;
      if (l.max != null) sr.max_length_in = l.max;
      return sr;
    }

    // ---- the room tab
    var roomKind = q('[data-' + p + '-room-kind]');
    var room = mountRoom({
      spec: function () { return ROOMS[roomKind.value] || ROOMS.living; },
      idPrefix: p + '-room-',
      fields: q('[data-' + p + '-room-inputs]'),
      covers: q('[data-' + p + '-room-covers]'),
      plan: q('[data-' + p + '-plan]'),
      planSvg: q('[data-' + p + '-plan-svg]'),
      planTitle: q('[data-' + p + '-plan-title]'),
      planSize: q('[data-' + p + '-plan-size]'),
      planText: q('[data-' + p + '-plan-text]')
    });
    roomKind.addEventListener('change', room.setRoom);

    /* An apply with nothing in its boxes puts the caret in the first empty
       one rather than applying nothing: the shopper pressed the button
       meaning to say a size, and the boxes are where that is said. */
    function apply(which) {
      var sr = null;
      if (which === 'exact') {
        var ex = exactRange();
        if (!ex) { exW.focus(); return; }
        sr = rangeOf(ex);
      } else if (which === 'range') {
        sr = rangeRange();
        if (!sr) { rW[0].focus(); return; }
      } else {
        var r = room.read();
        if (!r) { room.focusEmpty(); return; }
        sr = rangeOf(rugRange(r.rug));
      }
      if (opts.onApply) opts.onApply(sr);
    }

    qa('[data-' + p + '-apply]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        apply(btn.getAttribute('data-' + p + '-apply'));
      });
    });

    /* Enter in a box applies the tab it was typed in. The panel can sit
       inside the alert form, where the browser's own answer to Enter is to
       send the alert - with the size still sitting unapplied in the box the
       shopper was typing it into. */
    root.addEventListener('keydown', function (e) {
      var open;
      if (e.key !== 'Enter' || e.target.tagName !== 'INPUT') return;
      e.preventDefault();
      open = qa('[data-' + p + '-tabpanel]').filter(function (panel) {
        return !panel.hidden;
      })[0];
      if (open) apply(open.getAttribute('data-' + p + '-tabpanel'));
    });

    function clearBoxes() {
      [exW, exL].concat(rW, rL).forEach(function (box) { box.value = ''; });
      exTol.value = '12';
      exactNote();
    }

    qa('[data-' + p + '-clear]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearBoxes();
        selectTab(tabs[0]);
        if (opts.onClear) opts.onClear();
      });
    });

    return {
      /* Show a size the host already has, on the tab that can express it: an
         exact size if the bounds are one, the hand-drawn range otherwise. A
         picker that opened empty on a size the shopper had already given
         would read as having lost it. */
      show: function (sr) {
        clearBoxes();
        if (!sr) { selectTab(tabs[0]); return; }
        var w = [sr.min_width_in == null ? null : sr.min_width_in,
          sr.max_width_in == null ? null : sr.max_width_in];
        var l = [sr.min_length_in == null ? null : sr.min_length_in,
          sr.max_length_in == null ? null : sr.max_length_in];
        var sz = sizeAndGive(w, l);
        if (sz) {
          exW.value = String(sz.w);
          exL.value = String(sz.l);
          exTol.value = String(sz.give);
          exactNote();
          selectTab(tabs[0]);
          return;
        }
        [[rW, w], [rL, l]].forEach(function (side) {
          var boxes = side[0], b = side[1];
          boxes[0].value = b[0] == null ? '' : String(Math.floor(b[0] / 12));
          boxes[1].value = b[0] == null ? '' : String(Math.round(b[0] % 12));
          boxes[2].value = b[1] == null ? '' : String(Math.floor(b[1] / 12));
          boxes[3].value = b[1] == null ? '' : String(Math.round(b[1] % 12));
        });
        selectTab(tabs[1]);
      }
    };
  }

  /* Only what the callers reach for. Everything else above - the bed
     table, the fitting, the drawing - is theirs through mountRoom, which is
     the point: no caller should be assembling a room out of parts. */
  window.RugSizing = {
    ROOMS: ROOMS,
    mountRoom: mountRoom,
    rugRange: rugRange,
    clampFields: clampFields,
    clamp: clamp,
    feetIn: feetIn,
    bounds: bounds,
    spanText: spanText,
    /* Shared so the rows either caller draws for itself are built out of the
       same pieces as the rows built here. */
    el: el,
    num: num,
    pill: pill,
    /* The size picker, whole and in parts. The two filter bars wire their own
       apply and take the parts; a host that only needs the answer takes
       mountSizePanel. */
    givenRange: givenRange,
    sizeAndGive: sizeAndGive,
    rangeText: rangeText,
    mountSizePanel: mountSizePanel
  };
})();

/* ==========================================================================
   Rug alerts
   --------------------------------------------------------------------------
   "Alert me to new arrivals." One dialog, opened from three
   places: a sold rug's page, the similar-rugs toolbar on a rug that is still
   for sale, and the filter bar above a collection. Markup in
   snippets/rug-alert-dialog.liquid, styles under .ralert in custom.css.

   Two things live here. The first is the dialog, mounted the same way
   everywhere so the promise, the copy and the telemetry cannot drift apart
   between surfaces. The second is parseFilters(): the storefront's filter
   URL read back as the subscription shape the backend stores.

   That second one carries the interesting decisions.

   The URL is the only stable contract the filter app offers - its DOM is
   its own - so this is the adapter between it and a model that is ours.
   The reading is lossy in one direction only: a parameter this file cannot
   turn into the model is reported in `dropped` and left out, which makes
   the alert BROADER than the page the shopper was looking at. Narrower
   would be an alert that quietly never fires.

   Colour needs a word. The app filters on raw colour names and one family is
   many of them - blue as an accent is twenty-three, from "cobalt" to "steel
   blue". The backend stores a rug's colour already resolved to one of
   sixteen basic colours, so the expansion means nothing to it and an alert
   asking for "cobalt" would match no rug ever listed. The family key is what
   travels, and the filter bar's own familyOf() is what turns an expansion
   back into it. Without that resolver the colour params are dropped rather
   than sent wrong.
   ========================================================================== */
(function () {
  /* Same shape as the concierge endpoint above, for the same reason: the App
     Proxy signs the request on the way through, so the path has to be
     relative in production and absolute against the local server in dev. */
  var ENDPOINT = window.RUG_ALERTS_ENDPOINT ||
    ((location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://127.0.0.1:8787/apps/room-concierge/alerts'
      : '/apps/room-concierge/alerts');

  var UNREACHABLE = 'We could not set that up just now. Please try again in a moment.';
  var SENDING = 'Setting this up…';
  var CONFIRMED = 'Almost there. Check your email and click the link to switch this saved search on.';
  var BAD_EMAIL = 'That address does not look right. Please check it and try again.';

  /* Deliberately loose. The server decides what it will send to, and a
     pattern strict enough to argue with is a pattern that turns away real
     addresses; this one only catches the typo worth catching before a round
     trip - a missing @ or a missing dot. */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* The concierge funnel's allowlist rule holds here too: an address is the
     one thing this feature collects, and it never reaches the dataLayer. */
  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined) {
          payload[key] = params[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  /* The digest goes out on the subscriber's Sunday morning, not the store's.
     Not knowing is fine - the server falls back to the store's own zone. */
  function timeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch (e) {
      return undefined;
    }
  }

  function subscribe(payload) {
    return fetch(ENDPOINT + '/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (body) {
        return { status: response.status, body: body };
      });
    });
  }

  /* ------------------------------------------------------- the filter URL */

  /* Present on a filtered URL, but not something a shopper is asking to be
     told about. Skipped in silence rather than reported as unreadable. */
  var IGNORED = { sort_by: 1, page: 1, q: 1, view: 1 };

  /* The trailing underscore before the bracket is the app's, not a typo:
     it slugifies the facet's label and the label ends in "(inches)". */
  var SIZE_KEYS = {
    'filter_width__inches_[min]': 'min_width_in',
    'filter_width__inches_[max]': 'max_width_in',
    'filter_length__inches_[min]': 'min_length_in',
    'filter_length__inches_[max]': 'max_length_in'
  };

  var PRICE_KEYS = {
    'filter_price[min]': 'min_price_usd',
    'filter_price[max]': 'max_price_usd'
  };

  var COLOUR_KEYS = {
    'filter_custom.dominant_color': ['dominant', 'dominant_color'],
    'filter_accent_colors': ['accent', 'accent_colors']
  };

  var CUSTOM_PREFIX = 'filter_custom.';

  /* The app's spelling of a boolean facet and the backend's. */
  var BOOLEAN_WORDS = { yes: 'true', no: 'false' };

  /* Values of one facet arrive indexed when there is more than one of them;
     the facet is the same facet either way. */
  function baseKey(key) { return key.replace(/\[\d+\]$/, ''); }

  function positiveInt(value) {
    var n = parseInt(value, 10);
    return isFinite(n) && n > 0 ? n : null;
  }

  function lowerTrim(value) { return value.toLowerCase().replace(/^\s+|\s+$/g, ''); }

  /* A nominal size tag as the inch bounds the rest of the model speaks. The
     give is the same twelve inches the Size tab spends by default: a rug is
     woven rather than cut, so the label on it and a tape measure rarely
     agree to the inch. Small side first, which is how a rug is labelled and
     how the alert matcher compares one. */
  var NOMINAL_GIVE = 12;
  function nominalRange(value) {
    var m = /^\s*(\d{1,2})\s*'\s*[x×]\s*(\d{1,2})\s*'\s*$/.exec(value);
    if (!m) return null;
    var a = parseInt(m[1], 10);
    var b = parseInt(m[2], 10);
    if (!a || !b) return null;
    var small = Math.min(a, b) * 12;
    var large = Math.max(a, b) * 12;
    return {
      min_width_in: Math.max(1, small - NOMINAL_GIVE),
      max_width_in: small + NOMINAL_GIVE,
      min_length_in: Math.max(1, large - NOMINAL_GIVE),
      max_length_in: large + NOMINAL_GIVE
    };
  }

  /**
   * The storefront's filter query as the subscription's canonical filter.
   *
   * opts.family is the filter bar's familyOf(values, which): the reverse of
   * the expansion it applied when it built the URL. Without it colour is
   * dropped, because the raw names in the URL match nothing on the far end.
   *
   * Returns { filter, dropped }, where dropped names the filter parameters
   * that were on the URL and produced nothing, so a caller can say what the
   * alert covers without overstating it.
   */
  function parseFilters(search, opts) {
    var options = opts || {};
    var params = new URLSearchParams(search || '');
    var buckets = {};
    var order = [];

    params.forEach(function (value, key) {
      var base = baseKey(key);
      /* An empty value filters nothing on the app's side either, so it is
         not something this could not read. */
      if (value === '') return;
      if (!Object.prototype.hasOwnProperty.call(buckets, base)) {
        buckets[base] = [];
        order.push(base);
      }
      buckets[base].push(value);
    });

    var size = {};
    var facets = {};
    var shape = [];
    var prices = {};
    var nominal = null;
    var dropped = [];
    var facetCount = 0;

    order.forEach(function (key) {
      var values = buckets[key];
      if (Object.prototype.hasOwnProperty.call(IGNORED, key)) return;
      /* Availability is a storefront concern: every rug an alert sends is in
         stock by definition, because it just arrived. */
      if (key.indexOf('filter_availability') === 0) return;
      if (key.indexOf('filter_') !== 0) return;

      if (Object.prototype.hasOwnProperty.call(SIZE_KEYS, key)) {
        var inches = positiveInt(values[0]);
        if (inches === null) { dropped.push(key); return; }
        size[SIZE_KEYS[key]] = inches;
        return;
      }

      if (Object.prototype.hasOwnProperty.call(PRICE_KEYS, key)) {
        var usd = positiveInt(values[0]);
        if (usd === null) { dropped.push(key); return; }
        prices[PRICE_KEYS[key]] = usd;
        return;
      }

      if (key === 'filter_size') { nominal = values; return; }

      if (key === CUSTOM_PREFIX + 'shape') {
        values.forEach(function (v) {
          var lower = lowerTrim(v);
          if (lower && shape.indexOf(lower) === -1) shape.push(lower);
        });
        if (!shape.length) dropped.push(key);
        return;
      }

      if (Object.prototype.hasOwnProperty.call(COLOUR_KEYS, key)) {
        var pair = COLOUR_KEYS[key];
        var family = options.family ? options.family(values, pair[0]) : null;
        if (!family) { dropped.push(key); return; }
        facets[pair[1]] = [family];
        facetCount++;
        return;
      }

      if (key.indexOf(CUSTOM_PREFIX) === 0) {
        var name = key.slice(CUSTOM_PREFIX.length);
        if (!name) { dropped.push(key); return; }
        /* Sent as the app spells them, lowercased, but for one thing: the
           app filters a boolean metafield as Yes/No and the far end stores
           true/false, and no facet's vocabulary has "yes" in it for any
           other reason. Anything else the far end does not recognise it
           drops with a warning of its own; guessing at a vocabulary from
           here is how the two ends stop agreeing. */
        facets[name] = values.map(function (v) {
          var lower = lowerTrim(v);
          return Object.prototype.hasOwnProperty.call(BOOLEAN_WORDS, lower)
            ? BOOLEAN_WORDS[lower] : lower;
        });
        facetCount++;
        return;
      }

      dropped.push(key);
    });

    /* A nominal tag only ever arrives from the app's own sidebar - the bar
       converts to inches before it navigates - and it is a rounder answer
       than explicit bounds, so it yields to them. */
    if (nominal) {
      var range = nominal.length === 1 ? nominalRange(nominal[0]) : null;
      var haveBounds = false;
      for (var k in size) {
        if (Object.prototype.hasOwnProperty.call(size, k)) { haveBounds = true; break; }
      }
      if (range && !haveBounds) size = range;
      else dropped.push('filter_size');
    }

    var filter = {};
    var hasSize = false;
    for (var sk in size) {
      if (Object.prototype.hasOwnProperty.call(size, sk)) { hasSize = true; break; }
    }
    if (hasSize) filter.size_range = size;
    if (prices.min_price_usd !== undefined) filter.min_price_usd = prices.min_price_usd;
    if (prices.max_price_usd !== undefined) filter.max_price_usd = prices.max_price_usd;
    if (shape.length) filter.shape = shape;
    if (facetCount) filter.facets = facets;

    return { filter: filter, dropped: dropped };
  }

  /* ------------------------------------------------------------ the dialog */

  /* What differs between the three surfaces is what the alert is FOR, which
     the caller answers with build(). Everything from the open to the
     confirmation is the same promise and reads the same way. */
  function mount(opts) {
    var trigger = opts.trigger;
    var dialog = opts.dialog;
    if (!trigger || !dialog) return;

    var form = dialog.querySelector('[data-alert-form]');
    var input = dialog.querySelector('[data-alert-email]');
    var statusEl = dialog.querySelector('[data-alert-status]');
    var summaryEl = dialog.querySelector('[data-alert-summary]');
    if (!form || !input || !statusEl) return;

    /* What the snippet rendered, kept so a surface with static copy is not
       blanked by a build() that has no summary of its own. */
    var staticSummary = summaryEl ? summaryEl.textContent : '';
    var plan = null;
    var turn = 0;
    /* One press, one subscription. The answer takes a moment on a phone and
       the form is still sitting there while it does, so a second Enter in
       that moment is a second alert and a second conversion counted for it.
       Cleared the moment an answer lands, because a request that failed has
       to leave the form usable, and on reset, because a reopened dialog is a
       new turn. */
    var sending = false;

    /* The size row, on the surfaces that rendered one. What it says is what
       is sent. On a rug's page it opens on that rug's own size, a foot
       either way, which is what the far end narrows a "rugs like this one"
       alert to anyway when no size is stated; a surface that arrived
       already filtered by size opens on that instead. A filter alert with
       neither - the collection bar's, on a page with no size on it - covers
       every size, and the row says so and asks for one: the shopper almost
       always has a space in mind, and an alert that knows it never writes
       about a rug that would not fit. Asked, not demanded: the email row is
       still one line down and the alert is complete without an answer. The
       surface's other bounds still arrive through plan.filter whether or
       not the row is there to show them.

       source says where the bounds in the alert came from - carried in by
       the surface, the rug's own, chosen here, or never given - because
       "8 by 10" means something different when the shopper typed it than
       when the toolbar happened to be filtered that way.

       opts.sizeText, when given, says a range in the surface's own words, so
       the row reads as the chips beside it do; the default is the range
       wording the toolbar and a rug's page share. */
    var RS = window.RugSizing;
    var RUG_GIVE = 12;
    var sizeEl = dialog.querySelector('[data-alert-size]');
    var sizeSaid = sizeEl && sizeEl.querySelector('[data-alert-size-said]');
    var sizeOpen = sizeEl && sizeEl.querySelector('[data-alert-size-open]');
    var sizeAsk = sizeEl && sizeEl.querySelector('[data-alert-size-ask]');
    var sizePanel = sizeEl && sizeEl.querySelector('[data-alert-size-panel]');
    var sizePrefix = (sizeEl && sizeEl.getAttribute('data-alert-size-prefix')) || 'ralert';
    var sizePicker = null;
    var size = { range: null, source: 'none' };

    if (!(RS && RS.mountSizePanel && sizeSaid && sizeOpen && sizeAsk && sizePanel)) sizeEl = null;

    function rangeWords(range) {
      var words = opts.sizeText ? opts.sizeText(range) : '';
      return words || RS.rangeText(range);
    }

    /* The rug on the page a foot either way, smaller side as the width and
       in whole inches, so the picker's exact tab can say it back. */
    function rugRange() {
      var rug = plan && plan.rugSize;
      if (!rug) return { range: null, source: 'none' };
      var w = Math.round(Math.min(rug.w, rug.l));
      var l = Math.round(Math.max(rug.w, rug.l));
      return {
        range: {
          min_width_in: Math.max(1, w - RUG_GIVE), max_width_in: w + RUG_GIVE,
          min_length_in: Math.max(1, l - RUG_GIVE), max_length_in: l + RUG_GIVE
        },
        source: 'rug'
      };
    }

    /* What the size goes back to when the shopper clears it: the rug's own,
       or on a collection that is itself a size - "9' x 12' Rugs" - that
       size. An alert named for that collection with no size left in it
       would be every new rug in the store, so its floor cannot be cleared
       out of, only changed. */
    function restRange() {
      var floor = plan && plan.sizeFloor;
      return floor ? { range: floor, source: 'carried' } : rugRange();
    }

    function drawSize() {
      var rug = plan && plan.rugSize;
      /* No size, and no rug to keep the alert to the size of: it covers
         every size until the shopper names one, so the row asks. */
      var ask = !size.range && Boolean(plan && plan.kind === 'filter');
      if (size.source === 'rug' && rug) {
        sizeSaid.textContent = RS.feetIn(Math.min(rug.w, rug.l)) + ' \u00d7 '
          + RS.feetIn(Math.max(rug.w, rug.l)) + ', give or take a foot';
      } else if (size.range) {
        sizeSaid.textContent = rangeWords(size.range);
      } else if (ask) {
        sizeSaid.textContent = 'Any size';
      } else {
        /* No measurements on the page: nothing to send, and nothing needed,
           since the far end keeps the alert to the rug's size on its own. */
        sizeSaid.textContent = "This rug's size, give or take a foot";
      }
      sizeOpen.textContent = ask ? 'Add my size' : 'Change';
      sizeEl.classList.toggle('ralert__size--ask', ask);
      sizeAsk.hidden = !ask;
    }

    function openSize(open) {
      sizePanel.hidden = !open;
      sizeOpen.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /* The alert as it will be sent: the surface's bounds with this row's
       answer written over them. A shopper who cleared a size the surface
       carried in meant to clear it, so the key comes out rather than staying
       as it arrived. */
    function withSize(base) {
      var out = {}, key, any = false;
      for (key in base) {
        if (Object.prototype.hasOwnProperty.call(base, key) && key !== 'size_range') {
          out[key] = base[key];
        }
      }
      if (size.range) out.size_range = size.range;
      for (key in out) {
        if (Object.prototype.hasOwnProperty.call(out, key)) { any = true; break; }
      }
      return any ? out : undefined;
    }

    if (sizeEl) {
      sizePicker = RS.mountSizePanel({
        root: sizePanel,
        prefix: sizePrefix,
        onApply: function (range) {
          size = { range: range, source: 'chosen' };
          track('rug_alert_size_chosen', { surface: opts.surface });
          drawSize();
          openSize(false);
          sizeOpen.focus();
        },
        onClear: function () {
          /* Back to where the size rests, in the boxes as well as in words,
             so the next look starts from it rather than from nothing; on a
             surface with no rug and no floor, back to any size and the ask. */
          size = restRange();
          track('rug_alert_size_cleared', { surface: opts.surface });
          sizePicker.show(size.range);
          drawSize();
          openSize(false);
          sizeOpen.focus();
        }
      });
      sizeOpen.addEventListener('click', function () {
        var open = sizePanel.hidden;
        openSize(open);
        if (!open) return;
        track('rug_alert_size_opened', {
          surface: opts.surface,
          alert_size_source: size.source
        });
      });
    }

    /* Nothing to subscribe to means nothing to offer. Taking the button off
       the page beats a button that answers a click with an apology.

       The unhide is the other half of that: a trigger ships hidden, so a
       visitor whose JS never ran, or ran and threw before reaching here, is
       never shown a button that cannot open anything. */
    if (opts.build && !opts.build()) { trigger.hidden = true; return; }
    trigger.hidden = false;

    function status(text) {
      statusEl.textContent = text || '';
      statusEl.hidden = !text;
    }

    /* Reopening starts over: a shopper who subscribed once and came back
       should find the form, not last time's confirmation. Bumping the turn
       abandons an answer still in flight, which would otherwise land on a
       dialog that has since been reopened and empty it. */
    /* Reset seeds the size row as well as clearing the form, and runs after
       build(), so a surface that arrives already filtered opens with its own
       bounds shown, and any other with the rug's, rather than with a size
       it is about to send anyway left unsaid. */
    function reset() {
      turn++;
      sending = false;
      form.hidden = false;
      status('');
      if (!sizeEl) return;
      var carried = plan && plan.filter && plan.filter.size_range;
      size = carried ? { range: carried, source: 'carried' } : rugRange();
      /* The picker's clear was rendered to say what the size goes back to;
         on a collection that is a size, that is the collection's, by name. */
      if (plan && plan.sizeFloor) {
        var back = 'Back to ' + rangeWords(plan.sizeFloor);
        $$('[data-' + sizePrefix + '-clear="size"]', sizePanel).forEach(function (btn) {
          btn.textContent = back;
        });
      }
      sizePicker.show(size.range);
      drawSize();
      openSize(false);
    }

    /* Every way out lands here: the X, the backdrop, Escape, and the fallback
       in close(). Tracked before the reset wipes the answer, so the event can
       say whether the shopper left with a subscription or without one. */
    function closed() {
      track('rug_alert_closed', {
        surface: opts.surface,
        alert_kind: plan ? plan.kind : undefined,
        alert_outcome: form.hidden ? 'subscribed' : 'abandoned'
      });
      reset();
    }

    function close() {
      if (dialog.close) { dialog.close(); return; }
      dialog.open = false;
      closed();
    }

    trigger.addEventListener('click', function () {
      plan = opts.build ? opts.build() : null;
      if (!plan) return;
      reset();
      if (summaryEl) summaryEl.textContent = plan.summary || staticSummary;
      /* showModal brings the focus trap, the Escape key and the return of
         focus to the button that opened it; a browser without it still gets
         the panel rather than nothing. */
      if (dialog.showModal) dialog.showModal();
      else dialog.open = true;
      /* alert_scoped separates the two similar alerts that now exist: one for
         the rug, one for the rug at a size the shopper has already named. The
         second is the one worth knowing the conversion of. alert_base is the
         collection bar's: what its offer was built from. */
      track('rug_alert_opened', {
        surface: opts.surface,
        alert_kind: plan.kind,
        alert_scoped: Boolean(plan.filter),
        alert_base: plan.base
      });
      input.focus();
    });

    $$('[data-alert-close]', dialog).forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    /* A click on the backdrop lands on the dialog itself and never on
       anything inside it, which is the only way to tell the two apart. */
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close();
    });

    dialog.addEventListener('close', closed);
    window.RugDialogEscape(dialog, close);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!plan || sending) return;
      var email = (input.value || '').replace(/^\s+|\s+$/g, '');
      if (!EMAIL_RE.test(email)) {
        /* A dead end here is a UX signal, as it is on the concierge's questions. */
        track('concierge_input_error', {
          question: 'alert_email', reason: 'invalid_email', surface: opts.surface
        });
        status(BAD_EMAIL);
        input.focus();
        return;
      }
      var mine = ++turn;
      var sent = sizeEl ? withSize(plan.filter || {}) : plan.filter;
      var sizeSource = sizeEl ? size.source : 'unavailable';
      sending = true;
      status(SENDING);
      subscribe({
        email: email,
        kind: plan.kind,
        anchorSku: plan.anchorSku || undefined,
        anchorTitle: plan.anchorTitle || undefined,
        anchorHandle: plan.anchorHandle || undefined,
        filter: sent,
        source: plan.source,
        customerTz: timeZone()
      }).then(function (result) {
        var body = result.body || {};
        if (body.ok) {
          /* Tracked whichever dialog is on screen now: the subscription was
             made even if the shopper walked away before the answer landed. */
          track('rug_alert_subscribed', {
            surface: opts.surface,
            alert_kind: plan.kind,
            alert_status: body.status,
            alert_scoped: Boolean(sent),
            alert_size_source: sizeSource,
            alert_base: plan.base
          });
          if (mine !== turn) return;
          sending = false;
          /* The dialog stays open holding the answer. Closing it the moment
             it succeeded would take the confirmation away with it, and this
             one has an instruction in it. */
          form.hidden = true;
          status(body.message || CONFIRMED);
          return;
        }
        /* Mirrored to Grafana with the concierge's own request outcomes, so
           a failing endpoint shows up on a panel that already exists. */
        window.RugConciergeFailures.push('alert', body.status || 'server_error', result, opts.surface);
        if (mine !== turn) return;
        sending = false;
        status(body.message || UNREACHABLE);
      }).catch(function () {
        window.RugConciergeFailures.push('alert', 'unreachable', undefined, opts.surface);
        if (mine !== turn) return;
        sending = false;
        status(UNREACHABLE);
      });
    });
  }

  /* The bounds the surface had on screen when the button was pressed, left
     there as JSON rather than handed over in code: two doors open onto the
     same subscription - the button and the toolbar's opt-in box - and an
     attribute is the one thing both of them already read.

     Unreadable is treated as absent. An alert anchored on the rug is worth
     far more than no alert at all, and the far end drops what it cannot use
     anyway. */
  function attrFilter(trigger) {
    var raw = trigger.getAttribute('data-alert-filter');
    if (!raw) return undefined;
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return undefined;
    }
    if (!parsed || typeof parsed !== 'object') return undefined;
    for (var key in parsed) {
      if (Object.prototype.hasOwnProperty.call(parsed, key)) return parsed;
    }
    return undefined;
  }

  /* The rug's own measurements in inches, when the page has them; the size
     row opens on them. Missing or unreadable leaves the row saying the
     rug's size in words and sending nothing, which the far end reads the
     same way. */
  function rugSizeAttr(trigger) {
    var w = Number(trigger.getAttribute('data-alert-rug-w'));
    var l = Number(trigger.getAttribute('data-alert-rug-l'));
    if (!isFinite(w) || !isFinite(l) || w <= 0 || l <= 0) return undefined;
    return { w: w, l: l };
  }

  /* A rug's own page says everything the alert needs in attributes, so those
     two surfaces mount themselves. The filter bar does not - what it is
     subscribing to is the URL it just built - so it calls mount() itself and
     marks its trigger manual. */
  function planFromAttrs(trigger) {
    var kind = trigger.getAttribute('data-alert-kind') || 'similar';
    var sku = (trigger.getAttribute('data-alert-sku') || '').replace(/^\s+|\s+$/g, '');
    if (kind === 'similar' && !sku) return null;
    return {
      kind: kind,
      anchorSku: sku,
      anchorTitle: trigger.getAttribute('data-alert-title') || '',
      anchorHandle: trigger.getAttribute('data-alert-handle') || '',
      filter: attrFilter(trigger),
      rugSize: rugSizeAttr(trigger),
      /* The surface that set the bounds is the only thing that can say them
         in words, so it writes the line as well as the filter. */
      summary: trigger.getAttribute('data-alert-scope') || '',
      source: trigger.getAttribute('data-alert-source') || ''
    };
  }

  function autoMount() {
    $$('[data-alert-open]:not([data-alert-manual])').forEach(function (trigger) {
      var dialog = document.getElementById(trigger.getAttribute('data-alert-open'));
      if (!dialog) { trigger.hidden = true; return; }
      mount({
        trigger: trigger,
        dialog: dialog,
        surface: trigger.getAttribute('data-alert-source') || 'pdp',
        build: function () { return planFromAttrs(trigger); }
      });
    });
  }

  window.RugAlerts = {
    mount: mount,
    subscribe: subscribe,
    planFromAttrs: planFromAttrs,
    parseFilters: parseFilters,
    timeZone: timeZone,
    track: track,
    EMAIL_RE: EMAIL_RE,
    UNREACHABLE: UNREACHABLE
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
})();

/* ==========================================================================
   Collection filter bar
   --------------------------------------------------------------------------
   Drives snippets/collection-filter-bar.liquid. Four chips - Size, Price,
   Shape, Color - stand in for twenty-five XCloud facets, and "All filters"
   hands the shopper back the app's own sidebar for the rest.

   The app is entirely URL-driven, so this file never touches its DOM or JS;
   it only builds URLs and reads counts. The param vocabulary below was
   established by driving the live app, not from documentation, and it is
   inconsistent on purpose: some facets carry a "custom." prefix and some do
   not, and the range facets slugify their own label.

   Two facts about the app shape the whole design:

   1. Values inside one facet OR together, and facets AND with each other.
      That is what lets a single swatch stand for a whole colour family -
      "light blue" is emitted as lightblue + light blue + pale blue + sky
      blue and returns exactly the 132 rugs the census counted. It is also
      why accent colour is single-select: two accent families would read as
      "or", and a shopper picking two means "and".

   2. Only /a/search/<handle> filters. The same params on /collections/<handle>
      are ignored, so every apply navigates to the proxy.
   ========================================================================== */
(function () {
  'use strict';

  /* One copy of the room arithmetic, shared with the rug size guide pages.
     Unpacked under the names the tab already calls, so the tab reads the same
     whether the answer is computed here or next door. */
  var RS = window.RugSizing;
  var ROOMS = RS.ROOMS, clamp = RS.clamp, clampFields = RS.clampFields,
      rugRange = RS.rugRange, mountRoom = RS.mountRoom,
      feetIn = RS.feetIn, bounds = RS.bounds, spanText = RS.spanText,
      el = RS.el, num = RS.num, pill = RS.pill,
      givenRange = RS.givenRange, sizeAndGive = RS.sizeAndGive;

  /* The app's own sidebar is hidden by CSS keyed on this class, not by a class
     this file adds once it has finished booting. A shopper on a phone used to
     watch the full list of app filters paint and then vanish; now the rule is
     already in force by the time the sidebar arrives.

     The cost of hiding by default is that a broken deploy would leave a page
     with no filters at all, so the claim is staked here, at the top of the
     file, and dropped again by unclaim() on every path that ends without a
     working bar. If this file never runs, the class is never added and the
     app keeps its sidebar. */
  document.documentElement.classList.add('cfb-js');

  function unclaim() {
    document.documentElement.classList.remove('cfb-js');
  }

  /* Bump when the markup in the snippet changes, so a cached copy from a
     previous deploy is discarded rather than hydrated. See hydrate(). */
  var VERSION = '4';
  var CACHE_KEY = 'cfb:markup:' + VERSION;

  /* ---------------------------------------------------------------- params */
  var P = {
    tone: 'filter_custom.overall_tone',
    intensity: 'filter_custom.color_intensity',
    dominant: 'filter_custom.dominant_color',
    accent: 'filter_accent_colors',
    shape: 'filter_custom.shape',
    size: 'filter_size',
    wMin: 'filter_width__inches_[min]',
    wMax: 'filter_width__inches_[max]',
    lMin: 'filter_length__inches_[min]',
    lMax: 'filter_length__inches_[max]',
    priceMin: 'filter_price[min]',
    priceMax: 'filter_price[max]'
  };

  /* Everything this bar owns, grouped by the control that owns it. A control
     rewrites its own group and carries everything else across untouched: the
     other three groups, a facet the shopper set from the sidebar, sort_by.
     Dropping all four groups on every apply is what used to make this a
     one-filter-at-a-time bar, where choosing a price threw away the size. */
  var GROUPS = {
    size: [P.size, P.wMin, P.wMax, P.lMin, P.lMax],
    price: [P.priceMin, P.priceMax],
    shape: [P.shape],
    color: [P.tone, P.intensity, P.dominant, P.accent]
  };

  /* ------------------------------------------------------------ vocabulary */
  var TONES = [['warm', 'Warm'], ['neutral', 'Neutral'], ['cool', 'Cool']];
  var INTENSITIES = [['faded', 'Faded'], ['muted', 'Muted'], ['vibrant', 'Vibrant']];
  var SHAPES = [['Area', 'Area'], ['Runner', 'Runner'], ['Square', 'Square']];

  /* The sixteen colour families the backend already resolves every colour
     name down to (eliko/src/color/color_namer.ts, BasicColor). Each row is
     [key, label, swatch, dominant values, accent values, shades].

     The two value lists differ because the two metafields carry different
     vocabularies for the same sixteen ideas - dominant_color has 34 values,
     colors has 215 - and a family has to name every synonym on both sides to
     return the whole family.

     Four names are deliberately filed against the backend's own dictionary:
     indigo and slate blue sit under blue rather than purple, lavender under
     purple rather than ivory, and azure under light blue alone. The
     dictionary's hexes are CSS keywords, and those four mean something else
     in a rug. The backend needs the same correction; until it gets one, this
     table is right and it is not.

     Shades are the specific names large enough inside a family to be worth
     offering as a second step. They exist for accent only: 8,459 of 8,630
     rugs have a dominant value that is just the bare family name. */
  var FAMILIES = [
    ['red','Red','#A33F3B',
     ['crimson','red'],
     ['berry','brick','brick red','chestnut','crimson','dusty red','faded red','mahogany','muted crimson','muted red','orange red','raspberry','red','rose','ruby','soft red'],
     [['rose',['rose']],['crimson',['crimson']]]],
    ['blue','Blue & indigo','#4E6E8E',
     ['blue','indigo','navy'],
     ['blue','blue gray','cobalt','cornflower blue','deep blue','denim','denim blue','dusty blue','dusty_blue','electric blue','faded blue','faded navy','gray blue','indigo','ink blue','muted blue','muted navy','navy','peacock','periwinkle','petrol','slate blue','slate_blue','steel blue'],
     [['navy',['navy']],['indigo',['indigo']]]],
    ['light blue','Light blue','#A8BFD0',
     ['light blue','lightblue','pale blue','sky blue'],
     ['azure','dusty blue','dusty_blue','faded blue','faint blue','ice blue','light blue','light_blue','pale blue','pale sky blue','pale_blue','powder blue','powder_blue','sky blue','sky_blue','soft blue','soft_blue'],
     [['sky blue',['sky blue','sky_blue']],['pale blue',['pale blue','pale_blue']]]],
    ['light gray','Light gray','#C9C7C2',
     ['lightgray'],
     ['light gray','light_gray','mist','pale gray','pale_gray','silver gray','soft gray'],
     []],
    ['purple','Purple','#7C6087',
     ['purple'],
     ['aubergine','berry','dusty lavender','dusty mauve','eggplant','faded indigo','lavender','lilac','mauve','muted lavender','pale lavender','pale violet','periwinkle','plum','purple'],
     [['lavender',['lavender']],['mauve',['mauve']],['plum',['plum']]]],
    ['green','Green & sage','#7B8B5F',
     ['green','sage'],
     ['celadon','emerald','forest green','green','light green','lime','mint','olive','pale celadon','pale green','pale sage','sage','soft green'],
     [['sage',['sage']],['olive',['olive']]]],
    ['teal','Teal & aqua','#4F8B89',
     ['aqua','teal'],
     ['aqua','dusty teal','faint teal','muted teal','muted turquoise','pale aqua','pale teal','peacock','petrol','seafoam','soft teal','teal','turquoise'],
     [['aqua',['aqua']],['turquoise',['turquoise']],['pale aqua',['pale aqua']]]],
    ['gold','Gold','#C09A4C',
     ['gold'],
     ['butter','butter yellow','faded gold','faint gold','faint yellow','gold','light gold','mustard','muted gold','ochre','pale gold','pale yellow','pale_gold','saffron','soft gold','soft yellow','yellow'],
     []],
    ['gray','Gray & charcoal','#9B9A95',
     ['gray'],
     ['blue gray','charcoal','gray','gray blue','greige','mushroom','pale taupe','pewter','silver','silver gray','slate','soft gray','steel','stone','taupe','warm gray'],
     [['taupe',['taupe']],['charcoal',['charcoal']],['slate',['slate']]]],
    ['ivory','Ivory & cream','#EDE4D3',
     ['ivory'],
     ['cream','ivory','pearl','white'],
     []],
    ['beige','Beige & taupe','#D5C3A3',
     ['beige','camel','tan','taupe'],
     ['beige','camel','fawn','greige','mushroom','pale taupe','sand','stone','tan','taupe','warm gray'],
     [['taupe',['taupe']],['camel',['camel']]]],
    ['brown','Brown','#7B5B43',
     ['brown'],
     ['brick','brown','chestnut','chocolate brown','cinnamon','copper','dark brown','dark_brown','deep brown','espresso','faded rust','faded terracotta','light brown','light_brown','mahogany','mocha','muted rust','ochre','pale brown','pale terracotta','rust','sienna','soft brown','soft rust','soft terracotta','terra cotta','terracotta','walnut'],
     [['rust',['rust']],['terra cotta',['terra cotta']]]],
    ['terracotta','Terracotta & rust','#C06B41',
     ['coral','peach','rust','terra cotta','terracotta'],
     ['apricot','cinnamon','copper','coral','dusty coral','faded coral','faded rust','faded terracotta','faint peach','muted coral','muted rust','orange','pale peach','pale terracotta','peach','rust','saffron','soft coral','soft orange','soft peach','soft rust','soft terracotta','terra cotta','terracotta'],
     [['rust',['rust']],['terra cotta',['terra cotta']],['coral',['coral']],['peach',['peach']]]],
    ['pink','Pink & blush','#D2A2A2',
     ['blush','pink','rose'],
     ['apricot','blush','blush pink','dusty coral','dusty lavender','dusty mauve','dusty pink','dusty rose','dusty_rose','faded coral','faded rose','faint peach','faint rose','fuchsia','hot pink','lilac','magenta','mauve','muted coral','muted lavender','orchid','pale blush','pale lavender','pale peach','pale pink','pale rose','pale violet','peach','pink','plum','raspberry','rose','salmon','soft coral','soft peach','soft pink','soft rose','violet'],
     [['rose',['rose']],['blush',['blush']],['peach',['peach']]]],
    ['black','Black','#2F2C29',
     ['black','charcoal'],
     ['black','charcoal','ebony','espresso','ink'],
     []],
    ['burgundy','Burgundy','#6E2F38',
     ['burgundy'],
     ['burgundy','wine'],
     []]
  ];

  /* -------------------------------------------------------------- plumbing */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* The proxy resolves `collection`, but the URL is the authority on both
     pages and costs nothing to read. */
  function currentHandle() {
    var m = location.pathname.match(/^\/a\/search\/([^/?#]+)/)
         || location.pathname.match(/^\/collections\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : 'all';
  }

  function readParams() { return new URLSearchParams(location.search); }

  /* Drop the group the caller is about to rewrite, keep the rest, then let it
     add its own. Paging is dropped too: page 7 of the old result set means
     nothing against the new one. */
  function baseParams(group) {
    var src = readParams();
    var out = new URLSearchParams();
    var drop = GROUPS[group] || [];
    src.forEach(function (value, key) {
      if (drop.indexOf(key) !== -1) return;
      if (key === 'page') return;
      out.append(key, value);
    });
    return out;
  }

  function go(params) {
    var q = params.toString();
    location.href = '/a/search/' + encodeURIComponent(currentHandle()) + (q ? '?' + q : '');
  }

  var FE = window.RugFilterEvents;

  function track(name, value, method) {
    FE.push({
      name: name,
      value: value,
      method: method,
      source: 'filter_bar',
      /* Its own, not the shared path reader's: this bar answers off
         /a/search/<handle> as well, and calls an unfiltered search "all". */
      handle: currentHandle()
    });
  }

  /* ------------------------------------------------------------------ size */
  /* All three size tabs end at the same place: the width and length ranges
     the app filters on, in inches. They used to end at the nominal size tag
     instead, which meant a give asked for in inches had to be rounded down to
     whole feet of neighbouring labels before it could be spent - so twenty
     inches of give bought exactly what twelve did, and the control read as
     dead. Inches in, inches out, and the note says the range that was set. */

  /* A box read the way the Rug Finder reads its own: the raw string matters
     as much as the number, because an empty box means "no bound" and a zero
     is a real answer. */
  function raw(input) { return input ? String(input.value).replace(/^\s+|\s+$/g, '') : ''; }

  /* The same bound as the chips say it. A note has a line to itself; a chip
     shares a row with three others and gives up at twenty-two zeros of width,
     which this mix of primes and digits reaches at about twenty characters. */
  function spanTight(min, max) {
    var b = bounds(min, max), lo = b[0], hi = b[1];
    if (lo != null && hi != null) return feetIn(lo, 1) + '-' + feetIn(hi, 1);
    if (lo != null) return feetIn(lo, 1) + '+';
    if (hi != null) return 'up to ' + feetIn(hi, 1);
    return '';
  }

  /* Either box counts: feet alone, inches alone, or both. Tab two filters on
     the measured inches, and now the shopper types inches too, so nothing is
     widened on the way through - what was typed is what is asked for. */
  function bound(feetInput, inchInput) {
    var fr = raw(feetInput), ir = raw(inchInput);
    if (fr === '' && ir === '') return null;
    var f = fr === '' ? 0 : parseFloat(fr);
    var i = ir === '' ? 0 : parseFloat(ir);
    if (!isFinite(f) || !isFinite(i) || f < 0 || i < 0) return null;
    var total = Math.round(f * 12 + i);
    return total > 0 ? total : null;
  }

  /* ----------------------------------------------------------------- state */
  /* What the URL currently says, in the bar's own terms. */
  function readState() {
    var p = readParams();
    return {
      tone: p.getAll(P.tone),
      intensity: p.getAll(P.intensity),
      dominant: p.getAll(P.dominant),
      accent: p.getAll(P.accent),
      shape: p.getAll(P.shape),
      size: p.getAll(P.size),
      wMin: p.get(P.wMin), wMax: p.get(P.wMax),
      lMin: p.get(P.lMin), lMax: p.get(P.lMax),
      priceMin: p.get(P.priceMin), priceMax: p.get(P.priceMax)
    };
  }

  /* Which family a set of raw values represents, if it is exactly one. Used
     to light the right swatch back up after a page load. */
  function sameSet(a, b) {
    var i;
    if (a.length !== b.length) return false;
    for (i = 0; i < a.length; i++) if (b.indexOf(a[i]) === -1) return false;
    return true;
  }

  function familyOf(values, which) {
    var i, j, fam, hit = null;
    if (!values.length) return null;
    for (i = 0; i < FAMILIES.length; i++) {
      fam = FAMILIES[i];
      if (sameSet(values, which === 'dominant' ? fam[3] : fam[4])) return fam[0];
      if (which === 'accent') {
        for (j = 0; j < fam[5].length; j++) {
          if (sameSet(values, fam[5][j][1])) hit = fam[0];
        }
      }
    }
    return hit;
  }

  function famLabel(key) {
    var i;
    for (i = 0; i < FAMILIES.length; i++) if (FAMILIES[i][0] === key) return FAMILIES[i][1];
    return key;
  }

  /* --------------------------------------------------------------- counts */
  /* Read live from the app's own sidebar rather than baked in. The sidebar is
     already scoped to this collection and already recomputed against whatever
     else is active, so a baked number would be wrong on both counts.

     Dominant sums the family's values, which is exact: every rug has one
     dominant value. Accent cannot sum - a rug carrying both "rust" and
     "terra cotta" would be counted twice, and the facet truncates at 100 of
     its 215 values - so it reports the family's largest single value as a
     floor, rendered "N+". */
  function readCounts() {
    var out = { dominant: {}, accent: {}, raw: {} };
    var groups = $$('#cloud_search_filters_sidebar [data-filter-name]');
    if (!groups.length) return null;

    /* The app paints one group at a time, so "some groups exist" is not
       "the colour groups exist". Answering early would freeze a screenful
       of zeroes. */
    var seen = groups.map(function (g) { return g.getAttribute('data-filter-name'); });
    if (seen.indexOf('Dominant color') === -1 || seen.indexOf('Accent Colors') === -1) return null;

    groups.forEach(function (group) {
      var name = group.getAttribute('data-filter-name');
      var bag = out.raw[name] = {};
      $$('[data-filter-value]', group).forEach(function (row) {
        var label = row.querySelector('.cloud-search-filter-value__count');
        var n = label ? parseInt(label.textContent.replace(/\D/g, ''), 10) : NaN;
        if (isFinite(n)) bag[row.getAttribute('data-filter-value')] = n;
      });
    });

    FAMILIES.forEach(function (fam) {
      var dom = 0, acc = 0;
      fam[3].forEach(function (v) { dom += (out.raw['Dominant color'] || {})[v] || 0; });
      fam[4].forEach(function (v) {
        acc = Math.max(acc, (out.raw['Accent Colors'] || {})[v] || 0);
      });
      out.dominant[fam[0]] = dom;
      out.accent[fam[0]] = acc;
    });
    return out;
  }

  function fmt(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  /* A price param as the shop writes prices, or '' if the param is not one.
     Whole dollars: the app filters on them, and a chip is read at a glance. */
  function money(v) {
    var n = v == null || v === '' ? NaN : Number(v);
    return isFinite(n) && n > 0 ? '$' + fmt(Math.round(n)) : '';
  }

  /* ------------------------------------------------------------- rendering */
  function swatch(fam, active, count, onClick) {
    var b = el('button', 'cfb__swatch');
    b.type = 'button';
    b.setAttribute('aria-pressed', active ? 'true' : 'false');
    b.title = fam[1];

    var chip = el('span', 'cfb__swatch-chip');
    chip.style.backgroundColor = fam[2];
    chip.setAttribute('aria-hidden', 'true');
    b.appendChild(chip);
    b.appendChild(el('span', 'cfb__swatch-name', fam[1]));

    /* A count of zero is worth showing: it is the difference between a
       colour we do not stock here and one the shopper simply has not tried. */
    if (count != null) {
      b.appendChild(el('span', 'cfb__swatch-count', count.text));
      if (!count.n) b.disabled = true;
      b.setAttribute('aria-label', fam[1] + ', ' + count.label);
    }
    b.addEventListener('click', onClick);
    return b;
  }

  /* ------------------------------------------------------------------ boot */
  function boot(root) {
    var state = readState();
    var counts = readCounts();
    var openPanel = null;

    /* Colour is composed before it is applied, so the four axes travel as one
       navigation rather than four. */
    var draft = {
      tone: state.tone.slice(),
      intensity: state.intensity.slice(),
      dominant: [],
      accent: familyOf(state.accent, 'accent'),
      shade: null
    };
    var domFam = familyOf(state.dominant, 'dominant');
    if (domFam) draft.dominant = [domFam];

    /* ---- panels ---- */
    function setPanel(name) {
      $$('[data-cfb-panel]', root).forEach(function (p) {
        var on = p.getAttribute('data-cfb-panel') === name;
        p.hidden = !on;
      });
      $$('[data-cfb-chip]', root).forEach(function (c) {
        c.setAttribute('aria-expanded', c.getAttribute('data-cfb-chip') === name ? 'true' : 'false');
      });
      openPanel = name;
      root.classList.toggle('cfb--open', !!name);
    }

    $$('[data-cfb-chip]', root).forEach(function (chip) {
      chip.addEventListener('click', function () {
        var name = chip.getAttribute('data-cfb-chip');
        setPanel(openPanel === name ? null : name);
      });
    });

    /* The same job Escape does, for the shopper who is using a thumb. */
    $$('[data-cfb-close]', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var name = openPanel;
        var chip = name ? $('[data-cfb-chip="' + name + '"]', root) : null;
        setPanel(null);
        if (chip) chip.focus();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openPanel) {
        var chip = $('[data-cfb-chip="' + openPanel + '"]', root);
        setPanel(null);
        if (chip) chip.focus();
      }
    });

    /* Read on the way down, not on the way up: a control that re-renders its
       own row - a swatch, a tone pill - detaches the node that was clicked,
       and by the time the click bubbles here that node is no longer inside
       the bar. Asking at capture time is the only reading that stays true. */
    var fromInside = false;
    document.addEventListener('click', function (e) {
      fromInside = root.contains(e.target);
    }, true);

    document.addEventListener('click', function () {
      if (!openPanel || fromInside) return;
      setPanel(null);
    });

    /* ---- size: tabs ---- */
    /* A tablist is one tab stop and the arrow keys move within it. The roving
       tabindex is what makes that true, and it is also what strands a
       keyboard shopper on the first tab if the arrow keys are not handled. */
    var tabs = $$('[data-cfb-tab]', root);

    function selectTab(tab, focus) {
      var name = tab.getAttribute('data-cfb-tab');
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      $$('[data-cfb-tabpanel]', root).forEach(function (p) {
        p.hidden = p.getAttribute('data-cfb-tabpanel') !== name;
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectTab(tab); });
      tab.addEventListener('keydown', function (e) {
        var to = null;
        if (e.key === 'ArrowRight') to = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') to = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') to = tabs[0];
        else if (e.key === 'End') to = tabs[tabs.length - 1];
        if (!to) return;
        e.preventDefault();
        selectTab(to, true);
      });
    });

    /* ---- size: the Size tab ---- */
    var exW = $('[data-cfb-exact-w]', root);
    var exL = $('[data-cfb-exact-l]', root);
    var exTol = $('[data-cfb-exact-tol]', root);
    var exNote = $('[data-cfb-exact-note]', root);

    /* The give is asked in inches because inches are what a shopper measuring
       a floor has written down, and it is now spent in inches too: it widens
       the width and length range this bar already filters on. Twenty inches
       of give means twenty inches, where it used to be rounded down to the
       nearest whole foot of neighbouring size labels and come to the same
       thing as twelve. */
    function exactTol() {
      /* An empty box means the shopper never answered, not that they want no
         give at all, so it falls back to the twelve inches the box ships with
         and the note underneath promises. A typed zero is an answer, but it
         is floored at six: a rug labelled 9' x 12' measures within half a
         foot of that, so a tighter range would drop rugs that are exactly the
         size being asked for. Five feet is as much give as anybody means. */
      var tol = parseFloat(raw(exTol));
      if (!(tol >= 0)) tol = 12;
      return clamp(Math.round(tol), 6, 60);
    }

    function exactRange() {
      var w = num(exW), l = num(exL);
      if (!w || !l) return null;
      return givenRange({ w: w, l: l, give: exactTol() });
    }

    function exactNote() {
      var r = exactRange();
      if (!r) {
        exNote.textContent = 'Hand-knotted rugs are never exactly nine by twelve, so a foot of give is the default.';
        return;
      }
      exNote.textContent = 'Looks for rugs ' + spanText(r.wMin, r.wMax) +
        ' wide and ' + spanText(r.lMin, r.lMax) + ' long.';
    }
    [exW, exL, exTol].forEach(function (n) {
      n.addEventListener('input', exactNote);
      n.addEventListener('change', exactNote);
    });

    function applyExact() {
      var r = exactRange();
      if (!r) { exW.focus(); return; }
      var p = baseParams('size');
      p.set(P.wMin, r.wMin);
      p.set(P.wMax, r.wMax);
      p.set(P.lMin, r.lMin);
      p.set(P.lMax, r.lMax);
      /* Small side first here as well as in the range: 12 by 9 and 9 by 12
         set identical filters, so they have to report as one size. */
      track('size', Math.min(num(exW), num(exL)) + 'x' + Math.max(num(exW), num(exL)) +
        ' +/-' + exactTol() + 'in', 'exact');
      go(p);
    }

    /* ---- size: the Range tab ---- */
    /* Feet and inches in separate boxes: nothing to parse, nothing to guess
       about what "8.5" or "8'6" was meant to mean, and the same shape as the
       Rug Finder's own range row. Four boxes per dimension, in reading
       order: min feet, min inches, max feet, max inches. */
    var rW = [$('[data-cfb-range-wminf]', root), $('[data-cfb-range-wmini]', root),
      $('[data-cfb-range-wmaxf]', root), $('[data-cfb-range-wmaxi]', root)];
    var rL = [$('[data-cfb-range-lminf]', root), $('[data-cfb-range-lmini]', root),
      $('[data-cfb-range-lmaxf]', root), $('[data-cfb-range-lmaxi]', root)];

    /* Typed low to high or high to low, the shopper means the same range. */
    function side(v) { return v == null ? 'any' : v; }

    function pair(boxes) {
      var min = bound(boxes[0], boxes[1]), max = bound(boxes[2], boxes[3]), t;
      if (min != null && max != null && min > max) { t = min; min = max; max = t; }
      return { min: min, max: max };
    }

    function applyRange() {
      var p = baseParams('size');
      var w = pair(rW);
      var l = pair(rL);
      if (w.min == null && w.max == null && l.min == null && l.max == null) {
        rW[0].focus();
        return;
      }
      if (w.min != null) p.set(P.wMin, w.min);
      if (w.max != null) p.set(P.wMax, w.max);
      if (l.min != null) p.set(P.lMin, l.min);
      if (l.max != null) p.set(P.lMax, l.max);
      /* An unbounded end reads "any", not the word null: a shopper who bounds
         the width alone is the common case, and GA4 keeps whatever string it
         is handed. */
      track('size', 'w ' + side(w.min) + '-' + side(w.max)
        + ' l ' + side(l.min) + '-' + side(l.max), 'range');
      go(p);
    }

    /* ---- size: what the URL already says ---- */
    /* A shopper who opens this panel with a size on came to change it, so the
       boxes open holding it rather than empty.

       The Range tab can always show it: those four params are exactly the
       four bounds it asks for. The Size tab can only honestly show a range it
       could have set itself, which is what sizeAndGive answers, and the chip
       above asks the same question of the same function, so the two never
       disagree about whether there is a size here to name. */

    /* A param that is not there is not a bound. Number(null) is 0, which is,
       so the boxes below read their bounds through here. */
    function inches(v) {
      var n = v == null || v === '' ? NaN : Number(v);
      return isFinite(n) && n > 0 ? n : NaN;
    }

    function fillRange() {
      var filled = false;
      [[rW, state.wMin, state.wMax], [rL, state.lMin, state.lMax]].forEach(function (row) {
        var boxes = row[0], i, n;
        for (i = 0; i < 2; i++) {
          n = Math.round(inches(row[i + 1]));
          if (!isFinite(n)) continue;
          boxes[i * 2].value = String(Math.floor(n / 12));
          boxes[i * 2 + 1].value = n % 12 ? String(n % 12) : '';
          filled = true;
        }
      });
      return filled;
    }

    function fillExact() {
      /* What the panel opened holding, so backing out puts back the default
         the markup states rather than a second copy of it kept here. */
      var tol = exTol.value;
      var sz = sizeAndGive(bounds(state.wMin, state.wMax),
                           bounds(state.lMin, state.lMax));
      if (!sz) {
        exW.value = '';
        exL.value = '';
        exTol.value = tol;
        return false;
      }
      /* Smaller side first without having to sort it: the range these filter
         on was laid over the one that arrived, and that range puts the
         smaller side in the width, so the width box is where it belongs. */
      exW.value = String(sz.w);
      exL.value = String(sz.l);
      exTol.value = String(sz.give);
      return true;
    }

    var onExact = fillExact();
    var onRange = fillRange();
    exactNote();
    /* Landing on an empty Size tab while the Range tab holds the filter reads
       as the prefill having failed, so the panel opens where the answer is. */
    if (!onExact && onRange) selectTab($('[data-cfb-tab="range"]', root));

    /* ---- size: fit my room ---- */
    /* The controls are RugSizing's, so the tab and the guide pages cannot
       drift apart. All the tab keeps is the select the room comes from and
       what happens when the shopper applies it. */
    var roomKind = $('[data-cfb-room-kind]', root);

    function roomSpec() { return ROOMS[roomKind.value] || ROOMS.living; }

    var room = mountRoom({
      spec: roomSpec,
      idPrefix: 'cfb-room-',
      fields: $('[data-cfb-room-inputs]', root),
      covers: $('[data-cfb-room-covers]', root),
      plan: $('[data-cfb-plan]', root),
      planSvg: $('[data-cfb-plan-svg]', root),
      planTitle: $('[data-cfb-plan-title]', root),
      planSize: $('[data-cfb-plan-size]', root),
      planText: $('[data-cfb-plan-text]', root)
    });

    roomKind.addEventListener('change', room.setRoom);

    function applyRoom() {
      var r = room.read();
      if (!r) {
        // The same courtesy the other two tabs pay.
        room.focusEmpty();
        return;
      }
      var p = baseParams('size');
      var g = rugRange(r.rug);
      p.set(P.wMin, g.wMin);
      p.set(P.wMax, g.wMax);
      p.set(P.lMin, g.lMin);
      p.set(P.lMax, g.lMax);
      /* Small side first, as the panel says it and as a rug is
         labelled, so two rooms that recommend the same rug do not
         land in the report as two different values. */
      track('size', roomKind.value + '/' + room.cover() + ' ' +
        Math.min(r.rug.w, r.rug.l) + 'x' + Math.max(r.rug.w, r.rug.l), 'room');
      go(p);
    }

    /* ---- price ---- */
    /* Two boxes, the way the app's own sidebar asked, with a slider over them
       for a shopper who has not landed on a number yet. The same reading as
       the Range tab above: either box on its own is a bound, both empty is no
       filter, and typed high to low means the same range as low to high. */
    var pMinBox = $('[data-cfb-price-min]', root);
    var pMaxBox = $('[data-cfb-price-max]', root);
    var pSlider = $('[data-cfb-price-slider]', root);
    var pFrom = $('[data-cfb-price-from]', root);
    var pTo = $('[data-cfb-price-to]', root);
    var pFill = $('[data-cfb-price-fill]', root);

    /* Whatever a shopper types a price as. The separators and the sign are
       ours to draw, so they are also ours to forgive on the way back in;
       cents are dropped because the app filters on whole dollars. */
    function dollars(input) {
      var s = raw(input).replace(/[$,\s]/g, '');
      if (s === '') return null;
      var n = parseFloat(s);
      return isFinite(n) && n > 0 ? Math.round(n) : null;
    }

    function applyPrice() {
      var lo = dollars(pMinBox), hi = dollars(pMaxBox), t;
      if (lo == null && hi == null) { pMinBox.focus(); return; }
      if (lo != null && hi != null && lo > hi) { t = lo; lo = hi; hi = t; }

      var p = baseParams('price');
      if (lo != null) p.set(P.priceMin, lo);
      if (hi != null) p.set(P.priceMax, hi);
      track('price', side(lo) + '-' + side(hi));
      go(p);
    }

    /* This collection's own cheapest and dearest, read where the app already
       states them: the placeholders of its sidebar's price boxes. Baking a
       pair in would be wrong on the first collection that is not the whole
       shop, and stale on the day the shop's top end changes. */
    var pEnds = (function () {
      var group = $('#cloud_search_filters_sidebar [data-filter-name="Price"]');
      var boxes = group ? $$('.cloud-search-num-field__input', group) : [];
      if (boxes.length < 2) return null;
      var lo = Math.floor(parseFloat(boxes[0].placeholder));
      var hi = Math.ceil(parseFloat(boxes[1].placeholder));
      return isFinite(lo) && isFinite(hi) && lo > 0 && hi > lo
        ? { lo: lo, hi: hi } : null;
    })();

    /* The track is 1000 stops of ratio, not of money. Rug prices are not
       spread evenly between the cheapest and the dearest - the great bulk of
       the shop sits in the first few percent of that span - so a slider that
       divided the money evenly would bury almost everything we sell in the
       first few pixels. Dividing the ratio instead gives every doubling of
       the price the same length of track. */
    var STOPS = 1000;

    function stopOf(d) {
      return Math.round(STOPS * Math.log(d / pEnds.lo) / Math.log(pEnds.hi / pEnds.lo));
    }

    /* A handle lands on a number a shopper would have typed. Somewhere near
       the middle of this track a single pixel is worth tens of dollars, and
       "$4,732" reads as a slip rather than a decision. */
    function priceAt(stop) {
      if (stop <= 0) return pEnds.lo;
      if (stop >= STOPS) return pEnds.hi;
      var d = pEnds.lo * Math.pow(pEnds.hi / pEnds.lo, stop / STOPS);
      var step = d < 1000 ? 10 : d < 10000 ? 100 : 1000;
      return Math.max(pEnds.lo, Math.round(d / step) * step);
    }

    function paintSlider() {
      var a = Number(pFrom.value), b = Number(pTo.value);
      pFill.style.insetInlineStart = (a / STOPS * 100) + '%';
      pFill.style.insetInlineEnd = (100 - b / STOPS * 100) + '%';
      /* A range input can only say its own 0-1000; the money it stands for
         has to be spelled out or the handle announces a position. */
      pFrom.setAttribute('aria-valuetext', '$' + fmt(priceAt(a)));
      pTo.setAttribute('aria-valuetext', '$' + fmt(priceAt(b)));
    }

    /* A handle parked at its own end of the track is not a bound, it is the
       absence of one, and the box says so by going empty - which is what the
       note under it promises. */
    function fromSlider(moved) {
      var a = Number(pFrom.value), b = Number(pTo.value);
      if (a > b) {
        if (moved === pFrom) { a = b; pFrom.value = b; }
        else { b = a; pTo.value = a; }
      }
      pMinBox.value = a <= 0 ? '' : fmt(priceAt(a));
      pMaxBox.value = b >= STOPS ? '' : fmt(priceAt(b));
      paintSlider();
    }

    function fromBoxes() {
      var lo = dollars(pMinBox), hi = dollars(pMaxBox);
      function clamp(n) { return Math.max(0, Math.min(STOPS, n)); }
      pFrom.value = lo == null ? 0 : clamp(stopOf(lo));
      pTo.value = hi == null ? STOPS : clamp(stopOf(hi));
      /* A typed low above a typed high is a range the shopper meant, and the
         apply reads it either way round; the track cannot draw it backwards,
         so it shows the pair as the filter will treat them. */
      if (Number(pFrom.value) > Number(pTo.value)) {
        var t = pFrom.value; pFrom.value = pTo.value; pTo.value = t;
      }
      paintSlider();
    }

    /* A shopper who opens this panel with a price on came to change it, so
       the boxes open holding it, grouped the way the chip says it. */
    if (state.priceMin) pMinBox.value = fmt(state.priceMin);
    if (state.priceMax) pMaxBox.value = fmt(state.priceMax);

    if (pEnds) {
      pMinBox.placeholder = fmt(pEnds.lo);
      pMaxBox.placeholder = fmt(pEnds.hi);
      $('[data-cfb-price-floor]', root).textContent = '$' + fmt(pEnds.lo);
      $('[data-cfb-price-ceil]', root).textContent = '$' + fmt(pEnds.hi);
      pSlider.hidden = false;
      pFrom.addEventListener('input', function () { fromSlider(pFrom); });
      pTo.addEventListener('input', function () { fromSlider(pTo); });
      [pMinBox, pMaxBox].forEach(function (box) {
        box.addEventListener('input', fromBoxes);
      });
      fromBoxes();
    }

    [pMinBox, pMaxBox].forEach(function (box) {
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); applyPrice(); }
      });
    });

    /* ---- shape ---- */
    var shapeHost = $('[data-cfb-shapes]', root);
    function renderShapes() {
      shapeHost.textContent = '';
      SHAPES.forEach(function (row) {
        var active = state.shape.indexOf(row[0]) !== -1;
        var n = counts && counts.raw['Shape'] ? counts.raw['Shape'][row[0]] : null;
        shapeHost.appendChild(pill(row[1] + (n != null ? ' (' + fmt(n) + ')' : ''), active, function () {
          var p = baseParams('shape');
          state.shape.forEach(function (v) { if (v !== row[0]) p.append(P.shape, v); });
          if (!active) p.append(P.shape, row[0]);
          track('shape', row[0]);
          go(p);
        }));
      });
    }
    renderShapes();

    /* ---- colour ---- */
    var toneHost = $('[data-cfb-tone]', root);
    var intHost = $('[data-cfb-intensity]', root);
    var domHost = $('[data-cfb-dominant]', root);
    var accHost = $('[data-cfb-accent]', root);
    var shadeHost = $('[data-cfb-shades]', root);

    function toggleIn(list, value) {
      var i = list.indexOf(value);
      if (i === -1) list.push(value); else list.splice(i, 1);
    }

    function axisRow(host, rows, key, facet) {
      host.textContent = '';
      rows.forEach(function (row) {
        var n = counts && counts.raw[facet] ? counts.raw[facet][row[0]] : null;
        var b = pill(row[1] + (n != null ? ' (' + fmt(n) + ')' : ''),
          draft[key].indexOf(row[0]) !== -1, function () {
            toggleIn(draft[key], row[0]);
            axisRow(host, rows, key, facet);
          });
        host.appendChild(b);
      });
    }

    function countFor(kind, famKey) {
      if (!counts) return null;
      var n = counts[kind][famKey] || 0;
      if (kind === 'accent') {
        return { n: n, text: n ? fmt(n) + '+' : '0', label: n ? 'at least ' + fmt(n) + ' rugs' : 'none here' };
      }
      return { n: n, text: fmt(n), label: n ? fmt(n) + ' rugs' : 'none here' };
    }

    function renderShades() {
      shadeHost.textContent = '';
      var fam = null, i;
      for (i = 0; i < FAMILIES.length; i++) if (FAMILIES[i][0] === draft.accent) fam = FAMILIES[i];
      if (!fam || !fam[5].length) { shadeHost.hidden = true; return; }
      shadeHost.hidden = false;
      shadeHost.appendChild(el('span', 'cfb__shades-label', 'Or a particular shade of ' + fam[1].toLowerCase().split(' & ')[0]));
      fam[5].forEach(function (shade) {
        var n = 0;
        shade[1].forEach(function (v) {
          n = Math.max(n, (counts && counts.raw['Accent Colors'] ? counts.raw['Accent Colors'][v] : 0) || 0);
        });
        shadeHost.appendChild(pill(shade[0] + (counts ? ' (' + fmt(n) + '+)' : ''),
          draft.shade === shade[0], function () {
            draft.shade = draft.shade === shade[0] ? null : shade[0];
            renderShades();
          }));
      });
    }

    function renderColour() {
      axisRow(toneHost, TONES, 'tone', 'Overall tone');
      axisRow(intHost, INTENSITIES, 'intensity', 'Color intensity');

      domHost.textContent = '';
      FAMILIES.forEach(function (fam) {
        domHost.appendChild(swatch(fam, draft.dominant.indexOf(fam[0]) !== -1,
          countFor('dominant', fam[0]), function () {
            toggleIn(draft.dominant, fam[0]);
            renderColour();
          }));
      });

      accHost.textContent = '';
      FAMILIES.forEach(function (fam) {
        accHost.appendChild(swatch(fam, draft.accent === fam[0],
          countFor('accent', fam[0]), function () {
            /* Single select. Two accent families would OR, and a shopper who
               picks two means "and" - so the second choice replaces the first
               rather than quietly widening the result. */
            draft.accent = draft.accent === fam[0] ? null : fam[0];
            draft.shade = null;
            renderColour();
          }));
      });
      renderShades();
    }
    renderColour();

    function applyColour() {
      var p = baseParams('color');
      draft.tone.forEach(function (v) { p.append(P.tone, v); });
      draft.intensity.forEach(function (v) { p.append(P.intensity, v); });

      draft.dominant.forEach(function (key) {
        FAMILIES.forEach(function (fam) {
          if (fam[0] !== key) return;
          fam[3].forEach(function (v) { p.append(P.dominant, v); });
        });
      });

      if (draft.accent) {
        FAMILIES.forEach(function (fam) {
          if (fam[0] !== draft.accent) return;
          var values = fam[4];
          if (draft.shade) {
            fam[5].forEach(function (s) { if (s[0] === draft.shade) values = s[1]; });
          }
          values.forEach(function (v) { p.append(P.accent, v); });
        });
      }

      track('color', [draft.tone.join('+'), draft.intensity.join('+'),
        draft.dominant.join('+'), draft.accent || '', draft.shade || ''].join('|'));
      go(p);
    }

    /* ---- apply and clear ---- */
    var APPLY = {
      exact: applyExact, range: applyRange, room: applyRoom,
      price: applyPrice, color: applyColour
    };
    $$('[data-cfb-apply]', root).forEach(function (b) {
      b.addEventListener('click', function () { APPLY[b.getAttribute('data-cfb-apply')](); });
    });

    $$('[data-cfb-clear]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        var keys = GROUPS[b.getAttribute('data-cfb-clear')];
        var p = readParams();
        keys.forEach(function (k) { p['delete'](k); });
        p['delete']('page');
        go(p);
      });
    });

    /* ---- what is on, and how to take it off ---- */
    function label(kind) {
      if (kind === 'size') {
        /* Nominal size tags still arrive from the app's own sidebar, so the
           bar reads them back. Everything the bar itself sets is a range, and
           a chip that only said "a range" left the shopper opening the panel
           to find out which one.

           A range that is a size plus a give is said that way, because that
           is what was asked for and it is a third the length of the
           arithmetic it became: 9' x 12' +-12" against 8' - 10' x 11' - 13",
           which took the whole width the chip allows to say four numbers the
           shopper never typed. Anything else is said as a range, set tight
           enough that the widest of them still fits. */
        if (state.size.length === 1) return state.size[0];
        if (state.size.length > 1) return state.size.length + ' sizes';
        var fit = sizeAndGive(bounds(state.wMin, state.wMax),
                              bounds(state.lMin, state.lMax));
        if (fit) {
          return feetIn(fit.w * 12, 1) + ' \u00d7 ' + feetIn(fit.l * 12, 1)
            + ' \u00b1' + fit.give + '\u2033';
        }
        var w = spanTight(state.wMin, state.wMax);
        var l = spanTight(state.lMin, state.lMax);
        if (w && l) return w + ' \u00d7 ' + l;
        if (w) return w + ' wide';
        if (l) return l + ' long';
        return '';
      }
      if (kind === 'price') {
        /* The bound itself, not the word "a range". The chip is the only
           place a shopper sees what the price filter is currently doing
           without opening the panel, and half a bound is still worth saying:
           an open top end reads "and up", an open bottom "Under". */
        var lo = money(state.priceMin), hi = money(state.priceMax);
        if (lo && hi) return lo + ' to ' + hi;
        if (lo) return lo + ' and up';
        if (hi) return 'Under ' + hi;
        return '';
      }
      if (kind === 'shape') return state.shape.join(', ');
      if (kind === 'color') {
        var parts = [];
        var dom = familyOf(state.dominant, 'dominant');
        var acc = familyOf(state.accent, 'accent');
        if (state.tone.length) parts.push(state.tone.join('/'));
        if (state.intensity.length) parts.push(state.intensity.join('/'));
        if (dom) parts.push(famLabel(dom));
        else if (state.dominant.length) parts.push(state.dominant.length + ' colors');
        if (acc) parts.push('+ ' + famLabel(acc));
        else if (state.accent.length) parts.push('+ accent');
        return parts.join(', ');
      }
      return '';
    }

    ['size', 'price', 'shape', 'color'].forEach(function (kind) {
      var text = label(kind);
      var host = $('[data-cfb-chip-value="' + kind + '"]', root);
      var chip = $('[data-cfb-chip="' + kind + '"]', root);
      if (host) host.textContent = text;
      if (chip) chip.classList.toggle('cfb__chip--on', !!text);
      if (!text) return;

      var active = $('[data-cfb-active]', root);
      active.hidden = false;
      var b = el('button', 'cfb__active-chip');
      b.type = 'button';
      b.appendChild(el('span', 'cfb__active-name', kind.charAt(0).toUpperCase() + kind.slice(1) + ': ' + text));
      b.appendChild(el('span', 'cfb__active-x', '×'));
      b.setAttribute('aria-label', 'Remove ' + kind + ' filter');
      b.addEventListener('click', function () {
        var p = readParams();
        GROUPS[kind].forEach(function (k) { p['delete'](k); });
        p['delete']('page');
        go(p);
      });
      /* Ahead of the offer, which the snippet wrote into the empty row and
         which belongs at the end of it. Ordering it there in CSS would move
         only what is painted: a keyboard would still reach the offer before
         the chips it is an offer about. */
      active.insertBefore(b, $('[data-cfb-alert]', active));
    });

    /* ---- alert me to new arrivals ---- */
    /* Built from two things, layered. The collection's own alert, when the
       sync could turn its rules into one, arrives on the nav as a filter and
       the page's title, and is what an unfiltered page offers. Filters on
       the URL that survive the trip into the subscription's own shape narrow
       it, and are the whole of it on a collection with no alert of its own -
       where an unfiltered page has nothing to offer but "every new rug",
       which is not what a shopper standing in front of a narrow grid is
       asking for, and a page whose every filter was dropped would promise
       exactly that while appearing to promise this grid. */
    (function () {
      var trigger = $('[data-cfb-alert]', root);
      var dialog = $('[data-alert-dialog]', root);
      if (!trigger || !dialog || !window.RugAlerts) return;
      var label = $('[data-cfb-alert-label]', trigger);

      function hasKeys(obj) {
        for (var key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) return true;
        }
        return false;
      }

      /* The collection's own alert, as the sync wrote it. Unreadable is no
         alert: a guess here would email rugs the page never showed. */
      function collectionBase() {
        var raw = root.getAttribute('data-cfb-alert-filter');
        if (raw === null) return null;
        var filter;
        try { filter = JSON.parse(raw); } catch (e) { return null; }
        if (!filter || typeof filter !== 'object' || filter instanceof Array) return null;
        return {
          filter: filter,
          title: root.getAttribute('data-cfb-alert-title') || 'this collection'
        };
      }
      var base = collectionBase();

      function parsed() {
        return window.RugAlerts.parseFilters(location.search, { family: familyOf });
      }

      /* The URL's filters written over the collection's, key by key, and
         facet by facet within facets. The URL wins wherever both speak: on a
         page that shows anything at all, what the shopper picked sits inside
         what the collection admits, so it is the narrower of the two. (The
         overlap of the two lists would get this wrong: "light blue" sits
         under "blue" by meaning, not by string.) */
      function merged(urlFilter) {
        var out = {}, key, f;
        if (base) {
          for (key in base.filter) {
            if (Object.prototype.hasOwnProperty.call(base.filter, key)) out[key] = base.filter[key];
          }
        }
        for (key in urlFilter) {
          if (!Object.prototype.hasOwnProperty.call(urlFilter, key)) continue;
          if (key !== 'facets' || !out.facets) { out[key] = urlFilter[key]; continue; }
          var facets = {};
          for (f in out.facets) {
            if (Object.prototype.hasOwnProperty.call(out.facets, f)) facets[f] = out.facets[f];
          }
          for (f in urlFilter.facets) {
            if (Object.prototype.hasOwnProperty.call(urlFilter.facets, f)) facets[f] = urlFilter.facets[f];
          }
          out.facets = facets;
        }
        return out;
      }

      /* Said from the filter that is actually being sent, never from the
         chips beside it, which disagree with it in both directions: a chip
         can name a parameter that was dropped, and a parameter dropped in
         favour of another - a nominal size tag yielding to explicit bounds -
         leaves a constraint the alert still carries with no chip standing for
         it. Either way, a summary that does not match what was sent is how a
         shopper ends up with a Sunday email full of rugs they ruled out, or
         with none of the ones they wanted.

         The words are the chips' words, so the two still read as one row,
         and the collection's part is said by the page's name, which is what
         the shopper is looking at. A facet off the app's own sidebar has no
         chip to borrow from, so it is said the way the backend's own label
         says it - "Borderless", not the "No" the URL spells it as - which
         is also what the confirmation email will say. It is a preview, not
         the promise: a facet this sends and the backend does not know is
         dropped there too, and only the backend can say which; the scope it
         actually stored is what the confirmation email and the manage page
         carry, in its words. */
      function said(v) { return v.charAt(0).toUpperCase() + v.slice(1); }

      /* Mirrors describe() in the backend's facet registry, facet by facet.
         A boolean asked both ways is no constraint at all, and says nothing. */
      var FACET_BOOL = {
        has_border: ['with a border', 'borderless'],
        has_medallion: ['with a medallion', 'no medallion'],
        has_fringe: ['with fringe', 'no fringe'],
        has_abrash: ['with abrash', 'no abrash'],
        is_neutral: ['neutral palette', 'colorful palette'],
        design_symmetry: ['symmetrical', 'asymmetrical']
      };
      var FACET_WRAP = {
        origin: ['', ' rugs'],
        basic_colors: ['with ', ''],
        colors: ['', ' in it'],
        dominant_colors: ['mostly ', ''],
        background_color: ['', ' ground'],
        color_intensity: ['', ' color'],
        overall_tone: ['', ' tone'],
        number_of_colors: ['', ' colors'],
        pattern_type: ['', ' pattern'],
        design_density: ['', ' design'],
        primary_motif: ['', ' motif'],
        room_suitability: ['for the ', ''],
        decorative_complexity: ['', ' decoration']
      };

      function joinOr(list) {
        if (list.length < 2) return list[0] || '';
        if (list.length === 2) return list[0] + ' or ' + list[1];
        return list.slice(0, -1).join(', ') + ', or ' + list[list.length - 1];
      }

      function facetSaid(key, values) {
        if (key === 'dominant_color') return famLabel(values[0]);
        if (key === 'accent_colors') return '+ ' + famLabel(values[0]);
        if (Object.prototype.hasOwnProperty.call(FACET_BOOL, key)) {
          if (values.length !== 1) return '';
          if (values[0] === 'true') return said(FACET_BOOL[key][0]);
          if (values[0] === 'false') return said(FACET_BOOL[key][1]);
          /* Spelled neither way: the backend will drop it, so this says
             nothing rather than "borderless" for a value that is not "no". */
          return '';
        }
        var wrap = FACET_WRAP[key] || ['', ''];
        var words = joinOr(values.map(function (v) { return v.replace(/_/g, ' '); }));
        return said(wrap[0] + words + wrap[1]);
      }

      function sizeSaid(range) {
        var w = bounds(range.min_width_in, range.max_width_in);
        var l = bounds(range.min_length_in, range.max_length_in);
        var fit = sizeAndGive(w, l);
        if (fit) {
          return feetIn(fit.w * 12, 1) + ' \u00d7 ' + feetIn(fit.l * 12, 1)
            + ' \u00b1' + fit.give + '\u2033';
        }
        var ws = spanTight(range.min_width_in, range.max_width_in);
        var ls = spanTight(range.min_length_in, range.max_length_in);
        if (ws && ls) return ws + ' \u00d7 ' + ls;
        if (ws) return ws + ' wide';
        if (ls) return ls + ' long';
        return '';
      }

      function summarize(result) {
        var f = result.filter;
        /* Size is left out: the dialog says it on a row of its own, editable,
           and a sentence that said it too would be wrong the moment the
           shopper changed it. */
        var parts = [];
        var lo = money(f.min_price_usd), hi = money(f.max_price_usd);
        if (lo && hi) parts.push(lo + ' to ' + hi);
        else if (lo) parts.push(lo + ' and up');
        else if (hi) parts.push('Under ' + hi);
        if (f.shape) parts.push(f.shape.map(said).join(', '));
        var facets = f.facets || {};
        Object.keys(facets).forEach(function (key) {
          var part = facetSaid(key, facets[key]);
          if (part) parts.push(part);
        });
        var where = base ? ' in ' + base.title : '';
        if (!parts.length) {
          return base ? 'New arrivals' + where + '.'
                      : 'New arrivals matching the filters on this page.';
        }
        if (parts.length > 5) {
          return 'New arrivals' + where + ' matching ' + parts.slice(0, 5).join(' · ')
            + ' and ' + (parts.length - 5) + ' more.';
        }
        return 'New arrivals' + where + ' matching ' + parts.join(' · ') + '.';
      }

      var opening = parsed();
      if (!base && !hasKeys(opening.filter)) return;

      /* Nothing on the URL to match, so the offer is read as what it is:
         the collection's, by the name the shopper is looking at. */
      if (base && !hasKeys(opening.filter)) {
        label.textContent = 'Alert me to new arrivals in ' + base.title;
      }

      /* A filter no chip covers - anything off the app's own sidebar - or the
         collection's own alert on an unfiltered page leaves the row empty and
         still hidden, with this the only thing in it. */
      $('[data-cfb-active]', root).hidden = false;

      window.RugAlerts.mount({
        trigger: trigger,
        dialog: dialog,
        surface: 'collection',
        /* The size row in the chips' own words, so a size that arrived off
           the URL reads in the dialog as it does in the chip beside it. */
        sizeText: sizeSaid,
        build: function () {
          var result = parsed();
          var fromUrl = hasKeys(result.filter);
          var filter = merged(result.filter);
          return {
            kind: 'filter',
            filter: hasKeys(filter) ? filter : undefined,
            source: 'collection',
            summary: summarize(result),
            /* What the offer was built from: the collection's own alert, the
               URL's filters, or both. */
            base: base ? (fromUrl ? 'both' : 'collection') : 'url',
            /* A collection that is a size - "9' x 12' Rugs", "Large Rugs" -
               is the size the dialog's row goes back to, never past. */
            sizeFloor: base && base.filter.size_range ? base.filter.size_range : undefined
          };
        }
      });
    })();

    /* ---- the count the app already worked out ---- */
    var meta = $('.product-facet__meta-bar-item--count');
    var countHost = $('[data-cfb-count]', root);
    if (meta && countHost) countHost.textContent = meta.textContent.replace(/\s+/g, ' ').trim();

    /* ---- all filters ---- */
    /* The sidebar is hidden by a rule keyed on the cfb-js class this file
       claimed before anything painted, so there is nothing to collapse here:
       the button's job is to opt back in. A shopper whose JS never runs never
       gets the class, and keeps the app's own filters. */
    var layout = $('.cloud-search-sidebar-layout');
    var allBtn = $('[data-cfb-all]', root);

    /* Below 768px the app offers a "Filter" link of its own instead of the
       sidebar, but on this store that link is a bare <a href="#"> that opens
       nothing. So the same fold happens at every width: the sidebar drops in
       under the bar, full width, and folds away again on a second press. The
       CSS has to say display outright there, because the app hides the
       sidebar itself below 768px. */
    if (layout && allBtn) {
      allBtn.addEventListener('click', function () {
        var on = layout.classList.toggle('cfb-sidebar-expanded');
        allBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (!on) return;
        setPanel(null);
        /* On a phone the sidebar opens below the fold, so nothing visibly
           happens unless it is brought up. */
        if (window.matchMedia('(max-width: 767px)').matches) {
          var sidebar = $('#cloud_search_filters_sidebar');
          if (sidebar) sidebar.scrollIntoView({ block: 'start' });
        }
      });
    } else if (allBtn) {
      allBtn.hidden = true;
    }

    /* The app renders its sidebar after this runs, so the first pass has no
       numbers to show and the theme's result count is still the unfiltered
       one. Holding the whole bar back on the app would cost the shopper a
       second of nothing, so render now and read again the moment its values
       land. */
    function syncCount() {
      var m = $('.product-facet__meta-bar-item--count');
      if (!m || !countHost) return;
      var text = m.textContent.replace(/\s+/g, ' ').trim();
      /* Only write on a real change. This runs from a mutation observer, and
         rewriting the node with its own text would wake the observer again. */
      if (text !== countHost.textContent) countHost.textContent = text;
    }

    if (!counts) {
      var appObs = new MutationObserver(function () {
        var fresh = readCounts();
        syncCount();
        if (!fresh) return;
        /* Disconnect before rendering: the render is a burst of mutations,
           and this observer is watching the whole document. */
        appObs.disconnect();
        counts = fresh;
        renderShapes();
        renderColour();
      });
      appObs.observe(document.body, { childList: true, subtree: true });
      /* Give up quietly. Without counts every control still filters; it just
         does not say how many rugs sit behind each one. */
      setTimeout(function () { appObs.disconnect(); }, 15000);
    }

    exactNote();
    root.hidden = false;
  }

  /* -------------------------------------------------------------- placement */
  /* The bar has to sit directly above the grid, and the section it renders in
     does not always land there. On the collection page it is ordered above
     main; on the app proxy every custom-liquid section is re-emitted below
     main. Rather than depend on that ordering, move the node once. */
  function place(root) {
    var layout = $('.cloud-search-sidebar-layout');
    if (!layout || !layout.parentElement) return false;
    if (root.parentElement === layout.parentElement && root.nextElementSibling === layout) return true;
    var section = root.parentElement ? root.closest('.shopify-section') : null;
    layout.parentElement.insertBefore(root, layout);
    if (section && !$('.cfb', section)) section.classList.add('cfb-vacated');
    return true;
  }

  /* ------------------------------------------------------------- hydration */
  /* The app proxy does not render this section yet.

     /a/search/<handle> is served by XCloud, which rebuilds the collection
     template from its own snapshot of the *live* theme's section list. Liquid
     inside those sections is rendered against the current theme - a snippet
     edit shows up on the proxy immediately - but a section the snapshot has
     never seen is simply not emitted. The two custom-liquid sections added by
     earlier work are both present, so the snapshot does track the live theme;
     it just cannot know about a section that has not been published yet.

     Without this, a shopper would filter once and watch the bar disappear.
     So the markup is stashed on the collection page, where it does render,
     and put back on the proxy page if it is missing. It is the same static
     markup for every collection but for two things: the handle, which is
     read from the URL anyway, and the collection's own alert, which is kept
     only when the cache came from this handle.

     This costs one sessionStorage read and no network. A shopper who deep
     links straight to a filtered URL without passing through a collection
     page has nothing cached and simply keeps the app's own sidebar, which is
     the behaviour they get today.

     Remove this block once the proxy emits the section on its own. */
  function cacheMarkup(root) {
    try { sessionStorage.setItem(CACHE_KEY, root.outerHTML); } catch (e) { /* private mode */ }
  }

  function hydrate() {
    var html = null;
    try { html = sessionStorage.getItem(CACHE_KEY); } catch (e) { return null; }
    if (!html) return null;
    var host = document.createElement('div');
    host.innerHTML = html;
    var node = host.firstElementChild;
    if (!node || !node.hasAttribute('data-cfb')) return null;
    /* Another collection's alert on this page would promise its rugs. */
    if (node.getAttribute('data-cfb-handle') !== currentHandle()) {
      node.removeAttribute('data-cfb-alert-filter');
      node.removeAttribute('data-cfb-alert-title');
    }
    node.setAttribute('data-cfb-handle', currentHandle());
    return node;
  }

  /* ------------------------------------------------------------------ init */
  /* Placement needs the theme's own layout wrapper, which ships in the Liquid
     and is usually here already. Counts need the app's values, which arrive
     later; boot() watches for them rather than waiting on them here. */
  function whenReady(fn) {
    if ($('.cloud-search-sidebar-layout')) { fn(); return; }
    var timer;
    var obs = new MutationObserver(function () {
      if (!$('.cloud-search-sidebar-layout')) return;
      obs.disconnect();
      clearTimeout(timer);
      fn();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    /* If it never arrives this is not a page the bar belongs on, and staying
       hidden is the correct outcome. */
    timer = setTimeout(function () { obs.disconnect(); unclaim(); }, 10000);
  }

  function start() {
    var root = $('[data-cfb]');
    if (root) {
      cacheMarkup(root);
    } else {
      if (location.pathname.indexOf('/a/search/') !== 0) { unclaim(); return; }
      root = hydrate();
      if (!root) { unclaim(); return; }
    }
    whenReady(function () {
      if (!place(root)) { unclaim(); return; }
      /* Nothing below is worth a page with no filters on it. If the bar
         cannot finish building itself, it takes itself off the page and hands
         the shopper back the app's own sidebar. */
      try {
        boot(root);
      } catch (e) {
        root.hidden = true;
        unclaim();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();


/* ==========================================================================
   No price in the search dropdown for a rug nobody can buy
   --------------------------------------------------------------------------
   A sold rug and one out on approval both carry a price the shopper can no
   longer act on, so neither shows one. The theme already drops it everywhere
   it renders a price itself: the product page, the collection tiles, the
   predictive search suggestions, the Rug Finder. All of those key off a
   server-rendered signal, `.product-item--sold-out` or
   `.rug-finder-entry--gone`, in CSS alone (see "No price on a rug nobody can
   buy" in assets/custom.css).

   The instant-search dropdown is the one surface the theme does not render.
   The Cloud Search app owns it and builds it in the browser from its own
   index, and that index has no availability in it — a result is
   {id, url, image_src, sku, product_id, price, name, description, path} —
   so the card shows the price of a rug we cannot sell and no CSS selector
   can tell which card that is.

   The theme can answer for itself. templates/product.availability.liquid
   renders `available` and nothing else, which is false for sold and
   out-on-approval alike (both are inventory 0), the same pair the tile rule
   covers. So: read the handle out of the result's own product link, ask, and
   stamp the answer where CSS can act on it.

   It asks the theme rather than the storefront's own /products/<handle>.js,
   which carries the same one bit wrapped in every variant, image and option
   of the rug. That route is also the one the `shopify theme dev` proxy
   refuses (401, "Invalid token"), and it refuses it only there, so the live
   spec could never see this work: every lookup failed, every price came back
   hidden, and the one assertion that would have caught a leak skipped
   instead. A theme route is served the same in CI as in production.

   Every price is hidden the moment it appears and revealed only once the rug
   is confirmed buyable, so a price never paints and then disappears. If the
   lookup fails the price stays hidden: a preview card missing a number is
   recoverable, since the product page one click away still carries it, while
   a price on a rug that is not for sale is the thing being fixed.
   ========================================================================== */
(function () {
  // Both spellings of the dropdown. The one the app renders carries the id
  // and the class; a second copy on the same page could not repeat the id.
  var RESULTS = '.cloud-search-results, #cloud-search-results';
  var PRICES = ':is(' + RESULTS + ') .price';
  var STATE = 'data-cs-price';
  var HANDLE = 'data-cs-price-handle';

  // One lookup per rug per page view. The dropdown re-renders on every
  // keystroke, so the same handful of rugs is asked about over and over
  // through a single search. Keys are handles read out of the page, so the
  // map gets no prototype: `/products/constructor` would otherwise find
  // Object's own constructor sitting in the cache and try to `.then` it.
  var answers = Object.create(null);

  function handleFromHref(href) {
    var match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? match[1] : null;
  }

  /**
   * The handle of the rug a price belongs to, or null when the markup gives
   * no unambiguous answer. Cloud Search pairs a <dt> row with a <dd> preview
   * card and puts the price in the card, so the nearest ancestor holding a
   * product link is the result the price is for. The walk stops at the first
   * ancestor holding any link at all: one holding links to several rugs is
   * the results list itself, and guessing there would hide a price that
   * belongs to a rug still for sale.
   */
  function handleFor(price) {
    var stop = price.closest(RESULTS);
    var node = price.parentElement;
    while (node) {
      var links = node.querySelectorAll('a[href*="/products/"]');
      if (links.length) {
        var handle = null;
        for (var i = 0; i < links.length; i++) {
          var found = handleFromHref(links[i].getAttribute('href'));
          if (!found) continue;
          if (handle && found !== handle) return null;
          handle = found;
        }
        return handle;
      }
      if (node === stop) return null;
      node = node.parentElement;
    }
    return null;
  }

  function isAvailable(handle) {
    if (!answers[handle]) {
      // Anything but a parseable answer counts as unavailable: a theme
      // without the template renders the whole product page instead, and a
      // page of markup is not a yes.
      answers[handle] = fetch('/products/' + encodeURIComponent(handle) + '?view=availability')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (answer) { return !!answer && answer.available === true; })
        .catch(function () { return false; });
    }
    return answers[handle];
  }

  function settle(price) {
    var handle = handleFor(price);
    // No handle means no way to ask, and hiding on a guess would take the
    // price off rugs that are perfectly buyable.
    if (!handle) {
      price.setAttribute(HANDLE, '');
      price.setAttribute(STATE, 'ok');
      return;
    }
    // Already settled for this rug. Keyed on the handle rather than on the
    // presence of a stamp, because the app may keep a node and rewrite it
    // for a different rug as the query changes.
    if (price.getAttribute(HANDLE) === handle) return;

    price.setAttribute(HANDLE, handle);
    price.setAttribute(STATE, 'pending');
    isAvailable(handle).then(function (available) {
      // The node may have been recycled for another rug while we asked.
      if (price.getAttribute(HANDLE) !== handle) return;
      price.setAttribute(STATE, available ? 'ok' : 'gone');
    });
  }

  function scan() {
    // The dropdown only exists while a search is open, which is almost never:
    // one lookup that misses keeps the observer off the rest of the page's
    // hot path.
    if (!document.querySelector(RESULTS)) return;
    var prices = document.querySelectorAll(PRICES);
    for (var i = 0; i < prices.length; i++) {
      settle(prices[i]);
    }
  }

  // Started here rather than on DOMContentLoaded, and not because the
  // dropdown exists yet — it never does, the app builds it on the first
  // keystroke. Waiting would leave a window in which markup carrying a price
  // could be parsed and painted before anything was watching, and the whole
  // point is that a price is hidden before it can be seen. documentElement is
  // in place as soon as this file runs, in the head or at the end of the body.
  scan();
  // The dropdown is created and rewritten long after load, so there is
  // nothing to bind to: watch the document and settle whatever appears.
  // childList only — the stamps are attributes, so settling never feeds the
  // observer back into itself.
  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

/* ==========================================================================
   Rug Finder hand-off from a search that found nothing
   --------------------------------------------------------------------------
   The instant-search dropdown is the Cloud Search app's, built in the browser
   from its own index, and on zero results it renders nothing at all: the
   container it appends to <body> is removed (or never made), the input's
   aria-expanded flips back to false, and the shopper is left looking at an
   empty drawer. Checked on the live store 2026-10-05 with a nonsense query;
   a near miss ("antiqueX") instead keeps the container and prefixes it with
   "Showing results for “antique”", which counts as results here.

   So this cannot key off the dropdown's markup. It keys off the query: once
   typing has paused and the text is long enough to be a real query, if no
   dropdown holds a result, one quiet row goes into the drawer offering the
   Rug Finder. The row is removed the moment the query changes or results
   arrive, and comes back if the app takes the results away again.

   On a phone the app covers the drawer with a search bar of its own
   (.cloud-search-mobile, over a full-screen backdrop) and the shopper types
   there, so the row then goes under that bar instead, in the app's dialog.

   The typed text never travels in a URL. A click stores it for the finder
   page (next block) in sessionStorage under one key, per tab, and the page
   reads it once and clears it. Analytics carries the result bucket and the
   query length only, never the text.
   ========================================================================== */
(function () {
  var DRAWER = 'predictive-search-drawer, #search-drawer';
  var QUERY_INPUT = 'input[name="q"]';
  var MOBILE_BAR = '.cloud-search-mobile';
  var MOBILE_INPUT = 'input[type="search"]';
  var RESULTS = '.cloud-search-results, #cloud-search-results';
  // Anything the app would render a result as. Cloud Search's own rows are
  // dt.cs-item; a product link covers a re-skinned app, and a "0 results"
  // status or an explicit no-results node covers an app that keeps the
  // container open to say it found nothing.
  var RESULT_ITEM = '.cs-item, a[href*="/products/"]';
  var NO_RESULTS_NODE = '.cs-no-results, .cloud-search-no-results, .no-results, [class*="no-results"], [class*="noresults"]';
  var MIN_QUERY = 3;
  var PAUSE_MS = 600;
  var MAX_TEXT = 300;
  var FINDER_URL = '/pages/find-rugs-for-your-room?from=search';
  var ROW_CLASS = 'rs-search-handoff';
  var MOBILE_CLASS = ROW_CLASS + '--mobile';

  // Shared with the finder page's prefill block, which reads and clears it.
  var KEY = 'rs-search-handoff';

  var timer = null;
  var pausedFor = null; // the query the pause last elapsed for
  var before = null; // the dropdown as it stood at the last keystroke
  var waited = false; // settle() already gave the app a second pause

  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    for (var key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) payload[key] = params[key];
    }
    window.dataLayer.push(payload);
  }

  function queryInput(node) {
    if (!node || !node.matches) return null;
    if (node.matches(MOBILE_INPUT) && node.closest(MOBILE_BAR)) return node;
    if (node.matches(QUERY_INPUT) && node.closest(DRAWER)) return node;
    return null;
  }

  // The app's bar first: while it is up, the drawer's input is behind it.
  function queryInputs() {
    var found = [];
    var lists = [
      document.querySelectorAll(MOBILE_BAR + ' ' + MOBILE_INPUT),
      document.querySelectorAll(DRAWER + ' ' + QUERY_INPUT),
    ];
    for (var i = 0; i < lists.length; i++) {
      for (var j = 0; j < lists[i].length; j++) found.push(lists[i][j]);
    }
    return found;
  }

  function queryText(input) {
    return (input.value || '').trim().slice(0, MAX_TEXT);
  }

  // Where the row goes: the drawer's own results area, which the theme keeps
  // hidden while the app owns the dropdown, so the row is the only thing
  // standing in an otherwise empty drawer.
  function drawerHost(input) {
    var drawer = input.closest(DRAWER);
    if (!drawer) return null;
    return drawer.querySelector('.predictive-search__content-wrapper')
      || drawer.querySelector('.drawer__content')
      || drawer;
  }

  function place(input, el) {
    var bar = input.closest(MOBILE_BAR);
    if (bar) {
      // Fixed under the bar, in the app's dialog, above its backdrop.
      el.className += ' ' + MOBILE_CLASS;
      el.style.top = Math.round(bar.getBoundingClientRect().bottom) + 'px';
      bar.parentNode.appendChild(el);
      return true;
    }
    var where = drawerHost(input);
    if (!where) return false;
    where.insertBefore(el, where.firstChild);
    return true;
  }

  function hasResults() {
    var containers = document.querySelectorAll(RESULTS);
    for (var i = 0; i < containers.length; i++) {
      var box = containers[i];
      if (box.hidden || box.querySelector(NO_RESULTS_NODE)) continue;
      var status = box.querySelector('[role="status"], .cloud-search-visually-hidden');
      if (status && /^\s*(0|no)\b/i.test(status.textContent || '')) continue;
      if (box.querySelector(RESULT_ITEM)) return true;
    }
    return false;
  }

  function dropdownHtml() {
    var containers = document.querySelectorAll(RESULTS);
    var html = '';
    for (var i = 0; i < containers.length; i++) html += containers[i].innerHTML;
    return html || null;
  }

  function row() {
    return document.querySelector('.' + ROW_CLASS);
  }

  function removeRow() {
    var el = row();
    if (el) el.parentNode.removeChild(el);
  }

  function render(input, text) {
    removeRow();

    var el = document.createElement('div');
    el.className = ROW_CLASS;
    el.setAttribute('role', 'note');

    var line = document.createElement('p');
    line.className = ROW_CLASS + '__text';
    line.appendChild(document.createTextNode('No exact matches. Let the Rug Finder look for ‘'));
    var query = document.createElement('span');
    query.className = ROW_CLASS + '__query';
    query.textContent = text; // the shopper's own text, never parsed as markup
    line.appendChild(query);
    line.appendChild(document.createTextNode('’.'));

    var link = document.createElement('a');
    link.className = ROW_CLASS + '__link';
    link.href = FINDER_URL;
    link.textContent = 'Try the Rug Finder';
    link.addEventListener('click', function () {
      try {
        window.sessionStorage.setItem(KEY, JSON.stringify({ text: text, at: Date.now() }));
      } catch (err) { /* storage off: the finder opens with an empty box */ }
      track('search_dropdown_handoff_click', { query_length: text.length });
    });

    el.appendChild(line);
    el.appendChild(link);
    place(input, el);
  }

  // Runs once the pause has elapsed: records what the dropdown settled on and
  // shows the row when it settled on nothing.
  function settle(input) {
    timer = null;
    var text = queryText(input);
    if (text.length < MIN_QUERY) return;
    // A dropdown unchanged since the keystroke is the previous query's,
    // still up because the app has not answered yet: one more pause. An app
    // that re-renders nothing for a repeat query then counts as results.
    if (!waited && before !== null && dropdownHtml() === before) {
      waited = true;
      timer = setTimeout(function () { settle(input); }, PAUSE_MS);
      return;
    }
    pausedFor = text;
    var some = hasResults();
    track('search_dropdown_settled', {
      result_bucket: some ? 'some' : 'none',
      query_length: text.length,
    });
    if (!some) render(input, text);
  }

  function onInput(event) {
    var input = queryInput(event.target);
    if (!input) return;
    // The query changed: whatever the row said is about an older query.
    removeRow();
    pausedFor = null;
    if (timer) clearTimeout(timer);
    timer = null;
    if (queryText(input).length < MIN_QUERY) return;
    before = dropdownHtml();
    waited = false;
    timer = setTimeout(function () { settle(input); }, PAUSE_MS);
  }

  // Results can land after the pause (a slow answer) or be taken away after
  // it (the app clears the dropdown once it hears back with nothing). Either
  // way the row must agree with what the shopper sees now.
  function reconcile() {
    // Mid-typing settle() decides; with no query settled there is nothing to
    // agree with, and this runs on every mutation anywhere on the page.
    if (timer || pausedFor === null) return;
    var showing = row();
    if (hasResults()) {
      if (showing) removeRow();
      return;
    }
    // The app took its bar down (Cancel): a row fixed under it would float
    // over the page.
    if (showing && showing.classList.contains(MOBILE_CLASS)) {
      var bar = document.querySelector(MOBILE_BAR);
      if (!bar) {
        removeRow();
        showing = null;
      } else {
        showing.style.top = Math.round(bar.getBoundingClientRect().bottom) + 'px';
      }
    }
    if (showing) return;
    // The input the pause elapsed for is the one still holding that query.
    var inputs = queryInputs();
    for (var i = 0; i < inputs.length; i++) {
      if (queryText(inputs[i]) === pausedFor) {
        render(inputs[i], pausedFor);
        return;
      }
    }
  }

  // On window, not document: the app stops the search input's events at
  // window capture, so nothing registered below it ever hears them.
  window.addEventListener('input', onInput, true);
  // The row's own insertion is a mutation too; reconcile() finds nothing to
  // change when the row already matches the dropdown, so it does not loop.
  new MutationObserver(reconcile).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

/* ==========================================================================
   Rug Finder: pick up a search the dropdown could not answer
   --------------------------------------------------------------------------
   The block above stores the query under sessionStorage[rs-search-handoff]
   and sends the shopper to the finder page with ?from=search. This fills the
   composer with that text, grows the box to fit it and puts the cursor in it.
   It does not submit: every request is a paid call under a daily cap, and
   the shopper may want to add a photo or a palette first.

   A size in the query, "8x10" or "8' x 10'", is copied into the size inputs.
   The finder treats an explicit size as a hard filter that beats whatever it
   reads out of the text, and the inputs default to 9 x 12, so leaving them
   alone would have ruled out the very rugs the shopper asked for.
   ========================================================================== */
(function () {
  var KEY = 'rs-search-handoff';
  var MARKER = 'from';
  var MARKER_VALUE = 'search';
  var MAX_TEXT = 300;
  var MAX_AGE_MS = 60 * 60 * 1000;
  var INPUT = '#room-concierge-input';
  // Feet, in a range that only a rug can be; past it the pair is read as
  // inches, which is how trade sources write "96 x 120".
  var SIZE_RE = /(\d{1,3}(?:\.\d+)?)\s*(?:'|′|ft\.?|feet|foot)?\s*(?:x|×|by)\s*(\d{1,3}(?:\.\d+)?)\s*(?:'|′|ft\.?|feet|foot)?(?![\d.])/i;
  var MAX_FEET = 30;

  // URLSearchParams, not decodeURIComponent: a bad escape elsewhere in the
  // query string (a tracking tool's doing) must not throw the prefill away.
  function fromSearch() {
    try {
      return new URLSearchParams(window.location.search).get(MARKER) === MARKER_VALUE;
    } catch (err) {
      return false;
    }
  }

  // Reads the carried text and clears it in the same breath, so a reload or
  // a second finder page in this tab starts empty.
  function takeCarried() {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(KEY);
      if (raw !== null) window.sessionStorage.removeItem(KEY);
    } catch (err) {
      return '';
    }
    if (!raw) return '';
    var carried;
    try { carried = JSON.parse(raw); } catch (err) { return ''; }
    if (!carried || typeof carried.text !== 'string') return '';
    if (typeof carried.at === 'number' && Date.now() - carried.at > MAX_AGE_MS) return '';
    return carried.text.trim().slice(0, MAX_TEXT);
  }

  // Both numbers in the same unit: feet when both fit a rug, inches when
  // neither does. One of each is not a size.
  function sizeIn(text) {
    var match = SIZE_RE.exec(text);
    if (!match) return null;
    var w = parseFloat(match[1]);
    var l = parseFloat(match[2]);
    if (!(w > 0 && l > 0)) return null;
    var inches = w > MAX_FEET && l > MAX_FEET;
    if (inches) { w = w / 12; l = l / 12; }
    if (w > MAX_FEET || l > MAX_FEET) return null;
    return { width: Math.round(w * 4) / 4, length: Math.round(l * 4) / 4 };
  }

  function setSize(root, size) {
    var width = root.querySelector('[data-size="width"]');
    var length = root.querySelector('[data-size="length"]');
    if (!width || !length) return;
    width.value = String(size.width);
    length.value = String(size.length);
  }

  function prefill() {
    if (!fromSearch()) return;
    var input = document.querySelector(INPUT);
    if (!input) return;
    var text = takeCarried();
    if (!text) return;

    input.value = text;
    // The composer's autosize listens for input at the document, so the box
    // grows the way it would have had the shopper typed this.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    var size = sizeIn(text);
    var root = input.closest('.room-concierge') || input.closest('form') || document;
    if (size) setSize(root, size);
    input.focus();
    try { input.setSelectionRange(text.length, text.length); } catch (err) { /* not a text control */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prefill);
  } else {
    prefill();
  }
})();

/* ------------------------------------------------------------------------------------------------------------
 * FULL SCREEN PRODUCT PHOTOGRAPHS
 * ------------------------------------------------------------------------------------------------------------
 *
 * A click or tap anywhere on the product photograph opens it in the theme's
 * full screen viewer, on every device.
 *
 * What this replaces: Focal ships <flickity-carousel click-nav>, where a click
 * on the right half of the image advances a slide and a tap on a phone does
 * nothing whatsoever. snippets/rug-media-fullscreen.liquid strips that
 * attribute at parse time, before theme.js can bind either handler; this file
 * supplies the behaviour that takes its place.
 *
 * It opens the viewer by clicking the theme's own zoom button rather than
 * reaching for <product-image-zoom> directly. That button is a ToggleButton
 * wired to the openable element by aria-controls, so going through it keeps
 * aria-expanded truthful, keeps the close path working, and means we make no
 * assumption about the viewer's id. It is also why the zoom button stays on
 * the page: it is the affordance a keyboard reaches, and the only visible one
 * a touch device gets, and this handler is deliberately pointer-only.
 *
 * Deliberately not opened on: a drag (the carousel's own gesture), a long
 * press (the phone's save-image menu), video or 3D slides, and any click that
 * landed on a real control sitting over the image.
 */
(function () {
  // Flickity's own dragThreshold is 10px. Anything past it was a drag and
  // Flickity has already handled it as one.
  var MOVE_TOLERANCE_PX = 10;
  // Past this a press is a long press, and on a phone that is the browser's
  // save-image menu, not a request to see the rug bigger.
  var PRESS_MAX_MS = 700;

  var pressX = 0;
  var pressY = 0;
  var pressAt = 0;

  function onPointerDown(event) {
    pressX = event.clientX;
    pressY = event.clientY;
    pressAt = Date.now();
  }

  function onClick(event) {
    // Storefront apps dispatch clicks on document itself, which has no
    // closest(). Nothing here would run for one anyway; the check is so it
    // does not throw on the way to finding that out.
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var cell = target.closest('.product__media-list .product__media-item');
    if (!cell) return;

    // Only photographs. A video or model slide has its own click behaviour
    // and the theme hides the zoom button on those anyway.
    if (cell.getAttribute('data-media-type') !== 'image') return;

    // Anything genuinely clickable over the image keeps its click: the zoom
    // button itself, the AR button, a video's play button.
    if (target.closest('a, button, [role="button"], input, label')) return;

    if (Math.abs(event.clientX - pressX) > MOVE_TOLERANCE_PX) return;
    if (Math.abs(event.clientY - pressY) > MOVE_TOLERANCE_PX) return;
    if (Date.now() - pressAt > PRESS_MAX_MS) return;

    var wrapper = cell.closest('.product__media-list-wrapper');
    var zoomButton = wrapper && wrapper.querySelector('.product__zoom-button');

    // Zoom is a section setting and the button is hidden on non-image slides.
    // With no button there is no viewer to open, so leave the click alone.
    if (!zoomButton || zoomButton.hasAttribute('hidden')) return;

    event.preventDefault();
    zoomButton.click();
  }

  // Delegated on the document so this survives Focal swapping product
  // sections around on a variant change, and so it costs nothing on the
  // pages that have no product media at all.
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('click', onClick);
})();

/* ==========================================================================
   Rug size guide
   --------------------------------------------------------------------------
   Drives snippets/rug-size-guide.liquid on /pages/rug-size-guide and the
   room pages beside it. Same rooms, same arithmetic and same drawing as the
   Fit my room tab in the collection filter bar, because both read
   window.RugSizing above.

   The difference is where the answer lands. The tab is already standing in a
   collection, so it filters the one it is in. A guide page is standing in no
   collection at all, so the snippet hands it a room to collection map and the
   button carries the size to that collection through the app proxy. The href
   is a real collection link before this file touches it, so the button works
   without JS and reads as a link to a crawler.
   ========================================================================== */
(function () {
  'use strict';

  var RS = window.RugSizing;
  if (!RS) return;

  /* The two width and length pairs the XCloud app filters on. Spelled out
     again rather than shared with the bar: the bar owns a dozen more params
     and a grouping scheme for carrying them across each other, and this page
     sets four params on a URL that has none. */
  var P = {
    wMin: 'filter_width__inches_[min]', wMax: 'filter_width__inches_[max]',
    lMin: 'filter_length__inches_[min]', lMax: 'filter_length__inches_[max]'
  };

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function parseMap(raw) {
    if (!raw) return {};
    try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
  }

  function track(room, cover, rug, handle) {
    window.RugFilterEvents.push({
      name: 'size',
      /* Small side first, as the page says it and as a rug is labelled, so
         two rooms that recommend the same rug do not land in the report as
         two different values. */
      value: room + '/' + cover + ' ' +
        Math.min(rug.w, rug.l) + 'x' + Math.max(rug.w, rug.l),
      method: 'room',
      source: 'size_guide',
      /* The collection the button is about to filter, not the page the
         shopper is standing on. This page is the only emitter whose event
         is about a collection it is not on, and the report should not have
         to know that to count it with the rest. */
      handle: handle || ''
    });
  }

  function boot(root) {
    var pinned = root.getAttribute('data-rsg-room') || '';
    var collections = parseMap(root.getAttribute('data-rsg-collections'));
    var kindSelect = $('[data-rsg-room-kind]', root);
    var apply = $('[data-rsg-apply]', root);

    /* A room page pins one room and has no select; the hub has the select and
       no pin. Either way the key has to name a room this file knows, because
       a page handle is admin data and the map above is not. */
    function roomKey() {
      var key = kindSelect ? kindSelect.value : pinned;
      return RS.ROOMS[key] ? key : 'living';
    }

    function roomSpec() { return RS.ROOMS[roomKey()]; }

    /* The button is a plain collection link until there is a size to carry,
       and goes back to being one whenever a box is emptied. */
    function setHref(r) {
      var handle = collections[roomKey()];
      var g, q;
      if (!apply) return;
      if (!handle) return;
      if (!r) {
        apply.href = '/collections/' + encodeURIComponent(handle);
        return;
      }
      g = RS.rugRange(r.rug);
      q = new URLSearchParams();
      q.set(P.wMin, g.wMin);
      q.set(P.wMax, g.wMax);
      q.set(P.lMin, g.lMin);
      q.set(P.lMax, g.lMax);
      /* Only /a/search/<handle> filters. The same params on
         /collections/<handle> are ignored, so the sized link goes to the
         proxy and the unsized one does not have to. */
      apply.href = '/a/search/' + encodeURIComponent(handle) + '?' + q.toString();
    }

    /* The controls, the drawing and the arithmetic are the filter bar's, so
       an answer edited there is edited here. The href is this page's own. */
    var room = RS.mountRoom({
      spec: roomSpec,
      idPrefix: 'rsg-room-',
      onDraw: setHref,
      fields: $('[data-rsg-room-inputs]', root),
      covers: $('[data-rsg-room-covers]', root),
      plan: $('[data-rsg-plan]', root),
      planSvg: $('[data-rsg-plan-svg]', root),
      planTitle: $('[data-rsg-plan-title]', root),
      planSize: $('[data-rsg-plan-size]', root),
      planText: $('[data-rsg-plan-text]', root)
    });

    if (kindSelect) kindSelect.addEventListener('change', room.setRoom);

    if (apply) {
      apply.addEventListener('click', function () {
        var r = room.read();
        if (r) track(roomKey(), room.cover(), r.rug, collections[roomKey()]);
      });
    }
  }

  function start() {
    var root = document.querySelector('[data-rsg]');
    if (root) boot(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ==========================================================================
   Similar-rugs toolbar
   --------------------------------------------------------------------------
   Turns "Others you might love" on a live product page into a small
   collection: Size and Price chips over the grid, a tell-us box, and up to
   seven pages of thirty-six rugs. Markup:
   snippets/similar-rugs-toolbar.liquid, which borrows the collection filter
   bar's cfb__* classes so the two surfaces cannot drift apart visually; the wiring here is its own, because
   the collection bar navigates a URL and this toolbar asks the concierge.

   The section's server-rendered grid stays page one of the unfiltered view.
   Everything past it - more pages, any filter, anything typed - comes from
   the concierge (max_results 240, paged into the grid's thirty-sixes) and
   renders through the same search-section cards the drawer proved out.
   The transport and card plumbing come from window.RugConcierge; the
   room-fitting controls from window.RugSizing.

   What a shopper has asked for lives in the URL as sim.* params (sizes in
   feet, prices in dollars, the typed ask, the page), so the back button
   returns to the search they just left and a copied address reopens it.
   Results are kept per-session (sessionStorage) so that return trip is a
   lookup, not a second round through the concierge.
   ========================================================================== */

(function () {
  'use strict';

  function boot() {
    var RC = window.RugConcierge;
    var failures = window.RugConciergeFailures;
    var RS = window.RugSizing;
    var root = document.querySelector('[data-rft]');
    if (!root || !RC || !RS) return;

    var section = root.closest('[id^="shopify-section-"]');
    var listEl = section ? section.querySelector('.product-list__inner') : null;
    // No precomputed grid (empty metafield): nothing to filter or page.
    if (!listEl) return;

    /* Thirty-six, and templates/product.json renders the same number.

       The section lays its tiles out two, three or four across depending on
       the width, so a page only ends flush with a row if it divides by all
       three. Forty does not divide by three: between 741px and 1199px every
       page but the last used to end on a row holding one rug. Thirty-six
       does, and so does the 24 the concierge's cap leaves over, so no page
       with another behind it ends part-way through a row. */
    var PAGE_SIZE = 36;
    var MAX_RESULTS = 240;          // the concierge's cap
    var DEFAULT_PAGES = Math.ceil(MAX_RESULTS / PAGE_SIZE);  // six full, then 24
    var CACHE_KEY = 'rf_toolbar';
    var CACHE_MAX = 4;

    // SKU and title only. The toolbar runs on sold and withheld rugs too, and
    // an unavailable rug ships no price anywhere a shopper can reach, data
    // attributes included; nothing here ever needed the number anyway.
    var anchor = {
      sku: root.getAttribute('data-rft-sku') || '',
      title: root.getAttribute('data-rft-title') || ''
    };
    if (!anchor.sku) return;

    function $(sel, ctx) { return (ctx || root).querySelector(sel); }
    function $$(sel, ctx) {
      return Array.prototype.slice.call((ctx || root).querySelectorAll(sel));
    }
    function raw(input) { return input ? String(input.value).replace(/^\s+|\s+$/g, '') : ''; }

    var num = RS.num, clamp = RS.clamp, spanText = RS.spanText;

    // ---- state -----------------------------------------------------------

    var state = {
      constraints: {},   // size_range / max_price_usd / min_price_usd
      query: '',         // everything typed so far, joined with '; '
      brief: '',
      referenceStrength: 'none',
      rankingAnchors: [],
      us: false,
      rugs: null,        // the concierge's ranked list, once fetched
      page: 1
    };
    var origCards = Array.prototype.slice.call(listEl.children);
    var cardCache = {};        // searchKey + '#' + page -> { cards, rugs }
    var liveShowing = false;   // whether the grid currently shows fetched cards
    var searchTurn = 0;        // a newer search or restore supersedes older answers
    var renderTurn = 0;        // same, for card fetches
    var inFlight = false;      // a concierge ask is out; the pager waits for it
    var shownRugs = null;      // the rug objects behind the cards on screen
    var lastGood = null;       // the last state the grid actually answered for

    function filtered() {
      var c = state.constraints;
      return Boolean(c.size_range || c.max_price_usd != null ||
        c.min_price_usd != null || state.query);
    }

    /* One string per distinct search, page excluded, so the session cache and
       the card cache agree on what "the same search" means. */
    function searchKey() {
      var c = state.constraints, sr = c.size_range || {};
      return [
        sr.min_width_in != null ? sr.min_width_in : '',
        sr.max_width_in != null ? sr.max_width_in : '',
        sr.min_length_in != null ? sr.min_length_in : '',
        sr.max_length_in != null ? sr.max_length_in : '',
        c.min_price_usd != null ? c.min_price_usd : '',
        c.max_price_usd != null ? c.max_price_usd : '',
        state.query
      ].join('|');
    }

    // ---- session cache ---------------------------------------------------
    /* A shopper stepping back through their searches should land on the
       answer they already saw, not a reshuffled one. Keyed per anchor rug so
       a second product page in the same tab session does not replay this
       one's results. */

    function cacheRead() {
      try {
        var stored = window.sessionStorage.getItem(CACHE_KEY);
        var data = stored ? JSON.parse(stored) : null;
        return data && data.v === 1 && data.entries ? data : { v: 1, entries: [] };
      } catch (e) { return { v: 1, entries: [] }; }
    }

    function cacheGet(key) {
      var entries = cacheRead().entries;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].k === key && entries[i].sku === anchor.sku) {
          // An entry with nothing in it is no answer worth replaying.
          return entries[i].rugs && entries[i].rugs.length ? entries[i] : null;
        }
      }
      return null;
    }

    function cachePut(key) {
      try {
        var data = cacheRead();
        data.entries = data.entries.filter(function (e) {
          return !(e.k === key && e.sku === anchor.sku);
        });
        data.entries.unshift({
          k: key, sku: anchor.sku,
          rugs: state.rugs, brief: state.brief, us: state.us,
          referenceStrength: state.referenceStrength,
          rankingAnchors: state.rankingAnchors
        });
        data.entries = data.entries.slice(0, CACHE_MAX);
        window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
      } catch (e) { /* storage unavailable: back just re-asks */ }
    }

    // ---- URL state -------------------------------------------------------

    var Q = { w: 'sim.w', l: 'sim.l', pmin: 'sim.pmin', pmax: 'sim.pmax', q: 'sim.q', page: 'sim.page' };

    /* Sizes travel as feet ("6-8.5", open ends empty) because feet are what
       the shopper typed and what a pasted link should read as; they come back
       as the inches everything here filters on. */
    function feetStr(inches) { return String(Math.round(inches / 12 * 100) / 100); }

    function rangeParam(min, max) {
      if (min == null && max == null) return null;
      return (min == null ? '' : feetStr(min)) + '-' + (max == null ? '' : feetStr(max));
    }

    function parseFeetRange(s) {
      if (!s) return null;
      var m = /^([0-9.]*)-([0-9.]*)$/.exec(s);
      if (!m) return null;
      var lo = m[1] === '' ? NaN : Math.round(parseFloat(m[1]) * 12);
      var hi = m[2] === '' ? NaN : Math.round(parseFloat(m[2]) * 12);
      var out = {};
      if (isFinite(lo) && lo > 0) out.min = lo;
      if (isFinite(hi) && hi > 0) out.max = hi;
      return out.min != null || out.max != null ? out : null;
    }

    function writeUrl(push) {
      var p = new URLSearchParams(location.search);
      Object.keys(Q).forEach(function (k) { p.delete(Q[k]); });
      var c = state.constraints, sr = c.size_range;
      var w = sr ? rangeParam(sr.min_width_in, sr.max_width_in) : null;
      var l = sr ? rangeParam(sr.min_length_in, sr.max_length_in) : null;
      if (w) p.set(Q.w, w);
      if (l) p.set(Q.l, l);
      if (c.min_price_usd != null) p.set(Q.pmin, String(c.min_price_usd));
      if (c.max_price_usd != null) p.set(Q.pmax, String(c.max_price_usd));
      if (state.query) p.set(Q.q, state.query);
      if (state.page > 1) p.set(Q.page, String(state.page));
      var qs = p.toString();
      var url = location.pathname + (qs ? '?' + qs : '') + location.hash;
      var current = location.pathname + location.search + location.hash;
      try {
        // Re-applying the same search must not stack duplicate history
        // entries the back button then walks through one by one.
        if (push && url !== current) history.pushState({ rft: true }, '', url);
        else if (!push) history.replaceState(history.state, '', url);
      } catch (e) { /* history unavailable: the page still works, unlinkable */ }
    }

    function readUrl() {
      var p = new URLSearchParams(location.search);
      var out = {
        constraints: {},
        query: (p.get(Q.q) || '').slice(0, 300),
        page: parseInt(p.get(Q.page), 10) || 1
      };
      var w = parseFeetRange(p.get(Q.w));
      var l = parseFeetRange(p.get(Q.l));
      if (w || l) {
        var sr = {};
        if (w && w.min != null) sr.min_width_in = w.min;
        if (w && w.max != null) sr.max_width_in = w.max;
        if (l && l.min != null) sr.min_length_in = l.min;
        if (l && l.max != null) sr.max_length_in = l.max;
        out.constraints.size_range = sr;
      }
      var pmin = parseInt(p.get(Q.pmin), 10);
      var pmax = parseInt(p.get(Q.pmax), 10);
      if (isFinite(pmin) && pmin > 0) out.constraints.min_price_usd = pmin;
      if (isFinite(pmax) && pmax > 0) out.constraints.max_price_usd = pmax;
      out.page = Math.min(Math.max(out.page, 1), DEFAULT_PAGES);
      return out;
    }

    // ---- chips, count, status --------------------------------------------

    function fmtIn(n) {
      var f = Math.floor(n / 12), i = Math.round(n % 12);
      return i ? f + '′' + i + '″' : f + '′';
    }

    function boundsLabel(lo, hi, money) {
      var a = lo != null ? (money ? RC.formatPrice(lo) : fmtIn(lo)) : null;
      var b = hi != null ? (money ? RC.formatPrice(hi) : fmtIn(hi)) : null;
      if (a && b) return a + '–' + b;
      if (a) return a + ' and up';
      return 'up to ' + b;
    }

    function sizeChipText() {
      var sr = state.constraints.size_range;
      if (!sr) return '';
      var hasW = sr.min_width_in != null || sr.max_width_in != null;
      var hasL = sr.min_length_in != null || sr.max_length_in != null;
      var w = hasW ? boundsLabel(sr.min_width_in, sr.max_width_in) : 'any';
      var l = hasL ? boundsLabel(sr.min_length_in, sr.max_length_in) : 'any';
      return w + ' × ' + l;
    }

    function priceChipText() {
      var c = state.constraints;
      if (c.min_price_usd == null && c.max_price_usd == null) return '';
      return boundsLabel(c.min_price_usd, c.max_price_usd, true);
    }

    /* ---- what the bell subscribes to ---- */

    /* A shopper who has narrowed the bar to 9 × 12 has told us the size they
       will actually buy, and an alert that threw that away would spend its
       one Sunday email on rugs that cannot go in the room. So the bar's own
       bounds ride along with the anchor.

       Size and price only, which is all this bar holds - and all a similar
       alert can use anyway: the rug it is standing on says the style far
       better than a chip row could, so the far end drops the rest.

       The constraints are copied rather than referenced: this ends up in an
       attribute either way, but the copy keeps a later edit of state from
       silently rewriting an alert that has already been described. */
    function alertFilter() {
      var c = state.constraints;
      var filter = {};
      if (c.size_range) filter.size_range = JSON.parse(JSON.stringify(c.size_range));
      if (c.min_price_usd != null) filter.min_price_usd = c.min_price_usd;
      if (c.max_price_usd != null) filter.max_price_usd = c.max_price_usd;
      return Object.keys(filter).length ? filter : null;
    }

    /* The chips' own words, so the dialog and the row above it read as one
       thing. A preview, not the promise: the confirmation email and the
       manage page carry the scope the backend actually stored. */
    function alertScope(filter) {
      var parts = [];
      if (filter.size_range) parts.push(sizeChipText());
      if (priceChipText()) parts.push(priceChipText());
      return 'New arrivals closest to this rug, ' + parts.join(' · ') + '.';
    }

    /* Written to the button on every sync, so whichever door the shopper
       takes - the bell or the box beside Send - sends the bar as it stands at
       that moment, and an alert set up before a filter was cleared does not
       keep promising it. */
    function syncAlert() {
      var btn = $('[data-rft-alert]');
      if (!btn) return;
      var filter = alertFilter();
      if (!filter) {
        btn.removeAttribute('data-alert-filter');
        btn.removeAttribute('data-alert-scope');
        return;
      }
      btn.setAttribute('data-alert-filter', JSON.stringify(filter));
      btn.setAttribute('data-alert-scope', alertScope(filter));
    }

    function renderFchips() {
      var wrap = $('[data-rft-fchips]');
      wrap.textContent = '';
      function add(label, kind, what) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'cfb__active-chip';
        b.setAttribute('data-rft-x', kind);
        b.setAttribute('aria-label', 'Remove ' + what);
        b.appendChild(document.createTextNode(label));
        var x = document.createElement('span');
        x.className = 'cfb__active-x';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        b.appendChild(x);
        wrap.appendChild(b);
      }
      if (state.constraints.size_range) add(sizeChipText(), 'size', 'the size filter');
      if (priceChipText()) add(priceChipText(), 'price', 'the price filter');
      if (state.query) add('“' + state.query + '”', 'query', 'what you typed');
      wrap.hidden = !wrap.children.length;
    }

    function renderCount() {
      var el = $('[data-rft-count]');
      el.textContent = '';
      if (!filtered() || !state.rugs) return;
      var n = state.rugs.length;
      // At the cap the list is a ceiling, not a total, so the number wears a
      // plus rather than claiming to have counted every match.
      var capped = n >= MAX_RESULTS;
      var long = capped ? MAX_RESULTS + '+ rugs match'
        : n === 1 ? '1 rug matches' : n + ' rugs match';
      /* On a phone this line also carries Email and Share, and the three of
         them have 342pt to share, so up there the noun comes out. Both are
         written and the stylesheet picks one, which keeps the wording out of
         a resize listener - and the hidden half is not read out either. */
      var short = capped ? MAX_RESULTS + '+ match' : n + ' match';
      el.appendChild(countSpan('rft__count-long', long));
      el.appendChild(countSpan('rft__count-short', short));
    }

    function countSpan(cls, text) {
      var s = document.createElement('span');
      s.className = cls;
      s.textContent = text;
      return s;
    }

    function syncBar() {
      $('[data-rft-chip-value="size"]').textContent = sizeChipText();
      $('[data-rft-chip-value="price"]').textContent = priceChipText();
      $('[data-rft-chip="size"]').classList.toggle('cfb__chip--on', Boolean(state.constraints.size_range));
      $('[data-rft-chip="price"]').classList.toggle('cfb__chip--on', priceChipText() !== '');
      renderFchips();
      renderCount();
      syncAlert();
      $('[data-rft-meta]').hidden = !filtered();
    }

    function setStatus(text) {
      var el = $('[data-rft-status]');
      el.textContent = text || '';
      el.hidden = !text;
    }

    /* A search is two waits back to back - the concierge, then the cards -
       and a rail that blinked out between them would read as finished. One
       depth count spans both, so the toolbar stays visibly busy until there
       is something to look at. */
    var busyDepth = 0;
    var refocusGo = false;

    function setBusy(on) {
      busyDepth = Math.max(0, busyDepth + (on ? 1 : -1));
      var busy = busyDepth > 0;
      $('[data-rft-progress]').hidden = !busy;
      var go = $('.rft__ask-go');
      if (busy) go.setAttribute('data-rft-busy', '');
      else go.removeAttribute('data-rft-busy');
      // Disabling the element under the cursor is fine; disabling the one
      // under the keyboard drops focus to the body and sends the next Tab
      // back to the top of the page, so it is handed back when the wait ends.
      if (busy && !go.disabled) refocusGo = document.activeElement === go;
      go.disabled = busy;
      if (!busy && refocusGo) { go.focus(); refocusGo = false; }
      listEl.setAttribute('aria-busy', busy ? 'true' : 'false');
      // The rail is decorative and the count only arrives with the rugs, so
      // this line is the whole of the news for anyone listening.
      $('[data-rft-live]').textContent = busy ? 'Finding rugs…' : '';
    }

    // ---- pages -----------------------------------------------------------

    var pager = document.createElement('nav');
    pager.className = 'rft__pager';
    pager.setAttribute('aria-label', 'More pages of rugs');
    pager.innerHTML =
      '<button type="button" class="rft__pg" data-rft-prev>← Previous</button>' +
      '<span class="rft__pg-count" data-rft-page></span>' +
      '<button type="button" class="rft__pg" data-rft-next>Next →</button>';
    var productList = listEl.closest('product-list') || listEl;
    productList.insertAdjacentElement('afterend', pager);

    function pageCount() {
      /* Until the first fetch answers, promise the pages the concierge is
         good for; the pager corrects itself when the real count comes back. */
      if (!state.rugs) return filtered() ? 1 : DEFAULT_PAGES;
      return Math.max(1, Math.ceil(state.rugs.length / PAGE_SIZE));
    }

    function pageSlice(p) {
      return state.rugs ? state.rugs.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE) : [];
    }

    function renderPager() {
      var m = pageCount();
      var show = filtered() ? m > 1 : !(state.rugs && m <= 1);
      pager.hidden = !show;
      $('[data-rft-page]', pager).textContent = 'Page ' + state.page + ' of ' + m;
      $('[data-rft-prev]', pager).disabled = inFlight || state.page <= 1;
      $('[data-rft-next]', pager).disabled = inFlight || state.page >= m;
    }

    function showCards(cards) {
      listEl.textContent = '';
      cards.forEach(function (c) { listEl.appendChild(c); });
    }

    function restoreDefault() {
      ++renderTurn;
      /* Re-connecting replays the theme's reveal animation on cards the
         shopper has already seen; the drawer strips it the same way. */
      origCards.forEach(function (c) { c.removeAttribute('reveal'); });
      showCards(origCards);
      listEl.removeAttribute('data-rft-results');
      liveShowing = false;
      shownRugs = null;
    }

    /* Rugs a render asked about and could not put on screen leave the list for
       good. Sold ones follow the rule the drawer applies: the count, the
       pager, the email and the share only speak for rugs a shopper can still
       buy. Ones the search index did not answer for go the same way, because
       they cannot be tiled on any page and keeping them only moves the hole
       they leave to the next one.

       Page boundaries shift, so remembered cards are stale; the cache empties
       here and the caller writes the settled page back under the search's own
       key. `pruned` carries the two answers the caller needs: whether the
       boundaries moved at all, and whether any of it was a sale - the one
       reason permanent enough for the session to remember. */
    function notePruned(pruned, slice, cards, sold) {
      if (!state.rugs) return;
      var shown = {};
      cards.forEach(function (c) { shown[c.getAttribute('data-handle')] = true; });
      var gone = {};
      var lost = 0;
      slice.forEach(function (rug) {
        if (shown[rug.handle]) return;
        gone[rug.handle] = true;
        lost++;
        if (sold && sold[rug.handle]) pruned.sold = true;
      });
      if (!lost) return;
      state.rugs = state.rugs.filter(function (r) { return !gone[r.handle]; });
      cardCache = {};
      pruned.any = true;
    }

    /* The drawer's bespoke JSON tiles, for when the search section cannot
       answer; images load at once because these cards are already on screen. */
    function fallbackCards(slice, sold) {
      var cards = [];
      slice.forEach(function (rug, i) {
        if (sold && sold[rug.handle]) return;
        // settlePage renumbers every card it renders; this is only a seed.
        var card = RC.buildCard(rug, i + 1);
        cards.push(card);
        RC.loadCardImage(card);
      });
      return cards;
    }

    /* The rug objects behind a set of rendered cards, in card order, so the
       email and the share always speak for exactly what is on screen. */
    function rugsOf(cards) {
      var byHandle = {};
      (state.rugs || []).forEach(function (r) { byHandle[r.handle] = r; });
      return cards.map(function (c) {
        return byHandle[c.getAttribute('data-handle')];
      }).filter(Boolean);
    }

    function renderLivePage(p) {
      var key = searchKey() + '#' + p;
      var turn = ++renderTurn;
      liveShowing = true;
      if (cardCache[key]) {
        showCards(cardCache[key].cards);
        shownRugs = cardCache[key].rugs;
        listEl.setAttribute('data-rft-results', '');
        setStatus('');
        return;
      }
      if (!pageSlice(p).length) {
        if (!filtered()) { restoreDefault(); return; }
        // A filtered page with nothing left on it still owes an answer.
        showCards([]);
        shownRugs = [];
        listEl.setAttribute('data-rft-results', '');
        setStatus(state.rugs && !state.rugs.length
          ? 'Every one of those matches just sold. Try widening the size or price.'
          : 'Nothing fits those exact details. Try widening the size or price.');
        return;
      }
      setBusy(true);
      fillPage(p, turn, [], { any: false, sold: false });
    }

    /* A page owes a full grid of cards, not a full grid of picks.

       The section render answers for fewer rugs than it was asked about
       whenever one has sold or the search index has not caught up with it, and
       a page that showed only what came back left a short last row under a
       Next that promised more. So the ones it could not render leave the list
       and the page asks for the rugs behind them, until it is full or the list
       is spent - a short last row is honest on the last page and nowhere else.

       No round cap, because the shape of the thing already bounds it: a render
       that comes back with nothing settles below, so every round that reaches
       the recursion has added at least one card and shortened the next ask by
       that much. A page cannot spend more rounds than it has seats, and in
       practice spends one more than the number of rugs it lost.

       A page that does settle short leaves the rugs filling the rest of its
       window to the page behind it - see the note over that branch. */
    function fillPage(p, turn, cards, pruned) {
      var start = (p - 1) * PAGE_SIZE;
      var slice = state.rugs ? state.rugs.slice(start + cards.length, start + PAGE_SIZE) : [];
      if (!slice.length) { settlePage(p, turn, cards, pruned); return; }
      RC.fetchSectionCards(slice).then(function (found) {
        if (turn !== renderTurn) { setBusy(false); return; }
        if (!found.cards.length) {
          /* The index answered with none of them. First time through, the
             module's long-standing fallback tiles the whole slice from JSON.
             Later rounds already hold theme tiles and half a grid of each
             reads as a bug, so the page settles for being short, and the rugs
             it never reached stay in the list for the page behind it. */
          if (cards.length) { settlePage(p, turn, cards, pruned); return; }
          var tiles = fallbackCards(slice, found.sold);
          notePruned(pruned, slice, tiles, found.sold);
          settlePage(p, turn, tiles, pruned);
          return;
        }
        notePruned(pruned, slice, found.cards, found.sold);
        var got = cards.concat(found.cards);
        if (got.length >= PAGE_SIZE) { settlePage(p, turn, got, pruned); return; }
        fillPage(p, turn, got, pruned);
      }).catch(function () {
        if (turn !== renderTurn) { setBusy(false); return; }
        // Search is not answering at all: JSON tiles for a page with nothing
        // on it yet, and what already rendered for one part way through.
        settlePage(p, turn, cards.length ? cards : fallbackCards(slice, null), pruned);
      });
    }

    function settlePage(p, turn, cards, pruned) {
      setBusy(false);
      if (turn !== renderTurn) return;
      setStatus('');
      // Positions count through the pages a shopper actually sees, so page
      // two opens at 37 however many rugs the page had to skip to fill.
      var start = (p - 1) * PAGE_SIZE;
      cards.forEach(function (c, i) {
        c.setAttribute('data-position', String(start + i + 1));
      });
      if (pruned.any) {
        /* Only a sale is permanent enough to remember. A rug the search index
           had simply not caught up with is out of this render and no further:
           persisting that would lose it for the rest of the session over a
           lag that may already be over by the next page load. */
        if (pruned.sold && state.rugs.length) cachePut(searchKey());
        // Boundaries just moved: correct the pager, and if this page fell off
        // the end land on the last one that exists.
        if (state.page > pageCount()) { state.page = pageCount(); writeUrl(false); }
        renderPager();
        renderCount();
        if (!cards.length) { renderLivePage(state.page); return; }
      }
      /* Safe to remember even after a prune: notePruned empties the cache as
         it shrinks the list, so this entry is written against the boundaries
         the page has settled on. */
      var rugs = rugsOf(cards);
      cardCache[searchKey() + '#' + p] = { cards: cards, rugs: rugs };
      shownRugs = rugs;
      showCards(cards);
      listEl.setAttribute('data-rft-results', '');
    }

    function scrollToTop() {
      if (section && section.scrollIntoView) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function goToPage(p, push, scroll) {
      if (inFlight) return;
      p = Math.min(Math.max(p, 1), pageCount());
      // First step past the unfiltered grid: fetch the long list, then land.
      if (!filtered() && p > 1 && !state.rugs) { fetchBase(p, push); return; }
      state.page = p;
      if (!filtered() && p === 1) restoreDefault();
      else renderLivePage(p);
      writeUrl(push);
      renderPager();
      renderCount();
      if (scroll) scrollToTop();
    }

    // ---- talking to the concierge ----------------------------------------

    function constraintsInto(payload) {
      var c = state.constraints;
      if (c.size_range) payload.size_range = c.size_range;
      if (c.max_price_usd != null) payload.max_price_usd = c.max_price_usd;
      if (c.min_price_usd != null) payload.min_price_usd = c.min_price_usd;
    }

    /* The grid answers for lastGood; chips, count and URL must never claim a
       search it refused. The panel boxes keep what was typed so a shopper can
       retry a widened version without re-entering everything. */
    function snapshotGood() {
      lastGood = {
        constraints: JSON.parse(JSON.stringify(state.constraints)),
        query: state.query,
        brief: state.brief,
        referenceStrength: state.referenceStrength,
        rankingAnchors: state.rankingAnchors.slice()
      };
    }

    function rollback() {
      if (!lastGood) return;
      state.constraints = JSON.parse(JSON.stringify(lastGood.constraints));
      state.query = lastGood.query;
      state.brief = lastGood.brief;
      state.referenceStrength = lastGood.referenceStrength;
      state.rankingAnchors = lastGood.rankingAnchors.slice();
      syncBar();
    }

    function searchFailed(stage, result, opts) {
      var body = (result && result.body) || {};
      var noResults = failures.isNoResults(result);
      failures.push(stage, body.message ? 'server_message' : 'server_error', result, 'toolbar');
      setStatus(body.message ||
        (noResults ? 'Nothing fits those exact details. Try widening the size or price.' : RC.UNREACHABLE));
      // A replayed URL has no earlier answer to fall back on.
      if (!(opts && opts.replay)) rollback();
      renderPager();
    }

    /* opts.page lands the answer on a page other than the first (a restored
       URL); opts.push is false when the URL already says this search. */
    function handleSearch(result, stage, typedText, opts) {
      opts = opts || {};
      var body = result.body || {};
      if (result.status === 200 && body.rugs && body.rugs.length > 0) {
        state.brief = body.brief || '';
        state.rugs = body.rugs;
        state.us = body.us === true;
        if (stage === 'match' || stage === 'refine') {
          state.referenceStrength = body.reference_strength || 'none';
          state.rankingAnchors = body.anchor_skus || [];
        } else if (body.reference_strength) {
          state.referenceStrength = body.reference_strength;
          state.rankingAnchors = body.anchor_skus || state.rankingAnchors;
        }
        if (typedText) {
          state.query = state.query ? state.query + '; ' + typedText : typedText;
          /* The URL carries at most 300 characters of it: shed the oldest
             asks whole, so the link a shopper copies replays what they still
             mean; the brief keeps steering the ranking regardless. */
          while (state.query.length > 300) {
            var cut = state.query.indexOf('; ');
            if (cut === -1) { state.query = state.query.slice(0, 300); break; }
            state.query = state.query.slice(cut + 2);
          }
        }
        cardCache = {};
        cachePut(searchKey());
        snapshotGood();
        RC.track(stage === 'refine' ? 'concierge_results_refined' : 'concierge_results_shown',
          { count: body.rugs.length, source: 'live', surface: 'toolbar' });
        setStatus('');
        syncBar();
        goToPage(opts.page || 1, opts.push !== false);
        return;
      }
      searchFailed(stage, result, opts);
    }

    function runFast(tweak, opts) {
      opts = opts || {};
      var c = state.constraints;
      // Bumped before the early path too: clearing filters mid-flight must
      // supersede the answer still on its way back.
      var turn = ++searchTurn;
      if (!filtered()) {
        // Every filter cleared and nothing typed: the page's own picks.
        var wasLive = liveShowing || state.page > 1 ||
          /(^|[?&])sim\./.test(location.search);
        inFlight = false;
        state.rugs = null;
        state.brief = '';
        state.referenceStrength = 'none';
        state.rankingAnchors = [];
        state.page = 1;
        syncBar();
        setStatus('');
        if (wasLive) { restoreDefault(); writeUrl(true); }
        snapshotGood();
        renderPager();
        return;
      }
      RC.track('concierge_submit', {
        mode: 'fast', tweak: tweak,
        has_size: Boolean(c.size_range),
        has_price: c.max_price_usd != null || c.min_price_usd != null,
        surface: 'toolbar'
      });
      setStatus('');
      setBusy(true);
      inFlight = true;
      renderPager();
      var payload = {
        clientId: RC.clientId(),
        anchor_skus: state.query ? state.rankingAnchors : [anchor.sku],
        max_results: MAX_RESULTS
      };
      constraintsInto(payload);
      if (state.query && state.brief) {
        payload.prior_brief = state.brief;
        payload.reference_strength = state.referenceStrength || 'none';
        payload.context_skus = [anchor.sku];
      }
      RC.post(payload).then(function (result) {
        setBusy(false);
        if (turn !== searchTurn) return;
        inFlight = false;
        handleSearch(result, 'fast_match', null, opts);
      }).catch(function () {
        setBusy(false);
        if (turn !== searchTurn) return;
        inFlight = false;
        failures.push('fast_match', 'unreachable', undefined, 'toolbar');
        setStatus(RC.UNREACHABLE);
        if (!opts.replay) rollback();
        renderPager();
      });
    }

    /* opts.replay re-runs the accumulated query as one search (a restored
       URL) rather than treating it as a fresh refinement. */
    function runTyped(text, opts) {
      opts = opts || {};
      var isRefine = !opts.replay && Boolean(state.query && state.brief);
      var turn = ++searchTurn;
      if (isRefine) RC.track('concierge_refine_submit', { has_photo: false, surface: 'toolbar' });
      else RC.track('concierge_submit', { mode: 'typed', has_size: false, has_price: false, surface: 'toolbar' });
      var payload = { clientId: RC.clientId(), max_results: MAX_RESULTS };
      if (isRefine) {
        payload.refine = text;
        payload.prior_brief = state.brief;
        payload.prior_anchor_skus = state.rankingAnchors;
        payload.reference_strength = state.referenceStrength || 'none';
      } else {
        payload.notes = text;
        payload.context_skus = [anchor.sku];
      }
      constraintsInto(payload);
      setStatus('');
      setBusy(true);
      inFlight = true;
      renderPager();
      RC.post(payload).then(function (result) {
        setBusy(false);
        if (turn !== searchTurn) return;
        inFlight = false;
        handleSearch(result, isRefine ? 'refine' : 'match', opts.replay ? null : text, opts);
      }).catch(function () {
        setBusy(false);
        if (turn !== searchTurn) return;
        inFlight = false;
        failures.push(isRefine ? 'refine' : 'match', 'unreachable', undefined, 'toolbar');
        setStatus(RC.UNREACHABLE);
        if (!opts.replay) rollback();
        renderPager();
      });
    }

    /* "Next" with no filters on: the same precomputed ranking the grid shows,
       continued past its end. The metafield and this call are ranked by the
       same pipeline, so page two picks up where the grid left off. */
    function fetchBase(targetPage, push) {
      var turn = ++searchTurn;
      RC.track('concierge_submit', {
        mode: 'fast', tweak: 'more', has_size: false, has_price: false, surface: 'toolbar'
      });
      setStatus('');
      setBusy(true);
      inFlight = true;
      renderPager();
      var payload = { clientId: RC.clientId(), anchor_skus: [anchor.sku], max_results: MAX_RESULTS };
      RC.post(payload).then(function (result) {
        setBusy(false);
        if (turn !== searchTurn) return;
        inFlight = false;
        var body = result.body || {};
        if (result.status === 200 && body.rugs && body.rugs.length > 0) {
          state.rugs = body.rugs;
          state.brief = body.brief || '';
          state.us = body.us === true;
          state.referenceStrength = body.reference_strength || 'strong';
          state.rankingAnchors = body.anchor_skus || [anchor.sku];
          cachePut(searchKey());
          snapshotGood();
          RC.track('concierge_results_shown', { count: body.rugs.length, source: 'live', surface: 'toolbar' });
          setStatus('');
          goToPage(targetPage, push, true);
        } else {
          searchFailed('fast_match', result);
        }
      }).catch(function () {
        setBusy(false);
        if (turn !== searchTurn) return;
        inFlight = false;
        failures.push('fast_match', 'unreachable', undefined, 'toolbar');
        setStatus(RC.UNREACHABLE);
        renderPager();
      });
    }

    // ---- panels ----------------------------------------------------------
    /* The open/close, tab, and apply mechanics mirror the collection filter
       bar (assets/custom.js, "collection filter bar"), reworked to apply
       into a concierge search instead of a collection URL. */

    var openPanel = null;

    function setPanel(name) {
      $$('[data-rft-panel]').forEach(function (p) {
        p.hidden = p.getAttribute('data-rft-panel') !== name;
      });
      $$('[data-rft-chip]').forEach(function (c) {
        c.setAttribute('aria-expanded', c.getAttribute('data-rft-chip') === name ? 'true' : 'false');
      });
      openPanel = name;
      /* The literal cfb--open class, so the collection bar's mobile backdrop
         and bottom-sheet styles apply here without a second copy. */
      root.classList.toggle('cfb--open', !!name);
    }

    $$('[data-rft-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var name = chip.getAttribute('data-rft-chip');
        setPanel(openPanel === name ? null : name);
      });
    });

    /* Closing hands focus back to the chip that opened the panel, so a
       keyboard shopper is not dropped at the top of the document. */
    function closePanel() {
      var chip = openPanel ? $('[data-rft-chip="' + openPanel + '"]') : null;
      setPanel(null);
      if (chip) chip.focus();
    }

    $$('[data-rft-close]').forEach(function (btn) {
      btn.addEventListener('click', closePanel);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openPanel) closePanel();
    });

    /* Read at capture time: a control that re-renders its own row detaches
       the clicked node before the click bubbles this far. */
    var fromInside = false;
    document.addEventListener('click', function (e) {
      fromInside = root.contains(e.target);
    }, true);
    document.addEventListener('click', function () {
      if (!openPanel || fromInside) return;
      setPanel(null);
    });

    // ---- analytics -------------------------------------------------------
    /* The collection bar's own event, pushed with the bar's names and in the
       bar's vocabulary (its track(), further up this file), so a GA4 report
       grouped by filter_name, filter_type or size_method counts what a
       shopper asks for here alongside what they ask for on a collection page
       instead of leaving the product page out of the picture.

       The concierge_* events this toolbar already pushes say that a search
       ran and what came back; none of them says which control ran it, or
       what it was set to. Only the two chips push: anything typed is an ask
       rather than a filter, and reports as concierge_submit already. */
    var FE = window.RugFilterEvents;
    var side = FE.side;

    function track(name, value, method) {
      FE.push({
        name: name,
        value: value,
        method: method,
        /* Its own source and not the bar's: the same filters on a different
           page, and a report that cannot tell the two apart cannot say
           whether the product page earns them. The concierge events from
           here call the same control surface: 'toolbar'. */
        source: 'pdp_toolbar',
        /* A rug reached through a collection carries that collection in its
           path; a bare /products/<handle> has none to name and reports
           blank rather than a borrowed one. */
        handle: FE.handleFromPath()
      });
    }

    // ---- size panel ------------------------------------------------------

    var tabs = $$('[data-rft-tab]');

    function selectTab(tab, focus) {
      var name = tab.getAttribute('data-rft-tab');
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      $$('[data-rft-tabpanel]').forEach(function (p) {
        p.hidden = p.getAttribute('data-rft-tabpanel') !== name;
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectTab(tab); });
      tab.addEventListener('keydown', function (e) {
        var to = null;
        if (e.key === 'ArrowRight') to = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') to = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') to = tabs[0];
        else if (e.key === 'End') to = tabs[tabs.length - 1];
        if (!to) return;
        e.preventDefault();
        selectTab(to, true);
      });
    });

    /* The same reading the collection bar gives these boxes, out of the same
       function: a size in feet and a give in inches, small side first,
       floored so a rug labelled the asked-for size is never excluded by its
       own honesty. */
    var givenRange = RS.givenRange, sizeAndGive = RS.sizeAndGive;

    var exW = $('[data-rft-exact-w]');
    var exL = $('[data-rft-exact-l]');
    var exTol = $('[data-rft-exact-tol]');
    var exNote = $('[data-rft-exact-note]');

    function exactTol() {
      var tol = parseFloat(raw(exTol));
      if (!(tol >= 0)) tol = 12;
      return clamp(Math.round(tol), 6, 60);
    }

    function exactRange() {
      var w = num(exW), l = num(exL);
      if (!w || !l) return null;
      return givenRange({ w: w, l: l, give: exactTol() });
    }

    function exactNote() {
      var r = exactRange();
      if (!r) {
        exNote.textContent = 'Hand-knotted rugs are never exactly nine by twelve, so a foot of give is the default.';
        return;
      }
      exNote.textContent = 'Looks for rugs ' + spanText(r.wMin, r.wMax) +
        ' wide and ' + spanText(r.lMin, r.lMax) + ' long.';
    }
    [exW, exL, exTol].forEach(function (n) {
      n.addEventListener('input', exactNote);
      n.addEventListener('change', exactNote);
    });

    function applyExact() {
      var r = exactRange();
      if (!r) { exW.focus(); return; }
      state.constraints.size_range = {
        min_width_in: r.wMin, max_width_in: r.wMax,
        min_length_in: r.lMin, max_length_in: r.lMax
      };
      /* Small side first, as the bar reports it: 12 by 9 and 9 by 12 set the
         same filter, so they have to arrive as one value. */
      track('size', Math.min(num(exW), num(exL)) + 'x' + Math.max(num(exW), num(exL)) +
        ' +/-' + exactTol() + 'in', 'exact');
      closePanel();
      syncBar();
      runFast('size');
    }

    var rW = [$('[data-rft-range-wminf]'), $('[data-rft-range-wmini]'),
      $('[data-rft-range-wmaxf]'), $('[data-rft-range-wmaxi]')];
    var rL = [$('[data-rft-range-lminf]'), $('[data-rft-range-lmini]'),
      $('[data-rft-range-lmaxf]'), $('[data-rft-range-lmaxi]')];

    function bound(feetInput, inchInput) {
      var fr = raw(feetInput), ir = raw(inchInput);
      if (fr === '' && ir === '') return null;
      var f = fr === '' ? 0 : parseFloat(fr);
      var i = ir === '' ? 0 : parseFloat(ir);
      if (!isFinite(f) || !isFinite(i) || f < 0 || i < 0) return null;
      var total = Math.round(f * 12 + i);
      return total > 0 ? total : null;
    }

    function pair(boxes) {
      var min = bound(boxes[0], boxes[1]), max = bound(boxes[2], boxes[3]), t;
      if (min != null && max != null && min > max) { t = min; min = max; max = t; }
      return { min: min, max: max };
    }

    function applyRange() {
      var w = pair(rW), l = pair(rL);
      if (w.min == null && w.max == null && l.min == null && l.max == null) {
        rW[0].focus();
        return;
      }
      var sr = {};
      if (w.min != null) sr.min_width_in = w.min;
      if (w.max != null) sr.max_width_in = w.max;
      if (l.min != null) sr.min_length_in = l.min;
      if (l.max != null) sr.max_length_in = l.max;
      state.constraints.size_range = sr;
      /* Both dimensions always named, in inches, so a width-only range is
         told apart from one that bounded the length too. */
      track('size', 'w ' + side(w.min) + '-' + side(w.max)
        + ' l ' + side(l.min) + '-' + side(l.max), 'range');
      closePanel();
      syncBar();
      runFast('size');
    }

    var roomKind = $('[data-rft-room-kind]');
    var room = RS.mountRoom({
      spec: function () { return RS.ROOMS[roomKind.value] || RS.ROOMS.living; },
      idPrefix: 'rft-room-',
      fields: $('[data-rft-room-inputs]'),
      covers: $('[data-rft-room-covers]'),
      plan: $('[data-rft-plan]'),
      planSvg: $('[data-rft-plan-svg]'),
      planTitle: $('[data-rft-plan-title]'),
      planSize: $('[data-rft-plan-size]'),
      planText: $('[data-rft-plan-text]')
    });
    roomKind.addEventListener('change', room.setRoom);

    function applyRoom() {
      var r = room.read();
      if (!r) { room.focusEmpty(); return; }
      var g = RS.rugRange(r.rug);
      state.constraints.size_range = {
        min_width_in: g.wMin, max_width_in: g.wMax,
        min_length_in: g.lMin, max_length_in: g.lMax
      };
      track('size', roomKind.value + '/' + room.cover() + ' ' +
        Math.min(r.rug.w, r.rug.l) + 'x' + Math.max(r.rug.w, r.rug.l), 'room');
      closePanel();
      syncBar();
      runFast('size');
    }

    // ---- price panel -----------------------------------------------------

    var pMin = $('[data-rft-price-min]');
    var pMax = $('[data-rft-price-max]');

    function dollars(input) {
      var s = raw(input).replace(/[$,\s]/g, '');
      if (s === '') return null;
      var n = parseFloat(s);
      return isFinite(n) && n > 0 ? Math.round(n) : null;
    }

    function applyPrice() {
      var lo = dollars(pMin), hi = dollars(pMax), t;
      if (lo == null && hi == null) { pMin.focus(); return; }
      if (lo != null && hi != null && lo > hi) { t = lo; lo = hi; hi = t; }
      if (lo != null) state.constraints.min_price_usd = lo; else delete state.constraints.min_price_usd;
      if (hi != null) state.constraints.max_price_usd = hi; else delete state.constraints.max_price_usd;
      track('price', side(lo) + '-' + side(hi));
      closePanel();
      syncBar();
      runFast('price');
    }

    [pMin, pMax].forEach(function (box) {
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); applyPrice(); }
      });
    });

    // ---- apply / clear ---------------------------------------------------

    function clearSizeBoxes() {
      exW.value = '';
      exL.value = '';
      exTol.value = '12';
      rW.concat(rL).forEach(function (b) { b.value = ''; });
      exactNote();
    }

    $$('[data-rft-apply]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var kind = btn.getAttribute('data-rft-apply');
        if (kind === 'exact') applyExact();
        else if (kind === 'range') applyRange();
        else if (kind === 'room') applyRoom();
        else if (kind === 'price') applyPrice();
      });
    });

    $$('[data-rft-clear]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-rft-clear') === 'size') {
          delete state.constraints.size_range;
          clearSizeBoxes();
        } else {
          delete state.constraints.min_price_usd;
          delete state.constraints.max_price_usd;
          pMin.value = '';
          pMax.value = '';
        }
        closePanel();
        syncBar();
        runFast('filters');
      });
    });

    $('[data-rft-fchips]').addEventListener('click', function (e) {
      var b = e.target.closest('[data-rft-x]');
      if (!b) return;
      var kind = b.getAttribute('data-rft-x');
      if (kind === 'size') {
        delete state.constraints.size_range;
        clearSizeBoxes();
      } else if (kind === 'price') {
        delete state.constraints.min_price_usd;
        delete state.constraints.max_price_usd;
        pMin.value = '';
        pMax.value = '';
      } else {
        state.query = '';
        state.brief = '';
        state.referenceStrength = 'none';
        state.rankingAnchors = [];
      }
      syncBar();
      runFast('filters');
    });

    // ---- the tell-us box -------------------------------------------------

    var askInput = $('.rft__ask-input');
    $('[data-rft-ask]').addEventListener('submit', function (e) {
      e.preventDefault();
      // Enter is the other way to press the button, so it waits the same way
      // the disabled button does rather than stacking a second search.
      if (busyDepth > 0) return;
      var text = (askInput.value || '').trim();
      if (!text) { askInput.focus(); return; }
      askInput.value = '';
      // The box was borrowed from the strip on a phone; the ask is in, so it
      // goes back and leaves the chips where they were.
      setAsking(false);
      runTyped(text);
    });

    // ---- email these to me / share ---------------------------------------

    /* "These" means the rugs on screen: the current page's own, not the
       whole ranked list. On the unfiltered first page that is the grid's own
       picks, read from the JSON the section ships for the drawer. That JSON
       has a cap of its own (rf_shown < 40 in snippets/similar-rugs-data.liquid)
       and only the slice below keeps the two agreeing, so a PAGE_SIZE above
       forty would need the snippet raised with it. */
    function visibleRugs() {
      if (liveShowing) return shownRugs || pageSlice(state.page);
      return (RC.staticSimilars() || []).slice(0, PAGE_SIZE);
    }

    var captureForm = $('[data-rft-capture]');
    var shareRow = $('[data-rft-share-row]');
    var emailBtn = $('[data-rft-email]');
    var shareBtn = $('[data-rft-share]');
    var emailDialog = $('[data-rft-dialog="email"]');
    var shareDialog = $('[data-rft-dialog="share"]');
    var captureTurn = 0;

    /* showModal gives the focus trap, the Escape key and the return of focus
       to the button that opened it; a browser without it falls back to the
       open attribute, which still shows the panel rather than nothing. */
    function openDialog(dialog) {
      dialogStatus(dialog, '');
      if (dialog.showModal) dialog.showModal();
      else dialog.open = true;
    }

    function closeDialog(dialog) {
      if (dialog.close) dialog.close();
      else dialog.open = false;
    }

    function dialogStatus(dialog, text) {
      var el = dialog.querySelector('[data-rft-dialog-status]');
      el.textContent = text || '';
      el.hidden = !text;
    }

    /* The note promises what the button is about to do, so it goes when the
       form does: "we will send" reads wrong under "sent". */
    function dialogNote(dialog, show) {
      dialog.querySelector('.rft__dialog-note').hidden = !show;
    }

    $$('[data-rft-dialog-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeDialog(btn.closest('[data-rft-dialog]'));
      });
    });

    /* A click on the backdrop lands on the dialog itself, never on anything
       inside it, which is the only way to tell the two apart. */
    $$('[data-rft-dialog]').forEach(function (dialog) {
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) closeDialog(dialog);
      });
      window.RugDialogEscape(dialog, function () { closeDialog(dialog); });
    });

    emailBtn.addEventListener('click', function () {
      openDialog(emailDialog);
      captureForm.querySelector('.rft__capture-email').focus();
    });

    /* The box beside the send button promises new arrivals, which is a
       subscription and not a lead. The capture endpoint only records that the
       shopper asked; this is what acts on it, using the same anchor the Alert
       me button beside it does.

       Sent after the capture rather than instead of it, so an alert that
       fails cannot cost the shopper the rugs they came for, and the extra
       line appears only once the subscription has actually landed - nothing
       here claims an email that was never set up. The wording is ours rather
       than the server's: its "Almost there" is written to stand alone, and
       reads as a contradiction under "Sent." */
    var ALERT_LINE = {
      created: 'Check your email to switch on new arrivals like these.',
      resent: 'Check your email to switch on new arrivals like these.',
      already_on: 'You are already set up for new arrivals like these.'
    };

    function subscribeAlongside(email, sent, turn) {
      var alerts = window.RugAlerts;
      var btn = $('[data-rft-alert]');
      /* Read off the bell rather than assembled here, so the box and the
         button cannot end up subscribing to two different things - the bar's
         size and price included. */
      var plan = alerts && btn ? alerts.planFromAttrs(btn) : null;
      if (!plan) return;
      alerts.subscribe({
        email: email,
        kind: 'similar',
        anchorSku: plan.anchorSku,
        anchorTitle: plan.anchorTitle,
        anchorHandle: plan.anchorHandle,
        filter: plan.filter,
        source: 'toolbar-optin',
        marketingOptIn: true,
        customerTz: alerts.timeZone()
      }).then(function (result) {
        var body = result.body || {};
        if (!body.ok) {
          failures.push('alert', body.status || 'server_error', result, 'toolbar-optin');
          return;
        }
        alerts.track('rug_alert_subscribed', {
          surface: 'toolbar-optin',
          alert_kind: 'similar',
          alert_status: body.status,
          alert_scoped: Boolean(plan.filter)
        });
        var line = ALERT_LINE[body.status];
        if (!line || turn !== captureTurn) return;
        dialogStatus(emailDialog, sent + ' ' + line);
      }).catch(function () {
        failures.push('alert', 'unreachable', undefined, 'toolbar-optin');
      });
    }

    captureForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var rugs = visibleRugs();
      if (!rugs.length) return;
      var email = (captureForm.querySelector('.rft__capture-email').value || '').trim();
      if (!email) return;
      var optin = captureForm.querySelector('.rft__optin-box');
      /* Read here rather than when the answer lands: by then the shopper may
         have changed their mind, and what they asked for is what they had
         ticked when they pressed the button. */
      var wantsAlert = Boolean(optin && optin.checked);
      var turn = ++captureTurn;
      dialogStatus(emailDialog, 'Sending…');
      RC.post({
        captureOnly: true,
        clientId: RC.clientId(),
        email: email,
        marketing: wantsAlert,
        skus: rugs.map(function (r) { return r.sku; }),
        prior_brief: state.brief
      }).then(function (result) {
        var body = result.body || {};
        if (result.status === 200 && body.ok) {
          // Tracked whichever dialog the shopper is looking at now: the lead
          // was captured even if they walked away before the answer landed.
          RC.track('concierge_lead_captured', {
            count: rugs.length, surface: 'toolbar', marketing_opt_in: wantsAlert
          });
          if (turn !== captureTurn) return;
          // The dialog stays open holding the answer: closing it the instant
          // it succeeded would take the confirmation away with it.
          captureForm.hidden = true;
          dialogNote(emailDialog, false);
          var sent = body.message || 'Sent. These picks are on their way to your inbox.';
          dialogStatus(emailDialog, sent);
          if (wantsAlert) subscribeAlongside(email, sent, turn);
        } else {
          failures.push('capture', body.message ? 'server_message' : 'server_error', result, 'toolbar');
          if (turn !== captureTurn) return;
          dialogStatus(emailDialog, body.message || RC.UNREACHABLE);
        }
      }).catch(function () {
        failures.push('capture', 'unreachable', undefined, 'toolbar');
        if (turn !== captureTurn) return;
        dialogStatus(emailDialog, RC.UNREACHABLE);
      });
    });

    /* Reopening starts over: a shopper who sent once and came back should
       find the form, not last time's confirmation. Bumping the turn abandons
       an answer still on its way, which would otherwise arrive to hide the
       form of a dialog that has since been reopened and leave it empty. */
    emailDialog.addEventListener('close', function () {
      captureTurn++;
      captureForm.hidden = false;
      dialogNote(emailDialog, true);
      dialogStatus(emailDialog, '');
    });

    shareBtn.addEventListener('click', function () {
      var rugs = visibleRugs();
      if (!rugs.length) return;
      openDialog(shareDialog);
      shareRow.hidden = true;
      dialogStatus(shareDialog, 'Creating your link…');
      RC.post({
        share: true,
        clientId: RC.clientId(),
        skus: rugs.map(function (r) { return r.sku; }),
        prior_brief: state.brief
      }).then(function (result) {
        var body = result.body || {};
        if (result.status === 200 && body.share_url) {
          shareRow.querySelector('.rft__share-link').value = body.share_url;
          shareRow.hidden = false;
          dialogStatus(shareDialog, '');
          RC.track('concierge_share_created', { count: rugs.length, surface: 'toolbar' });
        } else {
          failures.push('share', body.message ? 'server_message' : 'server_error', result, 'toolbar');
          dialogStatus(shareDialog, body.message || RC.UNREACHABLE);
        }
      }).catch(function () {
        failures.push('share', 'unreachable', undefined, 'toolbar');
        dialogStatus(shareDialog, RC.UNREACHABLE);
      });
    });

    $('[data-rft-share-copy]').addEventListener('click', function () {
      var copyBtn = $('[data-rft-share-copy]');
      var link = shareRow.querySelector('.rft__share-link');
      if (!link || !navigator.clipboard) return;
      navigator.clipboard.writeText(link.value).then(function () {
        copyBtn.textContent = 'Copied';
        setTimeout(function () { copyBtn.textContent = 'Copy'; }, 1600);
      }).catch(function () {});
    });

    // ---- pager clicks ----------------------------------------------------

    pager.addEventListener('click', function (e) {
      var next = e.target.closest('[data-rft-next]');
      var prev = e.target.closest('[data-rft-prev]');
      if (!next && !prev) return;
      goToPage(state.page + (next ? 1 : -1), true, true);
    });

    // ---- result funnel events --------------------------------------------
    /* Only for fetched cards (data-rft-results): the server grid never
       tracked as concierge results, and the drawer's own handlers only match
       its .rf-results__grid, so nothing double-counts. */

    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      var card = event.target.closest('[data-rft-results] product-item, [data-rft-results] .room-concierge-results__card');
      if (!card) return;
      var link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      var pos = parseInt(card.getAttribute('data-position'), 10);
      var handle = card.getAttribute('data-handle') ||
        ((link.getAttribute('href') || '').split('/products/')[1] || '').split('?')[0];
      RC.track('concierge_result_click', {
        handle: handle || undefined,
        position: isFinite(pos) ? pos : undefined,
        surface: 'toolbar'
      });
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form.getAttribute || form.getAttribute('is') !== 'product-form') return;
      if (!form.closest('[data-rft-results]')) return;
      var item = form.closest('product-item');
      var pos = item ? parseInt(item.getAttribute('data-position'), 10) : NaN;
      RC.track('concierge_result_add_to_cart', {
        handle: (item && item.getAttribute('data-handle')) || undefined,
        position: isFinite(pos) ? pos : undefined,
        surface: 'toolbar'
      });
    }, true);

    // ---- restoring a URL -------------------------------------------------

    function fillRangeBoxes(boxes, ends) {
      for (var i = 0; i < 2; i++) {
        var n = ends[i];
        if (n == null) continue;
        boxes[i * 2].value = String(Math.floor(n / 12));
        boxes[i * 2 + 1].value = n % 12 ? String(n % 12) : '';
      }
    }

    /* The boxes open holding what the URL says, the way the collection bar
       prefills; a range only the Range tab could have set opens that tab. */
    function prefillPanels() {
      clearSizeBoxes();
      pMin.value = '';
      pMax.value = '';
      var sr = state.constraints.size_range;
      if (sr) {
        var w = [sr.min_width_in != null ? sr.min_width_in : null,
          sr.max_width_in != null ? sr.max_width_in : null];
        var l = [sr.min_length_in != null ? sr.min_length_in : null,
          sr.max_length_in != null ? sr.max_length_in : null];
        var sz = sizeAndGive(w, l);
        if (sz) {
          exW.value = String(sz.w);
          exL.value = String(sz.l);
          exTol.value = String(sz.give);
        } else {
          fillRangeBoxes(rW, w);
          fillRangeBoxes(rL, l);
          selectTab($('[data-rft-tab="range"]'));
        }
        exactNote();
      }
      var c = state.constraints;
      if (c.min_price_usd != null) pMin.value = String(c.min_price_usd);
      if (c.max_price_usd != null) pMax.value = String(c.max_price_usd);
    }

    function applyUrl() {
      ++searchTurn;
      ++renderTurn;
      inFlight = false;
      var u = readUrl();
      state.constraints = u.constraints;
      state.query = u.query;
      state.page = u.page;
      state.brief = '';
      state.referenceStrength = 'none';
      state.rankingAnchors = [];
      state.rugs = null;
      cardCache = {};
      var hit = cacheGet(searchKey());
      if (hit) {
        state.rugs = hit.rugs;
        state.brief = hit.brief || '';
        state.us = hit.us === true;
        state.referenceStrength = hit.referenceStrength || 'none';
        state.rankingAnchors = hit.rankingAnchors || [];
      }
      syncBar();
      prefillPanels();
      setStatus('');
      if (!filtered() && state.page <= 1) {
        if (liveShowing) restoreDefault();
        snapshotGood();
        renderPager();
        return;
      }
      if (state.rugs) {
        snapshotGood();
        goToPage(state.page, false);
        return;
      }
      // Nothing remembered: the one search that recreates this address.
      if (state.query) runTyped(state.query, { replay: true, page: state.page, push: false });
      else if (filtered()) runFast('filters', { replay: true, page: state.page, push: false });
      else fetchBase(state.page, false);
    }

    window.addEventListener('popstate', function () { applyUrl(); });

    // ---- the phone layout -------------------------------------------------

    /* Below 740px the toolbar reads bottom-heavy: chips, the box on its own
       row, the rule, and then one centred line carrying the count and both
       actions directly above the first rugs. The count and the actions are
       the same two elements either way - they are moved rather than
       duplicated, so every handler already wired above still holds. */
    var narrow = window.matchMedia('(max-width: 740px)');
    var bar = $('.rft__bar');
    var spacer = $('[data-rft-spacer]');
    var lineEl = $('[data-rft-line]');
    var metaEl = $('[data-rft-meta]');
    var actionsEl = $('[data-rft-actions]');
    var countEl = $('[data-rft-count]');
    var askOpen = $('[data-rft-ask-open]');

    function placeLine() {
      if (narrow.matches) {
        if (countEl.parentNode !== lineEl) lineEl.appendChild(countEl);
        if (actionsEl.parentNode !== lineEl) lineEl.appendChild(actionsEl);
      } else {
        // Back where the desktop has always had them: the count at the end of
        // the filter row, the actions at the end of the bar.
        if (countEl.parentNode !== metaEl) metaEl.appendChild(countEl);
        if (actionsEl.parentNode !== bar) bar.appendChild(actionsEl);
      }
    }

    /* On a phone the box is one seat of a pill that also has to hold Size and
       Price, and the long invitation runs out of it mid-word. The short one
       is an attribute on the input rather than a string in here, so the two
       readings of the same prompt sit next to each other in the snippet. */
    var askLong = askInput.getAttribute('placeholder') || '';
    var askShort = askInput.getAttribute('data-rft-ask-short') || askLong;
    function fitAsk() {
      askInput.setAttribute('placeholder', narrow.matches ? askShort : askLong);
    }

    /* How far down the screen a fixed strip has to start to clear whatever
       the theme is already holding up there. Read from the same custom
       properties the stylesheet uses, so the two cannot disagree. */
    function stickOffset() {
      var cs = getComputedStyle(document.documentElement);
      function px(name) { return parseFloat(cs.getPropertyValue(name)) || 0; }
      return px('--header-height') * px('--enable-sticky-header') +
        px('--announcement-bar-height') * px('--enable-sticky-announcement-bar');
    }

    var stuck = false;
    var asking = false;
    var ticking = false;

    function setStuck(on) {
      if (on === stuck) return;
      // Measured while the bar is still in the flow: once it goes fixed its
      // height is the strip's, not the space it has to leave behind.
      if (on) spacer.style.height = bar.offsetHeight + 'px';
      stuck = on;
      root.classList.toggle('rft--stuck', on);
      if (!on) {
        spacer.style.height = '';
        setAsking(false);
      }
    }

    function setAsking(on) {
      if (on === asking) return;
      asking = on;
      root.classList.toggle('rft--asking', on);
      askOpen.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) askInput.focus();
    }

    function syncStuck() {
      if (!narrow.matches) { setStuck(false); return; }
      // The spacer stands where the bar belongs in both states, so this one
      // question answers for sticking and for letting go again.
      setStuck(spacer.getBoundingClientRect().top <= stickOffset());
    }

    function scheduleStuck() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; syncStuck(); });
    }

    askOpen.addEventListener('click', function () { setAsking(!asking); });

    askInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !asking) return;
      setAsking(false);
      askOpen.focus();
    });

    window.addEventListener('scroll', scheduleStuck, { passive: true });
    window.addEventListener('resize', scheduleStuck);

    function onWidthChange() { placeLine(); fitAsk(); syncStuck(); }
    if (narrow.addEventListener) narrow.addEventListener('change', onWidthChange);
    else if (narrow.addListener) narrow.addListener(onWidthChange);

    // ---- boot ------------------------------------------------------------

    // The count and the actions take their places before the toolbar is
    // shown, so a phone never sees the desktop arrangement flash past.
    placeLine();
    fitAsk();
    root.hidden = false;
    // ...and only then is there anything to measure.
    syncStuck();
    /* The concierge module decided the stand-down before this boot could prove
       the toolbar live, so it decided it on stale information. Ask it to decide
       again rather than hiding the entry from here: standing down means
       something different on a sold rug than on an available one (see
       syncStandDown), and a second copy of that rule would drift from the
       first. */
    RC.syncStandDown();
    snapshotGood();
    renderPager();
    var initial = readUrl();
    if (initial.constraints.size_range || initial.constraints.min_price_usd != null ||
        initial.constraints.max_price_usd != null || initial.query || initial.page > 1) {
      applyUrl();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* ==========================================================================
   The 48 hour hold (snippets/rug-hold.liquid)
   --------------------------------------------------------------------------
   "Hold this rug" under Add to Cart, opening a dialog, posting to
   POST /apps/offers/hold (the offers App Proxy; inventory-sync src/holds).

   Why a dialog rather than an inline panel: the block sits directly above the
   trust row and the Rug Finder panel, and growing it in place pushes both
   down under the shopper mid-click.

   The markup ships hidden and this unhides it, so JS-off never shows a button
   that could only lie: a hold is a promise recorded by a fetch. Same stance as
   the rug alert capture buttons.

   The deadline shown on the page comes from the server's own answer
   (heldUntilLabel), never computed here, so the page and the email can never
   quote two different times.

   Copy rule, binding: no countdown, no em or en dashes, no internal reference.

   Analytics: the rug_hold_* dataLayer family below. GTM forwards it to GA4
   with one regex trigger and one tag (inventory-sync
   docs/gtm/rug_hold_events.json), the same way it forwards rug_alert_*. An
   event name outside that prefix never reaches GA4 without a container
   change.

   Covered by tests/rug-hold.spec.js.
   ========================================================================== */
(function () {
  'use strict';

  var API_BASE = window.RS_OFFER_API_BASE ||
    ((location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://127.0.0.1:8787'
      : '');
  var HOLD_PATH = '/apps/offers/hold';
  var STORAGE_PREFIX = 'rs_hold_';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* Customer-facing, so: no em or en dashes, and nothing that names a system. */
  var COPY = {
    badEmail: 'That email does not look right. Could you check it?',
    tooMany: 'That is a few holds today already. Reply to any of our emails and we will help.',
    gone: 'This one is no longer available to hold. We are sorry.',
    failed: 'Something went wrong at our end. Please try again in a moment.'
  };

  /* The reasons a hold can fail, as GA4 sees them. Only these strings ever
     reach the dataLayer: a server that one day answers with something new is
     filed as 'error' rather than trusted to be a sensible dimension value. */
  var FAIL_REASONS = ['invalid_email', 'rate_limited', 'unavailable', 'unknown_variant'];

  function failReason(code) {
    return FAIL_REASONS.indexOf(code) === -1 ? 'error' : code;
  }

  /* The rug_hold_* family. The address is the one thing this feature
     collects, and it never reaches the dataLayer; the concierge funnel's
     allowlist rule holds here too. Which rug it was is GA4's page path. */
  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined) {
          payload[key] = params[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  function errorCopy(code) {
    if (code === 'invalid_email') return COPY.badEmail;
    if (code === 'rate_limited') return COPY.tooMany;
    if (code === 'unavailable' || code === 'unknown_variant') return COPY.gone;
    return COPY.failed;
  }

  function browserZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
  }

  function attribution() {
    try {
      return typeof window.RS_ATTRIBUTION === 'function' ? window.RS_ATTRIBUTION() : undefined;
    } catch (e) {
      return undefined;
    }
  }

  /* Per-viewer convenience only: a reload keeps showing the confirmation the
     server already sent. Never the source of truth, and every access is
     guarded because private windows throw rather than return empty. */
  function remember(key, label, untilIso) {
    try {
      localStorage.setItem(
        STORAGE_PREFIX + key,
        JSON.stringify({ label: label, until: untilIso })
      );
    } catch (e) { /* storage unavailable: the confirmation is still on screen */ }
  }

  function recall(key) {
    try {
      var raw = localStorage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.label || !saved.until) return null;
      if (new Date(saved.until).getTime() <= Date.now()) {
        localStorage.removeItem(STORAGE_PREFIX + key);
        return null;
      }
      return saved;
    } catch (e) {
      return null;
    }
  }

  /* The offer widget owns its own modal; clicking its badge is the whole
     public surface it exposes, and reaching past that into its internals
     would couple the two widgets for nothing. */
  function openOfferWidget() {
    var badge = document.querySelector('.rs-offer-badge');
    if (badge) badge.click();
    return Boolean(badge);
  }

  function showConfirmation(root, label) {
    var cta = root.querySelector('[data-rs-hold-cta]');
    var done = root.querySelector('[data-rs-hold-done]');
    var until = root.querySelector('[data-rs-hold-until]');
    if (until) until.textContent = label;
    if (cta) cta.hidden = true;
    if (done) done.hidden = false;
  }

  function mount(root) {
    if (root.dataset.rsHoldReady === '1') return;
    root.dataset.rsHoldReady = '1';

    var dialog = root.querySelector('.rs-hold__dialog');
    var form = root.querySelector('.rs-hold__form');
    var opener = root.querySelector('.rs-hold__open');
    var closer = root.querySelector('[data-rs-hold-close]');
    var submit = root.querySelector('.rs-hold__submit');
    var errorBox = root.querySelector('.rs-hold__error');
    var offerLink = root.querySelector('[data-rs-hold-offer]');
    var variantId = root.getAttribute('data-variant-id') || '';
    var handle = root.getAttribute('data-product-handle') || '';
    if (!dialog || !form || !opener) return;

    root.hidden = false;

    /* A hold placed earlier in this browser: show it rather than offering to
       place the same one again. The server is idempotent for that case too,
       so a stale entry costs nothing worse than a second identical answer. */
    var saved = recall(variantId);
    if (saved) showConfirmation(root, saved.label);

    function setError(message) {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    }

    /* Whether the dialog now closing ended in a hold. Set on success, read
       and reset by the close event, so each opening reports one outcome. */
    var placed = false;

    function close() {
      if (dialog.close) dialog.close();
      else dialog.removeAttribute('open');
    }

    /* Every way out lands here: the X, the backdrop, Escape, and the close
       after a hold succeeds. The abandoned ones are the number worth having. */
    dialog.addEventListener('close', function () {
      track('rug_hold_closed', { hold_outcome: placed ? 'placed' : 'abandoned' });
      placed = false;
    });

    opener.addEventListener('click', function () {
      setError('');
      placed = false;
      track('rug_hold_opened');
      /* showModal brings the focus trap, the Escape key and the return of
         focus to the opener. Older engines fall back to a non-modal open. */
      if (dialog.showModal) dialog.showModal();
      else dialog.setAttribute('open', '');
    });

    if (closer) closer.addEventListener('click', close);
    window.RugDialogEscape(dialog, close);

    /* Click outside the card. The dialog IS the backdrop's event target, so a
       click landing on the element itself rather than its contents is one. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    if (offerLink) {
      offerLink.addEventListener('click', function () {
        track('rug_hold_offer_clicked');
        if (!openOfferWidget()) {
          /* No widget on this page: the rug's own page always has one. */
          location.href = '/products/' + encodeURIComponent(handle) + '?offer=1';
        }
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var emailField = form.querySelector('input[name="email"]');
      var noteField = form.querySelector('textarea[name="note"]');
      var optin = form.querySelector('input[name="marketing"]');
      var email = (emailField && emailField.value || '').trim();

      if (!EMAIL_RE.test(email)) {
        setError(COPY.badEmail);
        track('rug_hold_failed', { hold_reason: 'invalid_email' });
        if (emailField) emailField.focus();
        return;
      }
      var marketing = Boolean(optin && optin.checked);
      var note = (noteField && noteField.value || '').trim();

      setError('');
      if (submit) submit.disabled = true;

      fetch(API_BASE + HOLD_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variantId: variantId,
          productHandle: handle,
          email: email,
          note: note || undefined,
          marketing: marketing,
          tz: browserZone() || undefined,
          attribution: attribution()
        })
      })
        .then(function (response) {
          return response.json().catch(function () { return {}; });
        })
        .then(function (body) {
          if (!body || body.ok !== true || !body.heldUntilLabel) {
            setError(errorCopy(body && body.error));
            track('rug_hold_failed', { hold_reason: failReason(body && body.error) });
            if (submit) submit.disabled = false;
            return;
          }
          remember(variantId, body.heldUntilLabel, body.heldUntil);
          /* hold_marketing is the new-arrivals box: whether this hold also
             won a subscriber, the point of asking for the address at all. */
          track('rug_hold_placed', { hold_marketing: marketing, hold_note: Boolean(note) });
          placed = true;
          close();
          showConfirmation(root, body.heldUntilLabel);
        })
        .catch(function () {
          setError(COPY.failed);
          track('rug_hold_failed', { hold_reason: 'network' });
          if (submit) submit.disabled = false;
        });
    });
  }

  function autoMount() {
    var roots = document.querySelectorAll('[data-rs-hold]');
    for (var i = 0; i < roots.length; i++) mount(roots[i]);

    /* The hold email's second button lands here. The offer widget mounts on
       the same DOMContentLoaded pass, so give it the next frame to appear
       before asking it to open. */
    if (/[?&]offer=1\b/.test(location.search)) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(openOfferWidget);
      });
    }
  }

  window.RugHold = { mount: mount, autoMount: autoMount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
})();

/* ==========================================================================
   Exit survey (rug pages)
   --------------------------------------------------------------------------
   One tap question for a shopper who studied a rug and is leaving without
   acting on it, and one for a shopper who opened Make an Offer and closed it
   with nothing sent. Any answer leads to an optional email ask that reaches
   the store inbox as a contact message, or to chat.

   The rug question waits for engagement (20 seconds of visible time, or a
   scroll past the photos) and then a leaving signal: a minute idle, a return
   to the tab, a fast scroll back to the top, or (mouse only) the pointer
   leaving through the top of the window. There is no back-button trap.

   Gates, checked when the signal fires: once per visitor and once per tab
   session, and storage must work; the offer button is showing and no offer
   is stored for this rug; no hold placed; no click this page on the offer
   button, Add to Cart, the payment buttons or the hold; no drawer, dialog,
   offer form, Rug Finder or chat open. The offer question skips the
   per-visitor key and allows the offer button click.

   A div rather than <dialog>.showModal(): the email goes through Shopify's
   hCaptcha, whose challenge renders in the body and would sit under a
   top-layer modal. The form has no action for the same reason: Shopify's
   captcha bootstrap binds any form posting to /contact and then submits it
   natively, so this one binds itself through Shopify.captcha.protect and
   stays a fetch.

   Analytics: the exit_survey_* dataLayer family (inventory-sync
   src/analytics_sync/registry.ts). Only fixed codes reach the dataLayer and
   Clarity, never the email.

   Copy rule, binding: no em or en dashes.

   Covered by tests/exit-survey.spec.js.
   ========================================================================== */
(function () {
  'use strict';

  var SEEN_KEY = 'rs_exit_survey_seen';
  var SESSION_KEY = 'rs_exit_survey_session';
  var OFFER_KEY_PREFIX = 'rs_offer_'; // the offer widget's stored offer, per rug
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var ACTED_SELECTOR = '#AddToCart, .product-form__add-button, .shopify-payment-button, .rs-hold__open';
  var OFFER_BADGE_SELECTOR = '.rs-offer-badge';
  var ACTIVITY_EVENTS = ['pointermove', 'keydown', 'scroll', 'touchstart', 'click'];
  var SCROLL_WINDOW_MS = 800;
  var SCROLL_TOP_PX = 100;
  var SCROLL_FLICK_PX = 500;
  var SWIPE_CLOSE_PX = 80;
  var CAPTCHA_READY_MS = 8000;

  // Test fixtures shorten these through window globals.
  function timing(name, fallback) {
    return typeof window[name] === 'number' ? window[name] : fallback;
  }
  var ENGAGE_MS = timing('__RS_EXIT_SURVEY_ENGAGE_MS', 20000);
  var IDLE_MS = timing('__RS_EXIT_SURVEY_IDLE_MS', 60000);
  var RETURN_MS = timing('__RS_EXIT_SURVEY_RETURN_MS', 600);
  var OFFER_MS = timing('__RS_EXIT_SURVEY_OFFER_MS', 350);
  var CAPTCHA_MS = timing('__RS_EXIT_SURVEY_CAPTCHA_MS', 20000);

  var SURVEYS = {
    rug_exit: {
      eyebrow: 'Quick one before you go',
      question: 'What\'s keeping you from making an offer on this rug?',
      answers: [
        { code: 'price', label: 'The price is more than I planned' },
        { code: 'fit', label: 'Not sure it\'s the right size or colors for my room' },
        { code: 'photos', label: 'I want more photos, or to see it in person' },
        { code: 'shipping_returns_condition', label: 'Unsure about shipping, returns or condition' },
        { code: 'browsing', label: 'Just browsing today' },
        { code: 'other', label: 'Something else' }
      ]
    },
    offer_abandon: {
      eyebrow: 'One quick question',
      question: 'You opened Make an Offer but didn\'t send one. What stopped you?',
      answers: [
        { code: 'unsure_amount', label: 'I didn\'t know what to offer' },
        { code: 'email_wary', label: 'I didn\'t want to share my email' },
        { code: 'changed_mind', label: 'I changed my mind about the rug' },
        { code: 'see_in_room', label: 'I wanted to see it in my room first' },
        { code: 'other', label: 'Something else' }
      ]
    }
  };

  var COPY = {
    noThanks: 'No thanks',
    done: 'No thanks, I\'m done',
    helpEyebrow: 'Thanks, that helps',
    helpTitle: 'Want a hand with this rug?',
    helpLead: 'Leave your email and one of us will reply within a day with more photos, ' +
      'exact measurements, or a price that could work. No newsletter.',
    noEmailLead: 'No email needed. Chat with us now for more photos, exact measurements, ' +
      'or a price that could work.',
    thanksTitle: 'Thank you for telling us.',
    emailLabel: 'Email',
    send: 'Send',
    sending: 'Sending…',
    chatLink: 'Or chat with us now',
    chatButton: 'Chat with us',
    badEmail: 'Please enter a valid email address.',
    failed: 'We could not send that just now. Please try again, or chat with us.',
    failedNoChat: 'We could not send that just now. Please try again.',
    sentEyebrow: 'Sent',
    sentTitle: 'Thank you. We\'ll be in touch.',
    sentLead: 'Look for our reply in your inbox within a day.',
    back: 'Back to the rug',
    captchaLead: 'Protected by hCaptcha. Its ',
    captchaPrivacy: 'Privacy Policy',
    captchaJoin: ' and ',
    captchaTerms: 'Terms',
    captchaTail: ' apply.',
    // The store inbox only, never shown to the shopper.
    request: 'Rug page survey',
    contactBody: 'Not written by the shopper: they tapped the answer above and left ' +
      'their email. We told them to expect a reply within a day.'
  };

  var PATH_CROSS = 'M6 6l12 12M18 6L6 18';
  var PATH_CHEVRON = 'M9 6l6 6-6 6';
  var PATH_CHAT = 'M4 5h16v11H8l-4 4V5z';

  var page = {
    shown: false,
    acted: false,          // Add to Cart, a payment button or the hold
    offerTapped: false,
    engaged: false,
    engageTimer: null,
    engageStart: 0,
    engageLeft: ENGAGE_MS,
    lastActivity: Date.now(),
    idleTimer: null,
    awayEngaged: false,    // the tab was hidden after engagement
    returnTimer: null,
    offerTimer: null,
    scrolls: [],
    watching: false
  };
  var ui = null;
  var current = null;
  var chatOpen = false;

  /* ---- analytics -------------------------------------------------------- */

  // GTM keeps a key until a later push sets it, so every push names every key
  // and an undefined clears what an earlier event left (a failed send's error).
  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    for (var key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) payload[key] = params[key];
    }
    window.dataLayer.push(payload);
  }

  function params(s, extra) {
    var out = { survey_name: s.name, survey_trigger: s.trigger, survey_answer: s.answer, survey_error: undefined };
    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) out[key] = extra[key];
      }
    }
    return out;
  }

  function clarity() {
    if (typeof window.clarity !== 'function') return;
    try { window.clarity.apply(window, arguments); } catch (err) { /* never surfaces */ }
  }

  /* ---- chat ------------------------------------------------------------- */

  function chatAvailable() {
    var api = window.tidioChatApi;
    return Boolean(api && typeof api.open === 'function');
  }

  // Tidio's API has no listeners; mount() hears its open and close events, and
  // a chat already open at load shows as the .chat panel in its shadow root.
  function chatIsOpen() {
    if (chatOpen) return true;
    var host = document.getElementById('tidio-chat');
    var root = host && host.shadowRoot;
    return Boolean(root && root.querySelector('#tidio-chat-root > .chat'));
  }

  /* ---- gates ------------------------------------------------------------ */

  function rug() {
    var root = document.querySelector('[data-rs-offer]');
    return {
      handle: root ? root.getAttribute('data-product-handle') || '' : '',
      title: root ? root.getAttribute('data-product-title') || '' : '',
      sku: root ? root.getAttribute('data-product-sku') || '' : ''
    };
  }

  // Null when storage throws: a shopper we cannot remember is never asked.
  function stored(handle) {
    try {
      return {
        seen: window.localStorage.getItem(SEEN_KEY) !== null,
        session: window.sessionStorage.getItem(SESSION_KEY) !== null,
        offer: Boolean(handle) && window.localStorage.getItem(OFFER_KEY_PREFIX + handle) !== null
      };
    } catch (err) {
      return null;
    }
  }

  function remember() {
    try {
      window.localStorage.setItem(SEEN_KEY, String(Date.now()));
      window.sessionStorage.setItem(SESSION_KEY, '1');
      return true;
    } catch (err) {
      return false;
    }
  }

  function badgeShowing() {
    var badge = document.querySelector(OFFER_BADGE_SELECTOR);
    return Boolean(document.querySelector('[data-rs-offer][data-rs-offer-init]') &&
      badge && badge.getClientRects().length);
  }

  function holdPlaced() {
    return Boolean(document.querySelector('[data-rs-hold-done]:not([hidden])'));
  }

  function somethingOpen() {
    var html = document.documentElement.classList;
    return document.visibilityState !== 'visible' ||
      html.contains('lock-all') || html.contains('lock-mobile') || html.contains('rs-offer-lock') ||
      Boolean(document.querySelector('dialog[open]')) ||
      document.body.classList.contains('rug-finder-drawer-open') ||
      chatIsOpen();
  }

  function tryRugQuestion(trigger) {
    if (page.shown || !page.engaged || page.acted || page.offerTapped) return;
    var info = rug();
    var keys = stored(info.handle);
    if (!keys || keys.seen || keys.session || keys.offer) return;
    if (!badgeShowing() || holdPlaced() || somethingOpen()) return;
    show('rug_exit', trigger);
  }

  function tryOfferQuestion() {
    page.offerTimer = null;
    if (page.shown || page.acted) return;
    var keys = stored(rug().handle);
    if (!keys || keys.session || keys.offer) return;
    if (holdPlaced() || somethingOpen()) return;
    show('offer_abandon', 'offer_closed');
  }

  /* ---- leaving signals -------------------------------------------------- */

  function engage() {
    if (page.engaged) return;
    page.engaged = true;
    clearTimeout(page.engageTimer);
    page.engageTimer = null;
  }

  function resumeEngageClock() {
    if (page.engaged || page.engageTimer !== null) return;
    page.engageStart = Date.now();
    page.engageTimer = setTimeout(engage, Math.max(0, page.engageLeft));
  }

  function pauseEngageClock() {
    if (page.engageTimer === null) return;
    clearTimeout(page.engageTimer);
    page.engageTimer = null;
    page.engageLeft -= Date.now() - page.engageStart;
  }

  function scheduleIdle() {
    clearTimeout(page.idleTimer);
    page.idleTimer = null;
    if (!page.watching || document.visibilityState !== 'visible') return;
    page.idleTimer = setTimeout(function () {
      page.idleTimer = null;
      if (Date.now() - page.lastActivity >= IDLE_MS) {
        tryRugQuestion('idle');
        page.lastActivity = Date.now();
      }
      scheduleIdle();
    }, Math.max(0, page.lastActivity + IDLE_MS - Date.now()));
  }

  function onActivity() {
    page.lastActivity = Date.now();
  }

  function onVisibility() {
    if (document.visibilityState === 'visible') {
      resumeEngageClock();
      page.lastActivity = Date.now();
      scheduleIdle();
      if (page.awayEngaged) {
        page.awayEngaged = false;
        clearTimeout(page.returnTimer);
        page.returnTimer = setTimeout(function () {
          page.returnTimer = null;
          tryRugQuestion('tab_return');
        }, RETURN_MS);
      }
    } else {
      pauseEngageClock();
      clearTimeout(page.idleTimer);
      page.idleTimer = null;
      clearTimeout(page.returnTimer);
      page.returnTimer = null;
      if (page.engaged) page.awayEngaged = true;
    }
  }

  function onScroll() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (!page.engaged) {
      var media = document.querySelector('.product__media');
      if (media && media.getBoundingClientRect().bottom < 0) engage();
    }
    var now = Date.now();
    var highest = y;
    page.scrolls.push({ t: now, y: y });
    page.scrolls = page.scrolls.filter(function (sample) {
      if (now - sample.t > SCROLL_WINDOW_MS) return false;
      if (sample.y > highest) highest = sample.y;
      return true;
    });
    if (y <= SCROLL_TOP_PX && highest - y >= SCROLL_FLICK_PX) {
      page.scrolls = [];
      tryRugQuestion('scroll_top');
    }
  }

  function onMouseOut(event) {
    if (!event.relatedTarget && event.clientY <= 0) tryRugQuestion('pointer_exit');
  }

  function finePointer() {
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch (err) {
      return false;
    }
  }

  function startWatching() {
    page.watching = true;
    for (var i = 0; i < ACTIVITY_EVENTS.length; i++) {
      window.addEventListener(ACTIVITY_EVENTS[i], onActivity, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('scroll', onScroll, { passive: true });
    if (finePointer()) document.addEventListener('mouseout', onMouseOut);
    if (document.visibilityState === 'visible') {
      resumeEngageClock();
      scheduleIdle();
    }
  }

  function stopWatching() {
    if (!page.watching) return;
    page.watching = false;
    for (var i = 0; i < ACTIVITY_EVENTS.length; i++) {
      window.removeEventListener(ACTIVITY_EVENTS[i], onActivity, { passive: true, capture: true });
    }
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('scroll', onScroll, { passive: true });
    document.removeEventListener('mouseout', onMouseOut);
    clearTimeout(page.engageTimer);
    clearTimeout(page.idleTimer);
    clearTimeout(page.returnTimer);
    page.engageTimer = page.idleTimer = page.returnTimer = null;
  }

  /* ---- the sheet -------------------------------------------------------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function icon(path) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var line = document.createElementNS(ns, 'path');
    line.setAttribute('d', path);
    svg.appendChild(line);
    return svg;
  }

  function button(className, text, onClick) {
    var node = el('button', className, text);
    node.type = 'button';
    node.addEventListener('click', onClick);
    return node;
  }

  function build() {
    var overlay = el('div', 'rs-exit');
    overlay.hidden = true;
    var sheet = el('div', 'rs-exit__sheet');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-labelledby', 'rs-exit-title');
    var drag = el('div', 'rs-exit__drag');
    var grip = el('div', 'rs-exit__grip');
    grip.setAttribute('aria-hidden', 'true');
    var top = el('div', 'rs-exit__top');
    var eyebrow = el('p', 'rs-exit__eyebrow');
    var closer = button('rs-exit__close', null, function () { close(); });
    closer.setAttribute('aria-label', 'Close');
    closer.appendChild(icon(PATH_CROSS));
    var title = el('h2', 'rs-exit__title');
    title.id = 'rs-exit-title';
    title.tabIndex = -1;
    var body = el('div', 'rs-exit__body');

    top.appendChild(eyebrow);
    top.appendChild(closer);
    drag.appendChild(grip);
    drag.appendChild(top);
    sheet.appendChild(drag);
    sheet.appendChild(title);
    sheet.appendChild(body);
    overlay.appendChild(sheet);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });
    bindSwipe(drag, sheet);
    return { overlay: overlay, sheet: sheet, eyebrow: eyebrow, title: title, body: body };
  }

  // A downward drag on the grip row dismisses the sheet; a short one snaps back.
  function bindSwipe(handle, sheet) {
    var startY = null;
    var pointer = null;
    var dy = 0;
    handle.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' || event.target.closest('.rs-exit__close')) return;
      startY = event.clientY;
      pointer = event.pointerId;
      dy = 0;
      sheet.style.transition = 'none';
      try { handle.setPointerCapture(pointer); } catch (err) { /* moves still arrive while over the row */ }
    });
    handle.addEventListener('pointermove', function (event) {
      if (startY === null || event.pointerId !== pointer) return;
      dy = Math.max(0, event.clientY - startY);
      sheet.style.transform = dy ? 'translateY(' + dy + 'px)' : '';
    });
    function end(event) {
      if (startY === null || event.pointerId !== pointer) return;
      startY = null;
      sheet.style.transition = '';
      sheet.style.transform = '';
      if (dy > SWIPE_CLOSE_PX) close();
    }
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  function focusables() {
    var nodes = ui.sheet.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])');
    return Array.prototype.filter.call(nodes, function (node) {
      return node.offsetParent !== null;
    });
  }

  function onKeydown(event) {
    if (!current || current.closed) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var nodes = focusables();
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    var active = document.activeElement;
    var inside = ui.sheet.contains(active);
    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault();
      first.focus();
    }
  }

  function step(eyebrow, title) {
    ui.eyebrow.textContent = eyebrow;
    ui.title.textContent = title;
    while (ui.body.firstChild) ui.body.removeChild(ui.body.firstChild);
    try { ui.title.focus({ preventScroll: true }); } catch (err) { /* noop */ }
  }

  function skip(text) {
    return button('rs-exit__skip', text, function () { close(); });
  }

  function chatLink(s) {
    var link = button('rs-exit__chat', null, function () { openChat(s); });
    link.appendChild(icon(PATH_CHAT));
    link.appendChild(el('span', null, COPY.chatLink));
    return link;
  }

  function renderQuestion(s) {
    var survey = SURVEYS[s.name];
    step(survey.eyebrow, survey.question);
    var list = el('div', 'rs-exit__answers');
    survey.answers.forEach(function (choice) {
      var answer = button('rs-exit__answer', null, function () { choose(s, choice); });
      answer.appendChild(el('span', null, choice.label));
      if (choice.code === 'other') {
        answer.classList.add('rs-exit__answer--more');
        answer.appendChild(icon(PATH_CHEVRON));
      }
      list.appendChild(answer);
    });
    ui.body.appendChild(list);
    ui.body.appendChild(skip(COPY.noThanks));
  }

  function choose(s, choice) {
    if (s.answer || s.closed) return;
    s.answer = choice.code;
    s.answerLabel = choice.label;
    track('exit_survey_answered', params(s));
    clarity('set', 'exit_survey_answer', choice.code);
    clarity('event', 'exit_survey_answered');
    if (choice.code !== 'email_wary') renderHelp(s);
    else if (chatAvailable()) renderChatFirst(s);
    else renderThanks(s, COPY.helpEyebrow, COPY.thanksTitle, null);
  }

  // The shopper said they would rather not share an email, so do not ask for one.
  function renderChatFirst(s) {
    step(COPY.helpEyebrow, COPY.helpTitle);
    ui.body.appendChild(el('p', 'rs-exit__lead', COPY.noEmailLead));
    ui.body.appendChild(button('rs-exit__send', COPY.chatButton, function () { openChat(s); }));
    ui.body.appendChild(skip(COPY.done));
  }

  function renderThanks(s, eyebrow, title, lead) {
    step(eyebrow, title);
    if (lead) ui.body.appendChild(el('p', 'rs-exit__lead', lead));
    ui.body.appendChild(button('rs-exit__back', COPY.back, function () { close(); }));
  }

  function addHidden(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  function renderHelp(s) {
    step(COPY.helpEyebrow, COPY.helpTitle);
    var info = rug();
    var form = el('form', 'rs-exit__form');
    form.noValidate = true;
    addHidden(form, 'form_type', 'contact');
    addHidden(form, 'utf8', '✓');
    addHidden(form, 'contact[Request]', COPY.request);
    addHidden(form, 'contact[Rug]', info.title);
    addHidden(form, 'contact[Rug URL]', location.origin + location.pathname);
    addHidden(form, 'contact[SKU]', info.sku);
    addHidden(form, 'contact[Question]', SURVEYS[s.name].question);
    addHidden(form, 'contact[Answer]', s.answerLabel);
    addHidden(form, 'contact[body]', COPY.contactBody);

    var label = el('label', 'rs-exit__label', COPY.emailLabel);
    label.htmlFor = 'rs-exit-email';
    var input = el('input', 'rs-exit__input');
    input.type = 'email';
    input.id = 'rs-exit-email';
    input.name = 'contact[email]';
    input.setAttribute('autocomplete', 'email');
    input.required = true;
    input.spellcheck = false;
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('aria-describedby', 'rs-exit-error');
    var error = el('p', 'rs-exit__error');
    error.id = 'rs-exit-error';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    var send = el('button', 'rs-exit__send', COPY.send);
    send.type = 'submit';
    form.appendChild(label);
    form.appendChild(input);
    form.appendChild(error);
    form.appendChild(send);

    s.form = form;
    s.input = input;
    s.error = error;
    s.sendButton = send;
    // Shopify's captcha binder captures form.submit and calls it once the
    // challenge passes, so the send it finishes is this fetch, not a page load.
    form.submit = function () { sendNow(s); };
    form.addEventListener('submit', function (event) { onSubmit(s, event); });

    ui.body.appendChild(el('p', 'rs-exit__lead', COPY.helpLead));
    ui.body.appendChild(form);
    if (chatAvailable()) ui.body.appendChild(chatLink(s));
    ui.body.appendChild(skip(COPY.done));
    bindCaptcha(s);
  }

  function bindCaptcha(s) {
    var captcha = window.Shopify && window.Shopify.captcha;
    if (!captcha || typeof captcha.protect !== 'function') return;
    // Present before the bind, so Shopify skips its floating badge.
    var note = el('p', 'rs-exit__captcha');
    note.setAttribute('data-spam-detection-disclaimer', '');
    note.appendChild(document.createTextNode(COPY.captchaLead));
    note.appendChild(externalLink('https://hcaptcha.com/privacy', COPY.captchaPrivacy));
    note.appendChild(document.createTextNode(COPY.captchaJoin));
    note.appendChild(externalLink('https://hcaptcha.com/terms', COPY.captchaTerms));
    note.appendChild(document.createTextNode(COPY.captchaTail));
    ui.body.appendChild(note);
    s.captchaReady = new Promise(function (resolve) {
      try { captcha.protect(s.form, resolve); } catch (err) { resolve(); }
    });
  }

  function externalLink(href, text) {
    var link = el('a', null, text);
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener';
    return link;
  }

  function within(promise, ms) {
    return new Promise(function (resolve) {
      var timer = setTimeout(resolve, ms);
      function done() { clearTimeout(timer); resolve(); }
      promise.then(done, done);
    });
  }

  function setBusy(s, busy) {
    s.sendButton.disabled = busy;
    s.sendButton.textContent = busy ? COPY.sending : COPY.send;
  }

  // A failed send leaves a good address unmarked.
  function showError(s, message, badEmail) {
    s.error.textContent = message;
    s.error.hidden = false;
    if (badEmail) s.input.setAttribute('aria-invalid', 'true');
    else s.input.removeAttribute('aria-invalid');
  }

  function clearError(s) {
    s.error.hidden = true;
    s.error.textContent = '';
    s.input.removeAttribute('aria-invalid');
  }

  function onSubmit(s, event) {
    event.preventDefault();
    // Ahead of the captcha binder's own submit listener, which would send
    // without waiting for this check.
    event.stopImmediatePropagation();
    if (s.closed || s.phase === 'waiting' || s.phase === 'posting' || s.phase === 'sent') return;
    var email = s.input.value.trim();
    if (!EMAIL_RE.test(email)) {
      showError(s, COPY.badEmail, true);
      s.input.focus();
      return;
    }
    s.input.value = email;
    clearError(s);
    setBusy(s, true);
    s.phase = 'waiting';
    var ready = s.captchaReady ? within(s.captchaReady, CAPTCHA_READY_MS) : Promise.resolve();
    ready.then(function () {
      if (s.closed || s.phase !== 'waiting') return;
      if (!s.form.hasAttribute('data-hcaptcha-bound')) {
        sendNow(s);
        return;
      }
      // The binder only logs a challenge the shopper closed, so give up after
      // a while and let them retry. A late pass still sends (sendNow).
      s.watchdog = setTimeout(function () {
        s.watchdog = null;
        if (!s.closed && s.phase === 'waiting') fail(s, 'captcha');
      }, CAPTCHA_MS);
      try {
        s.form.submit();
      } catch (err) {
        clearTimeout(s.watchdog);
        s.watchdog = null;
        fail(s, 'captcha');
      }
    });
  }

  function sendNow(s) {
    if (s.closed || s.phase === 'posting' || s.phase === 'sent') return;
    clearTimeout(s.watchdog);
    s.watchdog = null;
    s.phase = 'posting';
    setBusy(s, true);
    clearError(s);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'contact', { method: 'POST', body: new FormData(s.form) })
      .then(function (response) {
        // Shopify redirects a good contact post to a URL carrying
        // contact_posted=true; anything else re-renders at the same URL.
        var ok = Boolean(response && response.ok && response.url &&
          response.url.indexOf('contact_posted=true') !== -1);
        return ok ? 'sent' : 'not_posted';
      }, function () {
        return 'unreachable';
      })
      .then(function (outcome) {
        if (outcome === 'sent') {
          s.phase = 'sent';
          track('exit_survey_email_sent', params(s));
          if (!s.closed) renderThanks(s, COPY.sentEyebrow, COPY.sentTitle, COPY.sentLead);
          return;
        }
        fail(s, outcome);
      });
  }

  function fail(s, reason) {
    s.phase = 'failed';
    track('exit_survey_email_failed', params(s, { survey_error: reason }));
    if (s.closed) return;
    setBusy(s, false);
    showError(s, chatAvailable() ? COPY.failed : COPY.failedNoChat);
  }

  function openChat(s) {
    if (s.chatOpened) return;
    s.chatOpened = true;
    track('exit_survey_chat_opened', params(s));
    close();
    try { window.tidioChatApi.open(); } catch (err) { /* Tidio errors never surface */ }
  }

  function show(name, trigger) {
    if (!remember()) return;
    page.shown = true;
    stopWatching();
    if (!ui) ui = build();
    current = {
      name: name,
      trigger: trigger,
      answer: undefined,
      answerLabel: '',
      phase: 'idle',
      closed: false,
      chatOpened: false,
      watchdog: null,
      captchaReady: null,
      restoreFocus: document.activeElement
    };
    ui.overlay.classList.toggle('rs-exit--offer', name === 'offer_abandon');
    ui.overlay.hidden = false;
    document.documentElement.classList.add('rs-exit-lock');
    document.addEventListener('keydown', onKeydown, true);
    renderQuestion(current);
    track('exit_survey_shown', params(current));
    clarity('set', 'exit_survey', name);
    clarity('event', 'exit_survey_shown');
  }

  function close() {
    var s = current;
    if (!s || s.closed) return;
    s.closed = true;
    clearTimeout(s.watchdog);
    s.watchdog = null;
    // A send in flight still reports its own outcome.
    if (s.phase !== 'sent' && s.phase !== 'posting' && !s.chatOpened) {
      track('exit_survey_dismissed', params(s));
    }
    ui.overlay.hidden = true;
    ui.sheet.style.transform = '';
    document.documentElement.classList.remove('rs-exit-lock');
    document.removeEventListener('keydown', onKeydown, true);
    var previous = s.restoreFocus;
    if (previous && previous !== document.body && document.contains(previous) &&
        typeof previous.focus === 'function') {
      try { previous.focus({ preventScroll: true }); } catch (err) { /* noop */ }
    }
  }

  /* ---- mount ------------------------------------------------------------ */

  function mount() {
    if (!document.querySelector('[data-rs-offer]')) return;

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest(ACTED_SELECTOR)) page.acted = true;
      else if (target.closest(OFFER_BADGE_SELECTOR)) page.offerTapped = true;
      else return;
      // The rug question is off for this page now, so its watchers can go.
      stopWatching();
    }, true);

    document.addEventListener('rs-offer:closed', function (event) {
      if (page.shown || (event.detail && event.detail.submitted)) return;
      clearTimeout(page.offerTimer);
      page.offerTimer = setTimeout(tryOfferQuestion, OFFER_MS);
    });

    document.addEventListener('tidioChat-open', function () { chatOpen = true; });
    document.addEventListener('tidioChat-close', function () { chatOpen = false; });

    // A returning visitor never gets the rug question, so skip its listeners.
    var keys = stored(rug().handle);
    if (keys && !keys.seen && !keys.session && !keys.offer) startWatching();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();

/* ==========================================================================
   Native saved rugs (replaces the Swym Wishlist Plus app)
   --------------------------------------------------------------------------
   The store's wishlist was a Swym app embed; its free-plan quota kept
   breaching and the heart kept disappearing (see the "hidden when disabled"
   plumbing this module retired). This is the native replacement: a
   localStorage list on a surface we own, no third-party handshake.

   Pieces:
   - Store:        localStorage 'rs-saved-rugs', one record per rug
                   ({handle,title,url,image,savedAt}). Guarded for Safari
                   private mode; cross-tab sync via the 'storage' event.
   - Account:      signed in (window.rsCustomer, set by layout/theme.liquid),
                   the list also syncs to the shopper's account through the
                   concierge App Proxy (/apps/room-concierge/saved), so it
                   follows them to every device they sign in on.
   - PDP heart:    injected into .product-meta, positioned by CSS on the
                   TITLE line (the approved option B placement). A hairline
                   heart, filled solid black once saved.
   - Tile hearts:  top-right corner of the rug in every collection tile
                   (the photo is letterboxed, so not the frame's corner).
   - Header heart: linked to /pages/saved with a count bubble; appears only
                   once the list is non-empty so first-time visitors see no
                   clutter.
   - Toast:        one-line "Saved · View your N rugs" confirmation linking
                   to the saved page, then fades.
   - Saved page:   hydrates snippets/saved-rugs-page.liquid (rendered via a
                   custom-liquid section on /pages/saved): fresh product data
                   per rug from ?view=saved-json, sold rugs stay dimmed with
                   a "See similar rugs" hand-off, a "More like your saved
                   rugs" row fed by the same similar_rugs metafield the PDP
                   block uses (?view=similar-json), and a sign-in card for
                   guests or an email opt-in for signed-in shoppers.

   Analytics: saves push add_to_wishlist_click with button_type 'wishlist' —
   the exact event/params the GTM container already tracks from the Swym era,
   so the GA series continues unbroken — plus placement and product_handle.
   Removals push remove_from_wishlist_click with the same shape.

   Why JS + no stock-Liquid edits: product-info.liquid, product-item.liquid
   and header.liquid are stock theme files; "Update from Shopify" pulls would
   overwrite edits there. Everything injects from here with idempotence
   guards, retried via MutationObserver like the sold-tile CTA injector.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'rs-saved-rugs';
  var SAVED_PAGE_URL = '/pages/saved';
  var HANDLE_RE = /\/products\/([^/?#]+)/;
  // The account copy's cap (MAX_SAVED_RUGS in inventory-sync), so a sync
  // never drops a rug this device still shows.
  var MAX_ITEMS = 200;

  // ── Store ───────────────────────────────────────────────

  // Parsed once and held, because the read is on a hot path: syncing the
  // hearts on a collection page asks "is this one saved?" per tile, and a
  // localStorage read plus a JSON.parse per tile per pass is real time on a
  // 700-rug grid. Every write goes through writeState, and a write from
  // another tab arrives on the storage event, so the two ways the list can
  // change both refresh this. Callers treat the array as read-only; the two
  // that change it (addItem, removeItem) hand the result straight back to
  // writeItems. Beside the list, the record keeps the account bookkeeping:
  // whose account it mirrors (owner), the saves and removes not yet sent
  // (pending), and when it last heard from the account (syncedAt).
  var cachedState = null;

  function hasHandle(entry) {
    return !!entry && typeof entry.handle === 'string' && entry.handle !== '';
  }

  function readState() {
    if (cachedState) return cachedState;
    var parsed = null;
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      parsed = raw ? JSON.parse(raw) : null;
    } catch (err) {
      parsed = null;
    }
    parsed = parsed || {};
    cachedState = {
      items: Array.isArray(parsed.items) ? parsed.items.filter(hasHandle) : [],
      owner: typeof parsed.owner === 'string' ? parsed.owner : null,
      pending: Array.isArray(parsed.pending) ? parsed.pending.filter(hasHandle) : [],
      syncedAt: typeof parsed.syncedAt === 'number' ? parsed.syncedAt : 0
    };
    return cachedState;
  }

  function readItems() {
    return readState().items;
  }

  // quiet: the list itself did not change, so nothing needs repainting.
  function writeState(state, quiet) {
    cachedState = state;
    var payload = { v: 1, items: state.items };
    if (state.owner) payload.owner = state.owner;
    if (state.pending.length) payload.pending = state.pending;
    if (state.syncedAt) payload.syncedAt = state.syncedAt;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      /* Safari private mode / quota — the in-page state still works */
    }
    if (!quiet) document.dispatchEvent(new CustomEvent('rs-saved:change'));
  }

  // op: the save or remove behind this change, queued for the account when
  // the shopper is signed in.
  function writeItems(items, op) {
    var state = readState();
    writeState({
      items: items,
      owner: state.owner,
      pending: op && CUSTOMER_ID ? queueOp(state.pending, op) : state.pending,
      syncedAt: state.syncedAt
    });
    if (op) scheduleSync(SYNC_DEBOUNCE_MS);
  }

  function hasItem(handle) {
    return readItems().some(function (item) { return item.handle === handle; });
  }

  function pushEvent(eventName, placement, handle) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      button_type: 'wishlist',
      placement: placement,
      product_handle: handle,
      page_url: window.location.href,
      page_path: window.location.pathname
    });
  }

  function addItem(item, placement) {
    if (!item || !item.handle || hasItem(item.handle)) return;
    var savedAt = Date.now();
    var items = [{
      handle: item.handle,
      title: item.title || '',
      url: item.url || '/products/' + item.handle,
      image: item.image || '',
      savedAt: savedAt
    }].concat(readItems()).slice(0, MAX_ITEMS);
    writeItems(items, makeOp('add', item.handle, savedAt));
    pushEvent('add_to_wishlist_click', placement, item.handle);
    // No toast on the saved page itself — the list updating IS the feedback.
    if (window.location.pathname.indexOf(SAVED_PAGE_URL) !== 0) {
      showToast(items.length);
    }
  }

  function removeItem(handle, placement) {
    var items = readItems().filter(function (item) { return item.handle !== handle; });
    writeItems(items, makeOp('remove', handle));
    pushEvent('remove_from_wishlist_click', placement, handle);
  }

  // ── Account sync ────────────────────────────────────────

  // layout/theme.liquid sets { id } when signed in and null when not. Left
  // undefined (a page outside that layout), the list is never synced or
  // cleared.
  var shopper = window.rsCustomer;
  var CUSTOMER_ID = shopper && shopper.id ? String(shopper.id) : null;
  // Same shape as the other concierge endpoints: relative so the App Proxy
  // signs it in production, the local server in dev.
  var SYNC_URL = window.RUG_SAVED_ENDPOINT ||
    ((location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://127.0.0.1:8787/apps/room-concierge/saved'
      : '/apps/room-concierge/saved');
  // The concierge's per-request cap (MAX_OPS in inventory-sync); the rest
  // waits for the next round.
  var MAX_SYNC_OPS = 400;
  var SYNC_DEBOUNCE_MS = 1000;
  var SYNC_STALE_MS = 5 * 60 * 1000;

  var syncTimer = null;
  var syncInFlight = false;
  var syncQueued = false;
  // True once the account has answered (or failed to) on this page load.
  var accountSettled = !CUSTOMER_ID;
  var opSerial = 0;

  // The id marks which queued ops a sync carried, so an answer clears exactly
  // those and keeps whatever was queued while it was on the wire.
  function makeOp(op, handle, at) {
    opSerial += 1;
    var entry = {
      id: Date.now().toString(36) + '.' + opSerial + '.' + Math.random().toString(36).slice(2, 8),
      op: op,
      handle: handle
    };
    if (op === 'add') entry.at = at;
    return entry;
  }

  // One rug's ops collapse as they queue, so a long offline spell stays
  // bounded: a remove supersedes what came before it, and a save after a
  // remove goes as remove + add so the account takes the new save time.
  function queueOp(pending, op) {
    var last = null;
    var rest = pending.filter(function (entry) {
      if (entry.handle !== op.handle) return true;
      last = entry;
      return false;
    });
    if (op.op === 'add' && last && last.op === 'add') return pending;
    if (op.op === 'add' && last) rest.push(makeOp('remove', op.handle));
    rest.push(op);
    return rest;
  }

  function postSaved(body) {
    return fetch(SYNC_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('http ' + res.status);
      return res.json();
    });
  }

  function scheduleSync(delay) {
    if (!CUSTOMER_ID) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncAccount, delay);
  }

  function syncAccount() {
    if (!CUSTOMER_ID) return;
    if (syncInFlight) {
      syncQueued = true;
      return;
    }
    var state = readState();
    if (state.owner !== CUSTOMER_ID) {
      // First sync for this account on this device: the list here goes up as
      // saves, so signing in merges it into the account instead of losing it.
      state = {
        items: state.items,
        owner: null,
        pending: state.items
          .map(function (item) { return makeOp('add', item.handle, item.savedAt); })
          .concat(state.pending)
          .reduce(queueOp, []),
        syncedAt: state.syncedAt
      };
      writeState(state, true);
    }
    var batch = state.pending.slice(0, MAX_SYNC_OPS);
    var ok = false;
    syncInFlight = true;
    postSaved({
      ops: batch.map(function (entry) {
        return entry.op === 'add'
          ? { op: 'add', handle: entry.handle, at: entry.at }
          : { op: 'remove', handle: entry.handle };
      })
    })
      .then(function (body) {
        if (!body || !Array.isArray(body.items)) throw new Error('unexpected answer');
        applyAccountList(body.items, batch);
        ok = true;
      })
      .catch(function () {
        // Signed out server-side, rate limited or unreachable: the list keeps
        // working on this device and the queue waits for the next sync.
      })
      .then(function () {
        syncInFlight = false;
        accountSettled = true;
        var again = syncQueued || (ok && readState().pending.length > 0);
        syncQueued = false;
        document.dispatchEvent(new CustomEvent('rs-saved:synced'));
        if (again) syncAccount();
      });
  }

  // The account's list becomes this device's, keeping this device's titles
  // and photos, with anything queued after the batch left laid back on top.
  function applyAccountList(entries, batch) {
    var state = readState();
    var sent = {};
    batch.forEach(function (entry) { sent[entry.id] = true; });
    var pending = state.pending.filter(function (entry) { return !sent[entry.id]; });
    var known = {};
    state.items.forEach(function (item) { known[item.handle] = item; });

    function record(handle, savedAt, title) {
      var mine = known[handle];
      return {
        handle: handle,
        title: (mine && mine.title) || title || '',
        url: (mine && mine.url) || '/products/' + handle,
        image: (mine && mine.image) || '',
        savedAt: savedAt
      };
    }

    var items = entries
      .filter(hasHandle)
      .map(function (entry) { return record(entry.handle, Number(entry.savedAt) || 0, entry.title); });
    pending.forEach(function (entry) {
      items = items.filter(function (item) { return item.handle !== entry.handle; });
      if (entry.op === 'add') items.push(record(entry.handle, entry.at));
    });
    items.sort(function (a, b) { return b.savedAt - a.savedAt; });
    items = items.slice(0, MAX_ITEMS);

    var same = items.length === state.items.length && items.every(function (item, i) {
      return item.handle === state.items[i].handle;
    });
    writeState({ items: items, owner: CUSTOMER_ID, pending: pending, syncedAt: Date.now() }, same);
  }

  function bootAccount() {
    var state = readState();
    if (shopper === null) {
      // Signed out: an account's list must not stay behind on a shared
      // computer. A list that never reached an account stays, as a guest's.
      if (state.owner) {
        writeState({ items: [], owner: null, pending: [], syncedAt: 0 });
      } else if (state.pending.length) {
        writeState({ items: state.items, owner: null, pending: [], syncedAt: 0 }, true);
      }
      return;
    }
    if (!CUSTOMER_ID) return;
    if (state.owner && state.owner !== CUSTOMER_ID) {
      // Another account's list: this one's comes down from the server.
      writeState({ items: [], owner: null, pending: [], syncedAt: 0 });
      state = readState();
    }
    // The saved page always asks: it is where a rug removed on another device
    // would otherwise linger.
    var savedPage = !!document.querySelector('[data-rs-saved-page]');
    var age = Date.now() - state.syncedAt;
    if (state.owner !== CUSTOMER_ID || state.pending.length > 0 || savedPage ||
        age < 0 || age > SYNC_STALE_MS) {
      syncAccount();
    } else {
      accountSettled = true;
    }
  }

  // Shared with the saved-page module below and asserted by tests.
  window.rsSavedRugs = {
    list: readItems,
    has: hasItem,
    add: addItem,
    remove: removeItem,
    count: function () { return readItems().length; }
  };

  // ── Heart button ────────────────────────────────────────

  function heartLabel(saved, title) {
    if (saved) return title ? 'Remove ' + title + ' from your saved rugs' : 'Remove from your saved rugs';
    return title ? 'Save ' + title : 'Save this rug';
  }

  function applyHeartState(btn) {
    var saved = hasItem(btn.dataset.handle);
    btn.classList.toggle('rs-heart--saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', heartLabel(saved, btn.dataset.title || ''));
  }

  function makeHeart(item, extraClass, placement) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rs-heart ' + extraClass;
    btn.dataset.handle = item.handle;
    btn.dataset.title = item.title || '';
    btn.addEventListener('click', function (e) {
      // Tile hearts sit inside/over anchors; a save must never navigate.
      e.preventDefault();
      e.stopPropagation();
      if (hasItem(item.handle)) removeItem(item.handle, placement);
      else addItem(item, placement);
    });
    applyHeartState(btn);
    return btn;
  }

  function syncAllHearts() {
    document.querySelectorAll('.rs-heart[data-handle]').forEach(applyHeartState);
    syncHeaderHearts();
  }

  // ── PDP heart (option B: the title line) ────────────────

  function getPdpProduct() {
    // h1 discriminates the real PDP from featured-product sections, which
    // render the same .product-meta markup with an h2 title.
    var title = document.querySelector('.product-meta h1.product-meta__title');
    if (!title) return null;
    var canonical = document.querySelector('link[rel="canonical"]');
    var og = document.querySelector('meta[property="og:url"]');
    var match =
      HANDLE_RE.exec((canonical && canonical.href) || '') ||
      HANDLE_RE.exec((og && og.content) || '') ||
      HANDLE_RE.exec(window.location.pathname);
    if (!match) return null;
    var handle = match[1].toLowerCase();
    var image = document.querySelector('meta[property="og:image"]');
    return {
      handle: handle,
      title: (title.textContent || '').trim(),
      url: '/products/' + handle,
      image: (image && image.content) || ''
    };
  }

  function injectPdpHeart() {
    var meta = document.querySelector('.product-meta');
    if (!meta || meta.querySelector('.rs-heart--pdp')) return;
    var item = getPdpProduct();
    if (!item) return;
    // CSS anchors it to the title line: .product-meta goes position:relative,
    // the heart sits absolute top-right, and the h1 gets right padding so a
    // two-line serif title wraps clear of it instead of pushing it around.
    meta.appendChild(makeHeart(item, 'rs-heart--pdp', 'pdp'));
  }

  // ── Collection tile hearts ──────────────────────────────

  function injectTileHearts() {
    document.querySelectorAll('product-item').forEach(function (tile) {
      if (tile.dataset.rsHeartInjected === '1') return;
      var wrapper = tile.querySelector('.product-item__image-wrapper');
      var titleLink = tile.querySelector('a.product-item-meta__title');
      if (!wrapper || !titleLink) return;
      var match = HANDLE_RE.exec(titleLink.getAttribute('href') || '');
      if (!match) return;
      var handle = match[1].toLowerCase();
      var img = tile.querySelector('.product-item__primary-image');
      tile.dataset.rsHeartInjected = '1';
      wrapper.appendChild(makeHeart({
        handle: handle,
        title: (titleLink.textContent || '').trim(),
        url: '/products/' + handle,
        image: (img && (img.currentSrc || img.src)) || ''
      }, 'rs-heart--tile', 'collection_tile'));
      watchTileHeart(wrapper, img);
    });
  }

  // Tile photos are letterboxed (object-fit: contain in a landscape frame),
  // so the frame's corner can sit in blank space beside a portrait rug. The
  // heart follows the rug's own top-right corner, via two CSS variables.
  var TILE_HEART_GAP = 8;

  // Reads layout only, so a batch of tiles measures before any style write.
  function measureTileHeart(wrapper) {
    var heart = wrapper.querySelector('.rs-heart--tile');
    var img = wrapper.querySelector('.product-item__primary-image');
    if (!heart || !img) return null;
    var boxW = img.clientWidth;
    var boxH = img.clientHeight;
    var natW = Number(img.getAttribute('width')) || img.naturalWidth;
    var natH = Number(img.getAttribute('height')) || img.naturalHeight;
    var insetX = 0;
    var insetY = 0;
    var beside = false;
    if (boxW && boxH && natW && natH && window.getComputedStyle(img).objectFit === 'contain') {
      var wrapRect = wrapper.getBoundingClientRect();
      var imgRect = img.getBoundingClientRect();
      var scale = Math.min(boxW / natW, boxH / natH);
      var rugW = natW * scale;
      insetX = (wrapRect.right - imgRect.right) + (boxW - rugW) / 2;
      insetY = (imgRect.top - wrapRect.top) + (boxH - natH * scale) / 2;
      // A runner can be narrower than the heart: sit just beside it, if there is room.
      var besideX = insetX - heart.offsetWidth - 2 * TILE_HEART_GAP;
      beside = rugW < heart.offsetWidth * 2 && besideX >= 0;
      if (beside) insetX = besideX;
    }
    return { heart: heart, x: insetX, y: insetY, beside: beside };
  }

  function applyTileHeart(place) {
    if (!place) return;
    place.heart.style.setProperty('--rs-rug-inset-x', place.x.toFixed(1) + 'px');
    place.heart.style.setProperty('--rs-rug-inset-y', place.y.toFixed(1) + 'px');
    // Off the photo, on the page: the stylesheet gives it the page's colour.
    place.heart.classList.toggle('rs-heart--beside', place.beside);
  }

  function placeTileHeart(wrapper) {
    applyTileHeart(measureTileHeart(wrapper));
  }

  var tileResizeObserver = window.ResizeObserver
    ? new ResizeObserver(function (entries) {
      var places = [];
      entries.forEach(function (entry) {
        // Filtering and paging swap the tiles out; let the old ones go.
        if (!entry.target.isConnected) tileResizeObserver.unobserve(entry.target);
        else places.push(measureTileHeart(entry.target));
      });
      places.forEach(applyTileHeart);
    })
    : null;

  function watchTileHeart(wrapper, img) {
    // The observer's first callback does the initial placement.
    if (tileResizeObserver) tileResizeObserver.observe(wrapper);
    else placeTileHeart(wrapper);
    // Without width/height attributes the ratio is only known once loaded.
    if (img && !img.getAttribute('width') && !img.complete) {
      img.addEventListener('load', function () { placeTileHeart(wrapper); }, { once: true });
    }
  }

  // ── Header heart with count ─────────────────────────────

  function injectHeaderHearts() {
    // The right-hand list only. The header has a second icon list (the phone
    // menu and search buttons, inside the inline navigation) and a heart there
    // lands beside the last desktop menu item.
    document.querySelectorAll('.header__secondary-links .header__icon-list').forEach(function (list) {
      if (list.querySelector('.rs-heart-header')) return;
      var link = document.createElement('a');
      link.href = SAVED_PAGE_URL;
      link.className = 'header__icon-wrapper tap-area rs-heart-header';
      link.setAttribute('aria-label', 'Your saved rugs');
      link.innerHTML =
        '<span class="rs-heart-header__icon" aria-hidden="true"></span>' +
        '<span class="rs-heart-header__count bubble-count" aria-hidden="true"></span>';
      var cart = list.querySelector('a[href*="/cart"]');
      if (cart) list.insertBefore(link, cart);
      else list.appendChild(link);
    });
    syncHeaderHearts();
  }

  function syncHeaderHearts() {
    var count = readItems().length;
    document.querySelectorAll('.rs-heart-header').forEach(function (link) {
      link.classList.toggle('rs-heart-header--active', count > 0);
      var bubble = link.querySelector('.rs-heart-header__count');
      // Change-gated: a textContent write fires the MutationObserver that
      // re-runs this module even when the value is identical — an
      // unconditional write here is an infinite loop that hangs the page
      // (same lesson as syncSoldOutState above).
      if (bubble && bubble.textContent !== String(count)) {
        bubble.textContent = String(count);
      }
    });
  }

  // ── Toast ───────────────────────────────────────────────

  var toastTimer = null;

  function showToast(count) {
    var toast = document.querySelector('.rs-saved-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'rs-saved-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        '<span class="rs-saved-toast__heart" aria-hidden="true">&hearts;</span>' +
        '<span>Saved &middot;&nbsp;</span>' +
        '<a class="rs-saved-toast__link" href="' + SAVED_PAGE_URL + '"></a>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.rs-saved-toast__link').textContent =
      count === 1 ? 'View your saved rug' : 'View your ' + count + ' rugs';
    toast.classList.add('rs-saved-toast--visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('rs-saved-toast--visible');
    }, 4000);
  }

  // ── Saved page (/pages/saved) ───────────────────────────

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var amount = cents / 100;
    var formatted = amount % 1 === 0
      ? amount.toLocaleString('en-US')
      : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var themeFormat =
      window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.moneyFormat;
    if (themeFormat && themeFormat.indexOf('{{') !== -1) {
      return themeFormat.replace(/\{\{\s*[\w_]+\s*\}\}/g, formatted).replace(/<[^>]*>/g, '').trim();
    }
    return '$' + formatted;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function fetchProduct(item) {
    // templates/product.saved-json.liquid — six fields, not the whole rug,
    // and a theme route rather than /products/<handle>.js, which the
    // `shopify theme dev` proxy refuses. See that template's comment.
    return fetch('/products/' + encodeURIComponent(item.handle) + '?view=saved-json')
      .then(function (res) {
        // 404/410 = unpublished/removed for good: the row renders from the
        // stored snapshot as sold, with no links — its PDP is gone.
        if (res.status === 404 || res.status === 410) return null;
        if (!res.ok) throw new Error('http ' + res.status);
        return res.json().then(function (product) {
          return {
            handle: item.handle,
            title: product.title,
            url: product.url || '/products/' + item.handle,
            image: (product.image || ''),
            price: product.price,
            available: product.available === true
          };
        });
      })
      .catch(function () {
        // Transient failure (network, 5xx): render the stored snapshot as a
        // live linked row (no price) rather than mislabeling the rug
        // "No longer available".
        return {
          handle: item.handle,
          title: item.title,
          url: item.url || '/products/' + item.handle,
          image: item.image || '',
          price: null,
          available: true
        };
      });
  }

  function renderRow(item, live) {
    var gone = live === null;
    var data = gone ? item : live;
    var sold = gone || !live.available;
    var row = el('div', 'rs-saved-row' + (sold ? ' rs-saved-row--sold' : ''));
    row.dataset.handle = item.handle;

    var thumbWrap = el(gone ? 'span' : 'a', 'rs-saved-row__thumb');
    if (!gone) thumbWrap.href = data.url;
    if (data.image) {
      var img = document.createElement('img');
      img.src = data.image;
      img.alt = data.title || '';
      img.loading = 'lazy';
      thumbWrap.appendChild(img);
    }
    row.appendChild(thumbWrap);

    var info = el('div', 'rs-saved-row__info');
    var title = el(gone ? 'span' : 'a', 'rs-saved-row__title', data.title || item.handle);
    if (!gone) title.href = data.url;
    info.appendChild(title);

    var metaLine = el('div', 'rs-saved-row__meta');
    if (sold) {
      metaLine.appendChild(el('span', 'rs-saved-row__sold-chip', 'Sold'));
      if (!gone) {
        // Hands the shopper to the similarity engine already live on the
        // PDP — the #similar-rugs hash scrolls to "Others you might love".
        var similar = el('a', 'rs-saved-row__see-similar', 'See similar rugs');
        similar.href = data.url + '#similar-rugs';
        metaLine.appendChild(similar);
      } else {
        metaLine.appendChild(el('span', 'rs-saved-row__gone-note', 'No longer available'));
      }
    } else {
      metaLine.textContent = formatMoney(data.price);
    }
    info.appendChild(metaLine);
    row.appendChild(info);

    var remove = el('button', 'rs-saved-row__remove');
    remove.type = 'button';
    remove.setAttribute('aria-label', 'Remove ' + (data.title || item.handle) + ' from your saved rugs');
    remove.innerHTML = '&#10005;';
    remove.addEventListener('click', function () {
      // The rs-saved:change listener wired at boot re-hydrates the list.
      removeItem(item.handle, 'saved_page');
    });
    row.appendChild(remove);
    return row;
  }

  function renderSimilarTile(entry) {
    var tile = document.createElement('product-item');
    tile.className = 'product-item';
    var wrapper = el('div', 'product-item__image-wrapper');
    var imgLink = el('a', 'product-item__aspect-ratio aspect-ratio');
    imgLink.href = entry.url;
    if (entry.image) {
      var img = document.createElement('img');
      img.className = 'product-item__primary-image';
      img.src = entry.image;
      img.alt = entry.title || '';
      img.loading = 'lazy';
      imgLink.appendChild(img);
    }
    wrapper.appendChild(imgLink);
    tile.appendChild(wrapper);

    var info = el('div', 'product-item__info');
    var meta = el('div', 'product-item-meta');
    var title = el('a', 'product-item-meta__title', entry.title || '');
    title.href = entry.url;
    meta.appendChild(title);
    info.appendChild(meta);
    tile.appendChild(info);
    // The tile-heart injector picks this up on its next MutationObserver
    // pass (same markup contract as a collection tile), so these tiles get
    // save hearts too.
    return tile;
  }

  function fetchSimilar(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?view=similar-json')
      .then(function (res) {
        if (!res.ok) throw new Error('no view');
        return res.json();
      })
      .then(function (data) { return Array.isArray(data.similar) ? data.similar : []; })
      .catch(function () { return []; });
  }

  function renderSimilarRow(page, items) {
    var section = page.querySelector('[data-rs-saved-similar]');
    if (!section) return;
    var grid = section.querySelector('[data-rs-saved-similar-grid]');
    var savedHandles = {};
    items.forEach(function (item) { savedHandles[item.handle] = true; });

    // Seed from the newest saves; four PDPs' worth of the similar_rugs
    // metafield is plenty of candidates for one row.
    var seeds = items.slice(0, 4).map(function (item) { return item.handle; });
    Promise.all(seeds.map(fetchSimilar)).then(function (lists) {
      var seen = {};
      var picks = [];
      lists.forEach(function (list) {
        list.forEach(function (entry) {
          if (!entry || !entry.handle || seen[entry.handle]) return;
          if (savedHandles[entry.handle] || entry.available !== true) return;
          seen[entry.handle] = true;
          picks.push(entry);
        });
      });
      picks.slice(0, 8).forEach(function (entry) {
        grid.appendChild(renderSimilarTile(entry));
      });
      if (picks.length > 0) section.removeAttribute('hidden');
    });
  }

  // Bumped per hydration, so a slow round of product fetches never paints
  // over a newer list.
  var hydration = 0;

  function hydrateSavedPage(page) {
    var items = readItems();
    var round = ++hydration;
    var listNode = page.querySelector('[data-rs-saved-list]');
    var countNode = page.querySelector('[data-rs-saved-count]');
    var emptyNode = page.querySelector('[data-rs-saved-empty]');
    var fullNodes = page.querySelectorAll('[data-rs-saved-full]');

    // Signed in on a fresh device: the list is still on its way down, so
    // "Nothing saved yet" would be a flash of the wrong answer.
    var waiting = items.length === 0 && !accountSettled;
    page.dataset.rsAwaitingAccount = waiting ? '1' : '';
    if (waiting) {
      if (countNode) countNode.hidden = true;
      if (emptyNode) emptyNode.hidden = true;
      fullNodes.forEach(function (node) { node.hidden = true; });
      return;
    }

    if (countNode) {
      // No "0 rugs" beside a heading whose empty state already says so.
      countNode.textContent = items.length === 1 ? '1 rug' : items.length + ' rugs';
      countNode.hidden = items.length === 0;
    }
    if (emptyNode) emptyNode.hidden = items.length > 0;
    fullNodes.forEach(function (node) { node.hidden = items.length === 0; });
    if (!listNode) return;
    if (items.length === 0) {
      listNode.textContent = '';
      var similarSection = page.querySelector('[data-rs-saved-similar]');
      if (similarSection) similarSection.hidden = true;
      return;
    }

    Promise.all(items.map(fetchProduct))
      .then(function (lives) {
        if (round !== hydration) return;
        listNode.textContent = '';
        items.forEach(function (item, i) { listNode.appendChild(renderRow(item, lives[i])); });
      });

    if (page.dataset.rsHydrated === '1') return;
    page.dataset.rsHydrated = '1';

    // First hydration only: later list changes don't invalidate the
    // suggestions, and re-fetching them on every save/remove would churn.
    renderSimilarRow(page, items);
  }

  // The signed-in card's "Email me new arrivals": an explicit click is the
  // consent, recorded on the shopper's Shopify customer by the concierge.
  function wireOptIn(page) {
    var box = page.querySelector('[data-rs-saved-optin]');
    if (!box || !CUSTOMER_ID) return;
    var button = box.querySelector('[data-rs-saved-optin-button]');
    var status = box.querySelector('[data-rs-saved-optin-status]');
    if (!button || !status) return;
    button.addEventListener('click', function () {
      button.disabled = true;
      status.textContent = '';
      postSaved({ marketing: true })
        .then(function (body) { return body && body.marketing; }, function () { return null; })
        .then(function (outcome) {
          var joined = outcome === 'subscribed' || outcome === 'already_subscribed';
          // Pending confirmation or an address Shopify will not mail: another
          // click cannot change that.
          var stuck = outcome === 'left_unchanged';
          if (joined) {
            status.textContent = 'You are on the list. Watch your inbox.';
          } else if (stuck) {
            status.textContent = 'We could not sign you up from here. Contact us and we will add you.';
          } else {
            status.textContent = 'We could not sign you up just now. Please try again in a moment.';
          }
          button.hidden = joined || stuck;
          button.disabled = false;
        });
    });
  }

  // ── Boot ────────────────────────────────────────────────

  function run() {
    injectPdpHeart();
    injectTileHearts();
    injectHeaderHearts();
    var page = document.querySelector('[data-rs-saved-page]');
    if (page && page.dataset.rsBooted !== '1') {
      page.dataset.rsBooted = '1';
      hydrateSavedPage(page);
      // Any save/remove on this page (row ✕, a heart on a similar tile,
      // another tab via the storage relay, the account's list arriving)
      // refreshes the list.
      document.addEventListener('rs-saved:change', function () { hydrateSavedPage(page); });
      // An account answer that changed nothing is quiet, so a list held back
      // for it is released here.
      document.addEventListener('rs-saved:synced', function () {
        if (page.dataset.rsAwaitingAccount === '1') hydrateSavedPage(page);
      });
      wireOptIn(page);
    }
  }

  function start() {
    bootAccount();
    run();
  }

  // One pass per frame, however many mutations arrived in it. The pass walks
  // every product-item on the page, and the surfaces this observer exists for
  // arrive as bursts, not single nodes: Cloud Search re-renders a whole grid
  // on a filter change, Focal's <product-rerender> swaps the price and
  // custom-liquid blocks on every variant change, the offers widget repaints
  // its panel on every poll. Running the pass per batch made those bursts
  // measurably slower (it delayed the offers widget's re-render past its own
  // poll interval); coalescing keeps the cost to once a frame while still
  // catching the same DOM. The pass is idempotent, so a frame that changed
  // nothing relevant costs three guarded queries.
  var passQueued = false;

  function queueRun() {
    if (passQueued) return;
    passQueued = true;
    var schedule = window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : function (fn) { return setTimeout(fn, 16); };
    schedule(function () {
      passQueued = false;
      run();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  if (window.MutationObserver) {
    new MutationObserver(queueRun).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('rs-saved:change', syncAllHearts);
  // Another tab changed the list: drop the cache so the next read reparses,
  // then funnel through the same change event so hearts, the header count,
  // and the saved page all refresh from one path.
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    cachedState = null;
    document.dispatchEvent(new CustomEvent('rs-saved:change'));
  });
})();
